/**
 * Documentation Translations
 * 
 * This file contains all translations for the documentation module.
 * Separated from main i18n.js for better maintainability.
 * 
 * Structure:
 * - documentation.sidebar: Navigation menu items
 * - documentation.[pageName]: Page-specific translations
 */

export const docTranslations = {
  en: {
    documentation: {
      // Sidebar Navigation
      sidebar: {
        backToSurvey: "Back to Survey",
        survey: "Survey",
        analytics: "Analytics",
        activityLog: "Activity log",
        tickets: "Tickets",
        settings: "Settings",
        // Survey submenu
        surveyList: "Survey List",
        qandaBuilder: "Q & A Builder",
        formBuilder: "Form Builder",
        reportBuilder: "Report Builder",
        emailTemplates: "Email Templates",
        variables: "Variables",
        publishShare: "Publish & Share",
        integration: "Integration",
        webhooks: "Webhooks",
        questionnaire: "Questionnaire",
        thankYouPage: "Thank You Page",
        leadErasureRules: "Lead Erasure Rules",
        privacyPolicy: "Privacy Policy",
        cookiePolicy: "Cookie Policy",
        createSurvey: "Create Survey",
        recommendations: "Recommendations",
        surveyGroups: "Survey Groups",
        surveySync: "Survey Sync",
        // Settings submenu
        profile: "Profile",
        security: "Security",
        notifications: "Notifications",
        organizationDetails: "Organization details",
        users: "Users",
        roles: "Roles",
        teams: "Teams",
        settingsGeneral: "General Settings",
        settingsUserManagement: "User Management",
        settingsOrganizationDetails: "Organization Details",
        privacyPolicy: "Privacy policy",
        termsOfUse: "Terms of use",
        cookiePolicy: "Cookie policy",
      },

      settingsMenu: {
        intro: "When you open Settings, the nested menu is organized as follows:",
        generalSettings: "General settings",
        profile: "Profile",
        security: "Security",
        notifications: "Notifications",
        userManagement: "User management",
        users: "Users",
        roles: "Roles",
        teams: "Teams",
        organizationDetails: "Organization details",
        privacyPolicy: "Privacy policy",
        termsOfUse: "Terms of use",
        cookiePolicy: "Cookie policy",
      },

      // Survey Sync Page
      surveySync: {
        title: "Survey Sync",
        intro: "The Survey Sync functionality allows administrators to synchronize surveys from the UAT (User Acceptance Testing) environment to the Production environment.",
        
        syncFromUat: {
          title: "Sync From UAT",
          whenToUse: "Use this feature when a survey exists in UAT but is <b>NOT present in Production/Live</b>. This is primarily used for deploying new surveys from UAT to Production.",
          stepsTitle: "Steps:",
          steps: {
            step1: "Navigate to <b>Survey → Surveys</b> from the left sidebar menu",
            step2: "On the Survey List page, click the <b>\"Sync from UAT\"</b> button located at the top-right (next to \"Add to group\" button)",
            step3: "A modal titled <b>\"Sync from UAT\"</b> will open, displaying all surveys available in UAT",
            step4: "Select <b>ONE survey</b> from the list using the radio button (only single selection allowed)",
            step5: "Click <b>\"Sync\"</b> to copy the selected survey from UAT to Production",
            step6: "Click <b>\"Cancel\"</b> to close without syncing",
          },
          note: "This option copies the complete survey (with all questions, sections, variables, etc.) from UAT to Production. It's ideal for initial deployment of new surveys.",
          imageAlt: "Sync from UAT button location",
        },

        fullReplacement: {
          title: "Full Replacement Sync",
          whenToUse: "Use this when a survey <b>already exists in both UAT and Production</b>, and you want to completely replace the Production version with the UAT version.",
          stepsTitle: "Steps:",
          steps: {
            step1: "Navigate to <b>Survey → Surveys</b> from the left sidebar menu",
            step2: "Find the survey in the list and click the <b>Actions menu</b> (three dots) on the right",
            step3: "Select <b>\"Sync from UAT\"</b> from the dropdown",
            step4: "In the modal, select <b>\"Full Replacement\"</b> option",
            step5: "Click <b>\"Sync\"</b> to replace Production survey with UAT version",
          },
          safetyTitle: "Safety Features:",
          safety: {
            backup: "A version backup is automatically created before replacement",
            responses: "User responses and lead data are never affected",
            rollback: "You can rollback using Version History if needed",
          },
          imageAlt: "Full replacement sync option",
        },

        mergeSync: {
          title: "Merge Sync",
          whenToUse: "Use this when you want to <b>add new items from UAT</b> while preserving existing Production items. Ideal for incremental updates.",
          stepsTitle: "Steps:",
          steps: {
            step1: "Navigate to <b>Survey → Surveys</b> from the left sidebar menu",
            step2: "Find the survey in the list and click the <b>Actions menu</b> (three dots) on the right",
            step3: "Select <b>\"Sync from UAT\"</b> from the dropdown",
            step4: "In the modal, select <b>\"Merge\"</b> option",
            step5: "Click <b>\"Next\"</b> to preview changes before applying",
            step6: "Review the merge preview showing:",
            step7: "Click <b>\"Accept & Sync\"</b> to apply the merge, or <b>\"Cancel\"</b> to abort",
          },
          previewItems: {
            newItems: "<b>New Items (Green):</b> Items from UAT that will be added",
            conflicts: "<b>Conflicts (Yellow):</b> Items with same UUID but different content - UAT version will replace",
            duplicates: "<b>Duplicates (Gray):</b> Items with same content but different UUIDs - both kept",
          },
          safetyTitle: "Safety Features:",
          safety: {
            preview: "Preview changes before applying",
            backup: "Version backup created before merge",
            atomic: "Atomic operation - all changes succeed or all rollback",
            preserved: "Production-only items are preserved",
          },
          noChangesTitle: "Understanding \"No changes to merge\"",
          noChangesDesc: "If the preview shows: <i>\"No changes to merge. Survey is already in sync.\"</i>",
          noChangesItems: {
            identical: "This means UAT and Production are identical",
            noNew: "No new items, conflicts, or duplicates were found",
            safe: "You can safely close the modal - no sync is needed",
          },
          imageAlt1: "Merge option selection",
          imageAlt2: "Merge preview screen",
        },

        versionHistory: {
          title: "Version History & Rollback",
          intro: "Every Full Replacement and Merge operation creates a version backup, allowing rollback if needed.",
          accessTitle: "To access Version History:",
          accessSteps: {
            step1: "Click the <b>Actions menu</b> (three dots) on any survey",
            step2: "Select <b>\"Version History\"</b>",
            step3: "View all previous versions with dates and types",
            step4: "Click <b>\"Restore\"</b> on any version to rollback",
          },
          rollbackTitle: "How to Rollback to a Previous Version",
          rollbackSteps: {
            step1: "Open Version History modal from survey Actions menu",
            step2: "Review the version list and identify the version you want to restore",
            step3: "Click the <b>\"Restore\"</b> button for that version",
            step4: "The system:",
            step4Items: {
              backup: "Creates a new version backup of the current state (before restore)",
              restore: "Restores all survey components from the selected version",
              maintain: "Maintains survey ID (existing links continue working)",
            },
            step5: "Success message appears:",
            step5Message: "\"Restored to v3! Backup v7 created.\"",
            step6: "Your survey is now restored to the selected version",
          },
          note: "Version History only tracks survey structure and settings. User responses and lead data are never modified during sync or restore operations.",
          imageAlt: "Version history modal",
        },
      },

      // Email Templates Page
      emailTemplates: {
        title: "Email Templates",
        intro: "The Email Templates page lets administrators customize the report notification email sent to respondents when their survey report is ready. Each survey has its own template with a live preview and a style panel.",
        accessing: {
          title: "Accessing the Email Templates Page",
          imageAlt: "Survey sidebar showing Email templates navigation link",
          steps: {
            step1: "Open a survey from the <b>Survey List</b> so that a <b>survey_id</b> is active in the URL.",
            step2: "In the left sidebar, click <b>Email templates</b> (requires <b>M14 view</b> permission).",
            step3: "The page opens at <b>/admin/survey/email-template?survey_id={id}</b>.",
          },
        },
        overview: {
          title: "Overview",
          imageAlt: "Email Templates editor with live preview and style panel",
          desc: "The editor is split into two panes: a live WYSIWYG email preview on the left and a style/configuration panel on the right. Changes in either pane update the preview in real time.",
        },
        enableDisable: {
          title: "Enable / Disable Report Emails",
          imageAlt: "Email Templates page header with report email toggle",
          items: {
            item1: "Use the toggle in the page header to enable or disable report notification emails for this survey.",
            item2: "When disabled, emails are not sent but your custom template content is retained.",
            item3: "Toggling requires <b>M14 edit</b> permission.",
          },
        },
        editingContent: {
          title: "Editing Email Content",
          intro: "Click directly in the preview pane to edit text, or use the subject field in the right panel:",
          items: {
            subject: "Email subject — set at the top of the style panel.",
            heading: "Heading — main title of the email.",
            description: "Description — body text below the heading.",
            button: "Button text — label on the report download CTA.",
            securityNote: "Security note — disclaimer about link security.",
            assistanceNote: "Assistance note — help text for recipients.",
            signoff: "Sign-off — closing line (supports {{organizationName}}).",
            footer: "Footer — bottom text (supports {{supportEmail}}).",
          },
        },
        styling: {
          title: "Styling the Template",
          imageAlt: "Email Templates style accordion panel",
          intro: "Expand accordion sections in the right panel to customize appearance:",
          sections: {
            general: "General — email background, card background, font family, card padding.",
            heading: "Heading — color and font size.",
            description: "Description — color and font size.",
            button: "Button — background, text color, section background, radius, height, width, and font size.",
            notes: "Notes & assistance — colors and sizes for security and assistance notes.",
            signoff: "Sign-off — color and font size.",
            footer: "Footer — color, font size, and border color.",
          },
        },
        placeholders: {
          title: "Placeholders",
          imageAlt: "Placeholders accordion with copyable tokens",
          intro: "Optional dynamic tokens can be inserted into text fields. Open the Placeholders accordion and click a pill to copy it, then paste into any preview field:",
          items: {
            orgName: "<b>{{organizationName}}</b> — replaced with the respondent's organization name.",
            supportEmail: "<b>{{supportEmail}}</b> — replaced with your support email address.",
            reportUrl: "<b>{{reportUrl}}</b> — automatically used for the report button link at send time; does not need to be pasted manually.",
          },
        },
        saveReset: {
          title: "Save and Reset",
          imageAlt: "Save template and Reset to default buttons",
          items: {
            save: "Click <b>Save template</b> to persist the subject and all style/content settings. A success toast confirms the save.",
            reset: "Click <b>Reset to default</b> to remove your custom template and restore the built-in Vodafone defaults. A confirmation modal appears — this action permanently deletes the saved template.",
          },
        },
        permissionsNote: "Users with M14 view but without M14 edit can open the page and review the template but cannot edit text, change styles, toggle emails, save, or reset.",
      },

      // Cookie Policy Page
      cookiePolicy: {
        title: "Cookie Policy",
        intro: "The Cookie Policy page lets administrators manage per-survey cookie policy content, control whether a link appears in the survey footer, and configure accept/reject banner button labels.",
        accessing: {
          title: "Accessing the Cookie Policy Page",
          imageAlt: "Survey sidebar showing Cookie policy navigation link",
          steps: {
            step1: "Open a survey from the <b>Survey List</b> so that a <b>survey_id</b> is active.",
            step2: "In the left sidebar, click <b>Cookie policy</b> (requires <b>M20 view</b> permission).",
            step3: "The page opens at <b>/admin/survey/cookie-policy?survey_id={id}</b>.",
          },
        },
        overview: {
          title: "Overview",
          imageAlt: "Cookie Policy page with editor and banner settings",
          desc: "This page manages cookie policy content for a specific survey. Respondents can view the policy from the survey footer when enabled.",
          orgNote: "Note: This is separate from the organization-level cookie policy found under Settings → Organization Details → Cookie Policy.",
        },
        footerToggle: {
          title: "Footer Visibility Toggle",
          imageAlt: "Show cookie policy in survey footer toggle",
          items: {
            item1: "Use the \"Show cookie policy in survey footer\" switch to enable or disable the footer link.",
            item2: "Changes to this toggle are saved <b>immediately</b> — you do not need to click Save.",
            item3: "The label shows Enabled or Disabled to reflect the current state.",
          },
        },
        editor: {
          title: "Policy Content Editor",
          imageAlt: "Rich-text cookie policy editor",
          intro: "Use the rich-text editor to author the cookie policy HTML body:",
          formattingTitle: "Formatting options:",
          formattingItems: {
            item1: "Bold, italic, and underline.",
            item2: "Headings (H1, H2, H3), bulleted lists, and numbered lists.",
            item3: "Hyperlinks — click the link button and enter a URL when prompted.",
            item4: "Text color — pick a color or reset to default.",
          },
          tablesTitle: "Tables:",
          tableItems: {
            item1: "Insert a 3-column, 6-row table with a header row using the table button.",
            item2: "Edit cell content directly in the editor.",
          },
        },
        bannerSettings: {
          title: "Cookie Banner Button Settings",
          imageAlt: "Accept and reject button toggles and custom labels",
          items: {
            accept: "Accept button — toggle to show or hide the accept button on the respondent cookie banner.",
            reject: "Reject button — toggle to show or hide the reject button.",
            customLabels: "Button text — optionally enter custom labels for each button. Leave empty to use the default translated text shown to respondents.",
          },
        },
        saving: {
          title: "Saving Changes",
          imageAlt: "Save button on Cookie Policy page",
          items: {
            item1: "Click Save to persist the editor content and banner button settings.",
            item2: "The footer visibility toggle is saved separately and immediately when toggled.",
          },
        },
        permissionsNote: "Users with M20 view but without M20 edit can view the page but cannot edit content, toggle settings, or save.",
      },

      // Lead Erasure Rules Page
      leadErasure: {
        title: "Lead Erasure Rules",
        intro: "Lead Erasure Rules let administrators define automated conditions to archive or erase leads based on field values, duplicate detection, or UTM parameters.",
        accessing: {
          title: "Accessing the Lead Erasure Rules Page",
          imageAlt: "Survey sidebar showing Lead erasure rules navigation link",
          steps: {
            step1: "Open a survey from the <b>Survey List</b> so that a <b>survey_id</b> is active.",
            step2: "In the left sidebar, click <b>Lead erasure rules</b> (requires <b>M23 view</b> permission).",
            step3: "The page opens at <b>/admin/survey/lead-erasure-rules?survey_id={id}</b>.",
          },
        },
        overview: {
          title: "Overview",
          imageAlt: "Lead Erasure Rules page with rules table",
          desc: "Rules are evaluated against incoming leads. When a rule matches, the system archives or erases the lead according to the rule configuration. All rules for a survey are listed in a paginated table.",
        },
        rulesTable: {
          title: "Rules Table",
          imageAlt: "Lead erasure rules table with type, description, and status columns",
          items: {
            columns: "Columns: Type, Description, Status (Active/Inactive), and Actions (Edit, Delete).",
            pagination: "The table shows 10 rules per page with pagination controls.",
            empty: "If no rules exist, the message \"No lead erasure rules configured.\" is displayed.",
          },
        },
        ruleTypes: {
          title: "Rule Types",
          fieldMatch: {
            title: "Field Match",
            desc: "Match a lead form field using an operator and value (or regex pattern). Only eligible lead-gen fields are available.",
          },
          duplicate: {
            title: "Duplicate",
            desc: "Detect duplicate submissions by a chosen field. Choose whether to keep the newest or oldest submission and archive the other.",
          },
          utmMatch: {
            title: "UTM Match",
            desc: "Match UTM parameters (utm_source, utm_medium, or utm_campaign) using an operator and value.",
          },
        },
        operators: {
          title: "Operators",
          fieldMatch: "Field match supports: Equals, Does not equal, Contains, Starts with, Ends with, Is empty, Matches regex, Length equals, and Length does not equal.",
          utmMatch: "UTM match supports: Equals, Does not equal, Contains, Starts with, Ends with, Is empty, and Matches regex.",
        },
        regexPresets: {
          title: "Regex Presets",
          items: {
            letters: "Only letters (A–Z, a–z).",
            digits: "Only digits (0–9).",
            exactly9: "Exactly 9 characters.",
            nineSame: "All 9 characters identical (e.g. 111111111).",
            eightPlus: "8 or more identical digits in a row.",
            custom: "Custom (advanced) — enter a valid JavaScript-style regular expression.",
          },
        },
        addEdit: {
          title: "Adding and Editing Rules",
          imageAlt: "Add rule modal with rule type and configuration fields",
          items: {
            step1: "Click <b>Add rule</b> to open the modal (requires <b>M23 add</b>).",
            step2: "Select a rule type and configure the required fields. Rule type cannot be changed after the rule is created.",
            step3: "Set status to Active or Inactive.",
            step4: "Click <b>Add</b> or <b>Update</b> to save. A success toast appears and the table refreshes.",
          },
        },
        toggleStatus: {
          title: "Toggle Rule Status",
          desc: "Click the Active or Inactive badge in the Status column to toggle a rule on or off without opening the edit modal. Requires M23 edit permission.",
        },
        deleteRule: {
          title: "Delete a Rule",
          desc: "Click the delete icon on a rule row. A confirmation modal appears showing the rule description. Confirm to permanently remove the rule. Requires M23 del permission.",
        },
        permissionsNote: "M23 add is required to create rules, M23 edit to edit or toggle status, and M23 del to delete rules.",
      },

      // Analytics Page
      analytics: {
        title: "Analytics",
        intro: "The Analytics Dashboard in the Vodafone Business survey platform provides insights into survey responses and maturity levels.",
        accessingPage: {
          title: "Accessing Analytics Page",
          navigate: "Navigate to Analytics from the navigation menu",
          imageAlt: "Analytics navigation",
        },
        surveyDataAnalysis: {
          title: "Survey Data Analysis",
          viewDataIntro: "To view the data representation in the form of graphs, follow these steps:",
          steps: {
            selectSurvey: "Select the desired survey from the dropdown menu labeled \"Select survey\". The list of surveys will be displayed in the dropdown.",
            chooseDateRange: "Choose the date range for which you want to see the data representation. Here are several options one can choose from, and customize the range according to their requirements.",
            graphsDisplay: "The graphs will display the number or percentage data based on the selected survey and date range.",
            topLeads: "The Graph also represents the \"Top 10 Leads\" of a particular survey.",
            viewAll: "There is also an option to \"View All\" which takes the admin to a page where all leads, along with their related information, are represented in the form of a table. This table can also be exported as an Excel file.",
          },
          note: "Please note that the graphs provide a visual analysis of the survey data and may require specific survey selection and date range for accurate representation.",
          graphTypes: "There are four types of graphs and one list used in this analytics report to categorize different types of data:",
          graphTypesList: {
            completion: "Completion Analysis",
            maturity: "Maturity Analysis",
            questionWise: "Question Wise Analysis",
            exit: "Exit Analysis",
            topLeads: "Top 10 Leads",
          },
        },
        dataAnalysisGraphs: {
          title: "Data Analysis graphs",
          completionAnalysis: {
            title: "Completion Analysis",
            desc1: "This graph shows the number of people who have completed the survey compared to the total number of participants.",
            desc2: "For example, in this graph, \"total survey response\" refers to the number of response attempts made by end users, which is 16. \"Complete surveys\" refers to the number of surveys that have been completed, which is 14. This complete and incomplete represent by two different color in the graph.",
          },
          maturityAnalysis: {
            title: "Maturity Analysis",
            desc1: "This graph shows the percentage of Maturity Stage achieved for a specific survey. The graph represents the progress of the survey in different stages, which have been defined by the admin in the \"Variables Stages\" section.",
            desc2: "The graph represents stages in percentage. The total percentage is always 100%. Each state is represented by a different color, indicating the percentage it represents. Hovering over the graph will display the specific percentage.",
          },
          questionWiseAnalysis: {
            title: "Question Wise Analysis",
            desc1: "This graph represents the distribution of responses for each question in the survey.",
            desc2: "The question-wise analysis refers to the examination and evaluation of the survey responses for each individual question. It involves analyzing the distribution of responses and identifying any patterns, trends, or insights that can be derived from the data.",
          },
          exitAnalysis: {
            title: "Exit Analysis",
            desc: "Exit analysis shows at which question the end user left the survey while filling the lead gen form. It helps identify any specific questions or areas in the survey that may have caused participants to drop out or not complete the survey.",
          },
        },
        dataLeadsTable: {
          title: "Data Leads Table",
          topLeadsTitle: "Top 10 Leads",
          topLeadsDesc: "The table below displays the information of the top 10 highest scoring latest users. Clicking on \"View All\" will show the complete list of generated leads.",
          viewAllClick: "\"View All\" click",
          fields: {
            date: "The date of survey is shown when the lead generation form is filled out by the end user.",
            reportLink: "Report Link shows the link of report which was generated by filling survey by the end user.",
            firstName: "The First Name field displays the name of the end user who responded to the survey.",
            lastName: "The Last Name field displays the last name of the user.",
            email: "The Email Address field displays the email of the lead.",
            phone: "The Phone Number shows the phone number of the lead.",
            organization: "Organization Name shows the organization of the lead.",
            industry: "Industry represents the industry to which the lead belongs.",
            responses: "The No. of Responses field indicates the maximum number of questions visited by the lead out of the total number of questions.",
            maturityLevel: "The maturity level indicates the recommended status based on the scoring of the response.",
            totalScore: "Total score shows the total scoring of the response.",
            completed: "The completed field indicates whether the survey was completed or not, with a status of \"yes\" or \"no\".",
            uniqueId: "The Unique ID field represents a distinctive identifier assigned to each lead.",
            utmSource: "The UTM Source field captures the traffic source information.",
            utmMedium: "The UTM Medium field specifies the medium of the campaign.",
            utmCampaign: "The UTM Campaign field records the campaign name.",
            termsConditions: "The T & C field indicates whether the lead has accepted the terms and conditions.",
            action: "The Action column provides quick actions for each record.",
          },
          exportButton: "Export to Excel Button",
          exportDesc: "When click on export button the table data will export to excel and download in the user's system.",
          deleteSelectedTitle: "Delete Selected Leads Button",
          deleteSelectedSteps: {
            step1: "Select one or more leads from the table by clicking the checkbox beside each record.",
            step2: "Click on the Delete selected leads button above the table.",
            step3: "A confirmation popup will appear asking for approval before deletion.",
            step4: "After confirming, the selected leads will be permanently removed.",
          },
        },
      },

      // Activity Logs Page  
      activityLogs: {
        title: "Activity log",
        intro: "The Activity log provides a read-only audit trail of actions taken by users and the system (e.g., webhook updates, survey status changes, stage edits). Each entry shows the message, action type, who performed it, and the exact date and time.",
        table: {
          title: "Activity log table",
          intro: "The table lists recent activities with the following columns:",
          columns: {
            message: "Message - Human-readable description of the event (e.g., Webhook eloqua for RCA Portuguese updated by shonbit).",
            action: "Action - Categorical action label (e.g., Update webhooks, Update eloqua status, Add webhook, Update Survey Status, Add Eloqua form, Delete stage, Update stage, Add stage, Update Report Page, Update Report Name).",
            addedBy: "Added by - User who performed the action (e.g., shonbit) or System for automated events.",
            dateTime: "Date and time - Timestamp of the event in DD-MM-YYYY | hh:mm A.",
          },
          note: "Rows are informational; there is no separate action column on this page. Use pagination to browse the full history.",
          imageAlt: "Activity log table view",
        },
        pagination: {
          title: "Pagination and display controls",
          rowsPerPage: "Rows per page - Choose how many records to show per page (e.g., 30).",
          rangeTotal: "Range and total - Shows the current slice and total count (e.g., 1-30 of 51).",
          navigation: "Navigation - Use the first/previous/next/last controls to move between pages.",
        },
      },

      tickets: {
        title: "Tickets",
        intro: "Tickets track security alerts and manual issues raised in the platform. System-generated tickets are created automatically for suspicious activity; users with permission can also create tickets manually, assign them, update status, and add comments.",
        accessing: {
          title: "Accessing the Tickets Page",
          imageAlt: "Main sidebar showing Tickets navigation link",
          steps: {
            step1: "Log in to the admin portal.",
            step2: "In the left sidebar, click <b>Tickets</b> (requires <b>M7 view_own</b> permission).",
            step3: "The page opens at <b>/admin/tickets</b>.",
          },
        },
        listOverview: {
          title: "Tickets List",
          imageAlt: "Tickets list with dashboard summary cards, filters, and data table",
          intro: "The tickets list shows all tickets you are allowed to see. Users with view_all also see summary cards by status at the top of the page.",
          dashboardTitle: "Dashboard summary (view_all only)",
          dashboardItems: {
            open: "Open — tickets awaiting action.",
            acknowledged: "Acknowledged — tickets that have been seen but not yet worked on.",
            inProgress: "In progress — tickets currently being handled.",
            resolved: "Resolved — closed tickets.",
            total: "Total — count of all tickets.",
          },
          filtersTitle: "Search and filters",
          filtersItems: {
            search: "Search — filter the table by title, assignee, or who raised the ticket.",
            status: "Status — Open, Acknowledged, In progress, Resolved, or False positive.",
            severity: "Severity — Low, Medium, High, or Critical.",
            source: "Source — System (auto-generated) or User (manually created).",
            assignee: "Assignee — filter by assigned user or Unassigned (view_all only).",
          },
          tableTitle: "Table columns",
          tableItems: {
            title: "Title — click a title or row to open ticket details.",
            type: "Type — alert category (e.g. Brute force, Manual).",
            severity: "Severity — colour-coded badge.",
            status: "Status — colour-coded badge.",
            source: "Source — system or user.",
            assignedTo: "Assigned to — responsible user, or Unassigned.",
            created: "Created — date and time the ticket was raised.",
            action: "Action — View button opens the detail page.",
          },
        },
        createTicket: {
          title: "Creating a Ticket",
          imageAlt: "Create ticket modal with title, severity, description, and assign fields",
          items: {
            step1: "Click <b>+ Create ticket</b> (requires <b>M7 view_own</b>).",
            step2: "Enter a title and select severity (required). Optionally add a description and assign to a user.",
            step3: "Click <b>Create ticket</b> to save. A success message appears and the list refreshes.",
            step4: "System-generated tickets are created automatically and cannot be created through this modal.",
          },
        },
        ticketDetail: {
          title: "Ticket Detail",
          imageAlt: "Ticket detail page with information, comments, status, and assignment panels",
          items: {
            overview: "The main panel shows title, description, status, severity, alert type, source, IP address, raised by, assignee, and timestamps.",
            comments: "The comments section lists notes from users. Add a comment in the text area and click Post Comment (requires M7 comment or ticket permission).",
            status: "Users with M7 view_all can change status from the right panel and click Update Status.",
            assign: "Users with M7 view_all can assign the ticket to a user from the Assign To dropdown and click Assign.",
          },
        },
        alertTypes: {
          title: "Alert Types",
          items: {
            bruteForce: "Brute force — repeated failed login attempts.",
            unauthorizedToken: "Unauthorized token — invalid or expired authentication token use.",
            unknownUser: "Unknown user — login attempt by an unrecognised account.",
            inactiveUser: "Inactive user — login by a deactivated account.",
            suspiciousIp: "Suspicious IP — activity from an unusual IP address.",
            afterHours: "After hours — access outside configured business hours.",
            manual: "Manual — ticket created by a user through the Create ticket flow.",
          },
        },
        notifications: {
          title: "Ticket Notification Settings",
          imageAlt: "Ticket notification recipients settings page",
          intro: "Administrators can configure which users receive email notifications when new tickets are created.",
          items: {
            step1: "Go to <b>Settings → Ticket notifications</b> at <b>/admin/settings/ticket-notifications</b>.",
            step2: "Search for users and add them as notification recipients. Remove recipients with the delete control on each chip.",
            step3: "Click <b>Save</b> to persist the recipient list.",
          },
        },
        permissionsNote: "M7 view_own allows viewing and creating tickets. M7 view_all adds the dashboard summary, assignee filter, status updates, and assignment. M7 comment allows posting comments on tickets.",
      },

      // Dashboard/Survey List Page
      dashboard: {
        title: "Survey List",
        intro: "The Survey List provides a structured way to manage, track, and edit surveys. It includes options to search, filter, and create new surveys while displaying essential details like completion status, language, and publication details.",
        
        navigating: {
          title: "Navigating to Surveys List",
          step1: "Log in to your account.",
          step2: "On the left sidebar, on successful login user can redirect to Survey List view. Or else click on Survey to access the Survey List page.",
          step3: "The detail view of survey is shown in the image",
          imageAlt: "Survey List Navigation",
        },

        header: {
          title: "Header Section",
          userProfile: "User Profile and Language Selector",
          userProfileItems: {
            languageDropdown: "Language Dropdown: Allows users to switch between different languages.",
            userProfile: "User Profile: Displays the logged-in user's name and email.",
            dropdownMenu: "Dropdown Menu: Likely contains options to log out or change Profile settings.",
          },
          breadcrumb: "Breadcrumb",
          breadcrumbDesc: "In the Survey List, the currently active page name is displayed on the right side of the layout. This helps users quickly identify which section of the platform they are working on.",
          createSurvey: "Create Survey Button",
          createSurveyItems: {
            location: "Located on the right side as a red button labeled + Create Survey.",
            purpose: "Allows users to create a new survey quickly.",
          },
        },

        filters: {
          title: "Survey List Filters",
          intro: "This section displays a list of surveys with details, including their completion status, language and modification details.",
          searchBar: "Search Bar",
          searchBarDesc: "Users can search for a specific survey by entering a keyword.",
          filtersTitle: "Filters",
          filtersDesc: "The Survey List provides filtering options to help users easily find and manage surveys. Below are the details of the Language Filter and Status Filter.",
          languageFilter: "Language Filter",
          languageFilterDesc: "Allows users to filter surveys based on the language in which they are created.",
          allLanguages: "All - Shows all surveys regardless of language.",
          statusFilter: "Status Filter",
          statusFilterDesc: "Helps users filter surveys based on their publication status.",
          statusOptions: {
            all: "All Survey - Displays all surveys, regardless of their status.",
            published: "Published - Shows only surveys that are live and available for respondents.",
            draft: "Draft - Displays surveys that are still in draft mode and not yet published.",
            example: "Example Usage: If a user selects \"Draft\", only the surveys that have a \"Draft\" status will be shown, hiding the published ones.",
          },
        },

        tableColumns: {
          title: "Survey Table Columns",
          intro: "The survey list is displayed in a table format with the following columns:",
          surveyName: "Survey Name - Displays the survey title and the number of sections/questions.",
          completion: "Completion / Responses - Shows the number of completed responses vs. total responses.",
          language: "Language - Indicates the language of the survey.",
          status: "Status - Shows whether the survey is in \"Draft\" or \"Published\" status.",
          addedOn: "Added on / Added by - Displays the survey creation date and the name of the user who created it.",
          lastModified: "Last Modified - Shows when the survey was last updated.",
          publishedOn: "Published on - Displays the date the survey was published.",
          actions: "Actions - This column contains various actions that can be performed on each survey. The detail explanations is provided below in 'Action in survey list' section",
        },

        actions: {
          title: "Action in survey list",
          editSurvey: {
            label: "Action 1 - Edit Survey:",
            desc: "When the user clicks on the 'edit survey' action, they will be redirected to the Q&A builder for editing. In the Q&A builder, they will be able to manage the survey categories, including the \"Sections\", \"Questions and Answers\", \"Add/Edit variables\", Manage \"Reports\", Manage \"URL\".",
          },
          shareSurvey: {
            label: "Action 2 - Share Survey:",
            desc: "'Share survey' leads to 'public and share' page where one can customize the URL by adding different combinations of UTM variables to track the source, medium, and campaign. The survey URL also includes a copy-paste option for easy sharing.",
          },
          moveToPublish: {
            label: "Action 3 - Move To Publish/Move to Drafts:",
            desc: "This action allows the user to publish a survey if in Drafts or Move a Survey to Drafts if already published. Once the survey is published, the survey frontend URL becomes accessible.",
          },
          viewLeads: {
            label: "Action 4 - View Leads:",
            desc: "This action redirects to the 'All Leads' page in analytics, showing all responses filled by the end-users for that particular Survey.",
          },
          duplicate: {
            label: "Action 5 - Duplicate:",
            desc: "This feature duplicates all data, variables, reports, and all other data from the chosen survey under a new survey name. The Leads, Analytics, URL, though is not copied.",
          },
          delete: {
            label: "Action 6 - Delete:",
            desc: "\"Delete\" feature helps in deleting a Survey. Every detail of a survey is deleted from everywhere including Leads, URL, Q&A, Variables.",
          },
        },
      },

      // Q&A Builder Page
      qandaBuilder: {
        title: "Q & A Builder",
        intro: "This interface provides a structured and user-friendly approach to creating and managing surveys.",
        introItems: {
          organize: "Users can Organize questions into sections",
          questionTypes: "Choose different question types (text, multiple choice, etc.)",
          editManage: "Edit, delete, or rearrange questions",
          navigate: "Navigate through various survey-building tools efficiently",
        },
        access: {
          title: "Accessing Q & A Builder",
          step1: "Navigate to Surveys, and edit any survey by clicking the Actions button from listing table.",
          step2: "Click on Highlighted name from the dropdown to open the page.",
          imageAlt: "Access Q & A Builder",
        },
        surveyInfo: {
          title: "Survey Information (Main Content Area)",
          surveyTitleLabel: "Survey Title:",
          surveyTitleValue: "\"IE Test\"",
          surveyDescriptionLabel: "Survey Description:",
          surveyDescriptionValue: "Placeholder text indicating that users can add a brief description of the survey.",
          duplicateNote: "One can Duplicate the current survey Questionaries by click on duplicate icon",
          statsLabel: "Survey Stats:",
          statsValue: "Shows that the survey consists of 7 sections and 35 questions.",
          exampleLabel: "For example",
          exampleDesc: "a 'IE Test' survey may have 7 sections and each containing questions relevant to that specific aspect of the customer experience. the Total no of questions is 35 all together",
          imageAlt: "Survey Information",
        },
        sectionsQuestions: {
          title: "Survey Sections & Questions",
          sectionTitle: "Section:",
          sectionDesc: "Sections in a survey are divisions or categories within the survey that help organize and structure the questions. They are used to group related questions together. This not only enhances the clarity of the survey but also aids in data analysis.",
          sectionItems: {
            focus: "This section focuses on how businesses approach cloud technology adoption.",
            describes: "Describes factors such as business strategy, goals, cloud adoption readiness, and investment willingness.",
            questionsCount: "In this example Contains 5 questions related to the topic.",
            iconsNote: "Each secction has icons next to it for editing, deleting, duplicating",
            exampleQuestionsTitle: "Example Questions:",
            exampleQuestion1: "\"How important is the adoption of cloud technology for your business's growth and success?\" Answer type: Text input",
            exampleQuestion2: "\"What level of budget has your business allocated for cloud adoption?\" Answer type: Multiple choice",
            questionIcons: "Each question has icons next to it for editing, deleting, duplicating, or rearranging questions.",
          },
          imageAlt: "Survey Sections & Questions",
        },
        addButtons: {
          title: "Add Question and Add Section Button",
          addQuestionTitle: "Add Question",
          addQuestionDesc: "This button is used to add a new question to the survey. Clicking this button will open a form where the user can choose the question type (e.g., multiple choice, text input, etc.) and configure its details. \"Add Question\" adds a single question within an existing section.",
          addSectionTitle: "Add Section",
          addSectionDesc: "This button is used to add a new section to the survey. Sections help in organizing questions into different categories or topics. \"Add Section\" creates a new section to group related questions together.",
          imageAlt: "Add Question and Section",
        },
        questionTypes: {
          title: "Types of Questions",
          intro: "In survey design, various types of questions can be used to collect different types of information. Some common types include",
          multipleChoiceTitle: "Multiple Choice Questions:",
          multipleChoiceDesc: "These questions provide a list of options, and respondents are required to select one or more choices from the list. These questions are used when you want to offer predefined response options. They are efficient for collecting structured, quantitative data. For example, \"How important is the adoption of cloud technology for your business? (Check all that apply).\"",
          textTitle: "Text Questions:",
          textDesc: "These questions require respondents to provide answers in the form of text. They are used when you want to collect open-ended responses and allow participants to provide their own thoughts and opinions. For example, \"Please describe your experience with our product in your own words.\"",
          opinionTitle: "Opinion Scale Questions",
          opinionDesc: "These questions allow respondents to rate their opinion or satisfaction on a scale, typically ranging from good to bad or strongly agree to strongly disagree. They are used to gather quantitative data and measure attitudes or perceptions. For example, \"On a scale of 1 to 5, how would you rate your overall satisfaction with our customer service?\"",
          imageAlt: "Question Types",
        },
      },

      // Form Builder Page
      formBuilder: {
        title: "Form Builder",
        intro: "The Form Builder is an intuitive interface that allows users to create and customize forms for data collection, assessments, and surveys. The tool provides an easy way to configure form fields, set required inputs, and preview the final form before publishing.",
        access: {
          title: "Accessing form builder",
          step1: "Navigate to Surveys, and edit any survey by clicking the Actions button from listing table.",
          step2: "Click on Highlighted name from the dropdown to open the page.",
          imageAlt: "Access Form Builder",
        },
        leadForm: {
          title: "Lead Form Page",
          breadcrumbDesc: "Customize the lead form page where users fill out the survey. Also, display the chosen survey name in the breadcrumb in Form Builder page.",
          headingDesc: "Enter the heading and description to provide a better understanding of what the form is about.",
          addFieldDesc: "Click on the 'Add field' icon located at the bottom of the form. \"Add Field\" will Add a new field giving an option to select its \"Type\" as per requirement. The Types can be – \"Text Field\", \"Text area\", \"Email\", \"Drop Down\", \"Phone Number\", \"Checkboxes\", \"Radio buttons\", \"Hidden\". The detail explains of input field with general example is explain in Types of input field page.",
          addedFieldDesc: "The added field is visible as last field with the selected \"Type\". The input field in the form displays the \"Label\" and \"Placeholder\" along with the \"Edit\" icon, \"Delete\" icon, \"Required\" toggle, and reorder icon. This allows for customization of inputs and labels in the form.",
          imageAlt: "Lead Form Page",
        },
        userGuide: {
          title: "User Guide: Form Builder",
          step1Title: "Step 1: Field Configuration",
          editIconDesc: "Edit icon - is used to edit the particular field details. On its click a popup with the form appeared on screen where \"Name\", \"Label\", \"placeholder\" and options for \"Radio Buttons\" and \"Checkboxes\" type can be edited. All the fields are mandatory.",
          noteDesc: "NOTE - It is recommended not to edit the field name. It's a unique name which is added as \"lead gen variable\". If the name field is edited, a unique name must be provided for each field as it will become difficult for user to track the record associated with that name.",
          step2Title: "Step 2: Delete field",
          step2Desc: "Users have the option to delete any field at any given point in time. The confirmation delete popup will appear on screen.",
          step4Title: "Step 4: Add New Fields",
          step4Desc: "\"Add field\" can be clicked to add a new field and its type can be selected from dropdown.",
          step5Title: "Step 5: Form Preview",
          step5Desc: "This screenshot gives you a preview of the lead form, allowing you to see how it will appear to users. Review the layout and design of the form.",
          step6Title: "Step 6: CTA Button Customization",
          step6Desc: "You can even customize the alignment of the submit button. By default, its text is set to \"submit\" and aligned to the left. Save the necessary changes on clicking on save button",
          imageAlt: "Form Builder Guide",
        },
      },

      // Report Builder Page
      reportBuilder: {
        title: "Report Builder",
        intro: "Report Builder helps create a comprehensive report to present information, findings, or analysis of a particular survey. It typically includes data, facts, and insights gathered through research or investigation. The report can be customized in this section to convey information in a clear and organized manner, often to inform decision-making or provide recommendations. The report can be downloaded in PDF format. Results in the form of graphs can also be shown. Questionnaire recommendations to users also can be given.",
        featuresTitle: "Features of a Report -",
        features: {
          edit: "Edit - This feature allows you to make changes to a report.",
          duplicate: "Duplicate - With this feature, you can create a copy of a report, including all its pages, in the same order but with a different name.",
          default: "Make it Default - This feature enables you to select a specific report as the default for a particular survey.",
          delete: "Delete - This feature allows you to remove a report from the system",
        },
        builder: {
          title: "Report Builder",
          step1: "Navigate to Surveys, and edit any survey by clicking the Actions button from listing table.",
          step2: "Click on Highlighted name from the dropdown to open the page.",
          imageAlt: "Report Builder Access",
        },
        editorView: {
          title: "Editor View",
          intro: "This tool helps build your customized report by utilizing various props provided within it. The following points explain the top bar panel of the editor and its features one by one.",
          items: {
            responsive: "Responsive report can be made for desktop, tab and mobile",
            highlight: "The view component highlights every component on the report page individually.",
            preview: "Eye icon is to Preview the Report page.",
            fullscreen: "Fullscreen icon is used to resize (Maximize/Minimize) report page. The editing can be done when the report page is maximized. Report pages can be switched when pages are minimized.",
            viewCode: "View code used to see the html and css code behind a report page.",
            undoRedo: "The \"Undo\" and \"Redo\" icons are used to undo i.e. reverse a mistake, such as deleting the wrong word in a sentence. The redo function restores any actions that were previously undone using an undo.",
            download: "Download Arrow to download the report.",
            deleteAll: "Use the delete icon to remove all elements from the report.",
            blocks: "Open blocks toggle menu to view the elements.",
          },
        },
        basicElements: {
          title: "Basic elements include",
          items: {
            columns: "Column - Different combinations of columns can be used according to the report requirements.",
            text: "Text - use for inserting text on report page",
            link: "Link - used for proving web links to button or text",
            media: "Images, videos, and maps can be included on the report page according to one's needs simply by dragging and dropping them.",
          },
        },
        formElements: {
          title: "Forms elements include",
          items: {
            intro: "If you need to include a form in your report, there are several options available to you:",
            types: "Different types of input fields are available in this. They can be used according to one's needs. A brief explanation of each input field and its general use is provided in the \"Types of Input Fields\" section.",
          },
        },
        extraElements: {
          title: "Extra elements include",
          items: {
            tooltip: "Tooltip - A tooltip is a small pop-up box that appears when a user hovers over or clicks on an element, such as a button or an icon. It provides additional information or context about the element, helping users understand its purpose or functionality. Tooltips are often used to provide brief descriptions, instructions, or labels for interface elements. They can enhance user experience by providing helpful hints or clarifying ambiguous content.",
            countdown: "Countdown - Countdown is a feature that can be included in a report. It is a visual element that displays a timer counting down to a specific date or event. It can be used to create a sense of urgency or excitement, or to highlight an upcoming deadline or important date. The countdown can be customized to show the remaining days, hours, minutes, and seconds, and can be styled to match the overall design of the report.",
            customCode: "Custom code - Custom code refers to the ability to add and incorporate your own code into the report. This feature allows you to include custom scripts or snippets of code to modify the behavior or appearance of the report. It provides flexibility and customization options beyond the built-in functionality of the report builder tool. With custom code, you can implement specific functionalities or design elements according to your unique requirements. However, it's important to note that adding custom code requires coding knowledge and expertise to ensure its proper implementation and compatibility with the report builder tool.",
            typed: "Typed - The \"Typed\" element is a feature that can be included in a report. It allows you to add a typed effect to text, simulating the appearance of someone typing the content. This effect can be used to create a sense of interactivity or to engage readers. The \"Typed\" element typically displays text one character at a time, with a slight delay between each character. It can be customized to control the speed of the typing effect and to add additional styling if desired.",
            tabs: "Tabs - Tabs in the context of a report builder refer to a feature that allows you to create multiple sections or pages within the report. Each tab represents a separate section of the report where you can organize and present different information or content. Using tabs, you can divide your report into logical sections, making it easier for readers to navigate and access specific information. Each tab can have its own unique content, such as text, images, graphs, or any other elements that you want to include in that section of the report. Tabs provide a structured and organized way to present your report, allowing readers to focus on one section at a time while still having the ability to switch between different sections when needed. This feature helps to improve the overall readability and user experience of the report. In the context of the provided document, the \"Tabs\" feature is mentioned as one of the \"Extra elements\" that can be included in a report. However, there is no specific further information or examples provided about how tabs are used or implemented in the report builder tool being discussed.",
          },
        },
        customization: {
          title: "General  customization options and Dimensions:",
          intro: "When any elements are included in the above explanation, this panel open for extra customization in your report.",
          generalTitle: "General",
          generalDesc: "In this different options are available where one can set position of element as static,",
          positionTitle: "Position in CSS",
          positionItems: {
            static: "Static - This is the default value. Elements with position: Static are positioned according to the normal flow of the document. They are not affected by the top, bottom, left, right, or z-index properties.",
            relative: "Relative - Elements with position: Relative are positioned relative to their normal position in the document flow. By using the top, bottom, left, and right properties, you can adjust the position of the element relative to its normal position.",
            absolute: "Absolute - Elements with position: Absolute are positioned relative to the nearest positioned ancestor (an ancestor with a position other than static). If no positioned ancestor is found, the element is positioned relative to the initial containing block, which is usually the viewport. The top, bottom, left, and right properties can be used to specify the exact position of the element.",
            fixed: "Fixed - Elements with position: Fixed are positioned relative to the viewport and do not move when the page is scrolled. They are always displayed in the same position, regardless of the scroll position. The top, bottom, left, and right properties can be used to specify the position of the element.",
          },
          floatDisplayTitle: "Float and Display Property in CSS",
          floatDisplayIntro: "These CSS properties are powerful tools for controlling the layout and positioning of elements on a webpage.",
          floatItem: "float - The float property is used to specify whether an element should float to the left, right, or not at all. When an element is floated, it is taken out of the normal flow of the document and other elements flow around it. This can be useful for creating multi-column layouts or positioning elements side by side. The possible values for float are left, right, and none.",
          displayItem: "display - The display property is used to specify how an element should be displayed. It controls whether an element is treated as a block-level element, an inline-level element, or something else. The most commonly used values for display are block, inline, and none.",
          displaySubItems: {
            block: "block elements start on a new line and take up the full width available. They can have margins, padding, and width/height properties applied to them.",
            inline: "inline elements do not start on a new line and only take up as much width as necessary. They cannot have width/height properties applied to them, and margins and padding only affect their surrounding text.",
            none: "none is used to hide an element completely. The element is not rendered and does not take up any space on the page.",
          },
          dimensionTitle: "Dimension",
          dimensionDesc: "To provide proper dimension and space for any element, you can use and set the property for each individual element using this panel.",
          dimensionIntro: "In CSS, the following properties are used to control the dimensions and spacing of elements",
          dimensionItems: {
            width: "Width - The width property is used to set the width of an element. It can be specified in different units such as pixels (px), percentage (%), or other relative units.",
            maxWidth: "Max-width - The max-width property is used to set the maximum width an element can have. If the content of the element exceeds this width, it will be automatically resized to fit within the specified limit.",
            maxHeight: "Max-height - The max-height property is used to set the maximum height an element can have. If the content of the element exceeds this height, it will be automatically resized to fit within the specified limit.",
            margin: "Margin - The margin property is used to control the space around an element. It sets the space between the element's border and adjacent elements. It can have different values for each side of the element (top, right, bottom, left), or it can have a single value that applies to all sides.",
            padding: "Padding - The padding property is used to control the space between the element's content and its border. It sets the space between the element's content and its border. It can also have different values for each side of the element or a single value that applies to all sides.",
            note: "These properties are essential for controlling the layout and spacing of elements on a webpage. They allow you to create responsive and visually appealing designs by adjusting the dimensions and spacing of elements to meet your specific needs.",
          },
        },
        typography: {
          title: "Typography",
          intro: "Typography refers to the style, arrangement, and appearance of text in a document or design. It involves various aspects such as font selection, font size, font weight, line spacing, and text alignment. The typography choices can greatly impact the readability, visual appeal, and overall user experience of the content.",
          items: {
            fontFamily: "Font Family - The font-family property is used to specify the font or list of fonts to be used for an element's text content. It allows you to define a prioritized list of font family names or generic font families. For example: font-family - Arial, sans-serif; will set the font to Arial if available, otherwise it will use a sans-serif font as a fallback.",
            fontSize: "Font Size - The font-size property is used to set the size of the text. It can be specified using various units such as pixels (px), percentages %. For example: font-size: 16px; will set the text size to 16 pixels.",
            fontWeight: "Font Weight - The font-weight property is used to specify the thickness or boldness of the text. It can have values such as normal, bold, bolder, lighter, or numeric values 100, 200, …, 900. For example: font-weight: bold; will make the text bold.",
            letterSpacing: "Letter Spacing - The letter-spacing property is used to control the amount of space between characters in a text. It can be specified in pixels (px) or relative units (em, rem). For example: letter-spacing: 1px; will increase the space between characters by 1 pixel.",
            color: "Color - The color property is used to set the color of the text. It can be specified using various formats such as named colors (Red, Blue, etc.), hexadecimal codes, RGB values, or HSL values.",
            lineHeight: "Line Height - The line-height property is used to set the height of a line of text. It can be specified using various units such as pixels (px), percentages. For example: line-height: 1.5; will set the line height to 1.5 times the font size.",
            textAlign: "Text Align - The text-align property is used to specify the horizontal alignment of the text within its containing element. It can have values such as left, right, center, or justify. For example: text-align: left; will center-align the text.",
            textShadow: "Text Shadow - This property is used to add a shadow effect to the text. It allows you to specify the color, horizontal offset, vertical offset, and blur radius of the shadow. For example: text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5); will add a shadow with a 2-pixel horizontal offset, 2-pixel vertical offset, 4-pixel blur radius, and semi-transparent black color.",
          },
          decorationsTitle: "Decorations",
          decorationsItems: {
            backgroundColor: "Background Color - The background color property is used to specify the color of the background of an element. It can be applied to various HTML elements such as divs, sections, or the entire webpage. For example, background-color: #000; sets the background color to a black gray.",
            border: "Border - The border property is used to create a border around an element. It can define the width, style, and color of the border. For example, border: 1px solid black creates a border with a width of 1 pixel, a solid line style, and a black color.",
            borderRadius: "Border Radius - The border-radius property is used to create rounded corners on an element's border. It defines the radius of the curvature for each corner. For example, border-radius: 5px; creates rounded corners with a radius of 5 pixels.",
            boxShadow: "Box Shadow - The box-shadow property is used to add a shadow effect to an element. It can create a drop shadow, an inner shadow, or multiple shadows. The property accepts values for the horizontal and vertical offset, blur radius, spread distance, and color. For example, box-shadow: 2px 2px 4px rgba(0,0,0,0.5); creates a drop shadow with a 2-pixel horizontal offset, a 2-pixel vertical offset, a 4-pixel blur radius, and a semi-transparent black color.",
            background: "Background - The background property is used to set multiple background-related properties at once, such as the background color, image, and repeat behavior.",
            gradient: "Gradient - A gradient is a smooth transition between two or more colors. It can be applied to an element's background using the linear-gradient() or radial gradient() functions.",
            note: "Without highlighting the words, these properties and values can be used to customize the appearance of elements in a report. They allow you to add colors, borders, rounded corners, shadow effects, and gradients to enhance the visual design and aesthetics of the report.",
          },
          extraTitle: "Extra",
          extraItems: {
            opacity: "Opacity - Opacity refers to the transparency level of an element. It determines how much an element is visible or hidden. The opacity property in CSS can be used to adjust the opacity level of an element. The value of opacity ranges from 0 to 1, where 0 means the element is completely transparent (invisible) and 1 means the element is fully opaque (visible).",
            transitions: "Transitions - Transitions in CSS allow for smooth animations between different states of an element. By applying transitions to certain CSS properties, such as opacity, color, or position, you can create gradual changes in those properties over a specified duration. This gives the effect of smooth and gradual animations instead of abrupt changes. Transitions can be defined using the transition property in CSS, specifying the property to transition, the duration of the transition, and the timing function (easing) to control the speed and acceleration of the transition.",
            transform: "Transform - Transformations in CSS allow for the manipulation and modification of an element's shape, size, or position. The transform property can be used to apply various transformations to an element, such as scaling, rotating, skewing, or translating (moving) it. These transformations can be specified using functions like scale, rotate, skew, and translate. For example, transform: rotate(45deg) would rotate the element by 45 degrees.",
            transformDetails: {
              layout: "Layout: Customize the layout of each page, including the placement of images and text.",
              dimensions: "Dimensions: Adjust the dimensions of elements within the report.",
              variables: "Custom variables: Include custom variables or data in the report.",
              formulas: "Custom formulas: Use custom formulas to calculate values or perform calculations within the report.",
              stages: "Stages graphs: Display graphs or visualizations to represent stages or progress.",
              recommendations: "Recommendations: Include recommendations or suggestions based on the survey data.",
              overview: "Overview: Provide an overview or summary of the survey results.",
              sequence: "Page sequence alignment: Arrange the order of pages in the report.",
            },
          },
          note: "Please note that these customization options may vary depending on the specific features and capabilities of the report builder tool you are using.",
        },
      },

      // Variables Page
      variables: {
        title: "Variables",
        intro: "This platform is for managing surveys and lead-generation forms. Lead-gen variables are unique field names used for capturing form data. Variables can be referenced in reports using a specific syntax. User profile and language settings are accessible in the top-right corner.",
        
        accessingVariables: {
          title: "Accessing Variables",
          steps: {
            step1: "Navigate to Surveys, and edit any survey by clicking the Actions button from listing table.",
            step2: "Click on Highlighted name from the dropdown to open the page.",
          },
          imageAlt1: "Navigate",
          imageAlt2: "Navigate",
        },

        leadGenVariable: {
          title: "Lead Gen Variable",
          intro: "Lead gen variable are variable names stored from names of 'Form builder'. Lead Gen Variables provide a way to capture and utilize data from a Lead Generation form. Each field in the form is assigned a unique name, which can be used to reference and display the corresponding field value in reports or custom formulas. The syntax to access the value of a lead gen variable is #[leadForm:fieldName]#.",
          usage: "Usage Syntax: To access the value of a lead gen variable, use the following syntax: #[leadForm:fieldName]#. Replace \"fieldName\" with the unique name of the desired field. The lead gen variable syntax should be enclosed within #[ and ]#.",
          example: "Example: Suppose you have a Lead Gen form with the following fields and unique names:",
          exampleFields: {
            firstName: "First Name (fieldName: \"firstName\")",
            lastName: "Last Name (fieldName: \"lastName\")",
            email: "Email (fieldName: \"email\")",
          },
          displayValue: "To display the value of the First Name field in a report, you would use the lead gen variable syntax as follows: #[leadForm:firstName]#",
          dynamicReplacement: "This syntax will be dynamically replaced with the actual value entered by the user in the First Name field of the Lead Gen form.",
          notesTitle: "Notes:",
          notes: {
            note1: "1. The lead gen variable syntax is case-sensitive, so make sure to use the correct capitalization for field names.",
            note2: "2. Ensure that the field name used in the syntax matches the unique name assigned to the field in the Lead Gen form.",
            note3: "3. The lead gen variable syntax can be used in various contexts, such as report, custom formulas.",
          },
          conclusion: "By leveraging the lead gen variable syntax, you can easily access and incorporate lead gen form data into your reports, calculations, or custom processes. This allows for dynamic and personalized reporting based on the specific information collected through lead generation forms.",
          imageAlt: "Lead Gen Variable",
        },

        customVariables: {
          title: "Custom Variables",
          intro: "Custom Variables provide a way to assign dynamic values based on conditions defined in the Stage module. These variables are standalone and have unique names that can be used to reference and utilize their values throughout the system. The syntax to access the value of a custom variable is #[fieldName]#.",
          usage: "Usage Syntax: To access the value of a custom variable, use the following syntax: #[fieldName]#. Replace 'fieldName' with the unique name of the desired custom variable. The custom variable syntax should be enclosed within #[ and ]#. The recommendations are displayed in report section which can be customised using 'Custom Variables'.",
          example: "Example: Suppose you have a survey form where participants provide feedback and rate their overall experience on a scale of 1 to 10. Based on their response, you want to assign a recommendation to a custom variable named \"recommendation\". The recommendation will indicate whether the participant would take your service, with values such as \"Yes\", \"No\", or \"Maybe\". The recommendation can be given for every question as per the selected answer using custom variables.",
          displayValue: "To use the value of the \"recommendation\" custom variable in a report or display it in an email, you would use the custom variable syntax as follows: # [recommendation]#",
          dynamicReplacement: "This syntax will be dynamically replaced with the actual value assigned to the \"recommendation\" custom variable in the Stage module, based on the survey participant's overall experience rating.",
          notesTitle: "Notes:",
          notes: {
            note1: "The custom variable syntax is case-sensitive, so make sure to use the correct capitalization for the variable name.",
            note2: "Ensure that the variable name used in the syntax matches the unique name assigned to the custom variable in the Stage module.",
            note3: "Custom variables can be assigned values based on various conditions or calculations, allowing for dynamic and personalized data handling.",
            note4: "The custom variable syntax can be used in different contexts, such as reports, or any other place where you want to utilize the value of a custom variable.",
          },
          conclusion: "By leveraging the custom variable syntax, you can assign and access dynamic values based on defined conditions, enabling flexible and customizable data management within the system. In the example above, the \"recommendation\" custom variable represents the recommendation based on the participant's survey response",
          editTitle: "Edit Custom variable",
          editIntro: "The \"Edit Variable\" feature allows you to modify any variable in the system, including its name, description, or syntax. This feature enables you to make changes to existing variables and update them according to your requirements.",
          editStepsTitle: "To use the \"Edit Variable\" feature:",
          editSteps: {
            step1: "Go to the section where the variable is located. Navigate to the appropriate section or page within the software application where the variable is stored or displayed.",
            step2: "Locate the variable you wish to edit. Identify the specific variable you want to modify among the list of variables displayed on the page.",
            step3: "Click on the \"Edit\" button or option associated with the variable. Most likely, there will be an \"Edit\" button or a similar option next to or below the variable's details. Click on it to initiate the editing process.",
            step4: "Make the necessary changes to the variable's name, description, or syntax. Once in the editing mode, you can modify various aspects of the variable, such as its name, description, or syntax. Update the fields as needed.",
            step5: "Save the modifications. After making the desired changes, look for a \"Save\" button or a similar option to apply the modifications to the variable. Click on it to save your edits. Some systems might automatically save changes as you make them.",
            conclusion: "By utilizing the \"Edit Variable\" feature, you can easily update and customize your variables as needed, ensuring that they accurately represent the desired information or functionality.",
          },
          deleteTitle: "Delete Custom variable",
          deleteIntro: "Deleting a Custom Variable will remove all the values associated with that specific Custom Variable in the system. This includes any recommendations in reports or customized formulas that utilize the variable. Custom Variables as are used to define conditions in stages, custom formulas, recommendation in Q&A Builder will all be removed if a custom variable is deleted.",
          addTitle: "Add New variable",
          addIntro: "To add a new variable to the project, click on the Add new Variable button. A popup will appear on the screen, displaying a form that is the same as the one used in the edit icon of this page.",
          addFields: {
            label: "label - add label name with max characters of 250",
            type: "Select the type of variable from the options of 'number', 'single line text', or 'multi line text'.",
            name: "Enter the Name of the variable without spaces and with a maximum of 50 characters.",
            description: "Provide a Description of the variable and explain its usage.",
          },
          addConclusion: "Click on the \"Add new variable\" button to add a variable. The new variable will be updated in the list.",
          imageAlt: "Custom Variables",
          imageAltEdit: "Edit Custom variable",
          imageAltDelete: "Delete Custom variable",
          imageAltAddButton: "Add New variable button",
          imageAltAdd: "Add New variable",
        },

        customFormula: {
          title: "Custom Formula",
          intro: "Custom Formulas provide a way to create dynamic calculations and expressions using variables from Lead Gen, Custom, and Q&A variables. These formulas are assigned unique names and can be used as variables themselves in reports and stages. The syntax to access the value of a custom formula is #[formulaName]#.",
          usage: "Usage Syntax: To access the value of a custom formula, use the following syntax: #[formulaName]#. Replace \"formulaName\" with the unique name assigned to the desired custom formula. The custom formula syntax should be enclosed within #[ and ]#.",
          example: "Example: Suppose you have a survey with multiple questions, and you want to calculate the overall percentage of the survey score based on the obtained marks and maximum marks. You can create a custom formula named \"overallPercentage\" to perform this calculation.",
          displayValue: "To use the value of the \"recommendation\" custom variable in a report or display it in an email, you would use the custom variable syntax as follows: # [recommendation]#",
          formulaDefinition: "The custom formula definition might look like this: overallPercentage = (que_1_obtained_marks + que_2_obtained_marks + ... ) / (que_1_max_marks + que_2_max_marks + ...) * 100",
          usageExample: "To use the value of the \"overallPercentage\" custom formula in a report or further stages, you would use the custom formula syntax as follows: overallPercentage This syntax will be dynamically replaced with the actual calculated value assigned to the \"overallPercentage\" custom formula.",
          notesTitle: "Notes:",
          notes: {
            note1: "The custom formula syntax is case-sensitive, so make sure to use the correct capitalization for the formula name.",
            note2: "Ensure that the formula name used in the syntax matches the unique name assigned to the custom formula.",
            note3: "Custom formulas can incorporate variables from Lead Gen and Q&A variables, allowing for dynamic calculations based on collected data.",
            note4: "The custom formula syntax can be used in different contexts, such as report templates, stage conditions.",
          },
          conclusion: "By utilizing the custom formula syntax, you can create powerful calculations and expressions based on variables from Lead Gen and Q&A variables. In the example provided, the \"overallPercentage\" custom formula calculates the overall percentage of the survey score based on obtained marks and maximum marks from individual survey questions.",
          editTitle: "Edit Formula",
          editIntro: "The \"Edit Formula\" feature allows you to modify any formula from the \"Custom Formula\" section. This feature enables you to make changes to existing formulas and update them according to your requirements.",
          editStepsTitle: "To use the \"Edit Formula\" feature:",
          editSteps: {
            step1: "Go to the \"Custom Formula\" section of the system.",
            step2: "Locate the formula you wish to edit.",
            step3: "Click on the \"Edit\" button or option associated with the formula.",
            step4: "Make the necessary changes to the formula.",
            step5: "Save the modifications.",
            conclusion: "By utilizing the \"Edit Formula\" feature, you can easily update and adjust your custom formulas as needed, ensuring that they reflect the desired calculations and expressions.",
          },
          deleteTitle: "Delete formula",
          deleteIntro: "Deleting a formula is a permanent action that should be done with caution. When you delete a formula, it will be removed from all sections and reports where it is used. This means that any calculations or expressions relying on the formula will no longer be valid.",
          deleteWarning1: "It's important to note that deleting a formula will also delete all calculations that are based on it. This can have a significant impact on your data analysis and reporting. Therefore, it is recommended to carefully review the implications before proceeding with the deletion.",
          deleteWarning2: "If you are unsure about deleting a formula, it's a good practice to create a backup or make a copy of your document before making any changes. This way, you can revert back to the previous version if needed.",
          deleteStepsTitle: "To delete a formula, follow these steps:",
          deleteSteps: {
            step1: "Locate the formula you want to delete.",
            step2: "Click on the \"Delete\" button or option associated with the formula.",
            step3: "Confirm the deletion when prompted.",
          },
          deleteWarning3: "Once the formula is deleted, it cannot be recovered. Therefore, it is important to double-check your decision before proceeding.",
          deleteConclusion: "Remember, deleting a formula is a permanent action and can have a significant impact on your data analysis and reporting. Make sure to carefully consider the consequences before deleting any formulas.",
          addTitle: "Add formula",
          addIntro: "one can Add new formulaʼs on clicking on Add custom formula button, In this one can add label and name for the formula",
          addFieldIntro: "In the formula input field, you can use variables along with operators. The operator can be selected from symbols or typed from the keyboard. Here is a simple explanation of the meaning and usage of operators:",
          operatorsIntro: "In the context of mathematical formulas and expressions, operators are symbols or characters that represent specific operations or actions to be performed on one or more operands. Here are some commonly used operators:",
          operators: {
            addition: "Addition (+) Adds two or more variables together.",
            subtraction: "Subtraction ( - ) Subtracts one variables from another.",
            multiplication: "Multiplication ( * ) Multiplies two or more variables .",
            division: "Division ( / ) Divides one variables by another.",
            brackets: "Curly brackets are used in regular expressions to define patterns. They can specify repetition or ranges of characters.",
            variable: "( x ) could refer to a variable or value that is part of a formula or calculation. It is used to demonstrate the usage of operators and variables in formulas.",
          },
          operatorsConclusion: "These are just a few examples of operators commonly used in mathematical calculations. Operators can be combined to create complex formulas and expressions to perform advanced calculations and manipulations of data.",
          precedenceIntro: "When using operators in formulas, it's important to consider the order of operations, also known as precedence. The order of operations dictates the sequence in which operators are evaluated. The common order of operations is:",
          precedence: {
            parentheses: "<b>Parentheses:</b> Operations within parentheses are evaluated first.",
            exponents: "<b>Exponents:</b> Exponentiation is performed next.",
            multiplicationDivision: "<b>Multiplication and Division:</b> These operations are performed from left to right.",
            additionSubtraction: "<b>Addition and Subtraction:</b> These operations are performed from left to right.",
          },
          precedenceNote: "Using parentheses can override the default order of operations and ensure that specific operations are evaluated first.",
          precedenceConclusion: "By understanding and utilizing operators effectively, you can create powerful formulas and expressions to perform calculations and manipulate data in your custom formulas.",
          imageAlt: "Custom Formula",
          imageAltEdit: "Edit Formula",
          imageAltDelete: "Delete Formula",
          imageAltAdd: "Add Formula",
          imageAltOperators: "Formula",
        },

        utmVariables: {
          title: "UTM Variables",
          intro: "The unique variables that can be passed in 'Source', 'Medium' and 'Campaign'. These variables can be used to create new URLs for a particular survey, enabling the tracking of response sources. By incorporating UTM variables into the URLs, you can identify and analyze the effectiveness of different marketing campaigns or channels in generating survey responses. The Variables can be added, edited or deleted.",
          context: "In the context of a URL, the source, medium, and campaign are UTM variables that can be used to track and analyze the effectiveness of different marketing campaigns or channels in generating survey responses.",
          source: "<b>Source</b> - The source parameter identifies where the traffic is coming from. It indicates the specific platform, website, or app that is driving the traffic to the survey. For example, if you are sharing the survey link on Facebook, you can use \"facebook\" as the value for the source parameter.",
          medium: "<b>Medium</b> - The medium parameter identifies the marketing medium or channel through which the survey link is being shared. It specifies the type of traffic, such as email, social media, or banner ads. For example, if you are sharing the survey link through an email campaign, you can use \"email\" as the value for the medium parameter.",
          campaign: "<b>Campaign</b> - The campaign parameter is used to identify a specific marketing campaign or promotion. It helps distinguish different initiatives or strategies within a source and medium. For example, if you are running multiple campaigns on Facebook, you can use unique campaign names like \"summer_sale\" or \"holiday_promo\" as the value for the campaign parameter.",
          conclusion: "By incorporating these UTM variables into the survey URL, you can track and measure the performance of different sources, mediums, and campaigns in driving survey responses. This allows you to analyze the effectiveness of your marketing efforts and make data-informed decisions.",
          imageAlt: "UTM Variables",
        },

        stages: {
          title: "Stages",
          intro: "The stages enable participants to receive personalized feedback and recommendations based on their responses and the score they have achieved. This means that participants will not only receive general guidance, but also specific insights and suggestions that are relevant to their individual performance.",
          summary: "In summary, the customizable stages in the system offer a comprehensive and tailored approach to feedback and recommendations. By allowing users to define stages as different levels and providing personalized insights.",
          addTitle: "To add a stage, follow these steps:",
          addSteps: {
            step1: "Go to the \"Stages\" section of the system.",
            step2: "Click on the \"Add Stage\" button or option.",
            step3: "Provide a name and description for the new stage.",
            step4: "Choose one formula from the \"If formula Name\" dropdown, which was previously customized in the \"variable - custom formula\" section and now appears in the dropdown menu.",
            step5: "Select a range for the output result of the chosen formula, i.e., from one range to another range. If the output scores fall within a specific range, choose a custom variable for that formula and display a recommendation message for that range which will be typed in the provided editor.",
            step6: "Conditions can be applied using \"Custom variables\". The \"+\" button can be used to add a new condition, and the \"-\" button can be used to remove any existing condition.",
            step7: "Customize the recommendations and feedback specific to the stage.",
            step8: "Save the changes.",
          },
          addConclusion: "This process allows you to define and create new stages in the system, each with its own criteria, recommendations, and feedback tailored to the participants' performance and progress.",
          editTitle: "To edit a stage, follow these steps:",
          editSteps: {
            step1: "Go to the \"Stages\" section of the system.",
            step2: "Locate the stage you wish to edit.",
            step3: "Add any condition to have a particular stage can be done.",
            step4: "Any pre-existing condition can also be removed using custom variables.",
            step5: "Click on the \"Edit\" button or option associated with the stage.",
            step6: "Make the necessary changes to the stage's name, description, or recommendations.",
            step7: "Save the modifications.",
          },
          deleteTitle: "To delete a stage, follow these steps:",
          deleteSteps: {
            step1: "Go to the \"Stages\" section of the system.",
            step2: "Locate the stage you want to delete.",
            step3: "Click on the \"Delete\" button or option associated with the stage.",
            step4: "Confirm the deletion when prompted.",
          },
          deleteWarning: "Please note that deleting a stage will permanently remove it from the system, along with any associated recommendations and feedback. Make sure to carefully review the implications before proceeding with the deletion.",
          conclusion: "By utilizing the stage customization features, you can tailor the system to meet your specific requirements and provide personalized feedback and recommendations to participants at different stages of their progress.",
          imageAlt: "Stages",
          imageAltEdit: "edit a stage",
        },
      },

      // Publish & Share Page
      publishShare: {
        title: "Publish & Share",
        intro: "You can customize the URL by adding different combinations of UTM variables to track the source, medium, and campaign. The survey URL also includes a copy paste option for easy sharing.",
        
        accessing: {
          title: "Accessing Publish & Share",
          steps: {
            step1: "Navigate to Surveys, and edit any survey by clicking the Actions button from listing table.",
            step2: "Click on Highlighted name from the dropdown to open the page.",
          },
          imageAlt1: "Navigate",
          imageAlt2: "Navigate",
        },

        urls: {
          title: "URl's",
          defaultLink: "By default, there is already a link provided for the selected survey on the page. This link can be used for your specific purpose. However, it is possible that you might prefer a more specific URL link for easier identification and unique customization.",
          createNewTitle: "<b> Create New URL -</b> To accomplish this, you can use the \"Create New URL\" button to create a new and customized URL. Here are the steps to do it:",
          steps: {
            step1: "Locate the \"Create New URL\" button.",
            step2: "Click on the button to initiate the process.",
            step3: "Follow the prompts or instructions provided to customize your new URL.",
            step4: "<b>Name</b> - Enter the name for the URL. For example, if you want to use the survey for collecting data from social media like Facebook, you can give the name of the link as 'facebook_link'.",
            step5: "The UTM variable has already been initialized in the variable/ UTM Campaign Variables.",
            step6: "<b>Source</b> - The source parameter identifies where the traffic is coming from. It indicates the specific platform, website, or app that is driving the traffic to the survey. For example, if you are sharing the survey link on Facebook, you can use \"facebook\" as the value for the source parameter",
            step7: "<b>Medium</b> - The medium parameter identifies the marketing medium or channel through which the survey link is being shared. It specifies the type of traffic, such as email, social media, or banner ads. For example, if you are sharing the survey link through an email campaign, you can use \"email\" as the value for the medium parameter.",
            step8: "<b>Campaign</b> - The campaign parameter is used to identify a specific marketing campaign or promotion. It helps distinguish different initiatives or strategies within a source and medium. For example, if you are running multiple campaigns on Facebook, you can use unique campaign names like \"summer_sale\" or \"holiday_promo\" as the value for the campaign parameter.",
            step9: "By incorporating these UTM variables into the survey URL, you can track and measure the performance of different sources, mediums, and campaigns in driving survey responses. This allows you to analyze the effectiveness of your marketing efforts and make data-informed decisions.",
            step10: "<b>Content</b> - In the context of a URL, the utm_content parameter is used to identify what specifically was clicked to bring the user to the site. It helps in tracking and analyzing the effectiveness of different elements within a marketing campaign or channel. For example, if you have multiple links or ads on Facebook leading to the survey, you can differentiate them by using different values for the utm_content parameter. Here's an example of how the utm_content parameter can be used:",
            step11: "To add additional specifications in the URL, you can use the 'Add parameter' option for more parameters in url",
            step12: "Once you have entered all the necessary information, click on the \"Update\" button to finalize the creation of your new URL. This URL will display in the table on same page like bellow",
          },
          contentExamples: {
            example1: "If you have a logo link and a text link directing users to the survey, you can use utm_content=logolink for the logo link and utm_content=textlink for the text link. This will help you differentiate and analyze the performance of each specific element within the Facebook campaign.",
            example2: "By incorporating the utm_content parameter into the survey URL, you can track and measure the performance of different content elements in driving survey responses. This allows you to evaluate the effectiveness of your marketing efforts and make data-informed decisions.",
          },
          imageAlt1: "Create New URL",
          imageAlt2: "Create New URL button",
          imageAlt3: "Create New URL",
        },

        tableListingFields: {
          title: "Table listing Fields",
          intro: "By default, there is already a link provided for the selected survey on the page. This link can be used for your specific purpose. However, it is possible that you might prefer a more specific URL link for easier identification and unique customization.",
          fields: {
            name: "<b>Name</b> - stored the name of link",
            response: "<b>Response</b> - it is the number of responses generated on this particular link",
            createdDate: "<b>Created date</b> - display the date when link was updated.",
            status: "<b>Status</b> - show the active and inactive status of link",
            actions: "<b>Actions</b> - one can edit, delete and copy the link by clicking on respective links",
          },
          conclusion: "By following these steps, you will be able to create a new and customized URL for your purpose. One can create as many URLs as they need for their specific purposes.",
          imageAlt: "Table listing Fields",
        },
      },

      // Integration Page
      integration: {
        title: "Integration",
        intro: "The Integration module allows you to connect your survey platform to Eloqua and map lead form fields for seamless data syncing. Once a mapping is created, form submissions flow into your Eloqua campaigns automatically.",
        
        accessingModule: {
          title: "Accessing the Integration Module",
          steps: {
            step1: "Navigate to the selected <b>Survey</b>, then click the <b>Integration</b> tab inside a Survey.",
            step2: "You will see the current Eloqua Form status and a list of existing field mappings.",
            step3: "Use the search bar to find a mapping or the <b>Sort by</b> filter to change the order.",
          },
          imageAlt: "Integration Tab Overview",
        },

        listingPage: {
          title: "Understanding the Listing Page",
          intro: "The listing displays the following columns:",
          columns: {
            leadFormField: "<b>Lead Form Field</b> — The field defined in your lead generation form.",
            eloquaFormField: "<b>Eloqua Form Field</b> — The corresponding Eloqua form field for syncing data.",
            action: "<b>Action</b> — Delete (🗑️) to remove the mapping.",
          },
          statusTitle: "At the top of the page, you'll also find the <b>Eloqua Form status</b>:",
          status: {
            active: "<b>Active</b> <span class=\"badge bg-success text-white\">Active</span> — Enabled and syncing data.",
            inactive: "<b>Inactive</b> <span class=\"badge bg-danger text-white\">Inactive</span> — Disabled; submissions will not sync.",
          },
        },

        toggleStatus: {
          title: "Toggling Eloqua Sync Status",
          intro: "You can quickly enable or disable Eloqua syncing using the <b>status dropdown</b> near the page header:",
          steps: {
            step1: "Select <b>Active</b> to enable form data syncing.",
            step2: "Select <b>Inactive</b> to pause syncing without deleting mappings.",
            step3: "When <b>Inactive</b>, all existing mappings are retained but no data is pushed.",
          },
          imageAlt: "Eloqua Status Toggle",
        },

        addMapping: {
          title: "Adding New Mapping",
          intro: "To add a new field mapping, click the red <b>+ Add Mapping</b> button in the top-right corner:",
          steps: {
            step1: "<b>Lead Form Field</b> — Select the form field you want to map from the dropdown.",
            step2: "<b>Eloqua Form Field</b> — Enter the exact Eloqua field name you're mapping to.",
            step3: "Click <b>Save</b> to create the mapping or <b>Cancel</b> to discard.",
          },
          note: "<b>Note:</b> Ensure the Eloqua field name matches the one defined in your Eloqua campaign to avoid data loss.",
        },

        deleteMapping: {
          title: "Deleting a Mapping",
          intro: "To remove an existing mapping, click the <b>Delete (🗑️) icon</b> in the row action.",
          steps: {
            step1: "A confirmation dialog will appear: <em>\"Confirm Delete\"</em>",
            step2: "Click <b>Confirm Delete</b> to remove the mapping permanently.",
            step3: "Click <b>Close</b> to cancel and keep the mapping.",
          },
          warning: "<b>Warning:</b> Deleted mappings cannot be recovered. You will need to re-add them manually.",
        },
      },

      // Webhooks Page
      webhooks: {
        title: "Webhooks",
        intro: "The Webhook module allows you to create triggers that send data to external systems when events like form submissions occur. You can configure webhook URLs, parameters, and status through a simple interface.",
        accessing: {
          title: "Accessing Webhook",
          steps: {
            step1: "Navigate to Surveys, select a survey (e.g., RCA Survey), and click the Webhook tab.",
            step2: "You will see a list of existing webhooks, along with options to add, edit, or delete them.",
            step3: "Click the red + Add Webhook button to open the creation modal.",
          },
          imageAlt: "Create Webhook Modal",
        },
        create: {
          title: "Create New Webhook",
          intro: "In the Create New Webhook modal, fill in the following details:",
          fields: {
            name: "Name* - A name for the webhook.",
            eventsTitle: "Events - Choose the trigger event for the webhook from the dropdown:",
            eventLeadForm: "On Submission of Lead Form - Triggers when a lead generation form is submitted.",
            eventResponse: "On Submission of Response - Fires when an individual question/response is submitted.",
            eventSurvey: "On Submission of Survey - Executes when a complete survey submission is completed.",
            methodTitle: "Method* - Select the HTTP method to call your endpoint.",
            methodPost: "POST - Recommended. Sends data in the request body.",
            methodGet: "GET - Sends data as query string parameters.",
            methodPutPatch: "PUT / PATCH - Update semantics; body supported.",
            methodDelete: "DELETE - Delete semantics; query/body based on your API.",
            contentTypeTitle: "Content-Type* - How the payload is encoded.",
            contentJson: "application/json - Sends a JSON body (default for most APIs).",
            contentForm: "application/x-www-form-urlencoded - Key-value pairs encoded in the body.",
            contentMultipart: "multipart/form-data - Use when uploading files or mixed parts.",
            contentText: "text/plain - Raw text payload.",
            dataFormatTitle: "Data Format* - Where/what structure the data uses.",
            dataJson: "JSON Body - Key-value data serialized as JSON in the request body.",
            dataForm: "Form Data - Key-value pairs in the body (works with x-www-form-urlencoded or multipart/form-data).",
            dataQuery: "Query String - Key-value pairs appended to the URL (common with GET).",
            dataFormatNote: "The available options may vary based on the selected Method and Content-Type.",
            url: "URL* - Target endpoint where the webhook will send data (must be HTTPS).",
            description: "Description - Optional additional notes.",
            addParameters: "Add Parameters* - Allows you to define key-value parameters that will be sent with the webhook request.",
            addParametersStep1: "Enter a name and select the parameter type from the dropdown.",
            addParametersStep2: "Click Add to include it in the payload.",
            generatedUrl: "Generated Webhook URL - Displays the final URL with added parameters.",
            status: "Status - Select Active or Inactive using the radio button at the bottom.",
            actions: "Click + Add to save or Cancel to discard changes.",
          },
          imageAlt: "Webhook List",
        },
        listing: {
          title: "Webhook Listing",
          intro: "All created webhooks will be displayed in a table with the following fields:",
          columns: {
            webhook: "Webhook - The name of the webhook.",
            url: "URL - A link icon that shows the presence of the endpoint.",
            status: "Status - Shows whether the webhook is Active or Inactive.",
            action: "Action - Edit and Delete options to manage the webhook.",
          },
          search: "Use the Search bar to find webhooks by name.",
          sort: "Use the Sort by dropdown to reorder by recently added or other criteria.",
          imageAlt: "Webhook List",
        },
        activation: {
          title: "Activation Status",
          intro: "Each webhook has a status that can be toggled at creation:",
          active: "Active - The webhook will be triggered when the selected event occurs.",
          inactive: "Inactive - The webhook will be saved but not executed until activated.",
          note: "Use the radio buttons to set this status before clicking + Add.",
        },
      },

      // Questionnaire Page
      questionnaire: {
        title: "Questionnaire",
        intro: "The Questionnaire Configuration Panel is an interface for configuring assessment-based questionnaires. This panel allows users to customize page headings, navigation buttons, report details, and stage-wise assessment content.",
        keyFeatures: {
          title: "Key Features",
          pageCustomization: "Page Customization - Set the Page Heading to define the questionnaire title. Configure Next, Submit, and Back Buttons with custom text.",
          reportSection: "Report Section - Customize the Report Page Heading and Report Heading. Define different stages of the report.",
          stagesConfig: "Stages Configuration - Define multiple stages (Stage 1, Stage 2, etc.). Provide descriptions for each stage in text fields.",
          save: "Save Functionality - A Save button allows users to store their configurations.",
        },
        accessing: {
          title: "Accessing Questionnaire",
          steps: {
            step1: "Navigate to Surveys, and edit any survey by clicking the Actions button from listing table.",
            step2: "Click on Highlighted name from the dropdown to open the page.",
          },
          imageAlt1: "Navigate",
          imageAlt2: "Navigate",
        },
        fields: {
          title: "Detailed Explanation of Fields",
          pageHeading: {
            title: "Page Heading",
            desc1: "This field allows users to define the main title of the questionnaire.",
            desc2: "Example: Vodafone Business Rapid Cloud Assessment.",
            imageAlt: "Page Heading",
          },
          buttonConfig: {
            title: "Button Configuration",
            next: "Next Button: Text to display for navigating to the next step.",
            submit: "Submit Button: Customizable text for submission (e.g., Get your personalised recommendation).",
            back: "Back Button: Defines the text for the back navigation button.",
            imageAlt: "Button Configuration",
          },
          reportSection: {
            title: "Report Section",
            reportPageHeading: "Report Page Heading: Title for the report generated after questionnaire completion.",
            reportHeading: "Report Heading: The main heading of the report.",
            stageField: "Stage Field: A general description field for the stage process.",
            imageAlt: "Report Section",
          },
          stages: {
            title: "Stages",
            desc1: "Allows configuration of individual stages in the report.",
            desc2: "Each stage (Stage 1, Stage 2, etc.) has a dedicated text field to enter relevant descriptions.",
            imageAlt: "Stages",
          },
          saveButton: {
            title: "Save Button",
            desc1: "Located at the bottom.",
            desc2: "Clicking Save stores all configured settings.",
            imageAlt: "Button",
          },
          conclusion: "The Questionnaire Configuration Panel provides a structured way to define questionnaire content, navigation, and report details. It ensures flexibility by allowing customization of button labels, headings, and stages.",
        },
      },

      // Thank You Page
      thankYouPage: {
        title: "Thank You Page",
        intro: "This page ensures users receive a personalized experience after completing an assessment by dynamically displaying their data and providing access to reports.",
        accessing: {
          title: "Accessing Thank You Page",
          steps: {
            step1: "Navigate to Surveys, and edit any survey by clicking the Actions button from listing table.",
            step2: "Click on Highlighted name from the dropdown to open the page.",
          },
          imageAlt1: "Navigate",
          imageAlt2: "Navigate",
        },
        keyElements: {
          title: "Key Elements",
          pageHeading: {
            title: "Page Heading",
            items: {
              item1: "Uses dynamic placeholders (#[leadForm:firstName]#, #[leadForm:lastName]#, #[leadForm:company]#, #[maturity_stage]#) to personalize the message with user details.",
              item2: "Example output: John Doe, your company ABC Corp is at Growth stage",
            },
          },
          pageDescription: {
            title: "Page Description",
            items: {
              item1: "Displays a confirmation message after survey completion.",
              item2: "Example: Your personalized report is now available! Explore your insights and recommendations today.",
            },
          },
          buttons: {
            title: "Buttons",
            items: {
              item1: "View Report Button: Allows users to view their generated report.",
              item2: "Download Report Button: Enables users to download the report.",
            },
          },
          saveButton: {
            title: "Save Button",
            items: {
              item1: "Saves the changes made to this Thank You Page configuration.",
            },
          },
          conclusion: "This page ensures users receive a personalized experience after completing an assessment by dynamically displaying their data and providing access to reports.",
          imageAlt: "Key Elements",
        },
      },

      // Privacy Policy Page
      privacyPolicy: {
        title: "Privacy Policy",
        intro: "Configure and maintain the privacy policy content shown to respondents. This editor supports rich text, links, colors, and tables.",
        accessing: {
          title: "Accessing Privacy Policy",
          steps: {
            step1: "Go to Surveys → Actions → Edit Survey.",
            step2: "Open Configuration and select Privacy Policy.",
          },
          imageAlt: "Privacy policy navigation",
        },
        editorBasics: {
          title: "Editor Basics",
          formattingTitle: "Formatting",
          formattingItems: {
            item1: "Use Bold, Italic, Underline, and Headings (H1–H3) for structure.",
            item2: "Create lists with Bulleted List and Numbered List.",
            item3: "Insert hyperlinks using the Link button.",
          },
          tablesTitle: "Tables",
          tableItems: {
            item1: "Insert a table via the Table button, then add text to cells.",
            item2: "Use it to structure sections like data categories and retention periods.",
          },
          imageAlt: "Privacy policy editor table",
        },
        saving: {
          title: "Saving Changes",
          items: {
            item1: "Click Save to persist changes to the current survey's privacy policy.",
            item2: "Saved content will be displayed wherever the privacy policy is rendered for respondents.",
          },
        },
      },

      // Create Survey Page
      createSurvey: {
        title: "Create Survey",
        intro: "By following these steps, you can create a new questionnaire and customize it according to your requirements. This user guide provides step-by-step instructions on how to create a new questionnaire using the provided platform.",
        guide: {
          title: "User Guide: Create New Survey",
          steps: {
            step1Title: "Step 1: Create New Survey",
            step1Desc: "Click on the Create New Survey button to initiate the process of creating a new questionnaire from Survey List page.",
            step2Title: "Step 2: Name Your Survey",
            step2Desc: "This mandatory field can be used to provide a suitable name to the Survey. Click Next button to proceed.",
            step3Title: "Step 3: Short Description of Your Survey",
            step3Desc: "Write a brief description of your survey and click Next to proceed further. This field is also mandatory.",
            step4Title: "Step 4: Number of Sections in Your Survey",
            step4Desc: "Specify the number of sections you want to include in your survey and click Next to continue.",
            step4Note: "Note - Number should be greater than 0 and this field is mandatory.",
            step5Title: "Step 5: Section Names and description",
            step5Desc: "Enter the names of each section you want to include in your survey and specify the description about it.",
            step5Note: "Note - Entering a section name is compulsory, but providing a description is optional.",
            step6Title: "Step 6: Select Language",
            step6Desc1: "Please select the desired language for your survey from the available options. You can choose multiple languages as well. By submitting the survey, it will be added to the survey list page.",
            step6Desc2: "Click on Submit button to move the next stage. Survey is been created and is displayed on the top of Survey List.",
            step6Note: "Note - By default, the English language is selected. If users need to modify any details or feel that something was missed in the previous steps, they can do so by using the Actions feature in the survey list table.",
            step7Title: "Step 7: Manage Your Survey on the Dashboard Page",
            step7Desc: "To access and manage your survey, go to the dashboard page. From there, you can make additional edits, share the survey, or duplicate it for different use cases.",
          },
          imageAlt1: "Create New Survey",
          imageAlt2: "Name Your Survey",
          imageAlt3: "Short Description",
          imageAlt4: "Number of Sections",
          imageAlt5: "Section Names",
          imageAlt6: "Select language",
          imageAlt7: "Manage Survey",
        },
      },

      // Recommendations Page
      recommendations: {
        title: "Question and Stage Recommendations with Custom Variables",
        overview: {
          title: "Add Question-Level Recommendations Using Custom Variables",
          intro1: "Custom Variables are simple name value pairs you can print anywhere with #[fieldName]#. A recommendation is just the value stored in one of these variables. When someone answers a question in the Q&A Builder, you decide which variable gets which text.",
          intro2: "Create one Custom Variable for the question, for example #[response_1_recommendation]#. In the Q&A Builder, link this same variable to option in Questions for which you want to show the recommendation and assign a different recommendation text to each option. When a respondent chooses an option, that option's text is saved into the variable. In your report, place one placeholder #[response_1_recommendation]# and it will show the recommendation that matches the selected option.",
          exampleTitle: "Example:",
          exampleIntro: "Suppose you have Question 1 with the following options and per-option texts:",
          exampleOptions: {
            optionA: "Option A — Run a baseline assessment within 30 days.",
            optionB: "Option B — Expand scope to include third-party risk.",
            optionC: "Option C — Schedule a comprehensive audit and gap plan.",
          },
          variableName: "Variable Name: #[response_1_recommendation]#",
          linkText: "Link #[response_1_recommendation]# to each option in Q&A Builder and set the corresponding text as the value.",
        },
        createVariables: {
          title: "Create Custom Variables for Recommendations",
          intro: "In this step you create the variable(s) that will hold your recommendation text. To add a new variable to the project, click on the Add New Variable button. A popup will appear with the same form you see when using the edit icon on this page.",
          steps: {
            step1: "Go to Custom Variables → Add New Variable.",
            step2Title: "Fill out the form:",
            fields: {
              label: "Label — add a human-readable label (max 250 characters).",
              type: "Type — choose one of: number, single line text, or multi line text. (Use multi line text for paragraphs/bullets.)",
              variableName: "Variable Name — enter a unique name with no spaces, max 50 characters. Names are case-sensitive. Example patterns: #[response_1_recommendation]#",
              description: "Description — briefly explain where and how this variable will be used (e.g., Q1 → Recommendation shown in report).",
            },
            step3: "Click Add New Variable to save. The new variable will appear in the list.",
          },
        },
        linkOptions: {
          title: "Link Variables to Options in Q&A Builder",
          intro: "Once your Custom Variables are created, the next step is to connect them to the actual survey questions and options inside the Q&A Builder. This is where you decide which recommendation should appear in the report when a participant selects a particular option. Think of this step as wiring up your variable to the choices that respondents will see.",
          steps: {
            step1: "Open Surveys → Q&A Builder and select the survey you want to configure.",
            step2: "Navigate to the question you're working on and look at its list of answer options. Each option can have its own recommendation linked through a variable.",
            step3: "On the right-hand side of the option row, click the \"C\" icon. This stands for Link a custom variable and opens a dialog where you can attach variables.",
            step4Title: "Inside the dialog, click Add Variable. You'll now specify:",
            step4Items: {
              selectVariable: "Select variable: Choose the variable you created earlier (e.g., #[response_1_recommendation]#).",
              enterValue: "Enter value: Type the exact recommendation text that should appear in the report when this option is selected. This could be a short tip, a detailed next step, or even a paragraph if you used the Multi line text variable type.",
            },
            step5: "Click Save. The variable and its assigned value will now be listed under that option, showing you the mapping is complete.",
            reuseTitle: "You can also reuse the same variable across multiple options in the same question.",
            reuseItems: {
              item1: "Enter a different recommendation text for each option while linking the same variable.",
              item2: "When a respondent selects an option, that option's text is saved into the variable.",
            },
          },
        },
        stageRecommendations: {
          title: "Add Stage-Level Recommendations Using Custom Variables",
          intro: "Stage-level recommendations let you assign custom variables that hold guidance or insights based on the overall stage a respondent qualifies for. Instead of linking variables to each option (as in question-level), here you define them once per stage. When someone's score falls into that stage, the corresponding variables are populated with the recommendation text you've written. In reports, these variables are printed using placeholders like #[stage_summary_business]# or #[stage_summary_security]#.",
          howToTitle: "How to add stage-level recommendations:",
          steps: {
            step1: "Go to the Stages section and click Add Stage.",
            step2: "Enter a Name and Description (e.g., Cloud Explorer).",
            step3: "Select an If formula from the dropdown. This formula decides which stage a respondent falls into.",
            step4: "Define the score range for the stage (e.g., 40–69).",
            step5Title: "Under Variables & Recommendations, click the + icon to add one or more custom variables. For each variable:",
            step5Items: {
              item1: "Select the variable (e.g., #[response_1_recommendation]#).",
              item2: "Type the recommendation text you want shown in the report.",
              item3: "Repeat + to attach multiple other variables, each with its own text.",
              item4: "Use the – icon to remove a variable if needed.",
            },
            step6: "Click Save. The stage now stores all its recommendations inside the custom variables you mapped.",
          },
          editTitle: "Editing stage-level recommendations:",
          editItems: {
            item1: "Open the Stages section and click Edit on the stage you want.",
            item2: "Add or remove variables with the + and – icons.",
            item3: "Update the recommendation text for any variable.",
            item4: "Click Save to apply your changes.",
          },
          deleteTitle: "Deleting a stage:",
          deleteItems: {
            item1: "Go to the Stages section and click Delete on the stage.",
            item2: "Confirm deletion — this removes the stage and all its recommendation mappings.",
          },
          conclusion: "By leveraging custom variables at stage-level, you ensure participants see strategic, big-picture recommendations tied to their maturity band — not just per-question tips. This makes reports more actionable and aligned with overall progress.",
        },
      },

      // Survey Groups Page
      surveyGroups: {
        title: "Survey Groups",
        intro: "Survey Groups allow you to combine multiple language versions of your survey into one link. Respondents can switch between languages easily while taking the survey, creating a seamless multilingual experience.",
        createGroup: {
          title: "1. Creating a Survey Group",
          beforeStart: "Before you start: Make sure you have already created individual surveys in each language you need. Each survey should have its language set correctly in the survey settings.",
          createTitle: "How to Create a Group:",
          steps: {
            step1: "Go to Surveys → Survey Groups from the main menu.",
            step2: "Click Create Group button.",
            step3: "Fill in the group details (see field explanations below).",
            step4: "Click Add Survey to select surveys to include in this group.",
            step5: "Important Rule: Only one survey per language allowed. The system will prevent you from adding duplicate languages.",
            step6: "Continue adding all language versions you need.",
            step7: "Click Create Group to save.",
          },
          imageAlt1: "Survey groups list",
          listFieldsTitle: "Survey Group List Fields:",
          listFields: {
            groupName: "Group Name — The name of your survey group displayed in the list.",
            totalSurveys: "Total Surveys — Shows the number of surveys (language versions) in this group.",
            addedOn: "Added on / Added by — Displays when the group was created and who created it.",
            action: "Action — Three-dot menu (⋮) with options:",
            actionItems: {
              rename: "Rename — Change the group name (doesn't change URL)",
              delete: "Delete Survey Group — Permanently delete the group",
            },
          },
          renameTitle: "How to Rename a Group:",
          renameSteps: {
            step1: "From the Survey Groups list, find your group.",
            step2: "Click Actions → Rename (three-dot menu).",
            step3: "Enter the new name in the popup.",
            step4: "Click Save.",
          },
          deleteTitle: "How to Delete a Group:",
          deleteSteps: {
            step1: "From the Survey Groups list, find your group.",
            step2: "Click Actions → Delete Survey Group (three-dot menu).",
            step3: "A warning popup appears explaining:",
            step4: "Click Confirm Delete.",
          },
          deleteItems: {
            item1: "Group will be permanently deleted",
            item2: "All group links will stop working immediately",
            item3: "Individual surveys will NOT be deleted (they're safe)",
          },
          imageAlt2: "Delete survey group",
        },
        manageGroup: {
          title: "2. Managing Surveys in a Group",
          intro: "After creating a group, you can add surveys, reorder them, set a primary survey, and remove surveys. This section covers all group management tasks.",
          addSurveysTitle: "Adding Surveys to a Group:",
          addSurveysSteps: {
            step1: "Go to Surveys → Surveys (main survey list page).",
            step2: "Select surveys you want to add by checking the boxes next to them.",
            step2Items: {
              item1: "You can select multiple surveys at once.",
              item2: "Remember: Only one survey per language per group.",
            },
            step3: "Click the Add to Group button at the top.",
            step3Items: {
              item1: "This button only appears when you have surveys selected.",
            },
            step4: "A dropdown appears with a search box and list of your groups.",
            step5: "(Optional) Use the search box to find your group if you have many groups.",
            step6: "Click on the group name to select it. A checkmark appears next to the selected group.",
            step7: "Click the Add button at the bottom of the dropdown.",
            step8: "Success! Your selected surveys are now added to the group.",
          },
          imageAlt3: "Add to group",
          manageTitle: "Managing Surveys Inside a Group:",
          manageIntro: "To manage surveys in a group (reorder, make primary, remove, or view leads):",
          manageSteps: {
            step1: "Go to Surveys → Survey Groups from the main menu.",
            step2: "Click on the group name you want to manage.",
            step3: "The group detail page opens, showing a table with all surveys in that group.",
            step4: "You'll see two tabs at the top:",
            step4Items: {
              item1: "Survey — Manage surveys in the group (reorder, primary, remove)",
              item2: "UTM Campaign Variables — Add tracking variables for campaigns",
            },
            step5: "From the Survey tab, you can:",
            fields: {
              surveyName: "Survey Name — The name of the survey with a badge (Primary, if applicable).",
              completion: "Completion / Responses — Shows completed vs total responses (e.g., 1/2 or 4/4).",
              language: "Language — The language of this survey version (e.g., English, Portuguese).",
              status: "Status — Published or Draft status with colored badge.",
              assignTeam: "Assign Team — Shows which team the survey is assigned to.",
              addedOn: "Added on / Added by — When the survey was created and by whom.",
              responses: "Responses — Total number of responses received.",
              publishedOn: "Published on — The date when the survey was published.",
              action: "Action — Three-dot menu (⋮) with options:",
              actionItems: {
                primary: "Make it Primary — Set this survey as the default language",
                viewLeads: "View Leads — View responses/leads for this survey",
                remove: "Remove From the Group — Remove this survey from the group",
              },
            },
          },
          imageAlt4: "Group detail table",
          utmTitle: "Adding UTM Campaign Variables to a Group:",
          utmIntro: "UTM variables help you track where your survey responses come from. You can add predefined values for Source, Medium, and Campaign that will be available when creating campaign links for this group.",
          utmSteps: {
            step1: "On the group detail page, click the UTM Campaign Variables tab.",
            step2: "You'll see three sections: Source, Medium, and Campaign.",
            step3: "Click + Add under any section to add a new variable.",
            step4: "Type the value (e.g., facebook for Source, social for Medium, spring_sale for Campaign).",
            step5: "Click outside the field or press Tab to save automatically.",
            step6: "To delete a variable, click the minus (−) icon next to it and confirm deletion.",
            step7: "These saved variables will appear as quick options when creating UTM campaign links for this group.",
          },
          imageAlt5: "UTM campaign variables",
        },
        links: {
          title: "3. Generating and Sharing Survey Group Links",
          intro: "Every Survey Group has a unique link. You can share the basic link or create campaign-specific links with UTM tracking parameters.",
          basicTitle: "Get Your Basic Group Link:",
          basicSteps: {
            step1: "From the Survey Groups list, click Share Survey Group.",
            step2: "You'll see the Base Link: https://yourplatform.com/group/your-group-slug",
            step3: "Click Copy Link to copy it.",
          },
          imageAlt6: "Share survey group link",
          campaignTitle: "Create Campaign Links with UTM Tracking:",
          campaignIntro: "UTM parameters help you track where your survey responses come from (e.g., email, social media, ads). This works exactly like individual survey links.",
          campaignSteps: {
            step1: "In Share Survey Group, click Create New URL.",
            step2: "Fill in the campaign fields:",
            fields: {
              name: "Name* — Your internal name (e.g., Facebook Ad Q1)",
              source: "Source* — Where traffic comes from (e.g., facebook, newsletter, google)",
              medium: "Medium* — Marketing channel (e.g., social, email, cpc)",
              campaign: "Campaign* — Campaign name (e.g., spring_sale_2024, product_launch)",
              content: "Content — (Optional) Specific ad or link version",
            },
            step3: "(Optional) Click Add Parameters to add custom tracking parameters.",
            step4: "Click Create.",
            step5: "Your UTM link is generated: https://yourplatform.com/group/your-group-slug?utm_source=facebook&utm_medium=social&utm_campaign=q1_promo",
            step6: "Copy and share this link in your campaigns.",
          },
          imageAlt7: "Create campaign link",
          whatHappensTitle: "What Happens When Respondents Open the Link:",
          whatHappensItems: {
            item1: "Primary Survey Loads First — Your primary survey language shows by default.",
            item2: "Language Switcher is Visible — A dropdown appears (usually at the top) showing all available languages.",
            item3: "They can select any language from the dropdown, and the survey switches instantly without reloading the page.",
          },
          imageAlt8: "Language switcher",
        },
        analytics: {
          title: "4. View Analytics for Survey Groups",
          intro: "Survey Groups have their own analytics section where you can see responses from all languages combined or filtered by specific languages.",
          accessTitle: "How to Access Group Analytics:",
          accessSteps: {
            step1: "Go to Analytics from the main menu.",
            step2: "In the Select Survey dropdown, you'll see: Individual Surveys and Survey Groups.",
            step2Items: {
              item1: "Individual Surveys — Regular surveys",
              item2: "Survey Groups — Your survey groups",
            },
            step3: "Select a Survey Group to view analytics.",
          },
          whatYouSeeTitle: "What You Can See:",
          combined: "Combined View — Total responses, completion rates, and metrics across all languages in the group.",
          byLanguageTitle: "By Language — Filter to see:",
          byLanguageItems: {
            item1: "Responses per language",
            item2: "Completion rates per language",
            item3: "Question performance by language",
          },
          imageAlt9: "Survey group analytics",
        },
      },

      // Settings - Profile
      accountSettings: {
        title: "Profile",
        intro: "The Profile page lets you manage your personal account information, including profile photo, email, user name, region, time zone, and default language.",
        accessing: {
          title: "Accessing Profile",
          intro: "To open the Profile page:",
          steps: {
            step1: "Click <b>Settings</b> in the main left sidebar.",
            step2: "Under <b>General settings</b>, click <b>Profile</b>.",
            step3: "The page opens at <b>/admin/settings/profile</b>.",
          },
          imageAlt: "Settings nested menu showing Profile",
        },
        availableFields: {
          title: "Available Fields & Functions",
          generalInfo: {
            title: "General Information",
            profilePhoto: "Profile photo: Upload, change, or remove your profile image using the avatar controls.",
            email: "Email: Displays the registered email of the user.",
            userName: "User name: The display name of the user, which can be modified.",
            imageAlt: "Profile form with photo and account fields",
          },
          localization: {
            title: "Localization Settings",
            region: "Region: Select the geographical region from a dropdown list.",
            country: "Country: Choose a country from the available options.",
            timeZone: "Time zone: Set the preferred time zone based on the user's location.",
            defaultLanguage: "Default language: Select the default language for the user interface.",
          },
          savingChanges: {
            title: "Saving Changes",
            step1: "Once modifications are made, click the 'Save Changes' button to update your account settings.",
            step2: "If any mandatory fields are missing or invalid, an error message will appear.",
            imageAlt: "Save changes button",
          },
        },
        assignedTeamsRoles: {
          title: "Assigned Teams and Roles",
          intro: "This section displays the list of teams the user is part of along with their assigned roles. It appears at the bottom of the Profile form for quick reference.",
          teams: "Teams: Shows the name of each team the user belongs to.",
          roles: "Roles: Displays the corresponding role of the user within that team (e.g., Admin, Editor).",
          conclusion: "This section is non-editable and intended for informational purposes only, providing visibility into team access and permissions assigned to the user.",
          imageAlt: "Assigned teams and roles",
        },
      },

      securitySettings: {
        title: "Security Settings",
        intro: "Security settings control login behaviour for all users, including Microsoft SSO, multi-factor authentication, account lockout, and password expiry.",
        accessing: {
          title: "Accessing Security Settings",
          steps: {
            step1: "Click <b>Settings</b> in the main left sidebar.",
            step2: "Under <b>General settings</b>, click <b>Security</b> (requires <b>M9 view</b>).",
            step3: "The page opens at <b>/admin/settings/security</b>.",
          },
          imageAlt: "Settings nested menu showing Security",
        },
        options: {
          title: "Security Options",
          imageAlt: "Security settings page with toggles and save",
          items: {
            sso: "Microsoft SSO — show or hide the Sign in with Microsoft button on the login page.",
            mfa: "Multi-factor authentication (MFA) — require a 6-digit email code after password login.",
            lockout: "Account lockout — lock accounts after failed login attempts; configure max attempts and lock duration.",
            passwordExpiry: "Password expiry — force password reset after a set number of days.",
            save: "Click <b>Save changes</b> at the bottom to apply all security settings.",
          },
        },
        permissionsNote: "Requires M9 view to open the page and M9 edit to save changes.",
      },

      ticketNotificationSettings: {
        title: "Notifications",
        intro: "Configure which users receive email notifications when tickets are created or assigned.",
        accessing: {
          title: "Accessing Notifications",
          steps: {
            step1: "Click <b>Settings</b> in the main left sidebar.",
            step2: "Under <b>General settings</b>, click <b>Notifications</b> (requires <b>M10 view</b>).",
            step3: "The page opens at <b>/admin/settings/ticket-notifications</b>.",
          },
          imageAlt: "Settings nested menu showing Notifications",
        },
        recipients: {
          title: "Notification Recipients",
          imageAlt: "Ticket notification recipients configuration",
          items: {
            search: "Search for users by name or email and add them as recipients.",
            add: "Selected users appear as chips in the recipients list.",
            remove: "Remove a recipient using the delete control on each chip.",
            save: "Click <b>Save</b> to persist the recipient list.",
          },
        },
        permissionsNote: "Requires M10 view to open the page and M10 edit to save recipients.",
      },

      organizationDetails: {
        privacyPolicy: {
          title: "Privacy Policy",
          intro: "Edit the organization Privacy Policy shown on the login page. This is separate from per-survey privacy policy pages.",
          accessing: {
            title: "Accessing Privacy Policy",
            imageAlt: "Settings menu with Privacy policy selected",
            steps: {
              step1: "Click <b>Settings</b> in the main left sidebar.",
              step2: "Under <b>Organization details</b>, click <b>Privacy policy</b>.",
              step3: "The page opens at <b>/admin/settings/organization-details/privacy-policy</b>.",
            },
          },
          editor: {
            title: "Editing Privacy Policy",
            imageAlt: "Privacy policy rich-text editor",
            items: {
              toolbar: "Use the formatting toolbar for bold, headings, lists, links, tables, and text colour.",
              content: "Edit the privacy policy body in the rich-text editor.",
              save: "Click <b>Save changes</b> to publish updates to the login page.",
            },
          },
          permissionsNote: "Requires M8 view to open and M8 edit to save.",
        },
        termsOfUse: {
          title: "Terms of Use",
          intro: "Edit the organization Terms of Use shown on the login page.",
          accessing: {
            title: "Accessing Terms of Use",
            imageAlt: "Settings menu with Terms of use selected",
            steps: {
              step1: "Click <b>Settings</b> in the main left sidebar.",
              step2: "Under <b>Organization details</b>, click <b>Terms of use</b>.",
              step3: "The page opens at <b>/admin/settings/organization-details/terms-of-use</b>.",
            },
          },
          editor: {
            title: "Editing Terms of Use",
            imageAlt: "Terms of use rich-text editor",
            items: {
              toolbar: "Use the formatting toolbar for bold, headings, lists, links, tables, and text colour.",
              content: "Edit the terms of use body in the rich-text editor.",
              save: "Click <b>Save changes</b> to publish updates to the login page.",
            },
          },
          permissionsNote: "Requires M8 view to open and M8 edit to save.",
        },
        cookiePolicy: {
          title: "Cookie Policy",
          intro: "Edit the organization Cookie Policy shown on the login page. This is separate from per-survey cookie policy pages.",
          accessing: {
            title: "Accessing Cookie Policy",
            imageAlt: "Settings menu with Cookie policy selected",
            steps: {
              step1: "Click <b>Settings</b> in the main left sidebar.",
              step2: "Under <b>Organization details</b>, click <b>Cookie policy</b>.",
              step3: "The page opens at <b>/admin/settings/organization-details/cookie-policy</b>.",
            },
          },
          editor: {
            title: "Editing Cookie Policy",
            imageAlt: "Cookie policy rich-text editor",
            items: {
              toolbar: "Use the formatting toolbar for bold, headings, lists, links, tables, and text colour.",
              content: "Edit the cookie policy body in the rich-text editor.",
              save: "Click <b>Save changes</b> to publish updates to the login page.",
            },
          },
          permissionsNote: "Requires M8 view to open and M8 edit to save.",
        },
      },

      // Settings - Users
      users: {
        title: "Users",
        accessing: {
          title: "Accessing Users",
          intro: "Navigating to the User Management section:",
          steps: {
            step1: "Click <b>Settings</b> in the main left sidebar.",
            step2: "Under <b>User management</b>, click <b>Users</b> (requires <b>M3 view</b>).",
            step3: "The page opens at <b>/admin/settings/user-permissions</b>.",
          },
          imageAlt: "Settings nested menu showing Users",
        },
        search: {
          title: "Searching for a User",
          steps: {
            step1: "Use the Search bar at the top to find a specific user by name or email.",
            step2: "The list will dynamically update as you type.",
          },
          imageAlt: "User search bar",
        },
        listOverview: {
          title: "User List Overview",
          intro: "The user list displays the following columns:",
          columns: {
            name: "Name - Displays the user's name along with initials in a colored circle.",
            defaultLanguage: "Default Language - Displays the language set for the user.",
            timezone: "Timezone - Shows the user's selected timezone.",
            actions: "Actions - Provides options to edit or delete the user.",
          },
          imageAlt: "Users table",
          actionsImageAlt: "User actions",
        },
        editing: {
          title: "Editing a User",
          steps: {
            step1: "Click the Edit (Pencil) icon under the Action column.",
            step2: "Modify user details such as name, email, language, and timezone.",
            step3: "Click Save to apply changes.",
          },
          imageAlt: "Edit user",
        },
        deleting: {
          title: "Deleting a User",
          steps: {
            step1: "Click the Delete (Trash) icon under the Action column.",
            step2: "A confirmation popup will appear.",
            step3: "Click Confirm to permanently remove the user.",
          },
          imageAlt: "Delete user",
        },
        addUser: {
          title: "Adding a New User",
          steps: {
            step1: "Click the + Create User button (red button on the top right).",
            step2: "A form will appear where you can enter:",
          },
          saveAction: "Click Save to create the user.",
          fields: {
            fullName: "Full Name - Displays the user's full name.",
            email: "Email Address - A new user will need to set a password",
            password: "Password - Shows a masked password input field (●●●●●). Clicking the 'X' button might clear the password field.",
            timezone: "Timezone - A dropdown to select the user's time zone.",
            defaultLanguage: "Default language - A dropdown to choose the preferred language.",
          },
          saveOptions: {
            cancel: "Cancel - Closes the modal without saving changes.",
            save: "Save - Saves the updated user details.",
          },
          imageAlt: "Create user form",
        },
      },

      // Settings - Roles
      roles: {
        title: "Roles",
        introLine1: "In this section, roles are granted and permissions are assigned to each role.",
        introLine2: "A role is a defined set of responsibilities and permissions assigned to a user or a group of users within an organization. It determines what actions and operations a user can perform within a system or an application. Roles help to manage access control and enforce security measures by granting or restricting privileges based on user responsibilities and job functions.",
        accessing: {
          title: "Accessing Roles",
          intro: "Navigating to the Roles management section:",
          steps: {
            step1: "Click <b>Settings</b> in the main left sidebar.",
            step2: "Under <b>User management</b>, click <b>Roles</b> (requires <b>M4 view</b>).",
            step3: "The page opens at <b>/admin/settings/user-roles</b>.",
          },
          imageAlt: "Settings nested menu showing Roles",
        },
        search: {
          title: "Searching for a Role:",
          steps: {
            step1: "Use the Search bar at the top to find a specific role by name.",
            step2: "The list will dynamically update as you type.",
          },
          imageAlt: "Roles search bar",
        },
        listOverview: {
          title: "Roles List Overview",
          intro: "The role list displays the following columns:",
          columns: {
            srNo: "Sr no - Displays serial number.",
            names: "Names - Displays the role name (e.g., Admin, Viewer, Editor).",
            dateAdded: "Date Added - Shows the date when it was added.",
            actions: "Actions - Provides options to edit or delete the role.",
          },
          imageAlt: "Roles table",
        },
        editing: {
          title: "Editing a Role",
          steps: {
            step1: "Click the Edit (Pencil) icon under the Action column.",
            step2: "Modify role details such as role name, parent role, permissions etc.",
            step3: "Click Save to apply changes.",
          },
          imageAlt: "Edit role",
        },
        deleting: {
          title: "Deleting a Role",
          steps: {
            step1: "Click the Delete (Trash) icon under the Action column.",
            step2: "A confirmation popup will appear.",
            step3: "Click Confirm to permanently remove the role.",
          },
          imageAlt: "Delete role",
        },
        addRole: {
          title: "Add New Role",
          steps: {
            step1: "Click the + Create Role button (red button on the top right).",
            step2: "A form will appear where you can enter:",
          },
          formImageAlt: "Role form",
          permissionsImageAlt: "Role permissions",
          fields: {
            roleName: "Role Name (Text Field) - This field is where the administrator enters the name of the new role.",
            parentRole: "Select Parent Role (Dropdown Menu) - This dropdown allows the user to select an existing role as a template or hierarchy base for the new role.",
            permissionsTitle: "Permissions — each module row shows checkboxes for the actions available to that module (for example View, Add, Edit, Delete, or ticket-specific Own / All / Comment):",
            view: "View — allows viewing content or data in that module.",
            add: "Add — allows creating new records (shown as Add where applicable).",
            edit: "Edit — allows updating existing records.",
            delete: "Delete — allows removing records.",
            checkbox: "Select or clear checkboxes per module to define what this role can do.",
            parentRolesNote: "Parent role — choose an existing role as a template. The built-in Admin role has full permissions and cannot be edited or deleted.",
            permissionsNote: "Grant only the permissions required for the role. Ticket modules may show Own, All, and Comment instead of the standard four actions.",
          },
          saveAction: "Click Save to create the role.",
          saveOptions: {
            cancel: "Cancel - Closes the modal without saving changes.",
            save: "Save - Saves the updated role details.",
          },
        },
      },

      // Settings - Teams
      teams: {
        title: "Teams",
        introLine1: "Teams are a way to organize groups of users within an organization. Each team can have its own unique name and consists of assigned roles for each user. It is possible for different teams to have the same user with different roles, and multiple teams may have the same name. In this case all permissions are merged for that name.",
        introLine2: "Teams are only visible to users who have created or are part of a specific team. The team creator has the ability to manage team members by adding, editing, or deleting them. However, they cannot remove or delete their own name from the team.",
        introLine3: "Teams provide a structured approach to collaboration and allow users to work together on specific projects or tasks. They help streamline communication, assign responsibilities, and facilitate efficient teamwork.",
        accessing: {
          title: "Accessing Teams",
          intro: "Navigating to the Teams section:",
          steps: {
            step1: "Click <b>Settings</b> in the main left sidebar.",
            step2: "Under <b>User management</b>, click <b>Teams</b> (requires <b>M5 view</b>).",
            step3: "The page opens at <b>/admin/settings/teams/list</b>.",
          },
          imageAlt: "Settings nested menu showing Teams",
        },
        search: {
          title: "Searching for a Team:",
          steps: {
            step1: "Use the Search bar at the top to find a specific team by name.",
            step2: "The list will dynamically update as you type.",
          },
          imageAlt: "Team search bar",
        },
        sort: {
          title: "Sort by filter:",
          steps: {
            step1: "Sort by filter available for easy access to data.",
            step2: "It will have two options for sorting alphabetically ascending (A-Z) or descending (Z-A) order.",
          },
          imageAlt: "Sort filter",
        },
        listOverview: {
          title: "Teams List Overview",
          intro: "The team list displays the following columns:",
          columns: {
            teamName: "Team name - Displays the team name.",
            member: "Member - Displays the role name (e.g., Admin, Viewer, Editor).",
            actions: "Actions - Provides options to edit, delete or leave the team.",
          },
          imageAlt: "Teams table",
        },
        editing: {
          title: "Editing a Team",
          intro: "The Edit Team interface allows administrators to modify an existing team by updating its name, adding new members with specific roles, and managing existing team members.",
          steps: {
            step1: "Click the Edit (Pencil) icon under the Action column.",
            step2: "Team Name - Input field displays the current team name and allows users to edit and update the team's name.",
            step3: "Save & Cancel Buttons (Team Name).",
            step4: "Save - Saves the updated team name.",
            step5: "Cancel - Discards any changes made to the team name.",
            step6: "Add Member Section",
            step7: "Add Member (Dropdown) - Allows selection of a user to be added to the team.",
            step8: "Select Role (Dropdown) - Assigns a role to the selected member.",
            step9: "Save Button - Confirms and adds the selected member with the assigned role.",
            step10: "Cancel Button - Discards the member selection.",
            step11: "Added Members List - Displays a list of users currently in the team, along with their assigned roles. Each row contains:",
            step12: "Member Name - The name of the team member.",
            step13: "Role Dropdown - Allows updating the user's assigned role directly from the list. Changes are applied immediately after saving.",
            step14: "Delete Icon - Removes the user from the team when clicked.",
            step15: "This interface is essential for team management, role assignment, and user access control within the system.",
          },
          imageAlt: "Edit team",
        },
        deleting: {
          title: "Deleting a User",
          steps: {
            step1: "Click the Delete (Trash) icon under the Action column.",
            step2: "A confirmation popup will appear.",
            step3: "Click Confirm to permanently remove the user.",
          },
          imageAlt: "Delete user",
        },
        manageMembers: {
          title: "Manage Members (Arrow Icon)",
          steps: {
            step1: "Opens the team's member management interface, enabling the addition or removal of members.",
          },
        },
        addTeam: {
          title: "Add New Team",
          steps: {
            step1: "Click the + Create Role button (red button on the top right).",
            step2: "User redirected to add team page.",
            step3: "When you enter team name and then save, it will create new team.",
          },
          imageAlt: "Add team form",
        },
      },

      // Common elements
      common: {
        note: "Note:",
        steps: "Steps:",
        whenToUse: "When to use:",
        safetyFeatures: "Safety Features:",
        imageAlt: "Screenshot",
      },
    },
  },

  // Portuguese translations
  pt: {
    documentation: {
      // Sidebar Navigation
      sidebar: {
        backToSurvey: "Voltar para Pesquisa",
        survey: "Pesquisa",
        analytics: "Análises",
        activityLog: "Registro de Atividades",
        tickets: "Tickets",
        settings: "Configurações",
        // Survey submenu
        surveyList: "Lista de Pesquisas",
        qandaBuilder: "Construtor de P&R",
        formBuilder: "Construtor de Formulários",
        reportBuilder: "Construtor de Relatórios",
        emailTemplates: "Modelos de Email",
        variables: "Variáveis",
        publishShare: "Publicar e Compartilhar",
        integration: "Integração",
        webhooks: "Webhooks",
        questionnaire: "Questionário",
        thankYouPage: "Página de Agradecimento",
        leadErasureRules: "Regras de Exclusão de Leads",
        privacyPolicy: "Política de Privacidade",
        cookiePolicy: "Política de Cookies",
        createSurvey: "Criar Pesquisa",
        recommendations: "Recomendações",
        surveyGroups: "Grupos de Pesquisa",
        surveySync: "Sincronização de Pesquisa",
        // Settings submenu
        profile: "Perfil",
        security: "Segurança",
        notifications: "Notificações",
        organizationDetails: "Detalhes da organização",
        users: "Usuários",
        roles: "Funções",
        teams: "Equipes",
        settingsGeneral: "Configurações gerais",
        settingsUserManagement: "Gestão de utilizadores",
        settingsOrganizationDetails: "Detalhes da organização",
        privacyPolicy: "Política de privacidade",
        termsOfUse: "Termos de utilização",
        cookiePolicy: "Política de cookies",
      },

      settingsMenu: {
        intro: "Ao abrir Configurações, o menu aninhado está organizado da seguinte forma:",
        generalSettings: "Configurações gerais",
        profile: "Perfil",
        security: "Segurança",
        notifications: "Notificações",
        userManagement: "Gestão de utilizadores",
        users: "Utilizadores",
        roles: "Funções",
        teams: "Equipas",
        organizationDetails: "Detalhes da organização",
        privacyPolicy: "Política de privacidade",
        termsOfUse: "Termos de utilização",
        cookiePolicy: "Política de cookies",
      },

      // Survey Sync Page
      surveySync: {
        title: "Sincronização de Pesquisa",
        intro: "A funcionalidade de Sincronização de Pesquisa permite que administradores sincronizem pesquisas do ambiente UAT (Teste de Aceitação do Usuário) para o ambiente de Produção.",
        
        syncFromUat: {
          title: "Sincronizar do UAT",
          whenToUse: "Use este recurso quando uma pesquisa existe no UAT, mas <b>NÃO está presente em Produção/Ao Vivo</b>. Isso é usado principalmente para implantar novas pesquisas do UAT para Produção.",
          stepsTitle: "Passos:",
          steps: {
            step1: "Navegue até <b>Pesquisa → Pesquisas</b> no menu lateral esquerdo",
            step2: "Na página Lista de Pesquisas, clique no botão <b>\"Sincronizar do UAT\"</b> localizado no canto superior direito (ao lado do botão \"Adicionar ao grupo\")",
            step3: "Um modal intitulado <b>\"Sincronizar do UAT\"</b> será aberto, exibindo todas as pesquisas disponíveis no UAT",
            step4: "Selecione <b>UMA pesquisa</b> da lista usando o botão de rádio (apenas seleção única permitida)",
            step5: "Clique em <b>\"Sincronizar\"</b> para copiar a pesquisa selecionada do UAT para Produção",
            step6: "Clique em <b>\"Cancelar\"</b> para fechar sem sincronizar",
          },
          note: "Esta opção copia a pesquisa completa (com todas as perguntas, seções, variáveis, etc.) do UAT para Produção. É ideal para a implantação inicial de novas pesquisas.",
          imageAlt: "Localização do botão Sincronizar do UAT",
        },

        fullReplacement: {
          title: "Sincronização de Substituição Completa",
          whenToUse: "Use isso quando uma pesquisa <b>já existe tanto no UAT quanto em Produção</b>, e você deseja substituir completamente a versão de Produção pela versão do UAT.",
          stepsTitle: "Passos:",
          steps: {
            step1: "Navegue até <b>Pesquisa → Pesquisas</b> no menu lateral esquerdo",
            step2: "Encontre a pesquisa na lista e clique no <b>menu de Ações</b> (três pontos) à direita",
            step3: "Selecione <b>\"Sincronizar do UAT\"</b> no menu suspenso",
            step4: "No modal, selecione a opção <b>\"Substituição Completa\"</b>",
            step5: "Clique em <b>\"Sincronizar\"</b> para substituir a pesquisa de Produção pela versão do UAT",
          },
          safetyTitle: "Recursos de Segurança:",
          safety: {
            backup: "Um backup de versão é criado automaticamente antes da substituição",
            responses: "Respostas de usuários e dados de leads nunca são afetados",
            rollback: "Você pode reverter usando o Histórico de Versões, se necessário",
          },
          imageAlt: "Opção de sincronização de substituição completa",
        },

        mergeSync: {
          title: "Sincronização por Mesclagem",
          whenToUse: "Use isso quando você quiser <b>adicionar novos itens do UAT</b> enquanto preserva os itens existentes em Produção. Ideal para atualizações incrementais.",
          stepsTitle: "Passos:",
          steps: {
            step1: "Navegue até <b>Pesquisa → Pesquisas</b> no menu lateral esquerdo",
            step2: "Encontre a pesquisa na lista e clique no <b>menu de Ações</b> (três pontos) à direita",
            step3: "Selecione <b>\"Sincronizar do UAT\"</b> no menu suspenso",
            step4: "No modal, selecione a opção <b>\"Mesclar\"</b>",
            step5: "Clique em <b>\"Próximo\"</b> para visualizar as alterações antes de aplicar",
            step6: "Revise a visualização da mesclagem mostrando:",
            step7: "Clique em <b>\"Aceitar e Sincronizar\"</b> para aplicar a mesclagem, ou <b>\"Cancelar\"</b> para abortar",
          },
          previewItems: {
            newItems: "<b>Novos Itens (Verde):</b> Itens do UAT que serão adicionados",
            conflicts: "<b>Conflitos (Amarelo):</b> Itens com mesmo UUID mas conteúdo diferente - versão do UAT substituirá",
            duplicates: "<b>Duplicados (Cinza):</b> Itens com mesmo conteúdo mas UUIDs diferentes - ambos mantidos",
          },
          safetyTitle: "Recursos de Segurança:",
          safety: {
            preview: "Visualize as alterações antes de aplicar",
            backup: "Backup de versão criado antes da mesclagem",
            atomic: "Operação atômica - todas as alterações são bem-sucedidas ou todas revertem",
            preserved: "Itens exclusivos de Produção são preservados",
          },
          noChangesTitle: "Entendendo \"Nenhuma alteração para mesclar\"",
          noChangesDesc: "Se a visualização mostrar: <i>\"Nenhuma alteração para mesclar. A pesquisa já está sincronizada.\"</i>",
          noChangesItems: {
            identical: "Isso significa que UAT e Produção são idênticos",
            noNew: "Nenhum novo item, conflito ou duplicado foi encontrado",
            safe: "Você pode fechar o modal com segurança - nenhuma sincronização é necessária",
          },
          imageAlt1: "Seleção de opção de mesclagem",
          imageAlt2: "Tela de visualização de mesclagem",
        },

        versionHistory: {
          title: "Histórico de Versões e Reversão",
          intro: "Cada operação de Substituição Completa e Mesclagem cria um backup de versão, permitindo reversão se necessário.",
          accessTitle: "Para acessar o Histórico de Versões:",
          accessSteps: {
            step1: "Clique no <b>menu de Ações</b> (três pontos) em qualquer pesquisa",
            step2: "Selecione <b>\"Histórico de Versões\"</b>",
            step3: "Visualize todas as versões anteriores com datas e tipos",
            step4: "Clique em <b>\"Restaurar\"</b> em qualquer versão para reverter",
          },
          rollbackTitle: "Como Reverter para uma Versão Anterior",
          rollbackSteps: {
            step1: "Abra o modal de Histórico de Versões no menu de Ações da pesquisa",
            step2: "Revise a lista de versões e identifique a versão que deseja restaurar",
            step3: "Clique no botão <b>\"Restaurar\"</b> para essa versão",
            step4: "O sistema:",
            step4Items: {
              backup: "Cria um novo backup de versão do estado atual (antes da restauração)",
              restore: "Restaura todos os componentes da pesquisa da versão selecionada",
              maintain: "Mantém o ID da pesquisa (links existentes continuam funcionando)",
            },
            step5: "Mensagem de sucesso aparece:",
            step5Message: "\"Restaurado para v3! Backup v7 criado.\"",
            step6: "Sua pesquisa agora está restaurada para a versão selecionada",
          },
          note: "O Histórico de Versões rastreia apenas a estrutura e configurações da pesquisa. Respostas de usuários e dados de leads nunca são modificados durante operações de sincronização ou restauração.",
          imageAlt: "Modal de histórico de versões",
        },
      },

      // Email Templates Page
      emailTemplates: {
        title: "Modelos de Email",
        intro: "A página Modelos de Email permite que administradores personalizem o email de notificação de relatório enviado aos respondentes quando o relatório da pesquisa estiver pronto. Cada pesquisa tem seu próprio modelo com visualização ao vivo e painel de estilo.",
        accessing: {
          title: "Acessando a Página de Modelos de Email",
          imageAlt: "Barra lateral da pesquisa com link Modelos de email",
          steps: {
            step1: "Abra uma pesquisa na <b>Lista de Pesquisas</b> para que um <b>survey_id</b> esteja ativo na URL.",
            step2: "Na barra lateral esquerda, clique em <b>Modelos de email</b> (requer permissão <b>M14 view</b>).",
            step3: "A página abre em <b>/admin/survey/email-template?survey_id={id}</b>.",
          },
        },
        overview: {
          title: "Visão Geral",
          imageAlt: "Editor de Modelos de Email com visualização e painel de estilo",
          desc: "O editor é dividido em dois painéis: uma visualização WYSIWYG do email à esquerda e um painel de estilo/configuração à direita. As alterações em qualquer painel atualizam a visualização em tempo real.",
        },
        enableDisable: {
          title: "Ativar / Desativar Emails de Relatório",
          imageAlt: "Cabeçalho da página com interruptor de email de relatório",
          items: {
            item1: "Use o interruptor no cabeçalho da página para ativar ou desativar emails de notificação de relatório para esta pesquisa.",
            item2: "Quando desativado, os emails não são enviados, mas o conteúdo do modelo personalizado é mantido.",
            item3: "Alternar requer permissão <b>M14 edit</b>.",
          },
        },
        editingContent: {
          title: "Editando o Conteúdo do Email",
          intro: "Clique diretamente no painel de visualização para editar o texto, ou use o campo de assunto no painel direito:",
          items: {
            subject: "Assunto do email — definido no topo do painel de estilo.",
            heading: "Título — título principal do email.",
            description: "Descrição — texto do corpo abaixo do título.",
            button: "Texto do botão — rótulo do CTA de download do relatório.",
            securityNote: "Nota de segurança — aviso sobre segurança do link.",
            assistanceNote: "Nota de assistência — texto de ajuda para os destinatários.",
            signoff: "Despedida — linha de encerramento (suporta {{organizationName}}).",
            footer: "Rodapé — texto inferior (suporta {{supportEmail}}).",
          },
        },
        styling: {
          title: "Estilizando o Modelo",
          imageAlt: "Painel de acordeão de estilo do modelo de email",
          intro: "Expanda as seções do acordeão no painel direito para personalizar a aparência:",
          sections: {
            general: "Geral — fundo do email, fundo do cartão, família de fonte, preenchimento do cartão.",
            heading: "Título — cor e tamanho da fonte.",
            description: "Descrição — cor e tamanho da fonte.",
            button: "Botão — fundo, cor do texto, fundo da seção, raio, altura, largura e tamanho da fonte.",
            notes: "Notas e assistência — cores e tamanhos para notas de segurança e assistência.",
            signoff: "Despedida — cor e tamanho da fonte.",
            footer: "Rodapé — cor, tamanho da fonte e cor da borda.",
          },
        },
        placeholders: {
          title: "Marcadores de Posição",
          imageAlt: "Acordeão de marcadores com tokens copiáveis",
          intro: "Tokens dinâmicos opcionais podem ser inseridos nos campos de texto. Abra o acordeão Marcadores de Posição e clique em um item para copiar, depois cole em qualquer campo da visualização:",
          items: {
            orgName: "<b>{{organizationName}}</b> — substituído pelo nome da organização do respondente.",
            supportEmail: "<b>{{supportEmail}}</b> — substituído pelo seu email de suporte.",
            reportUrl: "<b>{{reportUrl}}</b> — usado automaticamente para o link do botão de relatório no envio; não precisa ser colado manualmente.",
          },
        },
        saveReset: {
          title: "Salvar e Redefinir",
          imageAlt: "Botões Salvar modelo e Redefinir para padrão",
          items: {
            save: "Clique em <b>Salvar modelo</b> para persistir o assunto e todas as configurações de estilo/conteúdo. Um toast de sucesso confirma o salvamento.",
            reset: "Clique em <b>Redefinir para padrão</b> para remover seu modelo personalizado e restaurar os padrões Vodafone integrados. Um modal de confirmação aparece — esta ação exclui permanentemente o modelo salvo.",
          },
        },
        permissionsNote: "Usuários com M14 view mas sem M14 edit podem abrir a página e revisar o modelo, mas não podem editar texto, alterar estilos, alternar emails, salvar ou redefinir.",
      },

      // Cookie Policy Page
      cookiePolicy: {
        title: "Política de Cookies",
        intro: "A página Política de Cookies permite que administradores gerenciem o conteúdo da política de cookies por pesquisa, controlem se um link aparece no rodapé da pesquisa e configurem os rótulos dos botões aceitar/rejeitar do banner.",
        accessing: {
          title: "Acessando a Página de Política de Cookies",
          imageAlt: "Barra lateral com link Política de cookies",
          steps: {
            step1: "Abra uma pesquisa na <b>Lista de Pesquisas</b> para que um <b>survey_id</b> esteja ativo.",
            step2: "Na barra lateral esquerda, clique em <b>Política de cookies</b> (requer permissão <b>M20 view</b>).",
            step3: "A página abre em <b>/admin/survey/cookie-policy?survey_id={id}</b>.",
          },
        },
        overview: {
          title: "Visão Geral",
          imageAlt: "Página de Política de Cookies com editor e configurações do banner",
          desc: "Esta página gerencia o conteúdo da política de cookies para uma pesquisa específica. Os respondentes podem visualizar a política no rodapé da pesquisa quando ativada.",
          orgNote: "Nota: Isto é separado da política de cookies em nível de organização encontrada em Configurações → Detalhes da Organização → Política de Cookies.",
        },
        footerToggle: {
          title: "Alternância de Visibilidade no Rodapé",
          imageAlt: "Interruptor mostrar política de cookies no rodapé",
          items: {
            item1: "Use o interruptor \"Mostrar política de cookies no rodapé da pesquisa\" para ativar ou desativar o link do rodapé.",
            item2: "As alterações neste interruptor são salvas <b>imediatamente</b> — você não precisa clicar em Salvar.",
            item3: "O rótulo mostra Ativado ou Desativado para refletir o estado atual.",
          },
        },
        editor: {
          title: "Editor de Conteúdo da Política",
          imageAlt: "Editor rich-text da política de cookies",
          intro: "Use o editor de rich text para criar o corpo HTML da política de cookies:",
          formattingTitle: "Opções de formatação:",
          formattingItems: {
            item1: "Negrito, itálico e sublinhado.",
            item2: "Títulos (H1, H2, H3), listas com marcadores e listas numeradas.",
            item3: "Hiperlinks — clique no botão de link e insira uma URL quando solicitado.",
            item4: "Cor do texto — escolha uma cor ou redefina para o padrão.",
          },
          tablesTitle: "Tabelas:",
          tableItems: {
            item1: "Insira uma tabela de 3 colunas e 6 linhas com linha de cabeçalho usando o botão de tabela.",
            item2: "Edite o conteúdo das células diretamente no editor.",
          },
        },
        bannerSettings: {
          title: "Configurações dos Botões do Banner de Cookies",
          imageAlt: "Interruptores e rótulos dos botões aceitar e rejeitar",
          items: {
            accept: "Botão Aceitar — alterne para mostrar ou ocultar o botão aceitar no banner de cookies do respondente.",
            reject: "Botão Rejeitar — alterne para mostrar ou ocultar o botão rejeitar.",
            customLabels: "Texto do botão — opcionalmente insira rótulos personalizados para cada botão. Deixe vazio para usar o texto traduzido padrão mostrado aos respondentes.",
          },
        },
        saving: {
          title: "Salvando Alterações",
          imageAlt: "Botão Salvar na página de Política de Cookies",
          items: {
            item1: "Clique em Salvar para persistir o conteúdo do editor e as configurações dos botões do banner.",
            item2: "O interruptor de visibilidade do rodapé é salvo separadamente e imediatamente ao ser alternado.",
          },
        },
        permissionsNote: "Usuários com M20 view mas sem M20 edit podem visualizar a página, mas não podem editar conteúdo, alternar configurações ou salvar.",
      },

      // Lead Erasure Rules Page
      leadErasure: {
        title: "Regras de Exclusão de Leads",
        intro: "As Regras de Exclusão de Leads permitem que administradores definam condições automatizadas para arquivar ou excluir leads com base em valores de campo, detecção de duplicatas ou parâmetros UTM.",
        accessing: {
          title: "Acessando a Página de Regras de Exclusão de Leads",
          imageAlt: "Barra lateral com link Regras de exclusão de leads",
          steps: {
            step1: "Abra uma pesquisa na <b>Lista de Pesquisas</b> para que um <b>survey_id</b> esteja ativo.",
            step2: "Na barra lateral esquerda, clique em <b>Regras de exclusão de leads</b> (requer permissão <b>M23 view</b>).",
            step3: "A página abre em <b>/admin/survey/lead-erasure-rules?survey_id={id}</b>.",
          },
        },
        overview: {
          title: "Visão Geral",
          imageAlt: "Página de Regras de Exclusão de Leads com tabela",
          desc: "As regras são avaliadas contra leads recebidos. Quando uma regra corresponde, o sistema arquiva ou exclui o lead de acordo com a configuração da regra. Todas as regras de uma pesquisa são listadas em uma tabela paginada.",
        },
        rulesTable: {
          title: "Tabela de Regras",
          imageAlt: "Tabela de regras com colunas tipo, descrição e status",
          items: {
            columns: "Colunas: Tipo, Descrição, Status (Ativo/Inativo) e Ações (Editar, Excluir).",
            pagination: "A tabela mostra 10 regras por página com controles de paginação.",
            empty: "Se não existirem regras, a mensagem \"Nenhuma regra de exclusão de leads configurada.\" é exibida.",
          },
        },
        ruleTypes: {
          title: "Tipos de Regra",
          fieldMatch: {
            title: "Correspondência de Campo",
            desc: "Corresponda um campo do formulário de lead usando um operador e valor (ou padrão regex). Apenas campos elegíveis de lead-gen estão disponíveis.",
          },
          duplicate: {
            title: "Duplicata",
            desc: "Detecte envios duplicados por um campo escolhido. Escolha se mantém o envio mais recente ou mais antigo e arquiva o outro.",
          },
          utmMatch: {
            title: "Correspondência UTM",
            desc: "Corresponda parâmetros UTM (utm_source, utm_medium ou utm_campaign) usando um operador e valor.",
          },
        },
        operators: {
          title: "Operadores",
          fieldMatch: "Correspondência de campo suporta: Igual a, Diferente de, Contém, Começa com, Termina com, Está vazio, Corresponde a regex, Comprimento igual a e Comprimento diferente de.",
          utmMatch: "Correspondência UTM suporta: Igual a, Diferente de, Contém, Começa com, Termina com, Está vazio e Corresponde a regex.",
        },
        regexPresets: {
          title: "Predefinições de Regex",
          items: {
            letters: "Apenas letras (A–Z, a–z).",
            digits: "Apenas dígitos (0–9).",
            exactly9: "Exatamente 9 caracteres.",
            nineSame: "Todos os 9 caracteres idênticos (ex. 111111111).",
            eightPlus: "8 ou mais dígitos idênticos em sequência.",
            custom: "Personalizado (avançado) — insira uma expressão regular válida no estilo JavaScript.",
          },
        },
        addEdit: {
          title: "Adicionando e Editando Regras",
          imageAlt: "Modal Adicionar regra com campos de configuração",
          items: {
            step1: "Clique em <b>Adicionar regra</b> para abrir o modal (requer <b>M23 add</b>).",
            step2: "Selecione um tipo de regra e configure os campos obrigatórios. O tipo de regra não pode ser alterado após a criação.",
            step3: "Defina o status como Ativo ou Inativo.",
            step4: "Clique em <b>Adicionar</b> ou <b>Atualizar</b> para salvar. Um toast de sucesso aparece e a tabela é atualizada.",
          },
        },
        toggleStatus: {
          title: "Alternar Status da Regra",
          desc: "Clique no badge Ativo ou Inativo na coluna Status para alternar uma regra sem abrir o modal de edição. Requer permissão M23 edit.",
        },
        deleteRule: {
          title: "Excluir uma Regra",
          desc: "Clique no ícone de excluir na linha da regra. Um modal de confirmação aparece mostrando a descrição da regra. Confirme para remover permanentemente a regra. Requer permissão M23 del.",
        },
        permissionsNote: "M23 add é necessário para criar regras, M23 edit para editar ou alternar status, e M23 del para excluir regras.",
      },

      // Analytics Page
      analytics: {
        title: "Análises",
        intro: "O Painel de Análises na plataforma de pesquisas Vodafone Business fornece insights sobre respostas de pesquisas e níveis de maturidade.",
        accessingPage: {
          title: "Acessando a Página de Análises",
          navigate: "Navegue até Análises no menu de navegação",
          imageAlt: "Navegação de análises",
        },
        surveyDataAnalysis: {
          title: "Análise de Dados da Pesquisa",
          viewDataIntro: "Para visualizar a representação dos dados em forma de gráficos, siga estes passos:",
          steps: {
            selectSurvey: "Selecione a pesquisa desejada no menu suspenso rotulado \"Selecionar pesquisa\". A lista de pesquisas será exibida no menu suspenso.",
            chooseDateRange: "Escolha o intervalo de datas para o qual deseja ver a representação dos dados. Existem várias opções para escolher e personalizar o intervalo de acordo com suas necessidades.",
            graphsDisplay: "Os gráficos exibirão os dados de número ou porcentagem com base na pesquisa e intervalo de datas selecionados.",
            topLeads: "O gráfico também representa os \"Top 10 Leads\" de uma pesquisa específica.",
            viewAll: "Há também uma opção \"Ver Todos\" que leva o administrador a uma página onde todos os leads, junto com suas informações relacionadas, são representados em forma de tabela.",
          },
          note: "Por favor, note que os gráficos fornecem uma análise visual dos dados da pesquisa e podem exigir seleção específica de pesquisa e intervalo de datas para representação precisa.",
          graphTypes: "Existem quatro tipos de gráficos e uma lista usados neste relatório de análise para categorizar diferentes tipos de dados:",
          graphTypesList: {
            completion: "Análise de Conclusão",
            maturity: "Análise de Maturidade",
            questionWise: "Análise por Pergunta",
            exit: "Análise de Saída",
            topLeads: "Top 10 Leads",
          },
        },
        dataAnalysisGraphs: {
          title: "Gráficos de Análise de Dados",
          completionAnalysis: {
            title: "Análise de Conclusão",
            desc1: "Este gráfico mostra o número de pessoas que completaram a pesquisa em comparação com o número total de participantes.",
            desc2: "Por exemplo, neste gráfico, \"total de respostas da pesquisa\" refere-se ao número de tentativas de resposta feitas pelos usuários finais. \"Pesquisas completas\" refere-se ao número de pesquisas que foram concluídas.",
          },
          maturityAnalysis: {
            title: "Análise de Maturidade",
            desc1: "Este gráfico mostra a porcentagem do Estágio de Maturidade alcançado para uma pesquisa específica.",
            desc2: "O gráfico representa estágios em porcentagem. A porcentagem total é sempre 100%. Cada estado é representado por uma cor diferente.",
          },
          questionWiseAnalysis: {
            title: "Análise por Pergunta",
            desc1: "Este gráfico representa a distribuição de respostas para cada pergunta na pesquisa.",
            desc2: "A análise por pergunta refere-se ao exame e avaliação das respostas da pesquisa para cada pergunta individual.",
          },
          exitAnalysis: {
            title: "Análise de Saída",
            desc: "A análise de saída mostra em qual pergunta o usuário final deixou a pesquisa ao preencher o formulário de geração de leads.",
          },
        },
        dataLeadsTable: {
          title: "Tabela de Dados de Leads",
          topLeadsTitle: "Top 10 Leads",
          topLeadsDesc: "A tabela abaixo exibe as informações dos 10 principais usuários com maior pontuação.",
          viewAllClick: "Clique em \"Ver Todos\"",
          fields: {
            date: "A data da pesquisa é mostrada quando o formulário de geração de leads é preenchido pelo usuário final.",
            reportLink: "O Link do Relatório mostra o link do relatório gerado ao preencher a pesquisa.",
            firstName: "O campo Nome exibe o nome do usuário que respondeu à pesquisa.",
            lastName: "O campo Sobrenome exibe o sobrenome do usuário.",
            email: "O campo Endereço de Email exibe o email do lead.",
            phone: "O Número de Telefone mostra o telefone do lead.",
            organization: "Nome da Organização mostra a organização do lead.",
            industry: "Indústria representa a indústria à qual o lead pertence.",
            responses: "O campo Nº de Respostas indica o número máximo de perguntas visitadas pelo lead.",
            maturityLevel: "O nível de maturidade indica o status recomendado com base na pontuação da resposta.",
            totalScore: "Pontuação total mostra a pontuação total da resposta.",
            completed: "O campo concluído indica se a pesquisa foi concluída ou não.",
            uniqueId: "O campo ID Único representa um identificador distintivo atribuído a cada lead.",
            utmSource: "O campo UTM Source captura as informações da fonte de tráfego.",
            utmMedium: "O campo UTM Medium especifica o meio da campanha.",
            utmCampaign: "O campo UTM Campaign registra o nome da campanha.",
            termsConditions: "O campo T & C indica se o lead aceitou os termos e condições.",
            action: "A coluna Ação fornece ações rápidas para cada registro.",
          },
          exportButton: "Botão Exportar para Excel",
          exportDesc: "Ao clicar no botão exportar, os dados da tabela serão exportados para excel e baixados no sistema do usuário.",
          deleteSelectedTitle: "Botão Excluir Leads Selecionados",
          deleteSelectedSteps: {
            step1: "Selecione um ou mais leads da tabela clicando na caixa de seleção ao lado de cada registro.",
            step2: "Clique no botão Excluir leads selecionados acima da tabela.",
            step3: "Um popup de confirmação aparecerá pedindo aprovação antes da exclusão.",
            step4: "Após confirmar, os leads selecionados serão permanentemente removidos.",
          },
        },
      },

      // Activity Logs Page  
      activityLogs: {
        title: "Registro de Atividade",
        intro: "O Registro de Atividade fornece um rastro de auditoria somente leitura das ações realizadas por usuários e pelo sistema (por exemplo, atualizações de webhook, mudanças de status de pesquisa, edições de estágio). Cada entrada mostra a mensagem, o tipo de ação, quem executou e a data e hora exatas.",
        table: {
          title: "Tabela do registro de atividade",
          intro: "A tabela lista atividades recentes com as seguintes colunas:",
          columns: {
            message: "Mensagem - Descrição legível do evento (por exemplo, Webhook eloqua para RCA Português atualizado por shonbit).",
            action: "Ação - Rótulo categórico da ação (por exemplo, Atualizar webhooks, Atualizar status do eloqua, Adicionar webhook, Atualizar status da pesquisa, Adicionar formulário Eloqua, Excluir estágio, Atualizar estágio, Adicionar estágio, Atualizar página de relatório, Atualizar nome do relatório).",
            addedBy: "Adicionado por - Usuário que realizou a ação (por exemplo, shonbit) ou Sistema para eventos automáticos.",
            dateTime: "Data e hora - Registro do evento em DD-MM-YYYY | hh:mm A.",
          },
          note: "As linhas são informativas; não há uma coluna de ação separada nesta página. Use a paginação para navegar no histórico completo.",
          imageAlt: "Visualização da tabela do registro de atividade",
        },
        pagination: {
          title: "Paginação e controles de exibição",
          rowsPerPage: "Linhas por página - Escolha quantos registros mostrar por página (por exemplo, 30).",
          rangeTotal: "Intervalo e total - Mostra o recorte atual e a contagem total (por exemplo, 1-30 de 51).",
          navigation: "Navegação - Use os controles primeiro/anterior/próximo/último para mover entre páginas.",
        },
      },

      tickets: {
        title: "Tickets",
        intro: "Os tickets registam alertas de segurança e problemas manuais na plataforma. Tickets gerados pelo sistema são criados automaticamente para atividade suspeita; utilizadores com permissão também podem criar tickets manualmente, atribuí-los, atualizar o estado e adicionar comentários.",
        accessing: {
          title: "Aceder à Página de Tickets",
          imageAlt: "Barra lateral principal com a ligação Tickets",
          steps: {
            step1: "Inicie sessão no portal de administração.",
            step2: "Na barra lateral esquerda, clique em <b>Tickets</b> (requer permissão <b>M7 view_own</b>).",
            step3: "A página abre em <b>/admin/tickets</b>.",
          },
        },
        listOverview: {
          title: "Lista de Tickets",
          imageAlt: "Lista de tickets com cartões de resumo, filtros e tabela",
          intro: "A lista mostra todos os tickets que pode ver. Utilizadores com view_all também veem cartões de resumo por estado no topo.",
          dashboardTitle: "Resumo do painel (apenas view_all)",
          dashboardItems: {
            open: "Aberto — tickets à espera de ação.",
            acknowledged: "Reconhecido — tickets vistos mas ainda não tratados.",
            inProgress: "Em progresso — tickets em tratamento.",
            resolved: "Resolvido — tickets fechados.",
            total: "Total — contagem de todos os tickets.",
          },
          filtersTitle: "Pesquisa e filtros",
          filtersItems: {
            search: "Pesquisa — filtra por título, atribuído ou quem levantou o ticket.",
            status: "Estado — Aberto, Reconhecido, Em progresso, Resolvido ou Falso positivo.",
            severity: "Gravidade — Baixa, Média, Alta ou Crítica.",
            source: "Origem — Sistema (automático) ou Utilizador (manual).",
            assignee: "Atribuído — filtrar por utilizador ou Não atribuído (apenas view_all).",
          },
          tableTitle: "Colunas da tabela",
          tableItems: {
            title: "Título — clique no título ou linha para abrir detalhes.",
            type: "Tipo — categoria do alerta (ex.: Força bruta, Manual).",
            severity: "Gravidade — distintivo com cor.",
            status: "Estado — distintivo com cor.",
            source: "Origem — sistema ou utilizador.",
            assignedTo: "Atribuído a — responsável ou Não atribuído.",
            created: "Criado — data e hora.",
            action: "Ação — botão Ver abre a página de detalhe.",
          },
        },
        createTicket: {
          title: "Criar um Ticket",
          imageAlt: "Modal criar ticket com título, gravidade, descrição e atribuição",
          items: {
            step1: "Clique em <b>+ Criar ticket</b> (requer <b>M7 view_own</b>).",
            step2: "Introduza um título e selecione a gravidade (obrigatório). Opcionalmente adicione descrição e atribua a um utilizador.",
            step3: "Clique em <b>Criar ticket</b> para guardar. A lista é atualizada.",
            step4: "Tickets gerados pelo sistema são criados automaticamente e não podem ser criados por este modal.",
          },
        },
        ticketDetail: {
          title: "Detalhe do Ticket",
          imageAlt: "Página de detalhe com informação, comentários, estado e atribuição",
          items: {
            overview: "O painel principal mostra título, descrição, estado, gravidade, tipo de alerta, origem, IP, levantado por, atribuído e datas.",
            comments: "A secção de comentários lista notas dos utilizadores. Adicione um comentário e clique em Publicar (requer M7 comment).",
            status: "Utilizadores com M7 view_all podem alterar o estado no painel direito e clicar em Atualizar estado.",
            assign: "Utilizadores com M7 view_all podem atribuir o ticket e clicar em Atribuir.",
          },
        },
        alertTypes: {
          title: "Tipos de Alerta",
          items: {
            bruteForce: "Força bruta — tentativas repetidas de login falhadas.",
            unauthorizedToken: "Token não autorizado — uso de token inválido ou expirado.",
            unknownUser: "Utilizador desconhecido — tentativa de login por conta não reconhecida.",
            inactiveUser: "Utilizador inativo — login por conta desativada.",
            suspiciousIp: "IP suspeito — atividade de um IP invulgar.",
            afterHours: "Fora de horário — acesso fora do horário configurado.",
            manual: "Manual — ticket criado por um utilizador.",
          },
        },
        notifications: {
          title: "Definições de Notificação de Tickets",
          imageAlt: "Página de destinatários de notificação de tickets",
          intro: "Administradores podem configurar quais utilizadores recebem email quando novos tickets são criados.",
          items: {
            step1: "Vá a <b>Definições → Notificações de tickets</b> em <b>/admin/settings/ticket-notifications</b>.",
            step2: "Pesquise utilizadores e adicione-os como destinatários. Remova com o controlo de eliminar em cada chip.",
            step3: "Clique em <b>Guardar</b> para persistir a lista.",
          },
        },
        permissionsNote: "M7 view_own permite ver e criar tickets. M7 view_all adiciona resumo, filtro de atribuído, atualização de estado e atribuição. M7 comment permite publicar comentários.",
      },

      // Dashboard/Survey List Page
      dashboard: {
        title: "Lista de Pesquisas",
        intro: "A Lista de Pesquisas fornece uma maneira estruturada de gerenciar, rastrear e editar pesquisas. Inclui opções para pesquisar, filtrar e criar novas pesquisas, exibindo detalhes essenciais como status de conclusão, idioma e detalhes de publicação.",
        
        navigating: {
          title: "Navegando para a Lista de Pesquisas",
          step1: "Faça login em sua conta.",
          step2: "Na barra lateral esquerda, após login bem-sucedido, o usuário pode ser redirecionado para a visualização da Lista de Pesquisas. Ou clique em Pesquisa para acessar a página da Lista de Pesquisas.",
          step3: "A visualização detalhada da pesquisa é mostrada na imagem",
          imageAlt: "Navegação da Lista de Pesquisas",
        },

        header: {
          title: "Seção de Cabeçalho",
          userProfile: "Perfil do Usuário e Seletor de Idioma",
          userProfileItems: {
            languageDropdown: "Menu de Idiomas: Permite aos usuários alternar entre diferentes idiomas.",
            userProfile: "Perfil do Usuário: Exibe o nome e email do usuário conectado.",
            dropdownMenu: "Menu Suspenso: Provavelmente contém opções para sair ou alterar configurações do Perfil.",
          },
          breadcrumb: "Breadcrumb",
          breadcrumbDesc: "Na Lista de Pesquisas, o nome da página atualmente ativa é exibido no lado direito do layout. Isso ajuda os usuários a identificar rapidamente em qual seção da plataforma estão trabalhando.",
          createSurvey: "Botão Criar Pesquisa",
          createSurveyItems: {
            location: "Localizado no lado direito como um botão vermelho rotulado + Criar Pesquisa.",
            purpose: "Permite aos usuários criar uma nova pesquisa rapidamente.",
          },
        },

        filters: {
          title: "Filtros da Lista de Pesquisas",
          intro: "Esta seção exibe uma lista de pesquisas com detalhes, incluindo status de conclusão, idioma e detalhes de modificação.",
          searchBar: "Barra de Pesquisa",
          searchBarDesc: "Os usuários podem pesquisar uma pesquisa específica inserindo uma palavra-chave.",
          filtersTitle: "Filtros",
          filtersDesc: "A Lista de Pesquisas fornece opções de filtragem para ajudar os usuários a encontrar e gerenciar pesquisas facilmente. Abaixo estão os detalhes do Filtro de Idioma e Filtro de Status.",
          languageFilter: "Filtro de Idioma",
          languageFilterDesc: "Permite aos usuários filtrar pesquisas com base no idioma em que foram criadas.",
          allLanguages: "Todos - Mostra todas as pesquisas independentemente do idioma.",
          statusFilter: "Filtro de Status",
          statusFilterDesc: "Ajuda os usuários a filtrar pesquisas com base em seu status de publicação.",
          statusOptions: {
            all: "Todas as Pesquisas - Exibe todas as pesquisas, independentemente de seu status.",
            published: "Publicadas - Mostra apenas pesquisas que estão ativas e disponíveis para respondentes.",
            draft: "Rascunho - Exibe pesquisas que ainda estão em modo de rascunho e ainda não foram publicadas.",
            example: "Exemplo de Uso: Se um usuário selecionar \"Rascunho\", apenas as pesquisas com status \"Rascunho\" serão mostradas, ocultando as publicadas.",
          },
        },

        tableColumns: {
          title: "Colunas da Tabela de Pesquisas",
          intro: "A lista de pesquisas é exibida em formato de tabela com as seguintes colunas:",
          surveyName: "Nome da Pesquisa - Exibe o título da pesquisa e o número de seções/perguntas.",
          completion: "Conclusão / Respostas - Mostra o número de respostas concluídas vs. total de respostas.",
          language: "Idioma - Indica o idioma da pesquisa.",
          status: "Status - Mostra se a pesquisa está em status \"Rascunho\" ou \"Publicada\".",
          addedOn: "Adicionado em / Adicionado por - Exibe a data de criação da pesquisa e o nome do usuário que a criou.",
          lastModified: "Última Modificação - Mostra quando a pesquisa foi atualizada pela última vez.",
          publishedOn: "Publicado em - Exibe a data em que a pesquisa foi publicada.",
          actions: "Ações - Esta coluna contém várias ações que podem ser executadas em cada pesquisa. As explicações detalhadas são fornecidas abaixo na seção 'Ação na lista de pesquisas'",
        },

        actions: {
          title: "Ação na lista de pesquisas",
          editSurvey: {
            label: "Ação 1 - Editar Pesquisa:",
            desc: "Quando o usuário clica na ação 'editar pesquisa', ele será redirecionado para o construtor de P&R para edição. No construtor de P&R, ele poderá gerenciar as categorias da pesquisa, incluindo \"Seções\", \"Perguntas e Respostas\", \"Adicionar/Editar variáveis\", Gerenciar \"Relatórios\", Gerenciar \"URL\".",
          },
          shareSurvey: {
            label: "Ação 2 - Compartilhar Pesquisa:",
            desc: "'Compartilhar pesquisa' leva à página 'publicar e compartilhar' onde se pode personalizar a URL adicionando diferentes combinações de variáveis UTM para rastrear a fonte, meio e campanha. A URL da pesquisa também inclui uma opção de copiar e colar para fácil compartilhamento.",
          },
          moveToPublish: {
            label: "Ação 3 - Mover para Publicar/Mover para Rascunhos:",
            desc: "Esta ação permite ao usuário publicar uma pesquisa se estiver em Rascunhos ou Mover uma Pesquisa para Rascunhos se já estiver publicada. Uma vez que a pesquisa é publicada, a URL do frontend da pesquisa se torna acessível.",
          },
          viewLeads: {
            label: "Ação 4 - Ver Leads:",
            desc: "Esta ação redireciona para a página 'Todos os Leads' em análises, mostrando todas as respostas preenchidas pelos usuários finais para aquela Pesquisa específica.",
          },
          duplicate: {
            label: "Ação 5 - Duplicar:",
            desc: "Este recurso duplica todos os dados, variáveis, relatórios e todos os outros dados da pesquisa escolhida sob um novo nome de pesquisa. Os Leads, Análises, URL, porém, não são copiados.",
          },
          delete: {
            label: "Ação 6 - Excluir:",
            desc: "O recurso \"Excluir\" ajuda a excluir uma Pesquisa. Todos os detalhes de uma pesquisa são excluídos de todos os lugares, incluindo Leads, URL, P&R, Variáveis.",
          },
        },
      },

      // Q&A Builder Page
      qandaBuilder: {
        title: "Construtor de P&R",
        intro: "Esta interface fornece uma abordagem estruturada e fácil de usar para criar e gerenciar pesquisas.",
        introItems: {
          organize: "Os usuários podem organizar perguntas em seções",
          questionTypes: "Escolher diferentes tipos de perguntas (texto, múltipla escolha etc.)",
          editManage: "Editar, excluir ou reorganizar perguntas",
          navigate: "Navegar pelos vários recursos de criação de pesquisas com eficiência",
        },
        access: {
          title: "Acessando o Construtor de P&R",
          step1: "Navegue até Pesquisas e edite qualquer pesquisa clicando no botão Ações na tabela de listagem.",
          step2: "Clique no nome destacado no menu suspenso para abrir a página.",
          imageAlt: "Acesso ao Construtor de P&R",
        },
        surveyInfo: {
          title: "Informações da Pesquisa (Área de Conteúdo Principal)",
          surveyTitleLabel: "Título da Pesquisa:",
          surveyTitleValue: "\"IE Test\"",
          surveyDescriptionLabel: "Descrição da Pesquisa:",
          surveyDescriptionValue: "Texto de espaço reservado indicando que os usuários podem adicionar uma breve descrição da pesquisa.",
          duplicateNote: "É possível duplicar o questionário atual da pesquisa clicando no ícone de duplicar",
          statsLabel: "Estatísticas da Pesquisa:",
          statsValue: "Mostra que a pesquisa consiste em 7 seções e 35 perguntas.",
          exampleLabel: "Por exemplo",
          exampleDesc: "uma pesquisa \"IE Test\" pode ter 7 seções, cada uma contendo perguntas relevantes para esse aspecto específico da experiência do cliente. O total de perguntas é 35 no total",
          imageAlt: "Informações da Pesquisa",
        },
        sectionsQuestions: {
          title: "Seções e Perguntas da Pesquisa",
          sectionTitle: "Seção:",
          sectionDesc: "As seções em uma pesquisa são divisões ou categorias que ajudam a organizar e estruturar as perguntas. Elas são usadas para agrupar perguntas relacionadas. Isso não apenas melhora a clareza da pesquisa, mas também auxilia na análise de dados.",
          sectionItems: {
            focus: "Esta seção se concentra em como as empresas abordam a adoção de tecnologia em nuvem.",
            describes: "Descreve fatores como estratégia de negócios, metas, prontidão para adoção da nuvem e disposição para investimento.",
            questionsCount: "Neste exemplo, contém 5 perguntas relacionadas ao tema.",
            iconsNote: "Cada seção tem ícones ao lado para editar, excluir e duplicar",
            exampleQuestionsTitle: "Exemplos de perguntas:",
            exampleQuestion1: "\"Quão importante é a adoção de tecnologia em nuvem para o crescimento e sucesso do seu negócio?\" Tipo de resposta: texto",
            exampleQuestion2: "\"Qual nível de orçamento sua empresa alocou para adoção da nuvem?\" Tipo de resposta: múltipla escolha",
            questionIcons: "Cada pergunta tem ícones ao lado para editar, excluir, duplicar ou reorganizar perguntas.",
          },
          imageAlt: "Seções e Perguntas da Pesquisa",
        },
        addButtons: {
          title: "Botões Adicionar Pergunta e Adicionar Seção",
          addQuestionTitle: "Adicionar Pergunta",
          addQuestionDesc: "Este botão é usado para adicionar uma nova pergunta à pesquisa. Ao clicar, abre um formulário onde o usuário pode escolher o tipo de pergunta (por exemplo, múltipla escolha, texto etc.) e configurar os detalhes. \"Adicionar Pergunta\" adiciona uma única pergunta dentro de uma seção existente.",
          addSectionTitle: "Adicionar Seção",
          addSectionDesc: "Este botão é usado para adicionar uma nova seção à pesquisa. As seções ajudam a organizar perguntas em diferentes categorias ou tópicos. \"Adicionar Seção\" cria uma nova seção para agrupar perguntas relacionadas.",
          imageAlt: "Adicionar Pergunta e Seção",
        },
        questionTypes: {
          title: "Tipos de Perguntas",
          intro: "No design de pesquisas, diferentes tipos de perguntas podem ser usados para coletar diferentes tipos de informações. Alguns tipos comuns incluem",
          multipleChoiceTitle: "Perguntas de Múltipla Escolha:",
          multipleChoiceDesc: "Essas perguntas fornecem uma lista de opções, e os respondentes devem selecionar uma ou mais opções. São usadas quando você deseja oferecer opções de resposta predefinidas. São eficientes para coletar dados estruturados e quantitativos. Por exemplo, \"Quão importante é a adoção de tecnologia em nuvem para o seu negócio? (Marque todas as opções aplicáveis).\"",
          textTitle: "Perguntas de Texto:",
          textDesc: "Essas perguntas exigem que os respondentes forneçam respostas em forma de texto. São usadas quando você deseja coletar respostas abertas e permitir que os participantes forneçam suas próprias opiniões. Por exemplo, \"Descreva sua experiência com nosso produto com suas próprias palavras.\"",
          opinionTitle: "Perguntas de Escala de Opinião",
          opinionDesc: "Essas perguntas permitem que os respondentes classifiquem sua opinião ou satisfação em uma escala, geralmente variando de bom a ruim ou de concordo totalmente a discordo totalmente. São usadas para coletar dados quantitativos e medir atitudes ou percepções. Por exemplo, \"Em uma escala de 1 a 5, como você avaliaria sua satisfação geral com nosso atendimento ao cliente?\"",
          imageAlt: "Tipos de Perguntas",
        },
      },

      // Form Builder Page
      formBuilder: {
        title: "Construtor de Formulários",
        intro: "O Construtor de Formulários é uma interface intuitiva que permite aos usuários criar e personalizar formulários para coleta de dados, avaliações e pesquisas. A ferramenta fornece uma maneira fácil de configurar campos do formulário, definir entradas obrigatórias e visualizar o formulário final antes de publicar.",
        access: {
          title: "Acessando o construtor de formulários",
          step1: "Navegue até Pesquisas e edite qualquer pesquisa clicando no botão Ações na tabela de listagem.",
          step2: "Clique no nome destacado no menu suspenso para abrir a página.",
          imageAlt: "Acesso ao Construtor de Formulários",
        },
        leadForm: {
          title: "Página do Formulário de Leads",
          breadcrumbDesc: "Personalize a página do formulário de leads onde os usuários preenchem a pesquisa. Também exiba o nome da pesquisa escolhida no breadcrumb da página do Construtor de Formulários.",
          headingDesc: "Insira o título e a descrição para fornecer uma melhor compreensão do formulário.",
          addFieldDesc: "Clique no ícone \"Adicionar campo\" localizado na parte inferior do formulário. \"Adicionar Campo\" adicionará um novo campo permitindo selecionar o \"Tipo\" conforme necessário. Os tipos podem ser – \"Campo de Texto\", \"Área de texto\", \"Email\", \"Menu suspenso\", \"Número de telefone\", \"Caixas de seleção\", \"Botões de rádio\", \"Oculto\". A explicação detalhada do campo de entrada com exemplo geral está descrita na página Tipos de campos de entrada.",
          addedFieldDesc: "O campo adicionado fica visível como o último campo com o \"Tipo\" selecionado. O campo de entrada no formulário exibe o \"Rótulo\" e o \"Placeholder\" juntamente com os ícones \"Editar\", \"Excluir\", o alternador \"Obrigatório\" e o ícone de reordenar. Isso permite a personalização de entradas e rótulos no formulário.",
          imageAlt: "Página do Formulário de Leads",
        },
        userGuide: {
          title: "Guia do Usuário: Construtor de Formulários",
          step1Title: "Etapa 1: Configuração do Campo",
          editIconDesc: "Ícone de editar - é usado para editar os detalhes do campo específico. Ao clicar, um popup com o formulário aparece na tela onde \"Nome\", \"Rótulo\", \"placeholder\" e opções para \"Botões de rádio\" e \"Caixas de seleção\" podem ser editados. Todos os campos são obrigatórios.",
          noteDesc: "NOTA - Recomenda-se não editar o nome do campo. É um nome único adicionado como \"variável de lead gen\". Se o nome do campo for editado, um nome único deve ser fornecido para cada campo, pois será difícil para o usuário rastrear o registro associado a esse nome.",
          step2Title: "Etapa 2: Excluir campo",
          step2Desc: "Os usuários têm a opção de excluir qualquer campo a qualquer momento. O popup de confirmação de exclusão aparecerá na tela.",
          step4Title: "Etapa 4: Adicionar novos campos",
          step4Desc: "\"Adicionar campo\" pode ser clicado para adicionar um novo campo e seu tipo pode ser selecionado no menu suspenso.",
          step5Title: "Etapa 5: Pré-visualização do formulário",
          step5Desc: "Esta captura de tela oferece uma prévia do formulário de lead, permitindo ver como ele aparecerá para os usuários. Revise o layout e o design do formulário.",
          step6Title: "Etapa 6: Personalização do botão CTA",
          step6Desc: "Você pode personalizar o alinhamento do botão de envio. Por padrão, o texto está definido como \"submit\" e alinhado à esquerda. Salve as alterações necessárias clicando no botão salvar.",
          imageAlt: "Guia do Construtor de Formulários",
        },
      },

      // Report Builder Page
      reportBuilder: {
        title: "Construtor de Relatórios",
        intro: "O Construtor de Relatórios ajuda a criar um relatório abrangente para apresentar informações, descobertas ou análises de uma pesquisa específica. Normalmente inclui dados, fatos e insights obtidos por meio de pesquisa ou investigação. O relatório pode ser personalizado nesta seção para transmitir informações de forma clara e organizada, muitas vezes para apoiar a tomada de decisão ou fornecer recomendações. O relatório pode ser baixado em formato PDF. Resultados em forma de gráficos também podem ser exibidos. Recomendações do questionário aos usuários também podem ser fornecidas.",
        featuresTitle: "Recursos de um relatório -",
        features: {
          edit: "Editar - Este recurso permite fazer alterações em um relatório.",
          duplicate: "Duplicar - Com este recurso, você pode criar uma cópia de um relatório, incluindo todas as páginas, na mesma ordem, mas com um nome diferente.",
          default: "Tornar Padrão - Este recurso permite selecionar um relatório específico como padrão para uma pesquisa.",
          delete: "Excluir - Este recurso permite remover um relatório do sistema",
        },
        builder: {
          title: "Construtor de Relatórios",
          step1: "Navegue até Pesquisas e edite qualquer pesquisa clicando no botão Ações na tabela de listagem.",
          step2: "Clique no nome destacado no menu suspenso para abrir a página.",
          imageAlt: "Acesso ao Construtor de Relatórios",
        },
        editorView: {
          title: "Visualização do Editor",
          intro: "Esta ferramenta ajuda a criar seu relatório personalizado usando várias propriedades fornecidas. Os pontos a seguir explicam a barra superior do editor e seus recursos um a um.",
          items: {
            responsive: "Relatório responsivo pode ser feito para desktop, tablet e mobile",
            highlight: "O componente de visualização destaca cada componente na página do relatório individualmente.",
            preview: "O ícone de olho serve para pré-visualizar a página do relatório.",
            fullscreen: "O ícone de tela cheia é usado para redimensionar (Maximizar/Minimizar) a página do relatório. A edição pode ser feita quando a página está maximizada. As páginas do relatório podem ser alternadas quando estão minimizadas.",
            viewCode: "Ver código é usado para ver o código HTML e CSS por trás de uma página do relatório.",
            undoRedo: "Os ícones \"Desfazer\" e \"Refazer\" são usados para desfazer um erro, como apagar a palavra errada em uma frase. O refazer restaura ações que foram desfeitas anteriormente.",
            download: "Seta de download para baixar o relatório.",
            deleteAll: "Use o ícone de lixeira para remover todos os elementos do relatório.",
            blocks: "Abra o menu de blocos para ver os elementos.",
          },
        },
        basicElements: {
          title: "Elementos básicos incluem",
          items: {
            columns: "Coluna - Diferentes combinações de colunas podem ser usadas de acordo com as necessidades do relatório.",
            text: "Texto - usado para inserir texto na página do relatório",
            link: "Link - usado para fornecer links da web para botão ou texto",
            media: "Imagens, vídeos e mapas podem ser incluídos na página do relatório de acordo com a necessidade, simplesmente arrastando e soltando.",
          },
        },
        formElements: {
          title: "Elementos de formulário incluem",
          items: {
            intro: "Se você precisar incluir um formulário no seu relatório, há várias opções disponíveis:",
            types: "Diferentes tipos de campos de entrada estão disponíveis. Eles podem ser usados conforme necessário. Uma breve explicação de cada campo de entrada e seu uso geral é fornecida na seção \"Tipos de campos de entrada\".",
          },
        },
        extraElements: {
          title: "Elementos extras incluem",
          items: {
            tooltip: "Tooltip - Um tooltip é uma pequena caixa pop-up que aparece quando o usuário passa o mouse ou clica em um elemento, como um botão ou ícone. Ele fornece informações adicionais ou contexto sobre o elemento, ajudando os usuários a entender sua finalidade ou funcionalidade. Tooltips são frequentemente usados para fornecer descrições breves, instruções ou rótulos para elementos da interface. Eles podem melhorar a experiência do usuário fornecendo dicas úteis ou esclarecendo conteúdo ambíguo.",
            countdown: "Contagem regressiva - A contagem regressiva é um recurso que pode ser incluído em um relatório. É um elemento visual que exibe um temporizador contando para uma data ou evento específico. Pode ser usado para criar senso de urgência ou destacar um prazo ou data importante. A contagem pode ser personalizada para mostrar dias, horas, minutos e segundos restantes, e pode ser estilizada para combinar com o design do relatório.",
            customCode: "Código personalizado - Código personalizado refere-se à capacidade de adicionar e incorporar seu próprio código no relatório. Esse recurso permite incluir scripts personalizados ou trechos de código para modificar o comportamento ou a aparência do relatório. Ele oferece flexibilidade e opções de personalização além da funcionalidade padrão da ferramenta. É importante observar que adicionar código personalizado exige conhecimento de programação para garantir implementação e compatibilidade adequadas.",
            typed: "Typed - O elemento \"Typed\" permite adicionar um efeito de digitação ao texto, simulando a aparência de alguém digitando o conteúdo. Esse efeito pode ser usado para criar interatividade ou engajar leitores. O elemento normalmente exibe o texto caractere por caractere, com um pequeno atraso entre cada um. Pode ser personalizado para controlar a velocidade e aplicar estilos adicionais.",
            tabs: "Tabs - As abas, no contexto do construtor de relatórios, referem-se a um recurso que permite criar várias seções ou páginas dentro do relatório. Cada aba representa uma seção onde você pode organizar e apresentar informações diferentes. Usando abas, é possível dividir o relatório em seções lógicas, facilitando a navegação e o acesso a informações específicas. Cada aba pode ter seu próprio conteúdo, como texto, imagens ou gráficos. As abas oferecem uma forma estruturada de apresentação, permitindo que os leitores foquem em uma seção por vez e alternem quando necessário. Este recurso melhora a legibilidade e a experiência do usuário.",
          },
        },
        customization: {
          title: "Opções gerais de personalização e dimensões:",
          intro: "Quando quaisquer elementos são incluídos nas explicações acima, este painel é aberto para personalização adicional do relatório.",
          generalTitle: "Geral",
          generalDesc: "Aqui existem diferentes opções disponíveis onde é possível definir a posição do elemento como static,",
          positionTitle: "Posição no CSS",
          positionItems: {
            static: "Static - Este é o valor padrão. Elementos com position: static são posicionados de acordo com o fluxo normal do documento. Eles não são afetados por propriedades top, bottom, left, right ou z-index.",
            relative: "Relative - Elementos com position: relative são posicionados em relação à sua posição normal no fluxo do documento. Usando top, bottom, left e right, você pode ajustar a posição do elemento em relação ao seu lugar original.",
            absolute: "Absolute - Elementos com position: absolute são posicionados em relação ao ancestral posicionado mais próximo (um ancestral com position diferente de static). Se nenhum ancestral posicionado for encontrado, o elemento será posicionado em relação ao bloco contêiner inicial, geralmente o viewport. As propriedades top, bottom, left e right podem ser usadas para especificar a posição exata.",
            fixed: "Fixed - Elementos com position: fixed são posicionados em relação ao viewport e não se movem quando a página é rolada. Eles permanecem na mesma posição independentemente do scroll. As propriedades top, bottom, left e right podem ser usadas para especificar a posição do elemento.",
          },
          floatDisplayTitle: "Propriedade Float e Display no CSS",
          floatDisplayIntro: "Essas propriedades CSS são ferramentas poderosas para controlar o layout e o posicionamento de elementos em uma página.",
          floatItem: "float - A propriedade float é usada para especificar se um elemento deve flutuar à esquerda, à direita ou não flutuar. Quando um elemento flutua, ele sai do fluxo normal do documento e outros elementos fluem ao seu redor. Isso pode ser útil para criar layouts em colunas ou posicionar elementos lado a lado. Os valores possíveis para float são left, right e none.",
          displayItem: "display - A propriedade display é usada para especificar como um elemento deve ser exibido. Ela controla se o elemento é tratado como bloco, inline ou outro tipo. Os valores mais comuns são block, inline e none.",
          displaySubItems: {
            block: "Elementos block começam em uma nova linha e ocupam toda a largura disponível. Eles podem receber margens, preenchimento e propriedades de largura/altura.",
            inline: "Elementos inline não começam em uma nova linha e ocupam apenas a largura necessária. Eles não podem receber propriedades de largura/altura e margens/paddings afetam apenas o texto ao redor.",
            none: "none é usado para ocultar completamente um elemento. O elemento não é renderizado e não ocupa espaço na página.",
          },
          dimensionTitle: "Dimensão",
          dimensionDesc: "Para fornecer dimensão e espaço adequados a qualquer elemento, você pode usar e definir a propriedade para cada elemento individualmente usando este painel.",
          dimensionIntro: "No CSS, as seguintes propriedades são usadas para controlar as dimensões e o espaçamento de elementos",
          dimensionItems: {
            width: "Width - A propriedade width é usada para definir a largura de um elemento. Pode ser especificada em unidades como pixels (px), porcentagem (%) ou outras unidades relativas.",
            maxWidth: "Max-width - A propriedade max-width é usada para definir a largura máxima que um elemento pode ter. Se o conteúdo exceder essa largura, ele será redimensionado para caber no limite especificado.",
            maxHeight: "Max-height - A propriedade max-height é usada para definir a altura máxima que um elemento pode ter. Se o conteúdo exceder essa altura, ele será redimensionado para caber no limite especificado.",
            margin: "Margin - A propriedade margin é usada para controlar o espaço ao redor de um elemento. Ela define o espaço entre a borda do elemento e os elementos adjacentes. Pode ter valores diferentes para cada lado ou um único valor para todos os lados.",
            padding: "Padding - A propriedade padding é usada para controlar o espaço entre o conteúdo do elemento e sua borda. Pode ter valores diferentes para cada lado ou um único valor para todos os lados.",
            note: "Essas propriedades são essenciais para controlar o layout e o espaçamento de elementos em uma página. Elas permitem criar designs responsivos e visualmente atraentes ajustando dimensões e espaçamentos conforme necessário.",
          },
        },
        typography: {
          title: "Tipografia",
          intro: "Tipografia refere-se ao estilo, arranjo e aparência do texto em um documento ou design. Envolve seleção de fonte, tamanho, peso, espaçamento de linhas e alinhamento de texto. As escolhas tipográficas impactam a legibilidade, a aparência visual e a experiência geral do usuário.",
          items: {
            fontFamily: "Família da Fonte - A propriedade font-family é usada para especificar a fonte ou lista de fontes do texto. Permite definir uma lista de fontes prioritárias. Por exemplo: font-family: Arial, sans-serif; usará Arial se disponível, caso contrário uma fonte sans-serif.",
            fontSize: "Tamanho da Fonte - A propriedade font-size define o tamanho do texto. Pode ser especificada em pixels (px), porcentagens (%), etc. Por exemplo: font-size: 16px; define o texto com 16 pixels.",
            fontWeight: "Peso da Fonte - A propriedade font-weight define a espessura/negrito do texto. Pode ter valores como normal, bold, bolder, lighter, ou valores numéricos 100, 200, …, 900.",
            letterSpacing: "Espaçamento entre Letras - A propriedade letter-spacing controla o espaço entre caracteres. Pode ser especificada em pixels (px) ou unidades relativas (em, rem).",
            color: "Cor - A propriedade color define a cor do texto. Pode ser especificada por nomes de cores, códigos hexadecimais, valores RGB ou HSL.",
            lineHeight: "Altura da Linha - A propriedade line-height define a altura de uma linha de texto. Pode ser especificada em pixels (px) ou porcentagens. Por exemplo: line-height: 1.5; define a altura como 1,5 vezes o tamanho da fonte.",
            textAlign: "Alinhamento do Texto - A propriedade text-align define o alinhamento horizontal do texto. Pode ter valores left, right, center ou justify.",
            textShadow: "Sombra do Texto - Esta propriedade adiciona sombra ao texto, permitindo definir cor, deslocamento horizontal, deslocamento vertical e raio de desfoque.",
          },
          decorationsTitle: "Decorações",
          decorationsItems: {
            backgroundColor: "Cor de Fundo - A propriedade background-color define a cor de fundo de um elemento.",
            border: "Borda - A propriedade border cria uma borda ao redor do elemento e pode definir largura, estilo e cor.",
            borderRadius: "Raio da Borda - A propriedade border-radius cria cantos arredondados.",
            boxShadow: "Sombra da Caixa - A propriedade box-shadow adiciona sombra ao elemento.",
            background: "Background - A propriedade background define várias propriedades de fundo ao mesmo tempo (cor, imagem, repetição etc.).",
            gradient: "Gradiente - Um gradiente é uma transição suave entre duas ou mais cores.",
            note: "Sem destacar as palavras, essas propriedades e valores podem ser usados para personalizar a aparência de elementos em um relatório, adicionando cores, bordas, cantos arredondados, sombras e gradientes.",
          },
          extraTitle: "Extra",
          extraItems: {
            opacity: "Opacidade - Opacidade refere-se ao nível de transparência de um elemento. A propriedade opacity varia de 0 a 1, onde 0 é totalmente transparente e 1 é totalmente opaco.",
            transitions: "Transições - Transições no CSS permitem animações suaves entre diferentes estados de um elemento. Elas podem ser definidas com a propriedade transition, indicando qual propriedade animar, a duração e a função de timing (easing).",
            transform: "Transform - Transformações no CSS permitem manipular a forma, o tamanho ou a posição de um elemento (escala, rotação, inclinação, translação).",
            transformDetails: {
              layout: "Layout: Personalize o layout de cada página, incluindo o posicionamento de imagens e textos.",
              dimensions: "Dimensões: Ajuste as dimensões dos elementos no relatório.",
              variables: "Variáveis personalizadas: Inclua variáveis ou dados personalizados no relatório.",
              formulas: "Fórmulas personalizadas: Use fórmulas personalizadas para calcular valores ou realizar cálculos.",
              stages: "Gráficos de estágios: Exiba gráficos ou visualizações para representar estágios ou progresso.",
              recommendations: "Recomendações: Inclua recomendações ou sugestões com base nos dados da pesquisa.",
              overview: "Visão geral: Forneça uma visão geral ou resumo dos resultados da pesquisa.",
              sequence: "Alinhamento da sequência de páginas: Organize a ordem das páginas no relatório.",
            },
          },
          note: "Observe que essas opções de personalização podem variar dependendo dos recursos e capacidades específicos da ferramenta de construtor de relatórios que você estiver usando.",
        },
      },

      // Variables Page
      variables: {
        title: "Variáveis",
        intro: "Esta plataforma é para gerenciar pesquisas e formulários de geração de leads. Variáveis de lead-gen são nomes únicos de campos usados para capturar dados de formulários. As variáveis podem ser referenciadas em relatórios usando uma sintaxe específica. As configurações de perfil de usuário e idioma são acessíveis no canto superior direito.",
        
        accessingVariables: {
          title: "Acessando Variáveis",
          steps: {
            step1: "Navegue até Pesquisas e edite qualquer pesquisa clicando no botão Ações da tabela de listagem.",
            step2: "Clique no nome destacado no menu suspenso para abrir a página.",
          },
          imageAlt1: "Navegar",
          imageAlt2: "Navegar",
        },

        leadGenVariable: {
          title: "Variável de Lead Gen",
          intro: "Variáveis de lead gen são nomes de variáveis armazenados a partir de nomes do 'Form builder'. As Variáveis de Lead Gen fornecem uma maneira de capturar e utilizar dados de um formulário de Geração de Leads. Cada campo no formulário recebe um nome único, que pode ser usado para referenciar e exibir o valor do campo correspondente em relatórios ou fórmulas personalizadas. A sintaxe para acessar o valor de uma variável de lead gen é #[leadForm:fieldName]#.",
          usage: "Sintaxe de Uso: Para acessar o valor de uma variável de lead gen, use a seguinte sintaxe: #[leadForm:fieldName]#. Substitua 'fieldName' pelo nome único do campo desejado. A sintaxe da variável de lead gen deve estar entre #[ e ]#.",
          example: "Exemplo: Suponha que você tenha um formulário de Lead Gen com os seguintes campos e nomes únicos:",
          exampleFields: {
            firstName: "Primeiro Nome (fieldName: \"firstName\")",
            lastName: "Sobrenome (fieldName: \"lastName\")",
            email: "Email (fieldName: \"email\")",
          },
          displayValue: "Para exibir o valor do campo Primeiro Nome em um relatório, você usaria a sintaxe da variável de lead gen da seguinte forma: #[leadForm:firstName]#",
          dynamicReplacement: "Esta sintaxe será substituída dinamicamente pelo valor real inserido pelo usuário no campo Primeiro Nome do formulário de Lead Gen.",
          notesTitle: "Notas:",
          notes: {
            note1: "1. A sintaxe da variável de lead gen é sensível a maiúsculas e minúsculas, então certifique-se de usar a capitalização correta para os nomes dos campos.",
            note2: "2. Certifique-se de que o nome do campo usado na sintaxe corresponda ao nome único atribuído ao campo no formulário de Lead Gen.",
            note3: "3. A sintaxe da variável de lead gen pode ser usada em vários contextos, como relatórios, fórmulas personalizadas.",
          },
          conclusion: "Ao aproveitar a sintaxe da variável de lead gen, você pode acessar e incorporar facilmente dados de formulário de lead gen em seus relatórios, cálculos ou processos personalizados. Isso permite relatórios dinâmicos e personalizados com base nas informações específicas coletadas por meio de formulários de geração de leads.",
          imageAlt: "Variável de Lead Gen",
        },

        customVariables: {
          title: "Variáveis Personalizadas",
          intro: "Variáveis Personalizadas fornecem uma maneira de atribuir valores dinâmicos com base em condições definidas no módulo de Estágio. Essas variáveis são independentes e têm nomes únicos que podem ser usados para referenciar e utilizar seus valores em todo o sistema. A sintaxe para acessar o valor de uma variável personalizada é #[fieldName]#.",
          usage: "Sintaxe de Uso: Para acessar o valor de uma variável personalizada, use a seguinte sintaxe: #[fieldName]#. Substitua 'fieldName' pelo nome único da variável personalizada desejada. A sintaxe da variável personalizada deve estar entre #[ e ]#. As recomendações são exibidas na seção de relatório que pode ser personalizada usando 'Variáveis Personalizadas'.",
          example: "Exemplo: Suponha que você tenha um formulário de pesquisa onde os participantes fornecem feedback e avaliam sua experiência geral em uma escala de 1 a 10. Com base em sua resposta, você deseja atribuir uma recomendação a uma variável personalizada chamada \"recomendação\". A recomendação indicará se o participante contrataria seu serviço, com valores como \"Sim\", \"Não\" ou \"Talvez\". A recomendação pode ser dada para cada pergunta de acordo com a resposta selecionada usando variáveis personalizadas.",
          displayValue: "Para usar o valor da variável personalizada \"recomendação\" em um relatório ou exibi-lo em um e-mail, você usaria a sintaxe da variável personalizada da seguinte forma: # [recomendação]#",
          dynamicReplacement: "Esta sintaxe será substituída dinamicamente pelo valor real atribuído à variável personalizada \"recomendação\" no módulo de Estágio, com base na avaliação da experiência geral do participante da pesquisa.",
          notesTitle: "Notas:",
          notes: {
            note1: "A sintaxe da variável personalizada é sensível a maiúsculas e minúsculas, então certifique-se de usar a capitalização correta para o nome da variável.",
            note2: "Certifique-se de que o nome da variável usado na sintaxe corresponda ao nome único atribuído à variável personalizada no módulo de Estágio.",
            note3: "Variáveis personalizadas podem receber valores com base em várias condições ou cálculos, permitindo manipulação de dados dinâmica e personalizada.",
            note4: "A sintaxe da variável personalizada pode ser usada em diferentes contextos, como relatórios, ou qualquer outro lugar onde você queira utilizar o valor de uma variável personalizada.",
          },
          conclusion: "Ao aproveitar a sintaxe da variável personalizada, você pode atribuir e acessar valores dinâmicos com base em condições definidas, permitindo gerenciamento de dados flexível e personalizável dentro do sistema. No exemplo acima, a variável personalizada \"recomendação\" representa a recomendação com base na resposta da pesquisa do participante",
          editTitle: "Editar variável personalizada",
          editIntro: "O recurso \"Editar Variável\" permite que você modifique qualquer variável no sistema, incluindo seu nome, descrição ou sintaxe. Este recurso permite que você faça alterações em variáveis existentes e as atualize de acordo com suas necessidades.",
          editStepsTitle: "Para usar o recurso \"Editar Variável\":",
          editSteps: {
            step1: "Vá para a seção onde a variável está localizada. Navegue até a seção ou página apropriada dentro do aplicativo de software onde a variável está armazenada ou exibida.",
            step2: "Localize a variável que você deseja editar. Identifique a variável específica que você deseja modificar entre a lista de variáveis exibidas na página.",
            step3: "Clique no botão ou opção \"Editar\" associado à variável. Provavelmente, haverá um botão \"Editar\" ou uma opção semelhante ao lado ou abaixo dos detalhes da variável. Clique nele para iniciar o processo de edição.",
            step4: "Faça as alterações necessárias no nome, descrição ou sintaxe da variável. Uma vez no modo de edição, você pode modificar vários aspectos da variável, como seu nome, descrição ou sintaxe. Atualize os campos conforme necessário.",
            step5: "Salve as modificações. Depois de fazer as alterações desejadas, procure um botão \"Salvar\" ou uma opção semelhante para aplicar as modificações à variável. Clique nele para salvar suas edições. Alguns sistemas podem salvar automaticamente as alterações conforme você as faz.",
            conclusion: "Ao utilizar o recurso \"Editar Variável\", você pode atualizar e personalizar facilmente suas variáveis conforme necessário, garantindo que elas representem com precisão as informações ou funcionalidades desejadas.",
          },
          deleteTitle: "Excluir variável personalizada",
          deleteIntro: "Excluir uma Variável Personalizada removerá todos os valores associados a essa Variável Personalizada específica no sistema. Isso inclui quaisquer recomendações em relatórios ou fórmulas personalizadas que utilizam a variável. Variáveis Personalizadas são usadas para definir condições em estágios, fórmulas personalizadas, recomendações no Q&A Builder serão todas removidas se uma variável personalizada for excluída.",
          addTitle: "Adicionar nova variável",
          addIntro: "Para adicionar uma nova variável ao projeto, clique no botão Adicionar nova Variável. Um pop-up aparecerá na tela, exibindo um formulário que é o mesmo usado no ícone de edição desta página.",
          addFields: {
            label: "rótulo - adicione o nome do rótulo com no máximo 250 caracteres",
            type: "Selecione o tipo de variável entre as opções 'número', 'texto de linha única' ou 'texto de várias linhas'.",
            name: "Digite o Nome da variável sem espaços e com no máximo 50 caracteres.",
            description: "Forneça uma Descrição da variável e explique seu uso.",
          },
          addConclusion: "Clique no botão \"Adicionar nova variável\" para adicionar uma variável. A nova variável será atualizada na lista.",
          imageAlt: "Variáveis Personalizadas",
          imageAltEdit: "Editar variável personalizada",
          imageAltDelete: "Excluir variável personalizada",
          imageAltAddButton: "Botão Adicionar nova variável",
          imageAltAdd: "Adicionar nova variável",
        },

        customFormula: {
          title: "Fórmula Personalizada",
          intro: "Fórmulas Personalizadas fornecem uma maneira de criar cálculos e expressões dinâmicas usando variáveis de Lead Gen, Personalizadas e de P&R. Essas fórmulas recebem nomes únicos e podem ser usadas como variáveis em relatórios e estágios. A sintaxe para acessar o valor de uma fórmula personalizada é #[nomeFormula]#.",
          usage: "Sintaxe de Uso: Para acessar o valor de uma fórmula personalizada, use a seguinte sintaxe: #[nomeFormula]#. Substitua \"nomeFormula\" pelo nome único atribuído à fórmula personalizada desejada. A sintaxe da fórmula personalizada deve estar entre #[ e ]#.",
          example: "Exemplo: Suponha que você tenha uma pesquisa com várias perguntas e deseje calcular a porcentagem geral da pontuação da pesquisa com base nas notas obtidas e notas máximas. Você pode criar uma fórmula personalizada chamada \"porcentagemGeral\" para realizar este cálculo.",
          displayValue: "Para usar o valor da variável personalizada \"recomendação\" em um relatório ou exibi-lo em um e-mail, você usaria a sintaxe da variável personalizada da seguinte forma: # [recomendação]#",
          formulaDefinition: "A definição da fórmula personalizada pode ter a seguinte aparência: porcentagemGeral = (notas_obtidas_q1 + notas_obtidas_q2 + ... ) / (notas_maximas_q1 + notas_maximas_q2 + ...) * 100",
          usageExample: "Para usar o valor da fórmula personalizada \"porcentagemGeral\" em um relatório ou estágios futuros, você usaria a sintaxe da fórmula personalizada da seguinte forma: porcentagemGeral Esta sintaxe será substituída dinamicamente pelo valor calculado real atribuído à fórmula personalizada \"porcentagemGeral\".",
          notesTitle: "Notas:",
          notes: {
            note1: "A sintaxe da fórmula personalizada é sensível a maiúsculas e minúsculas, então certifique-se de usar a capitalização correta para o nome da fórmula.",
            note2: "Certifique-se de que o nome da fórmula usado na sintaxe corresponda ao nome único atribuído à fórmula personalizada.",
            note3: "Fórmulas personalizadas podem incorporar variáveis de Lead Gen e de P&R, permitindo cálculos dinâmicos baseados em dados coletados.",
            note4: "A sintaxe da fórmula personalizada pode ser usada em diferentes contextos, como modelos de relatório, condições de estágio.",
          },
          conclusion: "Ao utilizar a sintaxe da fórmula personalizada, você pode criar cálculos e expressões poderosas baseadas em variáveis de Lead Gen e de P&R. No exemplo fornecido, a fórmula personalizada \"porcentagemGeral\" calcula a porcentagem geral da pontuação da pesquisa com base nas notas obtidas e notas máximas de perguntas individuais da pesquisa.",
          editTitle: "Editar Fórmula",
          editIntro: "O recurso \"Editar Fórmula\" permite que você modifique qualquer fórmula da seção \"Fórmula Personalizada\". Este recurso permite que você faça alterações em fórmulas existentes e as atualize de acordo com suas necessidades.",
          editStepsTitle: "Para usar o recurso \"Editar Fórmula\":",
          editSteps: {
            step1: "Vá para a seção \"Fórmula Personalizada\" do sistema.",
            step2: "Localize a fórmula que você deseja editar.",
            step3: "Clique no botão ou opção \"Editar\" associado à fórmula.",
            step4: "Faça as alterações necessárias na fórmula.",
            step5: "Salve as modificações.",
            conclusion: "Ao utilizar o recurso \"Editar Fórmula\", você pode atualizar e ajustar facilmente suas fórmulas personalizadas conforme necessário, garantindo que elas reflitam os cálculos e expressões desejados.",
          },
          deleteTitle: "Excluir fórmula",
          deleteIntro: "Excluir uma fórmula é uma ação permanente que deve ser feita com cautela. Quando você exclui uma fórmula, ela será removida de todas as seções e relatórios onde é usada. Isso significa que quaisquer cálculos ou expressões que dependam da fórmula não serão mais válidos.",
          deleteWarning1: "É importante notar que excluir uma fórmula também excluirá todos os cálculos que são baseados nela. Isso pode ter um impacto significativo em sua análise de dados e relatórios. Portanto, é recomendado revisar cuidadosamente as implicações antes de prosseguir com a exclusão.",
          deleteWarning2: "Se você não tem certeza sobre excluir uma fórmula, é uma boa prática criar um backup ou fazer uma cópia de seu documento antes de fazer quaisquer alterações. Desta forma, você pode reverter para a versão anterior se necessário.",
          deleteStepsTitle: "Para excluir uma fórmula, siga estas etapas:",
          deleteSteps: {
            step1: "Localize a fórmula que você deseja excluir.",
            step2: "Clique no botão ou opção \"Excluir\" associado à fórmula.",
            step3: "Confirme a exclusão quando solicitado.",
          },
          deleteWarning3: "Uma vez que a fórmula é excluída, ela não pode ser recuperada. Portanto, é importante verificar duas vezes sua decisão antes de prosseguir.",
          deleteConclusion: "Lembre-se, excluir uma fórmula é uma ação permanente e pode ter um impacto significativo em sua análise de dados e relatórios. Certifique-se de considerar cuidadosamente as consequências antes de excluir quaisquer fórmulas.",
          addTitle: "Adicionar fórmula",
          addIntro: "pode-se Adicionar novas fórmulas clicando no botão Adicionar fórmula personalizada, Nesta pode-se adicionar rótulo e nome para a fórmula",
          addFieldIntro: "No campo de entrada da fórmula, você pode usar variáveis junto com operadores. O operador pode ser selecionado de símbolos ou digitado do teclado. Aqui está uma explicação simples do significado e uso de operadores:",
          operatorsIntro: "No contexto de fórmulas e expressões matemáticas, operadores são símbolos ou caracteres que representam operações ou ações específicas a serem executadas em um ou mais operandos. Aqui estão alguns operadores comumente usados:",
          operators: {
            addition: "Adição (+) Adiciona duas ou mais variáveis juntas.",
            subtraction: "Subtração ( - ) Subtrai uma variável de outra.",
            multiplication: "Multiplicação ( * ) Multiplica duas ou mais variáveis .",
            division: "Divisão ( / ) Divide uma variável por outra.",
            brackets: "Chaves são usadas em expressões regulares para definir padrões. Eles podem especificar repetição ou intervalos de caracteres.",
            variable: "( x ) pode se referir a uma variável ou valor que faz parte de uma fórmula ou cálculo. É usado para demonstrar o uso de operadores e variáveis em fórmulas.",
          },
          operatorsConclusion: "Estes são apenas alguns exemplos de operadores comumente usados em cálculos matemáticos. Os operadores podem ser combinados para criar fórmulas e expressões complexas para realizar cálculos avançados e manipulações de dados.",
          precedenceIntro: "Ao usar operadores em fórmulas, é importante considerar a ordem das operações, também conhecida como precedência. A ordem das operações determina a sequência em que os operadores são avaliados. A ordem comum de operações é:",
          precedence: {
            parentheses: "<b>Parênteses:</b> Operações dentro de parênteses são avaliadas primeiro.",
            exponents: "<b>Expoentes:</b> Exponenciação é realizada em seguida.",
            multiplicationDivision: "<b>Multiplicação e Divisão:</b> Essas operações são realizadas da esquerda para a direita.",
            additionSubtraction: "<b>Adição e Subtração:</b> Essas operações são realizadas da esquerda para a direita.",
          },
          precedenceNote: "Usar parênteses pode substituir a ordem padrão das operações e garantir que operações específicas sejam avaliadas primeiro.",
          precedenceConclusion: "Ao compreender e utilizar operadores de forma eficaz, você pode criar fórmulas e expressões poderosas para realizar cálculos e manipular dados em suas fórmulas personalizadas.",
          imageAlt: "Fórmula Personalizada",
          imageAltEdit: "Editar Fórmula",
          imageAltDelete: "Excluir Fórmula",
          imageAltAdd: "Adicionar Fórmula",
          imageAltOperators: "Fórmula",
        },

        utmVariables: {
          title: "Variáveis UTM",
          intro: "As variáveis únicas que podem ser passadas em 'Fonte', 'Meio' e 'Campanha'. Essas variáveis podem ser usadas para criar novos URLs para uma pesquisa específica, permitindo o rastreamento de fontes de resposta. Ao incorporar variáveis UTM nos URLs, você pode identificar e analisar a eficácia de diferentes campanhas de marketing ou canais na geração de respostas de pesquisa. As Variáveis podem ser adicionadas, editadas ou excluídas.",
          context: "No contexto de um URL, a fonte, o meio e a campanha são variáveis UTM que podem ser usadas para rastrear e analisar a eficácia de diferentes campanhas de marketing ou canais na geração de respostas de pesquisa.",
          source: "<b>Fonte</b> - O parâmetro de fonte identifica de onde o tráfego está vindo. Ele indica a plataforma específica, site ou aplicativo que está direcionando o tráfego para a pesquisa. Por exemplo, se você está compartilhando o link da pesquisa no Facebook, pode usar \"facebook\" como valor para o parâmetro de fonte.",
          medium: "<b>Meio</b> - O parâmetro de meio identifica o meio de marketing ou canal através do qual o link da pesquisa está sendo compartilhado. Ele especifica o tipo de tráfego, como e-mail, mídia social ou banners publicitários. Por exemplo, se você está compartilhando o link da pesquisa através de uma campanha de e-mail, pode usar \"email\" como valor para o parâmetro de meio.",
          campaign: "<b>Campanha</b> - O parâmetro de campanha é usado para identificar uma campanha de marketing ou promoção específica. Ele ajuda a distinguir diferentes iniciativas ou estratégias dentro de uma fonte e meio. Por exemplo, se você está executando várias campanhas no Facebook, pode usar nomes de campanha únicos como \"venda_de_verao\" ou \"promocao_feriado\" como valor para o parâmetro de campanha.",
          conclusion: "Ao incorporar essas variáveis UTM no URL da pesquisa, você pode rastrear e medir o desempenho de diferentes fontes, meios e campanhas na geração de respostas de pesquisa. Isso permite que você analise a eficácia de seus esforços de marketing e tome decisões baseadas em dados.",
          imageAlt: "Variáveis UTM",
        },

        stages: {
          title: "Estágios",
          intro: "Os estágios permitem que os participantes recebam feedback e recomendações personalizadas com base em suas respostas e na pontuação que alcançaram. Isso significa que os participantes não receberão apenas orientação geral, mas também insights e sugestões específicas que são relevantes para seu desempenho individual.",
          summary: "Em resumo, os estágios personalizáveis no sistema oferecem uma abordagem abrangente e personalizada para feedback e recomendações. Ao permitir que os usuários definam estágios como diferentes níveis e forneçam insights personalizados.",
          addTitle: "Para adicionar um estágio, siga estas etapas:",
          addSteps: {
            step1: "Vá para a seção \"Estágios\" do sistema.",
            step2: "Clique no botão ou opção \"Adicionar Estágio\".",
            step3: "Forneça um nome e descrição para o novo estágio.",
            step4: "Escolha uma fórmula no menu suspenso \"Se nome da fórmula\", que foi previamente personalizada na seção \"variável - fórmula personalizada\" e agora aparece no menu suspenso.",
            step5: "Selecione um intervalo para o resultado de saída da fórmula escolhida, ou seja, de um intervalo para outro intervalo. Se as pontuações de saída se enquadrarem em um intervalo específico, escolha uma variável personalizada para essa fórmula e exiba uma mensagem de recomendação para esse intervalo que será digitada no editor fornecido.",
            step6: "Condições podem ser aplicadas usando \"Variáveis personalizadas\". O botão \"+\" pode ser usado para adicionar uma nova condição, e o botão \"-\" pode ser usado para remover qualquer condição existente.",
            step7: "Personalize as recomendações e feedback específicos para o estágio.",
            step8: "Salve as alterações.",
          },
          addConclusion: "Este processo permite que você defina e crie novos estágios no sistema, cada um com seus próprios critérios, recomendações e feedback adaptados ao desempenho e progresso dos participantes.",
          editTitle: "Para editar um estágio, siga estas etapas:",
          editSteps: {
            step1: "Vá para a seção \"Estágios\" do sistema.",
            step2: "Localize o estágio que você deseja editar.",
            step3: "Adicionar qualquer condição para ter um estágio específico pode ser feito.",
            step4: "Qualquer condição pré-existente também pode ser removida usando variáveis personalizadas.",
            step5: "Clique no botão ou opção \"Editar\" associado ao estágio.",
            step6: "Faça as alterações necessárias no nome, descrição ou recomendações do estágio.",
            step7: "Salve as modificações.",
          },
          deleteTitle: "Para excluir um estágio, siga estas etapas:",
          deleteSteps: {
            step1: "Vá para a seção \"Estágios\" do sistema.",
            step2: "Localize o estágio que você deseja excluir.",
            step3: "Clique no botão ou opção \"Excluir\" associado ao estágio.",
            step4: "Confirme a exclusão quando solicitado.",
          },
          deleteWarning: "Por favor, note que excluir um estágio irá removê-lo permanentemente do sistema, juntamente com quaisquer recomendações e feedback associados. Certifique-se de revisar cuidadosamente as implicações antes de prosseguir com a exclusão.",
          conclusion: "Ao utilizar os recursos de personalização de estágios, você pode adaptar o sistema para atender aos seus requisitos específicos e fornecer feedback e recomendações personalizadas aos participantes em diferentes estágios de seu progresso.",
          imageAlt: "Estágios",
          imageAltEdit: "editar um estágio",
        },
      },

      // Publish & Share Page
      publishShare: {
        title: "Publicar e Compartilhar",
        intro: "Você pode personalizar a URL adicionando diferentes combinações de variáveis UTM para rastrear a fonte, meio e campanha. A URL da pesquisa também inclui uma opção de copiar e colar para fácil compartilhamento.",
        
        accessing: {
          title: "Acessando Publicar e Compartilhar",
          steps: {
            step1: "Navegue até Pesquisas e edite qualquer pesquisa clicando no botão Ações da tabela de listagem.",
            step2: "Clique no nome destacado no menu suspenso para abrir a página.",
          },
          imageAlt1: "Navegar",
          imageAlt2: "Navegar",
        },

        urls: {
          title: "URLs",
          defaultLink: "Por padrão, já existe um link fornecido para a pesquisa selecionada na página. Este link pode ser usado para seu propósito específico. No entanto, é possível que você prefira um link de URL mais específico para facilitar a identificação e personalização única.",
          createNewTitle: "<b> Criar Nova URL -</b> Para fazer isso, você pode usar o botão \"Criar Nova URL\" para criar uma URL nova e personalizada. Aqui estão os passos para fazer isso:",
          steps: {
            step1: "Localize o botão \"Criar Nova URL\".",
            step2: "Clique no botão para iniciar o processo.",
            step3: "Siga as instruções fornecidas para personalizar sua nova URL.",
            step4: "<b>Nome</b> - Insira o nome para a URL. Por exemplo, se você deseja usar a pesquisa para coletar dados de mídia social como o Facebook, pode dar o nome do link como 'facebook_link'.",
            step5: "A variável UTM já foi inicializada na variável/ Variáveis de Campanha UTM.",
            step6: "<b>Fonte</b> - O parâmetro de fonte identifica de onde o tráfego está vindo. Ele indica a plataforma específica, site ou aplicativo que está direcionando o tráfego para a pesquisa. Por exemplo, se você está compartilhando o link da pesquisa no Facebook, pode usar \"facebook\" como valor para o parâmetro de fonte",
            step7: "<b>Meio</b> - O parâmetro de meio identifica o meio de marketing ou canal através do qual o link da pesquisa está sendo compartilhado. Ele especifica o tipo de tráfego, como e-mail, mídia social ou banners publicitários. Por exemplo, se você está compartilhando o link da pesquisa através de uma campanha de e-mail, pode usar \"email\" como valor para o parâmetro de meio.",
            step8: "<b>Campanha</b> - O parâmetro de campanha é usado para identificar uma campanha de marketing ou promoção específica. Ele ajuda a distinguir diferentes iniciativas ou estratégias dentro de uma fonte e meio. Por exemplo, se você está executando várias campanhas no Facebook, pode usar nomes de campanha únicos como \"venda_de_verao\" ou \"promocao_feriado\" como valor para o parâmetro de campanha.",
            step9: "Ao incorporar essas variáveis UTM no URL da pesquisa, você pode rastrear e medir o desempenho de diferentes fontes, meios e campanhas na geração de respostas de pesquisa. Isso permite que você analise a eficácia de seus esforços de marketing e tome decisões baseadas em dados.",
            step10: "<b>Conteúdo</b> - No contexto de uma URL, o parâmetro utm_content é usado para identificar o que especificamente foi clicado para trazer o usuário ao site. Ele ajuda a rastrear e analisar a eficácia de diferentes elementos dentro de uma campanha de marketing ou canal. Por exemplo, se você tem vários links ou anúncios no Facebook levando à pesquisa, pode diferenciá-los usando diferentes valores para o parâmetro utm_content. Aqui está um exemplo de como o parâmetro utm_content pode ser usado:",
            step11: "Para adicionar especificações adicionais na URL, você pode usar a opção 'Adicionar parâmetro' para mais parâmetros na url",
            step12: "Depois de inserir todas as informações necessárias, clique no botão \"Atualizar\" para finalizar a criação de sua nova URL. Esta URL será exibida na tabela na mesma página como abaixo",
          },
          contentExamples: {
            example1: "Se você tem um link de logotipo e um link de texto direcionando usuários para a pesquisa, pode usar utm_content=logolink para o link do logotipo e utm_content=textlink para o link de texto. Isso ajudará você a diferenciar e analisar o desempenho de cada elemento específico dentro da campanha do Facebook.",
            example2: "Ao incorporar o parâmetro utm_content no URL da pesquisa, você pode rastrear e medir o desempenho de diferentes elementos de conteúdo na geração de respostas de pesquisa. Isso permite que você avalie a eficácia de seus esforços de marketing e tome decisões baseadas em dados.",
          },
          imageAlt1: "Criar Nova URL",
          imageAlt2: "Botão Criar Nova URL",
          imageAlt3: "Criar Nova URL",
        },

        tableListingFields: {
          title: "Campos da Listagem de Tabela",
          intro: "Por padrão, já existe um link fornecido para a pesquisa selecionada na página. Este link pode ser usado para seu propósito específico. No entanto, é possível que você prefira um link de URL mais específico para facilitar a identificação e personalização única.",
          fields: {
            name: "<b>Nome</b> - armazena o nome do link",
            response: "<b>Resposta</b> - é o número de respostas geradas neste link específico",
            createdDate: "<b>Data de criação</b> - exibe a data em que o link foi atualizado.",
            status: "<b>Status</b> - mostra o status ativo e inativo do link",
            actions: "<b>Ações</b> - pode-se editar, excluir e copiar o link clicando nos respectivos links",
          },
          conclusion: "Seguindo essas etapas, você poderá criar uma nova URL personalizada para seu propósito. Pode-se criar quantas URLs forem necessárias para seus propósitos específicos.",
          imageAlt: "Campos da Listagem de Tabela",
        },
      },

      // Integration Page
      integration: {
        title: "Integração",
        intro: "O módulo de Integração permite conectar sua plataforma de pesquisa ao Eloqua e mapear campos de formulário de lead para sincronização de dados sem interrupções. Uma vez que um mapeamento é criado, os envios de formulário fluem para suas campanhas Eloqua automaticamente.",
        
        accessingModule: {
          title: "Acessando o Módulo de Integração",
          steps: {
            step1: "Navegue até a <b>Pesquisa</b> selecionada e clique na aba <b>Integração</b> dentro de uma Pesquisa.",
            step2: "Você verá o status atual do Formulário Eloqua e uma lista de mapeamentos de campo existentes.",
            step3: "Use a barra de pesquisa para encontrar um mapeamento ou o filtro <b>Ordenar por</b> para alterar a ordem.",
          },
          imageAlt: "Visão Geral da Aba de Integração",
        },

        listingPage: {
          title: "Compreendendo a Página de Listagem",
          intro: "A listagem exibe as seguintes colunas:",
          columns: {
            leadFormField: "<b>Campo do Formulário de Lead</b> — O campo definido em seu formulário de geração de leads.",
            eloquaFormField: "<b>Campo do Formulário Eloqua</b> — O campo correspondente do formulário Eloqua para sincronização de dados.",
            action: "<b>Ação</b> — Excluir (🗑️) para remover o mapeamento.",
          },
          statusTitle: "No topo da página, você também encontrará o <b>status do Formulário Eloqua</b>:",
          status: {
            active: "<b>Ativo</b> <span class=\"badge bg-success text-white\">Ativo</span> — Habilitado e sincronizando dados.",
            inactive: "<b>Inativo</b> <span class=\"badge bg-danger text-white\">Inativo</span> — Desabilitado; envios não serão sincronizados.",
          },
        },

        toggleStatus: {
          title: "Alternando Status de Sincronização do Eloqua",
          intro: "Você pode habilitar ou desabilitar rapidamente a sincronização do Eloqua usando o <b>menu suspenso de status</b> próximo ao cabeçalho da página:",
          steps: {
            step1: "Selecione <b>Ativo</b> para habilitar a sincronização de dados do formulário.",
            step2: "Selecione <b>Inativo</b> para pausar a sincronização sem excluir mapeamentos.",
            step3: "Quando <b>Inativo</b>, todos os mapeamentos existentes são mantidos, mas nenhum dado é enviado.",
          },
          imageAlt: "Alternância de Status do Eloqua",
        },

        addMapping: {
          title: "Adicionando Novo Mapeamento",
          intro: "Para adicionar um novo mapeamento de campo, clique no botão vermelho <b>+ Adicionar Mapeamento</b> no canto superior direito:",
          steps: {
            step1: "<b>Campo do Formulário de Lead</b> — Selecione o campo do formulário que você deseja mapear no menu suspenso.",
            step2: "<b>Campo do Formulário Eloqua</b> — Digite o nome exato do campo Eloqua para o qual você está mapeando.",
            step3: "Clique em <b>Salvar</b> para criar o mapeamento ou <b>Cancelar</b> para descartar.",
          },
          note: "<b>Nota:</b> Certifique-se de que o nome do campo Eloqua corresponda ao definido em sua campanha Eloqua para evitar perda de dados.",
        },

        deleteMapping: {
          title: "Excluindo um Mapeamento",
          intro: "Para remover um mapeamento existente, clique no <b>ícone Excluir (🗑️)</b> na ação da linha.",
          steps: {
            step1: "Uma caixa de diálogo de confirmação aparecerá: <em>\"Confirmar Exclusão\"</em>",
            step2: "Clique em <b>Confirmar Exclusão</b> para remover o mapeamento permanentemente.",
            step3: "Clique em <b>Fechar</b> para cancelar e manter o mapeamento.",
          },
          warning: "<b>Aviso:</b> Mapeamentos excluídos não podem ser recuperados. Você precisará adicioná-los novamente manualmente.",
        },
      },

      // Webhooks Page
      webhooks: {
        title: "Webhooks",
        intro: "O módulo de Webhook permite criar gatilhos que enviam dados para sistemas externos quando eventos como envios de formulário ocorrem. Você pode configurar URLs, parâmetros e status do webhook por meio de uma interface simples.",
        accessing: {
          title: "Acessando Webhook",
          steps: {
            step1: "Navegue até Pesquisas, selecione uma pesquisa (por exemplo, RCA Survey) e clique na aba Webhook.",
            step2: "Você verá uma lista de webhooks existentes, junto com opções para adicionar, editar ou excluí-los.",
            step3: "Clique no botão vermelho + Adicionar Webhook para abrir o modal de criação.",
          },
          imageAlt: "Modal Criar Webhook",
        },
        create: {
          title: "Criar Novo Webhook",
          intro: "No modal Criar Novo Webhook, preencha os seguintes detalhes:",
          fields: {
            name: "Nome* - Um nome para o webhook.",
            eventsTitle: "Eventos - Escolha o evento de disparo do webhook no menu suspenso:",
            eventLeadForm: "Ao enviar formulário de lead - Dispara quando um formulário de geração de leads é enviado.",
            eventResponse: "Ao enviar resposta - Dispara quando uma pergunta/resposta individual é enviada.",
            eventSurvey: "Ao enviar pesquisa - Executa quando a submissão completa da pesquisa é concluída.",
            methodTitle: "Método* - Selecione o método HTTP para chamar seu endpoint.",
            methodPost: "POST - Recomendado. Envia dados no corpo da requisição.",
            methodGet: "GET - Envia dados como parâmetros de query string.",
            methodPutPatch: "PUT / PATCH - Semântica de atualização; corpo suportado.",
            methodDelete: "DELETE - Semântica de exclusão; query/corpo conforme sua API.",
            contentTypeTitle: "Content-Type* - Como o payload é codificado.",
            contentJson: "application/json - Envia um corpo JSON (padrão para a maioria das APIs).",
            contentForm: "application/x-www-form-urlencoded - Pares chave-valor codificados no corpo.",
            contentMultipart: "multipart/form-data - Use ao enviar arquivos ou partes mistas.",
            contentText: "text/plain - Payload de texto bruto.",
            dataFormatTitle: "Formato de Dados* - Onde/que estrutura os dados usam.",
            dataJson: "JSON Body - Dados chave-valor serializados como JSON no corpo da requisição.",
            dataForm: "Form Data - Pares chave-valor no corpo (funciona com x-www-form-urlencoded ou multipart/form-data).",
            dataQuery: "Query String - Pares chave-valor anexados à URL (comum com GET).",
            dataFormatNote: "As opções disponíveis podem variar com base no Método e no Content-Type selecionados.",
            url: "URL* - Endpoint de destino para onde o webhook enviará os dados (deve ser HTTPS).",
            description: "Descrição - Notas adicionais opcionais.",
            addParameters: "Adicionar Parâmetros* - Permite definir parâmetros chave-valor que serão enviados com a requisição do webhook.",
            addParametersStep1: "Insira um nome e selecione o tipo de parâmetro no menu suspenso.",
            addParametersStep2: "Clique em Adicionar para incluí-lo no payload.",
            generatedUrl: "URL do Webhook Gerada - Exibe a URL final com parâmetros adicionados.",
            status: "Status - Selecione Ativo ou Inativo usando o botão de rádio na parte inferior.",
            actions: "Clique em + Adicionar para salvar ou Cancelar para descartar alterações.",
          },
          imageAlt: "Lista de Webhooks",
        },
        listing: {
          title: "Listagem de Webhooks",
          intro: "Todos os webhooks criados serão exibidos em uma tabela com os seguintes campos:",
          columns: {
            webhook: "Webhook - O nome do webhook.",
            url: "URL - Um ícone de link que indica a presença do endpoint.",
            status: "Status - Mostra se o webhook está Ativo ou Inativo.",
            action: "Ação - Opções de Editar e Excluir para gerenciar o webhook.",
          },
          search: "Use a barra de Pesquisa para encontrar webhooks pelo nome.",
          sort: "Use o menu suspenso Ordenar por para reordenar por mais recentes ou outros critérios.",
          imageAlt: "Lista de Webhooks",
        },
        activation: {
          title: "Status de Ativação",
          intro: "Cada webhook tem um status que pode ser alternado na criação:",
          active: "Ativo - O webhook será acionado quando o evento selecionado ocorrer.",
          inactive: "Inativo - O webhook será salvo, mas não executado até ser ativado.",
          note: "Use os botões de rádio para definir esse status antes de clicar em + Adicionar.",
        },
      },

      // Questionnaire Page
      questionnaire: {
        title: "Questionário",
        intro: "O Painel de Configuração do Questionário é uma interface para configurar questionários baseados em avaliação. Este painel permite personalizar títulos de página, botões de navegação, detalhes do relatório e conteúdo de avaliação por estágio.",
        keyFeatures: {
          title: "Principais Recursos",
          pageCustomization: "Personalização de página - Defina o Título da Página para o título do questionário. Configure os botões Próximo, Enviar e Voltar com textos personalizados.",
          reportSection: "Seção de Relatório - Personalize o Título da Página de Relatório e o Cabeçalho do Relatório. Defina diferentes estágios do relatório.",
          stagesConfig: "Configuração de Estágios - Defina múltiplos estágios (Estágio 1, Estágio 2, etc.). Forneça descrições para cada estágio nos campos de texto.",
          save: "Funcionalidade de salvar - Um botão Salvar permite que os usuários armazenem suas configurações.",
        },
        accessing: {
          title: "Acessando Questionário",
          steps: {
            step1: "Navegue até Pesquisas e edite qualquer pesquisa clicando no botão Ações da tabela de listagem.",
            step2: "Clique no nome destacado no menu suspenso para abrir a página.",
          },
          imageAlt1: "Navegar",
          imageAlt2: "Navegar",
        },
        fields: {
          title: "Explicação Detalhada dos Campos",
          pageHeading: {
            title: "Título da Página",
            desc1: "Este campo permite que os usuários definam o título principal do questionário.",
            desc2: "Exemplo: Vodafone Business Rapid Cloud Assessment.",
            imageAlt: "Título da Página",
          },
          buttonConfig: {
            title: "Configuração de Botões",
            next: "Botão Próximo: Texto exibido para navegar para a próxima etapa.",
            submit: "Botão Enviar: Texto personalizável para envio (por exemplo, Obtenha sua recomendação personalizada).",
            back: "Botão Voltar: Define o texto do botão de navegação de retorno.",
            imageAlt: "Configuração de Botões",
          },
          reportSection: {
            title: "Seção de Relatório",
            reportPageHeading: "Título da Página de Relatório: Título para o relatório gerado após a conclusão do questionário.",
            reportHeading: "Cabeçalho do Relatório: O título principal do relatório.",
            stageField: "Campo de Estágio: Um campo de descrição geral para o processo de estágio.",
            imageAlt: "Seção de Relatório",
          },
          stages: {
            title: "Estágios",
            desc1: "Permite a configuração de estágios individuais no relatório.",
            desc2: "Cada estágio (Estágio 1, Estágio 2, etc.) tem um campo de texto dedicado para inserir descrições relevantes.",
            imageAlt: "Estágios",
          },
          saveButton: {
            title: "Botão Salvar",
            desc1: "Localizado na parte inferior.",
            desc2: "Clicar em Salvar armazena todas as configurações.",
            imageAlt: "Botão",
          },
          conclusion: "O Painel de Configuração do Questionário fornece uma forma estruturada de definir conteúdo, navegação e detalhes do relatório. Ele garante flexibilidade ao permitir a personalização de rótulos de botões, títulos e estágios.",
        },
      },

      // Thank You Page
      thankYouPage: {
        title: "Página de Agradecimento",
        intro: "Esta página garante que os usuários recebam uma experiência personalizada após concluir uma avaliação ao exibir dinamicamente seus dados e fornecer acesso aos relatórios.",
        accessing: {
          title: "Acessando Página de Agradecimento",
          steps: {
            step1: "Navegue até Pesquisas e edite qualquer pesquisa clicando no botão Ações da tabela de listagem.",
            step2: "Clique no nome destacado no menu suspenso para abrir a página.",
          },
          imageAlt1: "Navegar",
          imageAlt2: "Navegar",
        },
        keyElements: {
          title: "Elementos Principais",
          pageHeading: {
            title: "Título da Página",
            items: {
              item1: "Usa placeholders dinâmicos (#[leadForm:firstName]#, #[leadForm:lastName]#, #[leadForm:company]#, #[maturity_stage]#) para personalizar a mensagem com os dados do usuário.",
              item2: "Exemplo: John Doe, sua empresa ABC Corp está no estágio Growth",
            },
          },
          pageDescription: {
            title: "Descrição da Página",
            items: {
              item1: "Exibe uma mensagem de confirmação após a conclusão da pesquisa.",
              item2: "Exemplo: Seu relatório personalizado está disponível! Explore seus insights e recomendações hoje.",
            },
          },
          buttons: {
            title: "Botões",
            items: {
              item1: "Botão Ver Relatório: Permite que os usuários visualizem o relatório gerado.",
              item2: "Botão Baixar Relatório: Permite que os usuários façam o download do relatório.",
            },
          },
          saveButton: {
            title: "Botão Salvar",
            items: {
              item1: "Salva as alterações feitas nesta configuração da Página de Agradecimento.",
            },
          },
          conclusion: "Esta página garante que os usuários recebam uma experiência personalizada após concluir uma avaliação ao exibir dinamicamente seus dados e fornecer acesso aos relatórios.",
          imageAlt: "Elementos Principais",
        },
      },

      // Privacy Policy Page
      privacyPolicy: {
        title: "Política de Privacidade",
        intro: "Configure e mantenha o conteúdo da política de privacidade mostrado aos respondentes. Este editor suporta texto rico, links, cores e tabelas.",
        accessing: {
          title: "Acessando Política de Privacidade",
          steps: {
            step1: "Vá para Pesquisas → Ações → Editar Pesquisa.",
            step2: "Abra Configuração e selecione Política de Privacidade.",
          },
          imageAlt: "Navegação da política de privacidade",
        },
        editorBasics: {
          title: "Noções Básicas do Editor",
          formattingTitle: "Formatação",
          formattingItems: {
            item1: "Use Negrito, Itálico, Sublinhado e Títulos (H1–H3) para estrutura.",
            item2: "Crie listas com Lista com Marcadores e Lista Numerada.",
            item3: "Insira hyperlinks usando o botão Link.",
          },
          tablesTitle: "Tabelas",
          tableItems: {
            item1: "Insira uma tabela pelo botão Tabela e depois adicione texto nas células.",
            item2: "Use para estruturar seções como categorias de dados e períodos de retenção.",
          },
          imageAlt: "Tabela do editor de política",
        },
        saving: {
          title: "Salvando Alterações",
          items: {
            item1: "Clique em Salvar para persistir alterações na política de privacidade da pesquisa atual.",
            item2: "O conteúdo salvo será exibido onde a política de privacidade for apresentada aos respondentes.",
          },
        },
      },

      // Create Survey Page
      createSurvey: {
        title: "Criar Pesquisa",
        intro: "Seguindo estas etapas, você pode criar um novo questionário e personalizá-lo conforme suas necessidades. Este guia do usuário fornece instruções passo a passo sobre como criar um novo questionário usando a plataforma fornecida.",
        guide: {
          title: "Guia do Usuário: Criar Nova Pesquisa",
          steps: {
            step1Title: "Passo 1: Criar Nova Pesquisa",
            step1Desc: "Clique no botão Criar Nova Pesquisa para iniciar o processo de criação de um novo questionário na página Lista de Pesquisas.",
            step2Title: "Passo 2: Nomeie sua Pesquisa",
            step2Desc: "Este campo obrigatório pode ser usado para fornecer um nome adequado para a pesquisa. Clique no botão Próximo para prosseguir.",
            step3Title: "Passo 3: Descrição curta da sua pesquisa",
            step3Desc: "Escreva uma breve descrição da sua pesquisa e clique em Próximo para continuar. Este campo também é obrigatório.",
            step4Title: "Passo 4: Número de seções na sua pesquisa",
            step4Desc: "Especifique o número de seções que deseja incluir na pesquisa e clique em Próximo para continuar.",
            step4Note: "Nota - O número deve ser maior que 0 e este campo é obrigatório.",
            step5Title: "Passo 5: Nomes e descrição das seções",
            step5Desc: "Insira os nomes de cada seção que deseja incluir na pesquisa e especifique a descrição.",
            step5Note: "Nota - Inserir o nome da seção é obrigatório, mas fornecer uma descrição é opcional.",
            step6Title: "Passo 6: Selecionar Idioma",
            step6Desc1: "Selecione o idioma desejado para sua pesquisa entre as opções disponíveis. Você também pode escolher vários idiomas. Ao enviar a pesquisa, ela será adicionada à lista de pesquisas.",
            step6Desc2: "Clique no botão Enviar para avançar para a próxima etapa. A pesquisa será criada e exibida no topo da Lista de Pesquisas.",
            step6Note: "Nota - Por padrão, o idioma inglês é selecionado. Se os usuários precisarem modificar algum detalhe ou acharem que algo foi perdido nas etapas anteriores, podem fazê-lo usando o recurso Ações na tabela da lista de pesquisas.",
            step7Title: "Passo 7: Gerencie sua pesquisa na página do painel",
            step7Desc: "Para acessar e gerenciar sua pesquisa, vá para a página do painel. Lá, você pode fazer edições adicionais, compartilhar a pesquisa ou duplicá-la para diferentes casos de uso.",
          },
          imageAlt1: "Criar Nova Pesquisa",
          imageAlt2: "Nomeie sua Pesquisa",
          imageAlt3: "Descrição Curta",
          imageAlt4: "Número de Seções",
          imageAlt5: "Nomes das Seções",
          imageAlt6: "Selecionar idioma",
          imageAlt7: "Gerenciar Pesquisa",
        },
      },

      // Recommendations Page
      recommendations: {
        title: "Recomendações de Pergunta e Estágio com Variáveis Personalizadas",
        overview: {
          title: "Adicionar Recomendações por Pergunta usando Variáveis Personalizadas",
          intro1: "Variáveis Personalizadas são pares simples de nome e valor que você pode imprimir em qualquer lugar com #[fieldName]#. Uma recomendação é apenas o valor armazenado em uma dessas variáveis. Quando alguém responde a uma pergunta no Construtor de P&R, você decide qual variável recebe qual texto.",
          intro2: "Crie uma Variável Personalizada para a pergunta, por exemplo #[response_1_recommendation]#. No Construtor de P&R, vincule essa variável à opção nas perguntas para as quais deseja mostrar a recomendação e atribua um texto diferente para cada opção. Quando o respondente escolhe uma opção, o texto dessa opção é salvo na variável. No relatório, coloque o placeholder #[response_1_recommendation]# e ele mostrará a recomendação correspondente à opção escolhida.",
          exampleTitle: "Exemplo:",
          exampleIntro: "Suponha que você tenha a Pergunta 1 com as seguintes opções e textos:",
          exampleOptions: {
            optionA: "Opção A — Realize uma avaliação base em até 30 dias.",
            optionB: "Opção B — Amplie o escopo para incluir risco de terceiros.",
            optionC: "Opção C — Agende uma auditoria abrangente e um plano de lacunas.",
          },
          variableName: "Nome da variável: #[response_1_recommendation]#",
          linkText: "Vincule #[response_1_recommendation]# a cada opção no Construtor de P&R e defina o texto correspondente como valor.",
        },
        createVariables: {
          title: "Criar Variáveis Personalizadas para Recomendações",
          intro: "Nesta etapa, você cria a(s) variável(is) que conterão o texto das recomendações. Para adicionar uma nova variável ao projeto, clique no botão Adicionar Nova Variável. Um popup aparecerá com o mesmo formulário visto ao usar o ícone de edição.",
          steps: {
            step1: "Vá em Variáveis Personalizadas → Adicionar Nova Variável.",
            step2Title: "Preencha o formulário:",
            fields: {
              label: "Rótulo — adicione um rótulo legível (máx. 250 caracteres).",
              type: "Tipo — escolha: número, texto de linha única ou texto de várias linhas. (Use texto de várias linhas para parágrafos/listas.)",
              variableName: "Nome da variável — insira um nome único sem espaços, máx. 50 caracteres. Os nomes diferenciam maiúsculas e minúsculas. Exemplo: #[response_1_recommendation]#.",
              description: "Descrição — explique brevemente onde e como essa variável será usada (ex.: Q1 → Recomendação no relatório).",
            },
            step3: "Clique em Adicionar Nova Variável para salvar. A nova variável aparecerá na lista.",
          },
        },
        linkOptions: {
          title: "Vincular Variáveis às Opções no Construtor de P&R",
          intro: "Depois que as Variáveis Personalizadas forem criadas, o próximo passo é conectá-las às perguntas e opções no Construtor de P&R. É aqui que você decide qual recomendação aparecerá no relatório quando o participante selecionar uma opção.",
          steps: {
            step1: "Abra Pesquisas → Construtor de P&R e selecione a pesquisa que deseja configurar.",
            step2: "Vá até a pergunta e observe a lista de opções de resposta. Cada opção pode ter sua própria recomendação por meio de uma variável.",
            step3: "No lado direito da linha da opção, clique no ícone C. Isso abre um diálogo para vincular variáveis personalizadas.",
            step4Title: "Dentro do diálogo, clique em Adicionar Variável. Você irá especificar:",
            step4Items: {
              selectVariable: "Selecionar variável: escolha a variável criada anteriormente. Ex.: #[response_1_recommendation]#.",
              enterValue: "Inserir valor: digite o texto de recomendação que deve aparecer no relatório quando a opção for selecionada.",
            },
            step5: "Clique em Salvar. A variável e seu valor ficarão listados abaixo da opção.",
            reuseTitle: "Você também pode reutilizar a mesma variável em várias opções da mesma pergunta.",
            reuseItems: {
              item1: "Digite textos de recomendação diferentes para cada opção usando a mesma variável.",
              item2: "Quando o respondente seleciona uma opção, o texto dessa opção é salvo na variável.",
            },
          },
        },
        stageRecommendations: {
          title: "Adicionar Recomendações por Estágio usando Variáveis Personalizadas",
          intro: "Recomendações por estágio permitem atribuir variáveis personalizadas com orientações com base no estágio geral do respondente. Aqui você define uma vez por estágio. Quando a pontuação cai naquele estágio, as variáveis são preenchidas com o texto definido.",
          howToTitle: "Como adicionar recomendações por estágio:",
          steps: {
            step1: "Vá até a seção Estágios e clique em Adicionar Estágio.",
            step2: "Informe um Nome e Descrição (ex.: Cloud Explorer).",
            step3: "Selecione uma fórmula no dropdown. Ela decide em qual estágio o respondente cai.",
            step4: "Defina o intervalo de pontuação do estágio (ex.: 40–69).",
            step5Title: "Em Variáveis e Recomendações, clique no ícone + para adicionar variáveis:",
            step5Items: {
              item1: "Selecione a variável (ex.: #[response_1_recommendation]#).",
              item2: "Digite o texto da recomendação que deve aparecer no relatório.",
              item3: "Repita o + para adicionar outras variáveis, cada uma com seu texto.",
              item4: "Use o ícone – para remover uma variável, se necessário.",
            },
            step6: "Clique em Salvar. O estágio agora armazena as recomendações nas variáveis mapeadas.",
          },
          editTitle: "Editando recomendações por estágio:",
          editItems: {
            item1: "Abra a seção Estágios e clique em Editar no estágio desejado.",
            item2: "Adicione ou remova variáveis com os ícones + e –.",
            item3: "Atualize o texto de recomendação de qualquer variável.",
            item4: "Clique em Salvar para aplicar as alterações.",
          },
          deleteTitle: "Excluindo um estágio:",
          deleteItems: {
            item1: "Vá até a seção Estágios e clique em Excluir no estágio.",
            item2: "Confirme a exclusão — isso remove o estágio e seus mapeamentos.",
          },
          conclusion: "Ao usar variáveis personalizadas no nível de estágio, você garante recomendações estratégicas alinhadas ao progresso geral — não apenas dicas por pergunta.",
        },
      },

      // Survey Groups Page
      surveyGroups: {
        title: "Grupos de Pesquisa",
        intro: "Os Grupos de Pesquisa permitem combinar várias versões de idioma da pesquisa em um único link. Respondentes podem alternar entre idiomas facilmente durante a pesquisa.",
        createGroup: {
          title: "1. Criar um Grupo de Pesquisa",
          beforeStart: "Antes de começar: certifique-se de ter criado pesquisas individuais em cada idioma necessário.",
          createTitle: "Como criar um grupo:",
          steps: {
            step1: "Vá para Pesquisas → Grupos de Pesquisa no menu principal.",
            step2: "Clique no botão Criar Grupo.",
            step3: "Preencha os detalhes do grupo (veja os campos abaixo).",
            step4: "Clique em Adicionar Pesquisa para selecionar pesquisas do grupo.",
            step5: "Regra importante: apenas uma pesquisa por idioma é permitida.",
            step6: "Continue adicionando todas as versões de idioma necessárias.",
            step7: "Clique em Criar Grupo para salvar.",
          },
          imageAlt1: "Criar grupo de pesquisa",
          listFieldsTitle: "Campos da lista de grupos:",
          listFields: {
            groupName: "Nome do Grupo — Nome exibido na lista.",
            totalSurveys: "Total de Pesquisas — Número de versões de idioma no grupo.",
            addedOn: "Adicionado em / Adicionado por — Quando e por quem o grupo foi criado.",
            action: "Ação — Menu de três pontos (⋮) com opções:",
            actionItems: {
              rename: "Renomear — Altera o nome do grupo (não muda a URL)",
              delete: "Excluir Grupo de Pesquisa — Remove o grupo permanentemente",
            },
          },
          renameTitle: "Como renomear um grupo:",
          renameSteps: {
            step1: "Na lista de grupos, encontre seu grupo.",
            step2: "Clique em Ações → Renomear.",
            step3: "Digite o novo nome no popup.",
            step4: "Clique em Salvar.",
          },
          deleteTitle: "Como excluir um grupo:",
          deleteSteps: {
            step1: "Na lista de grupos, encontre seu grupo.",
            step2: "Clique em Ações → Excluir Grupo de Pesquisa.",
            step3: "Um aviso aparece informando:",
            step4: "Clique em Confirmar Exclusão.",
          },
          deleteItems: {
            item1: "O grupo será excluído permanentemente",
            item2: "Todos os links do grupo deixarão de funcionar",
            item3: "As pesquisas individuais NÃO serão excluídas",
          },
          imageAlt2: "Excluir grupo de pesquisa",
        },
        manageGroup: {
          title: "2. Gerenciar Pesquisas em um Grupo",
          intro: "Após criar um grupo, você pode adicionar pesquisas, reordenar, definir pesquisa principal e remover pesquisas.",
          addSurveysTitle: "Adicionar pesquisas a um grupo:",
          addSurveysSteps: {
            step1: "Vá para Pesquisas → Pesquisas (lista principal).",
            step2: "Selecione pesquisas marcando as caixas.",
            step2Items: {
              item1: "Você pode selecionar várias pesquisas.",
              item2: "Apenas uma pesquisa por idioma por grupo.",
            },
            step3: "Clique no botão Adicionar ao Grupo.",
            step3Items: {
              item1: "Este botão aparece apenas quando há pesquisas selecionadas.",
            },
            step4: "Um dropdown aparece com busca e lista de grupos.",
            step5: "Opcional: use a busca para encontrar seu grupo.",
            step6: "Clique no nome do grupo para selecionar.",
            step7: "Clique em Adicionar.",
            step8: "Sucesso! As pesquisas foram adicionadas ao grupo.",
          },
          imageAlt3: "Adicionar pesquisas ao grupo",
          manageTitle: "Gerenciar pesquisas dentro do grupo:",
          manageIntro: "Para gerenciar pesquisas no grupo (reordenar, definir principal, remover ou ver leads):",
          manageSteps: {
            step1: "Vá para Pesquisas → Grupos de Pesquisa.",
            step2: "Clique no nome do grupo.",
            step3: "A página de detalhes abre com a tabela de pesquisas.",
            step4: "Há duas abas: Pesquisa e Variáveis de Campanha UTM.",
            step4Items: {
              item1: "Pesquisa — Gerenciar pesquisas no grupo",
              item2: "Variáveis de Campanha UTM — Variáveis de rastreamento",
            },
            step5: "Na aba Pesquisa, você verá:",
            fields: {
              surveyName: "Nome da Pesquisa — Nome com badge (Principal, se aplicável).",
              completion: "Conclusão / Respostas — Concluídas vs total.",
              language: "Idioma — Idioma desta versão.",
              status: "Status — Publicada ou Rascunho.",
              assignTeam: "Equipe atribuída — Equipe responsável.",
              addedOn: "Adicionado em / por — Data e autor.",
              responses: "Respostas — Total de respostas.",
              publishedOn: "Publicado em — Data de publicação.",
              action: "Ação — Menu (⋮) com opções:",
              actionItems: {
                primary: "Tornar principal — Define idioma padrão",
                viewLeads: "Ver Leads — Ver respostas/leads",
                remove: "Remover do grupo — Remove a pesquisa",
              },
            },
          },
          imageAlt4: "Gerenciar pesquisas no grupo",
          utmTitle: "Adicionar Variáveis de Campanha UTM ao grupo:",
          utmIntro: "Variáveis UTM ajudam a rastrear a origem das respostas. Você pode adicionar valores pré-definidos para Source, Medium e Campaign.",
          utmSteps: {
            step1: "Na página do grupo, clique na aba Variáveis de Campanha UTM.",
            step2: "Você verá Source, Medium e Campaign.",
            step3: "Clique em + Adicionar em qualquer seção.",
            step4: "Digite o valor (ex.: facebook, social, spring_sale).",
            step5: "Clique fora do campo ou pressione Tab para salvar.",
            step6: "Para excluir, clique no ícone de menos (−) e confirme.",
            step7: "Esses valores aparecerão ao criar links UTM do grupo.",
          },
          imageAlt5: "Variáveis UTM no grupo",
        },
        links: {
          title: "3. Gerar e compartilhar links do grupo",
          intro: "Cada grupo possui um link único. Você pode compartilhar o link básico ou criar links com UTM.",
          basicTitle: "Obter o link básico:",
          basicSteps: {
            step1: "Na lista de grupos, clique em Compartilhar Grupo.",
            step2: "Você verá o Link Base: https://yourplatform.com/group/your-group-slug",
            step3: "Clique em Copiar Link.",
          },
          imageAlt6: "Compartilhar link do grupo",
          campaignTitle: "Criar links de campanha com UTM:",
          campaignIntro: "Parâmetros UTM ajudam a rastrear a origem das respostas (email, redes sociais, anúncios).",
          campaignSteps: {
            step1: "Em Compartilhar Grupo, clique em Criar Nova URL.",
            step2: "Preencha os campos da campanha:",
            fields: {
              name: "Name* — Nome interno (ex.: Facebook Ad Q1)",
              source: "Source* — Origem do tráfego",
              medium: "Medium* — Canal de marketing",
              campaign: "Campaign* — Nome da campanha",
              content: "Content — Opcional",
            },
            step3: "Opcional: clique em Adicionar Parâmetros.",
            step4: "Clique em Criar.",
            step5: "Seu link UTM é gerado.",
            step6: "Copie e compartilhe o link.",
          },
          imageAlt7: "Criar link de campanha",
          whatHappensTitle: "O que acontece quando o respondente abre o link:",
          whatHappensItems: {
            item1: "A pesquisa principal carrega primeiro.",
            item2: "O seletor de idioma fica visível.",
            item3: "O idioma pode ser trocado sem recarregar a página.",
          },
          imageAlt8: "Seletor de idioma",
        },
        analytics: {
          title: "4. Ver Analytics dos Grupos",
          intro: "Grupos têm uma seção de analytics própria, com respostas combinadas ou por idioma.",
          accessTitle: "Como acessar analytics do grupo:",
          accessSteps: {
            step1: "Vá para Analytics no menu principal.",
            step2: "No dropdown Selecionar Pesquisa, veja: Pesquisas Individuais e Grupos.",
            step2Items: {
              item1: "Pesquisas Individuais — Pesquisas normais",
              item2: "Grupos de Pesquisa — Seus grupos",
            },
            step3: "Selecione um Grupo para ver analytics.",
          },
          whatYouSeeTitle: "O que você pode ver:",
          combined: "Visão combinada — respostas totais e métricas em todos os idiomas.",
          byLanguageTitle: "Por idioma — filtre para ver:",
          byLanguageItems: {
            item1: "Respostas por idioma",
            item2: "Taxas de conclusão por idioma",
            item3: "Desempenho das perguntas por idioma",
          },
          imageAlt9: "Analytics do grupo",
        },
      },

      // Settings - Account Settings
      accountSettings: {
        title: "Perfil",
        intro: "A página Perfil permite gerir as suas informações pessoais, incluindo foto de perfil, email, nome de utilizador, região, fuso horário e idioma predefinido.",
        accessing: {
          title: "Aceder ao Perfil",
          intro: "Para abrir a página Perfil:",
          steps: {
            step1: "Clique em <b>Configurações</b> na barra lateral principal.",
            step2: "Em <b>Configurações gerais</b>, clique em <b>Perfil</b>.",
            step3: "A página abre em <b>/admin/settings/profile</b>.",
          },
          imageAlt: "Menu de configurações mostrando Perfil",
        },
        availableFields: {
          title: "Campos e Funções Disponíveis",
          generalInfo: {
            title: "Informações Gerais",
            profilePhoto: "Foto de perfil: carregue, altere ou remova a imagem de perfil pelos controlos do avatar.",
            email: "Email: exibe o email registado do utilizador.",
            userName: "Nome de utilizador: o nome de exibição, que pode ser modificado.",
            imageAlt: "Formulário de conta com foto de perfil e campos",
          },
          localization: {
            title: "Configurações de Localização",
            region: "Região: selecione a região geográfica numa lista suspensa.",
            country: "País: escolha um país entre as opções disponíveis.",
            timeZone: "Fuso horário: defina o fuso horário preferido.",
            defaultLanguage: "Idioma padrão: selecione o idioma da interface.",
          },
          savingChanges: {
            title: "Salvando Alterações",
            step1: "Após realizar modificações, clique no botão 'Salvar Alterações' para atualizar as configurações da conta.",
            step2: "Se algum campo obrigatório estiver ausente ou inválido, uma mensagem de erro será exibida.",
            imageAlt: "Botão salvar alterações",
          },
        },
        assignedTeamsRoles: {
          title: "Equipes e Funções Atribuídas",
          intro: "Esta seção exibe a lista de equipes das quais o usuário faz parte junto com suas funções atribuídas. Ela aparece no final do formulário de Configurações da Conta para referência rápida.",
          teams: "Equipes: Mostra o nome de cada equipe à qual o usuário pertence.",
          roles: "Funções: Exibe a função correspondente do usuário dentro dessa equipe (por exemplo, Admin, Editor).",
          conclusion: "Esta seção não é editável e é destinada apenas para fins informativos, fornecendo visibilidade sobre o acesso às equipes e permissões atribuídas ao usuário.",
          imageAlt: "Equipes e funções atribuídas",
        },
      },

      securitySettings: {
        title: "Configurações de Segurança",
        intro: "Controla o comportamento de login de todos os utilizadores: SSO Microsoft, MFA, bloqueio de conta e expiração de palavra-passe.",
        accessing: {
          title: "Aceder às Configurações de Segurança",
          steps: {
            step1: "Clique em <b>Configurações</b> na barra lateral principal.",
            step2: "Em <b>Configurações gerais</b>, clique em <b>Segurança</b> (requer <b>M9 view</b>).",
            step3: "A página abre em <b>/admin/settings/security</b>.",
          },
          imageAlt: "Menu mostrando Segurança",
        },
        options: {
          title: "Opções de Segurança",
          imageAlt: "Página de segurança com alternadores",
          items: {
            sso: "SSO Microsoft — mostrar ou ocultar o botão Entrar com Microsoft.",
            mfa: "Autenticação multifator (MFA) — código de 6 dígitos por email após a palavra-passe.",
            lockout: "Bloqueio de conta — bloquear após tentativas falhadas; configurar tentativas e duração.",
            passwordExpiry: "Expiração de palavra-passe — forçar alteração após X dias.",
            save: "Clique em <b>Guardar alterações</b> para aplicar.",
          },
        },
        permissionsNote: "Requer M9 view para abrir e M9 edit para guardar.",
      },

      ticketNotificationSettings: {
        title: "Notificações de Tickets",
        intro: "Configure quem recebe email quando tickets são criados ou atribuídos.",
        accessing: {
          title: "Aceder às Notificações de Tickets",
          steps: {
            step1: "Clique em <b>Configurações</b> na barra lateral principal.",
            step2: "Em <b>Configurações gerais</b>, clique em <b>Notificações</b> (requer <b>M10 view</b>).",
            step3: "A página abre em <b>/admin/settings/ticket-notifications</b>.",
          },
          imageAlt: "Menu mostrando Notificações de tickets",
        },
        recipients: {
          title: "Destinatários",
          imageAlt: "Configuração de destinatários de notificação",
          items: {
            search: "Pesquise utilizadores por nome ou email e adicione como destinatários.",
            add: "Os selecionados aparecem como chips na lista.",
            remove: "Remova com o controlo de eliminar em cada chip.",
            save: "Clique em <b>Guardar</b> para persistir a lista.",
          },
        },
        permissionsNote: "Requer M10 view para abrir e M10 edit para guardar.",
      },

      organizationDetails: {
        privacyPolicy: {
          title: "Política de Privacidade",
          intro: "Edite a Política de Privacidade da organização na página de login. É distinta das políticas por inquérito.",
          accessing: {
            title: "Aceder à Política de Privacidade",
            imageAlt: "Menu com Política de privacidade selecionada",
            steps: {
              step1: "Clique em <b>Configurações</b> na barra lateral principal.",
              step2: "Em <b>Detalhes da organização</b>, clique em <b>Política de privacidade</b>.",
              step3: "A página abre em <b>/admin/settings/organization-details/privacy-policy</b>.",
            },
          },
          editor: {
            title: "Editar Política de Privacidade",
            imageAlt: "Editor da política de privacidade",
            items: {
              toolbar: "Barra de formatação: negrito, títulos, listas, ligações, tabelas e cor.",
              content: "Edite o texto da política de privacidade no editor.",
              save: "Clique em <b>Guardar alterações</b> para publicar na página de login.",
            },
          },
          permissionsNote: "Requer M8 view para abrir e M8 edit para guardar.",
        },
        termsOfUse: {
          title: "Termos de Utilização",
          intro: "Edite os Termos de Utilização da organização na página de login.",
          accessing: {
            title: "Aceder aos Termos de Utilização",
            imageAlt: "Menu com Termos de utilização selecionados",
            steps: {
              step1: "Clique em <b>Configurações</b> na barra lateral principal.",
              step2: "Em <b>Detalhes da organização</b>, clique em <b>Termos de utilização</b>.",
              step3: "A página abre em <b>/admin/settings/organization-details/terms-of-use</b>.",
            },
          },
          editor: {
            title: "Editar Termos de Utilização",
            imageAlt: "Editor dos termos de utilização",
            items: {
              toolbar: "Barra de formatação: negrito, títulos, listas, ligações, tabelas e cor.",
              content: "Edite o texto dos termos de utilização no editor.",
              save: "Clique em <b>Guardar alterações</b> para publicar na página de login.",
            },
          },
          permissionsNote: "Requer M8 view para abrir e M8 edit para guardar.",
        },
        cookiePolicy: {
          title: "Política de Cookies",
          intro: "Edite a Política de Cookies da organização na página de login. É distinta das políticas por inquérito.",
          accessing: {
            title: "Aceder à Política de Cookies",
            imageAlt: "Menu com Política de cookies selecionada",
            steps: {
              step1: "Clique em <b>Configurações</b> na barra lateral principal.",
              step2: "Em <b>Detalhes da organização</b>, clique em <b>Política de cookies</b>.",
              step3: "A página abre em <b>/admin/settings/organization-details/cookie-policy</b>.",
            },
          },
          editor: {
            title: "Editar Política de Cookies",
            imageAlt: "Editor da política de cookies",
            items: {
              toolbar: "Barra de formatação: negrito, títulos, listas, ligações, tabelas e cor.",
              content: "Edite o texto da política de cookies no editor.",
              save: "Clique em <b>Guardar alterações</b> para publicar na página de login.",
            },
          },
          permissionsNote: "Requer M8 view para abrir e M8 edit para guardar.",
        },
      },

      // Settings - Users
      users: {
        title: "Usuários",
        accessing: {
          title: "Aceder a Utilizadores",
          intro: "Navegar até à gestão de utilizadores:",
          steps: {
            step1: "Clique em <b>Configurações</b> na barra lateral principal.",
            step2: "Em <b>Gestão de utilizadores</b>, clique em <b>Utilizadores</b> (requer <b>M3 view</b>).",
            step3: "A página abre em <b>/admin/settings/user-permissions</b>.",
          },
          imageAlt: "Menu mostrando Utilizadores",
        },
        search: {
          title: "Pesquisando um Usuário",
          steps: {
            step1: "Use a barra de Pesquisa no topo para encontrar um usuário específico por nome ou email.",
            step2: "A lista será atualizada dinamicamente conforme você digita.",
          },
          imageAlt: "Barra de pesquisa de usuários",
        },
        listOverview: {
          title: "Visão Geral da Lista de Usuários",
          intro: "A lista de usuários exibe as seguintes colunas:",
          columns: {
            name: "Nome - Exibe o nome do usuário junto com iniciais em um círculo colorido.",
            defaultLanguage: "Idioma padrão - Exibe o idioma definido para o usuário.",
            timezone: "Fuso horário - Mostra o fuso horário selecionado pelo usuário.",
            actions: "Ações - Fornece opções para editar ou excluir o usuário.",
          },
          imageAlt: "Tabela de usuários",
          actionsImageAlt: "Ações do usuário",
        },
        editing: {
          title: "Editando um Usuário",
          steps: {
            step1: "Clique no ícone Editar (lápis) na coluna Ação.",
            step2: "Modifique os detalhes do usuário como nome, email, idioma e fuso horário.",
            step3: "Clique em Salvar para aplicar as alterações.",
          },
          imageAlt: "Editar usuário",
        },
        deleting: {
          title: "Excluindo um Usuário",
          steps: {
            step1: "Clique no ícone Excluir (lixeira) na coluna Ação.",
            step2: "Um popup de confirmação aparecerá.",
            step3: "Clique em Confirmar para remover o usuário permanentemente.",
          },
          imageAlt: "Excluir usuário",
        },
        addUser: {
          title: "Adicionando um Novo Usuário",
          steps: {
            step1: "Clique no botão + Criar Usuário (botão vermelho no canto superior direito).",
            step2: "Um formulário aparecerá onde você pode inserir:",
          },
          saveAction: "Clique em Salvar para criar o usuário.",
          fields: {
            fullName: "Nome completo - Exibe o nome completo do usuário.",
            email: "Endereço de email - Um novo usuário precisará definir uma senha",
            password: "Senha - Mostra um campo de senha mascarada (●●●●●). Clicar no botão 'X' pode limpar o campo de senha.",
            timezone: "Fuso horário - Um menu suspenso para selecionar o fuso horário do usuário.",
            defaultLanguage: "Idioma padrão - Um menu suspenso para escolher o idioma preferido.",
          },
          saveOptions: {
            cancel: "Cancelar - Fecha o modal sem salvar alterações.",
            save: "Salvar - Salva os detalhes do usuário atualizados.",
          },
          imageAlt: "Formulário de criação de usuário",
        },
      },

      // Settings - Roles
      roles: {
        title: "Funções",
        introLine1: "Nesta seção, as funções são concedidas e as permissões são atribuídas a cada função.",
        introLine2: "Uma função é um conjunto definido de responsabilidades e permissões atribuídas a um usuário ou a um grupo de usuários dentro de uma organização. Ela determina quais ações e operações um usuário pode executar dentro de um sistema ou aplicativo. As funções ajudam a gerenciar o controle de acesso e a aplicar medidas de segurança ao conceder ou restringir privilégios com base nas responsabilidades e funções de trabalho do usuário.",
        accessing: {
          title: "Acessando Configurações da Conta e filtro",
          intro: "Navegando até a seção de gerenciamento de Funções:",
          steps: {
            step1: "Vá para o menu Configurações na barra lateral esquerda.",
            step2: "Em <b>Gestão de utilizadores</b>, clique em <b>Funções</b> (requer <b>M4 view</b>).",
            step3: "Esta seção permite gerenciar todas as Funções na sua organização.",
          },
          imageAlt: "Navegação de funções",
        },
        search: {
          title: "Pesquisando uma Função:",
          steps: {
            step1: "Use a barra de Pesquisa no topo para encontrar uma função específica pelo nome.",
            step2: "A lista será atualizada dinamicamente conforme você digita.",
          },
          imageAlt: "Barra de pesquisa de funções",
        },
        listOverview: {
          title: "Visão Geral da Lista de Funções",
          intro: "A lista de funções exibe as seguintes colunas:",
          columns: {
            srNo: "Nº - Exibe o número de série.",
            names: "Nomes - Exibe o nome da função (por exemplo, Admin, Viewer, Editor).",
            dateAdded: "Data de inclusão - Mostra a data em que foi adicionada.",
            actions: "Ações - Fornece opções para editar ou excluir a função.",
          },
          imageAlt: "Tabela de funções",
        },
        editing: {
          title: "Editando uma Função",
          steps: {
            step1: "Clique no ícone Editar (lápis) na coluna Ação.",
            step2: "Modifique detalhes da função como nome da função, função pai, permissões etc.",
            step3: "Clique em Salvar para aplicar as alterações.",
          },
          imageAlt: "Editar função",
        },
        deleting: {
          title: "Excluindo uma Função",
          steps: {
            step1: "Clique no ícone Excluir (lixeira) na coluna Ação.",
            step2: "Um popup de confirmação aparecerá.",
            step3: "Clique em Confirmar para remover a função permanentemente.",
          },
          imageAlt: "Excluir função",
        },
        addRole: {
          title: "Adicionar Nova Função",
          steps: {
            step1: "Clique no botão + Criar Função (botão vermelho no canto superior direito).",
            step2: "Um formulário aparecerá onde você pode inserir:",
          },
          formImageAlt: "Formulário de função",
          permissionsImageAlt: "Permissões da função",
          fields: {
            roleName: "Nome da função (Campo de texto) - Este campo é onde o administrador insere o nome da nova função.",
            parentRole: "Selecionar Função Pai (Menu suspenso) - Este menu permite selecionar uma função existente como modelo ou base de hierarquia para a nova função.",
            permissionsTitle: "Seção de Permissões - Exibe diferentes categorias (Pesquisa, Analítica, Usuários, Funções, Equipes) com opções de permissão correspondentes:",
            view: "Visualizar - Permite que a função visualize conteúdo ou dados no sistema ou aplicativo. Usuários com esta permissão podem ver e acessar informações, mas não podem modificá-las ou excluí-las.",
            write: "Escrever - Concede ao usuário a capacidade de criar, editar ou modificar conteúdo ou dados.",
            read: "Ler - Fornece acesso somente leitura. Usuários com esta permissão podem visualizar o conteúdo, mas podem não ter capacidade de fazer alterações.",
            delete: "Excluir - Permite que a função exclua conteúdo ou dados do sistema ou aplicativo.",
            checkbox: "Cada permissão é representada como uma caixa de seleção que pode ser marcada ou desmarcada.",
            parentRolesNote: "Para selecionar a função pai, há apenas três funções definidas: Admin, Executor e Editor. A função Admin tem todos os direitos e permissões concedidos e não pode ser editada ou excluída. Ela possui o nível mais alto de autoridade e pode gerenciar outras funções.",
            permissionsNote: "Com base na necessidade, a função pode ser atribuída com as permissões desejadas para pesquisa, analítica, usuários, funções e equipes.",
          },
          saveAction: "Clique em Salvar para criar a função.",
          saveOptions: {
            cancel: "Cancelar - Fecha o modal sem salvar alterações.",
            save: "Salvar - Salva os detalhes da função atualizados.",
          },
        },
      },

      // Settings - Teams
      teams: {
        title: "Equipes",
        introLine1: "Equipes são uma forma de organizar grupos de usuários dentro de uma organização. Cada equipe pode ter seu próprio nome exclusivo e consiste em funções atribuídas para cada usuário. É possível que equipes diferentes tenham o mesmo usuário com funções diferentes, e várias equipes podem ter o mesmo nome. Nesse caso, todas as permissões são mescladas para esse nome.",
        introLine2: "As equipes são visíveis apenas para usuários que criaram ou fazem parte de uma equipe específica. O criador da equipe tem a capacidade de gerenciar membros adicionando, editando ou excluindo-os. No entanto, ele não pode remover ou excluir seu próprio nome da equipe.",
        introLine3: "As equipes fornecem uma abordagem estruturada para colaboração e permitem que os usuários trabalhem juntos em projetos ou tarefas específicas. Elas ajudam a simplificar a comunicação, atribuir responsabilidades e facilitar o trabalho em equipe eficiente.",
        accessing: {
          title: "Acessando Configurações da Conta e filtro",
          intro: "Navegando até a seção de Equipes:",
          steps: {
            step1: "Vá para o menu Configurações na barra lateral esquerda.",
            step2: "Em <b>Gestão de utilizadores</b>, clique em <b>Equipas</b> (requer <b>M5 view</b>).",
            step3: "Esta seção permite gerenciar todas as Equipes na sua organização.",
          },
          imageAlt: "Navegação de equipes",
        },
        search: {
          title: "Pesquisando uma Equipe:",
          steps: {
            step1: "Use a barra de Pesquisa no topo para encontrar uma equipe específica pelo nome.",
            step2: "A lista será atualizada dinamicamente conforme você digita.",
          },
          imageAlt: "Barra de pesquisa de equipes",
        },
        sort: {
          title: "Filtro ordenar por:",
          steps: {
            step1: "Filtro ordenar por disponível para fácil acesso aos dados.",
            step2: "Há duas opções para ordenar alfabeticamente em ordem crescente (A-Z) ou decrescente (Z-A).",
          },
          imageAlt: "Filtro de ordenação",
        },
        listOverview: {
          title: "Visão Geral da Lista de Equipes",
          intro: "A lista de equipes exibe as seguintes colunas:",
          columns: {
            teamName: "Nome da equipe - Exibe o nome da equipe.",
            member: "Membro - Exibe o nome da função (por exemplo, Admin, Viewer, Editor).",
            actions: "Ações - Fornece opções para editar, excluir ou sair da equipe.",
          },
          imageAlt: "Tabela de equipes",
        },
        editing: {
          title: "Editando uma Equipe",
          intro: "A interface Editar Equipe permite que administradores modifiquem uma equipe existente ao atualizar seu nome, adicionar novos membros com funções específicas e gerenciar membros existentes.",
          steps: {
            step1: "Clique no ícone Editar (lápis) na coluna Ação.",
            step2: "Nome da equipe - O campo de entrada exibe o nome atual da equipe e permite editá-lo e atualizá-lo.",
            step3: "Botões Salvar e Cancelar (Nome da equipe).",
            step4: "Salvar - Salva o nome atualizado da equipe.",
            step5: "Cancelar - Descarta alterações feitas no nome da equipe.",
            step6: "Seção Adicionar Membro",
            step7: "Adicionar Membro (Menu suspenso) - Permite selecionar um usuário para ser adicionado à equipe.",
            step8: "Selecionar Função (Menu suspenso) - Atribui uma função ao membro selecionado.",
            step9: "Botão Salvar - Confirma e adiciona o membro selecionado com a função atribuída.",
            step10: "Botão Cancelar - Descarta a seleção do membro.",
            step11: "Lista de Membros Adicionados - Exibe uma lista de usuários atualmente na equipe, junto com suas funções atribuídas. Cada linha contém:",
            step12: "Nome do Membro - O nome do membro da equipe.",
            step13: "Menu suspenso de Função - Permite atualizar a função atribuída do usuário diretamente na lista. As alterações são aplicadas imediatamente após salvar.",
            step14: "Ícone Excluir - Remove o usuário da equipe quando clicado.",
            step15: "Esta interface é essencial para o gerenciamento de equipes, atribuição de funções e controle de acesso de usuários dentro do sistema.",
          },
          imageAlt: "Editar equipe",
        },
        deleting: {
          title: "Excluindo um Usuário",
          steps: {
            step1: "Clique no ícone Excluir (lixeira) na coluna Ação.",
            step2: "Um popup de confirmação aparecerá.",
            step3: "Clique em Confirmar para remover o usuário permanentemente.",
          },
          imageAlt: "Excluir usuário",
        },
        manageMembers: {
          title: "Gerenciar Membros (Ícone de seta)",
          steps: {
            step1: "Abre a interface de gerenciamento de membros da equipe, permitindo adicionar ou remover membros.",
          },
        },
        addTeam: {
          title: "Adicionar Nova Equipe",
          steps: {
            step1: "Clique no botão + Criar Função (botão vermelho no canto superior direito).",
            step2: "Usuário redirecionado para a página de adicionar equipe.",
            step3: "Ao inserir o nome da equipe e salvar, uma nova equipe será criada.",
          },
          imageAlt: "Formulário de adicionar equipe",
        },
      },

      // Common elements
      common: {
        note: "Nota:",
        steps: "Passos:",
        whenToUse: "Quando usar:",
        safetyFeatures: "Recursos de Segurança:",
        imageAlt: "Captura de tela",
      },
    },
  },

  // Albanian translations
  sq: {
    documentation: {
      // Sidebar Navigation
      sidebar: {
        backToSurvey: "Kthehu te Sondazhi",
        survey: "Sondazhi",
        analytics: "Analitika",
        activityLog: "Regjistri i Aktiviteteve",
        tickets: "Biletat",
        settings: "Cilësimet",
        // Survey submenu
        surveyList: "Lista e Sondazheve",
        qandaBuilder: "Ndërtuesi i P&P",
        formBuilder: "Ndërtuesi i Formularëve",
        reportBuilder: "Ndërtuesi i Raporteve",
        emailTemplates: "Shabllonet e Email-it",
        variables: "Variablat",
        publishShare: "Publiko dhe Shpërndaj",
        integration: "Integrimi",
        webhooks: "Webhooks",
        questionnaire: "Pyetësori",
        thankYouPage: "Faqja e Falënderimit",
        leadErasureRules: "Rregullat e Fshirjes së Drejtimeve",
        privacyPolicy: "Politika e Privatësisë",
        cookiePolicy: "Politika e Cookies",
        createSurvey: "Krijo Sondazh",
        recommendations: "Rekomandimet",
        surveyGroups: "Grupet e Sondazheve",
        surveySync: "Sinkronizimi i Sondazhit",
        // Settings submenu
        profile: "Profili",
        security: "Siguria",
        notifications: "Njoftimet",
        organizationDetails: "Detajet e organizatës",
        users: "Përdoruesit",
        roles: "Rolet",
        teams: "Ekipet",
        settingsGeneral: "Cilësimet e përgjithshme",
        settingsUserManagement: "Menaxhimi i përdoruesve",
        settingsOrganizationDetails: "Detajet e organizatës",
        privacyPolicy: "Politika e privatësisë",
        termsOfUse: "Kushtet e përdorimit",
        cookiePolicy: "Politika e cookies",
      },

      settingsMenu: {
        intro: "Kur hapni Cilësimet, menuja e brendshme është e organizuar si më poshtë:",
        generalSettings: "Cilësimet e përgjithshme",
        profile: "Profili",
        security: "Siguria",
        notifications: "Njoftimet",
        userManagement: "Menaxhimi i përdoruesve",
        users: "Përdoruesit",
        roles: "Rolet",
        teams: "Ekipet",
        organizationDetails: "Detajet e organizatës",
        privacyPolicy: "Politika e privatësisë",
        termsOfUse: "Kushtet e përdorimit",
        cookiePolicy: "Politika e cookies",
      },

      // Survey Sync Page
      surveySync: {
        title: "Sinkronizimi i Sondazhit",
        intro: "Funksionaliteti i Sinkronizimit të Sondazhit u lejon administratorëve të sinkronizojnë sondazhet nga mjedisi UAT (Testimi i Pranimit të Përdoruesit) në mjedisin e Prodhimit.",
        
        syncFromUat: {
          title: "Sinkronizo nga UAT",
          whenToUse: "Përdorni këtë funksion kur një sondazh ekziston në UAT por <b>NUK është i pranishëm në Prodhim/Live</b>. Kjo përdoret kryesisht për vendosjen e sondazheve të reja nga UAT në Prodhim.",
          stepsTitle: "Hapat:",
          steps: {
            step1: "Navigoni te <b>Sondazhi → Sondazhet</b> nga menyja anësore e majtë",
            step2: "Në faqen e Listës së Sondazheve, klikoni butonin <b>\"Sinkronizo nga UAT\"</b> i vendosur në të djathtën e sipërme (pranë butonit \"Shto në grup\")",
            step3: "Do të hapet një modal me titull <b>\"Sinkronizo nga UAT\"</b>, duke shfaqur të gjitha sondazhet e disponueshme në UAT",
            step4: "Zgjidhni <b>NJË sondazh</b> nga lista duke përdorur butonin radio (lejohet vetëm zgjedhja e vetme)",
            step5: "Klikoni <b>\"Sinkronizo\"</b> për të kopjuar sondazhin e zgjedhur nga UAT në Prodhim",
            step6: "Klikoni <b>\"Anulo\"</b> për të mbyllur pa sinkronizuar",
          },
          note: "Ky opsion kopjon sondazhin e plotë (me të gjitha pyetjet, seksionet, variablat, etj.) nga UAT në Prodhim. Është ideal për vendosjen fillestare të sondazheve të reja.",
          imageAlt: "Vendndodhja e butonit Sinkronizo nga UAT",
        },

        fullReplacement: {
          title: "Sinkronizimi i Zëvendësimit të Plotë",
          whenToUse: "Përdoreni këtë kur një sondazh <b>ekziston tashmë si në UAT ashtu edhe në Prodhim</b>, dhe dëshironi të zëvendësoni plotësisht versionin e Prodhimit me versionin e UAT.",
          stepsTitle: "Hapat:",
          steps: {
            step1: "Navigoni te <b>Sondazhi → Sondazhet</b> nga menyja anësore e majtë",
            step2: "Gjeni sondazhin në listë dhe klikoni <b>menynë e Veprimeve</b> (tre pikat) në të djathtë",
            step3: "Zgjidhni <b>\"Sinkronizo nga UAT\"</b> nga menyja zbritëse",
            step4: "Në modal, zgjidhni opsionin <b>\"Zëvendësim i Plotë\"</b>",
            step5: "Klikoni <b>\"Sinkronizo\"</b> për të zëvendësuar sondazhin e Prodhimit me versionin e UAT",
          },
          safetyTitle: "Veçoritë e Sigurisë:",
          safety: {
            backup: "Një kopje rezervë e versionit krijohet automatikisht para zëvendësimit",
            responses: "Përgjigjet e përdoruesve dhe të dhënat e drejtimeve nuk preken kurrë",
            rollback: "Mund të ktheheni duke përdorur Historikun e Versioneve nëse është e nevojshme",
          },
          imageAlt: "Opsioni i sinkronizimit të zëvendësimit të plotë",
        },

        mergeSync: {
          title: "Sinkronizimi i Bashkimit",
          whenToUse: "Përdoreni këtë kur dëshironi të <b>shtoni artikuj të rinj nga UAT</b> ndërsa ruani artikujt ekzistues të Prodhimit. Ideal për përditësime graduale.",
          stepsTitle: "Hapat:",
          steps: {
            step1: "Navigoni te <b>Sondazhi → Sondazhet</b> nga menyja anësore e majtë",
            step2: "Gjeni sondazhin në listë dhe klikoni <b>menynë e Veprimeve</b> (tre pikat) në të djathtë",
            step3: "Zgjidhni <b>\"Sinkronizo nga UAT\"</b> nga menyja zbritëse",
            step4: "Në modal, zgjidhni opsionin <b>\"Bashko\"</b>",
            step5: "Klikoni <b>\"Tjetër\"</b> për të parë ndryshimet para aplikimit",
            step6: "Rishikoni pamjen paraprake të bashkimit duke treguar:",
            step7: "Klikoni <b>\"Prano dhe Sinkronizo\"</b> për të aplikuar bashkimin, ose <b>\"Anulo\"</b> për të ndërprerë",
          },
          previewItems: {
            newItems: "<b>Artikuj të Rinj (Jeshile):</b> Artikuj nga UAT që do të shtohen",
            conflicts: "<b>Konflikte (E verdhë):</b> Artikuj me UUID të njëjtë por përmbajtje të ndryshme - versioni UAT do të zëvendësojë",
            duplicates: "<b>Dublikatë (Gri):</b> Artikuj me përmbajtje të njëjtë por UUID të ndryshme - të dy mbahen",
          },
          safetyTitle: "Veçoritë e Sigurisë:",
          safety: {
            preview: "Shikoni ndryshimet para aplikimit",
            backup: "Kopje rezervë e versionit krijohet para bashkimit",
            atomic: "Operacion atomik - të gjitha ndryshimet janë të suksesshme ose të gjitha kthehen",
            preserved: "Artikujt vetëm të Prodhimit ruhen",
          },
          noChangesTitle: "Kuptimi i \"Asnjë ndryshim për të bashkuar\"",
          noChangesDesc: "Nëse pamja paraprake tregon: <i>\"Asnjë ndryshim për të bashkuar. Sondazhi është tashmë i sinkronizuar.\"</i>",
          noChangesItems: {
            identical: "Kjo do të thotë se UAT dhe Prodhimi janë identike",
            noNew: "Asnjë artikull i ri, konflikt ose dublikatë nuk u gjet",
            safe: "Mund ta mbyllni modalin me siguri - nuk nevojitet asnjë sinkronizim",
          },
          imageAlt1: "Zgjedhja e opsionit të bashkimit",
          imageAlt2: "Ekrani i pamjes paraprake të bashkimit",
        },

        versionHistory: {
          title: "Historiku i Versioneve dhe Kthimi",
          intro: "Çdo operacion i Zëvendësimit të Plotë dhe Bashkimit krijon një kopje rezervë të versionit, duke lejuar kthimin nëse është e nevojshme.",
          accessTitle: "Për të hyrë në Historikun e Versioneve:",
          accessSteps: {
            step1: "Klikoni <b>menynë e Veprimeve</b> (tre pikat) në çdo sondazh",
            step2: "Zgjidhni <b>\"Historiku i Versioneve\"</b>",
            step3: "Shikoni të gjitha versionet e mëparshme me datat dhe llojet",
            step4: "Klikoni <b>\"Rikthe\"</b> në çdo version për të kthyer",
          },
          rollbackTitle: "Si të Ktheheni në një Version të Mëparshëm",
          rollbackSteps: {
            step1: "Hapni modalin e Historikut të Versioneve nga menyja e Veprimeve të sondazhit",
            step2: "Rishikoni listën e versioneve dhe identifikoni versionin që dëshironi të riktheni",
            step3: "Klikoni butonin <b>\"Rikthe\"</b> për atë version",
            step4: "Sistemi:",
            step4Items: {
              backup: "Krijon një kopje të re rezervë të versionit të gjendjes aktuale (para rikthimit)",
              restore: "Rikthe të gjithë komponentët e sondazhit nga versioni i zgjedhur",
              maintain: "Ruan ID-në e sondazhit (lidhjet ekzistuese vazhdojnë të funksionojnë)",
            },
            step5: "Mesazhi i suksesit shfaqet:",
            step5Message: "\"Rikthyer në v3! Kopja rezervë v7 u krijua.\"",
            step6: "Sondazhi juaj tani është rikthyer në versionin e zgjedhur",
          },
          note: "Historiku i Versioneve gjurmon vetëm strukturën dhe cilësimet e sondazhit. Përgjigjet e përdoruesve dhe të dhënat e drejtimeve nuk modifikohen kurrë gjatë operacioneve të sinkronizimit ose rikthimit.",
          imageAlt: "Modali i historikut të versioneve",
        },
      },

      // Email Templates Page
      emailTemplates: {
        title: "Shabllonet e Email-it",
        intro: "Faqja Shabllonet e Email-it u lejon administratorëve të personalizojnë email-in e njoftimit të raportit të dërguar respondentëve kur raporti i sondazhit është gati. Çdo sondazh ka shabllonin e vet me pamje paraprake live dhe panel stili.",
        accessing: {
          title: "Qasja në Faqen e Shablloneve të Email-it",
          imageAlt: "Shiriti anësor i sondazhit me lidhjen Shabllonet e email-it",
          steps: {
            step1: "Hapni një sondazh nga <b>Lista e Sondazheve</b> që një <b>survey_id</b> të jetë aktiv në URL.",
            step2: "Në shiritin anësor të majtë, klikoni <b>Shabllonet e email-it</b> (kërkon leje <b>M14 view</b>).",
            step3: "Faqja hapet në <b>/admin/survey/email-template?survey_id={id}</b>.",
          },
        },
        overview: {
          title: "Përmbledhje",
          imageAlt: "Redaktori i Shablloneve të Email-it me pamje paraprake dhe panel stili",
          desc: "Redaktori ndahet në dy panele: një pamje paraprake WYSIWYG e email-it në të majtë dhe një panel stili/konfigurimi në të djathtë. Ndryshimet në çdo panel përditësojnë pamjen paraprake në kohë reale.",
        },
        enableDisable: {
          title: "Aktivizo / Çaktivizo Email-et e Raportit",
          imageAlt: "Krye e faqes me ndërruesin e email-it të raportit",
          items: {
            item1: "Përdorni ndërruesin në krye të faqes për të aktivizuar ose çaktivizuar email-et e njoftimit të raportit për këtë sondazh.",
            item2: "Kur është çaktivizuar, email-et nuk dërgohen por përmbajtja e shabllonit tuaj personal ruhet.",
            item3: "Ndërrimi kërkon leje <b>M14 edit</b>.",
          },
        },
        editingContent: {
          title: "Redaktimi i Përmbajtjes së Email-it",
          intro: "Klikoni direkt në panelin e pamjes paraprake për të redaktuar tekstin, ose përdorni fushën e subjektit në panelin e djathtë:",
          items: {
            subject: "Subjekti i email-it — vendoset në krye të panelit të stilit.",
            heading: "Titulli — titulli kryesor i email-it.",
            description: "Përshkrimi — teksti i trupit nën titullin.",
            button: "Teksti i butonit — etiketa e CTA-së së shkarkimit të raportit.",
            securityNote: "Shënimi i sigurisë — paralajmërim për sigurinë e lidhjes.",
            assistanceNote: "Shënimi i asistencës — tekst ndihmës për marrësit.",
            signoff: "Përfundimi — rreshti mbyllës (mbështet {{organizationName}}).",
            footer: "Fundi — teksti i poshtëm (mbështet {{supportEmail}}).",
          },
        },
        styling: {
          title: "Stilizimi i Shabllonit",
          imageAlt: "Paneli i akordeonit të stilit të shabllonit të email-it",
          intro: "Zgjeroni seksionet e akordeonit në panelin e djathtë për të personalizuar pamjen:",
          sections: {
            general: "Të përgjithshme — sfondi i email-it, sfondi i kartës, familja e fontit, mbushja e kartës.",
            heading: "Titulli — ngjyra dhe madhësia e fontit.",
            description: "Përshkrimi — ngjyra dhe madhësia e fontit.",
            button: "Butoni — sfondi, ngjyra e tekstit, sfondi i seksionit, rrezja, lartësia, gjerësia dhe madhësia e fontit.",
            notes: "Shënimet dhe asistenca — ngjyrat dhe madhësitë për shënimet e sigurisë dhe asistencës.",
            signoff: "Përfundimi — ngjyra dhe madhësia e fontit.",
            footer: "Fundi — ngjyra, madhësia e fontit dhe ngjyra e kufirit.",
          },
        },
        placeholders: {
          title: "Mbajtësit e Vendeve",
          imageAlt: "Akordioni i mbajtësve të vendeve me tokena të kopjueshëm",
          intro: "Shenjat dinamike opsionale mund të futen në fushat e tekstit. Hapni akordeonin Mbajtësit e Vendeve dhe klikoni një pilulë për ta kopjuar, pastaj ngjiteni në çdo fushë të pamjes paraprake:",
          items: {
            orgName: "<b>{{organizationName}}</b> — zëvendësohet me emrin e organizatës së respondentit.",
            supportEmail: "<b>{{supportEmail}}</b> — zëvendësohet me email-in tuaj të mbështetjes.",
            reportUrl: "<b>{{reportUrl}}</b> — përdoret automatikisht për lidhjen e butonit të raportit në kohën e dërgimit; nuk ka nevojë të ngjitet manualisht.",
          },
        },
        saveReset: {
          title: "Ruaj dhe Rivendos",
          imageAlt: "Butonat Ruaj shabllonin dhe Rivendos në parazgjedhje",
          items: {
            save: "Klikoni <b>Ruaj shabllonin</b> për të ruajtur subjektin dhe të gjitha cilësimet e stilit/përmbajtjes. Një toast suksesi konfirmon ruajtjen.",
            reset: "Klikoni <b>Rivendos në parazgjedhje</b> për të hequr shabllonin tuaj personal dhe për të rikthyer parazgjedhjet e integruara Vodafone. Shfaqet një modal konfirmimi — ky veprim fshin përgjithmonë shabllonin e ruajtur.",
          },
        },
        permissionsNote: "Përdoruesit me M14 view por pa M14 edit mund të hapin faqen dhe të shqyrtojnë shabllonin, por nuk mund të redaktojnë tekst, të ndryshojnë stilet, të ndërrojnë email-et, të ruajnë ose të rivendosin.",
      },

      // Cookie Policy Page
      cookiePolicy: {
        title: "Politika e Cookies",
        intro: "Faqja Politika e Cookies u lejon administratorëve të menaxhojnë përmbajtjen e politikës së cookies për sondazh, të kontrollojnë nëse një lidhje shfaqet në fundin e sondazhit dhe të konfigurojnë etiketat e butonave prano/refuzo të bannerit.",
        accessing: {
          title: "Qasja në Faqen e Politikës së Cookies",
          imageAlt: "Shiriti anësor me lidhjen Politika e cookies",
          steps: {
            step1: "Hapni një sondazh nga <b>Lista e Sondazheve</b> që një <b>survey_id</b> të jetë aktiv.",
            step2: "Në shiritin anësor të majtë, klikoni <b>Politika e cookies</b> (kërkon leje <b>M20 view</b>).",
            step3: "Faqja hapet në <b>/admin/survey/cookie-policy?survey_id={id}</b>.",
          },
        },
        overview: {
          title: "Përmbledhje",
          imageAlt: "Faqja e Politikës së Cookies me redaktor dhe cilësimet e bannerit",
          desc: "Kjo faqe menaxhon përmbajtjen e politikës së cookies për një sondazh specifik. Respondentët mund ta shikojnë politikën nga fundi i sondazhit kur është aktivizuar.",
          orgNote: "Shënim: Kjo është e ndarë nga politika e cookies në nivel organizate e gjetur te Cilësimet → Detajet e Organizatës → Politika e Cookies.",
        },
        footerToggle: {
          title: "Ndërruesi i Dukshmërisë në Fund",
          imageAlt: "Ndërruesi shfaq politikat e cookies në fund të faqes",
          items: {
            item1: "Përdorni ndërruesin \"Shfaq politikën e cookies në fundin e sondazhit\" për të aktivizuar ose çaktivizuar lidhjen e fundit.",
            item2: "Ndryshimet në këtë ndërrues ruhen <b>menjëherë</b> — nuk keni nevojë të klikoni Ruaj.",
            item3: "Etiketa tregon Aktivizuar ose Çaktivizuar për të pasqyruar gjendjen aktuale.",
          },
        },
        editor: {
          title: "Redaktori i Përmbajtjes së Politikës",
          imageAlt: "Redaktori rich-text i politikës së cookies",
          intro: "Përdorni redaktorin rich text për të krijuar trupin HTML të politikës së cookies:",
          formattingTitle: "Opsionet e formatimit:",
          formattingItems: {
            item1: "Të trasha, kursive dhe nënvizuar.",
            item2: "Tituj (H1, H2, H3), lista me pika dhe lista të numëruara.",
            item3: "Hiperlidhje — klikoni butonin e lidhjes dhe futni një URL kur kërkohet.",
            item4: "Ngjyra e tekstit — zgjidhni një ngjyrë ose rivendosni në parazgjedhje.",
          },
          tablesTitle: "Tabela:",
          tableItems: {
            item1: "Futni një tabelë 3-kolonëshe, 6-rreshtore me rresht koke duke përdorur butonin e tabelës.",
            item2: "Redaktoni përmbajtjen e qelizave direkt në redaktor.",
          },
        },
        bannerSettings: {
          title: "Cilësimet e Butonave të Bannerit të Cookies",
          imageAlt: "Ndërruesit dhe etiketat e butonave prano dhe refuzo",
          items: {
            accept: "Butoni Prano — ndërroni për të shfaqur ose fshehur butonin prano në bannerin e cookies të respondentit.",
            reject: "Butoni Refuzo — ndërroni për të shfaqur ose fshehur butonin refuzo.",
            customLabels: "Teksti i butonit — opsionalisht futni etiketa të personalizuara për secilin buton. Lëreni bosh për të përdorur tekstin e përkthyer parazgjedhur të shfaqur respondentëve.",
          },
        },
        saving: {
          title: "Ruajtja e Ndryshimeve",
          imageAlt: "Butoni Ruaj në faqen e Politikës së Cookies",
          items: {
            item1: "Klikoni Ruaj për të ruajtur përmbajtjen e redaktorit dhe cilësimet e butonave të bannerit.",
            item2: "Ndërruesi i dukshmërisë së fundit ruhet veçmas dhe menjëherë kur ndërrohet.",
          },
        },
        permissionsNote: "Përdoruesit me M20 view por pa M20 edit mund ta shikojnë faqen por nuk mund të redaktojnë përmbajtje, të ndërrojnë cilësime ose të ruajnë.",
      },

      // Lead Erasure Rules Page
      leadErasure: {
        title: "Rregullat e Fshirjes së Drejtimeve",
        intro: "Rregullat e Fshirjes së Drejtimeve u lejojnë administratorëve të përcaktojnë kushte të automatizuara për të arkivuar ose fshirë drejtimet bazuar në vlerat e fushave, zbulimin e dublikatave ose parametrat UTM.",
        accessing: {
          title: "Qasja në Faqen e Rregullave të Fshirjes së Drejtimeve",
          imageAlt: "Shiriti anësor me lidhjen Rregullat e fshirjes së drejtimeve",
          steps: {
            step1: "Hapni një sondazh nga <b>Lista e Sondazheve</b> që një <b>survey_id</b> të jetë aktiv.",
            step2: "Në shiritin anësor të majtë, klikoni <b>Rregullat e fshirjes së drejtimeve</b> (kërkon leje <b>M23 view</b>).",
            step3: "Faqja hapet në <b>/admin/survey/lead-erasure-rules?survey_id={id}</b>.",
          },
        },
        overview: {
          title: "Përmbledhje",
          imageAlt: "Faqja e Rregullave të Fshirjes së Drejtimeve me tabelë",
          desc: "Rregullat vlerësohen kundër drejtimeve hyrëse. Kur një rregull përputhet, sistemi arkivon ose fshin drejtimin sipas konfigurimit të rregullit. Të gjitha rregullat për një sondazh listohen në një tabelë të paginuar.",
        },
        rulesTable: {
          title: "Tabela e Rregullave",
          imageAlt: "Tabela e rregullave me kolonat lloji, përshkrimi dhe statusi",
          items: {
            columns: "Kolonat: Lloji, Përshkrimi, Statusi (Aktiv/Joaktiv) dhe Veprimet (Redakto, Fshi).",
            pagination: "Tabela shfaq 10 rregulla për faqe me kontrolle paginimi.",
            empty: "Nëse nuk ka rregulla, shfaqet mesazhi \"Nuk ka rregulla fshirjeje drejtimesh të konfiguruara.\"",
          },
        },
        ruleTypes: {
          title: "Llojet e Rregullave",
          fieldMatch: {
            title: "Përputhje Fushë",
            desc: "Përputhni një fushë të formularit të drejtimit duke përdorur një operator dhe vlerë (ose model regex). Vetëm fushat e përshtatshme lead-gen janë të disponueshme.",
          },
          duplicate: {
            title: "Dublikatë",
            desc: "Zbuloni dërgesa të dyfishta sipas një fushe të zgjedhur. Zgjidhni nëse mbani dërgesën më të re ose më të vjetër dhe arkivoni tjetrën.",
          },
          utmMatch: {
            title: "Përputhje UTM",
            desc: "Përputhni parametrat UTM (utm_source, utm_medium ose utm_campaign) duke përdorur një operator dhe vlerë.",
          },
        },
        operators: {
          title: "Operatorët",
          fieldMatch: "Përputhja e fushës mbështet: Barazohet me, Nuk barazohet me, Përmban, Fillon me, Mbaron me, Është bosh, Përputhet me regex, Gjatësia barazohet me dhe Gjatësia nuk barazohet me.",
          utmMatch: "Përputhja UTM mbështet: Barazohet me, Nuk barazohet me, Përmban, Fillon me, Mbaron me, Është bosh dhe Përputhet me regex.",
        },
        regexPresets: {
          title: "Paracaktimet Regex",
          items: {
            letters: "Vetëm shkronja (A–Z, a–z).",
            digits: "Vetëm shifra (0–9).",
            exactly9: "Saktësisht 9 karaktere.",
            nineSame: "Të 9 karakteret identike (p.sh. 111111111).",
            eightPlus: "8 ose më shumë shifra identike radhazi.",
            custom: "E personalizuar (e avancuar) — futni një shprehje të rregullt të vlefshme në stilin JavaScript.",
          },
        },
        addEdit: {
          title: "Shtimi dhe Redaktimi i Rregullave",
          imageAlt: "Modali Shto rregull me fushat e konfigurimit",
          items: {
            step1: "Klikoni <b>Shto rregull</b> për të hapur modalin (kërkon <b>M23 add</b>).",
            step2: "Zgjidhni një lloj rregulli dhe konfiguroni fushat e kërkuara. Lloji i rregullit nuk mund të ndryshohet pas krijimit.",
            step3: "Vendosni statusin në Aktiv ose Joaktiv.",
            step4: "Klikoni <b>Shto</b> ose <b>Përditëso</b> për të ruajtur. Shfaqet një toast suksesi dhe tabela rifreskohet.",
          },
        },
        toggleStatus: {
          title: "Ndërrimi i Statusit të Rregullit",
          desc: "Klikoni badge-in Aktiv ose Joaktiv në kolonën Status për të ndërruar një rregull pa hapur modalin e redaktimit. Kërkon leje M23 edit.",
        },
        deleteRule: {
          title: "Fshirja e një Rregulli",
          desc: "Klikoni ikonën e fshirjes në rreshtin e rregullit. Shfaqet një modal konfirmimi me përshkrimin e rregullit. Konfirmoni për ta hequr përgjithmonë rregullin. Kërkon leje M23 del.",
        },
        permissionsNote: "M23 add kërkohet për të krijuar rregulla, M23 edit për të redaktuar ose ndërruar statusin, dhe M23 del për të fshirë rregullat.",
      },

      // Analytics Page
      analytics: {
        title: "Analitika",
        intro: "Paneli i Analitikës në platformën e sondazheve Vodafone Business ofron njohuri mbi përgjigjet e sondazhit dhe nivelet e pjekurisë.",
        accessingPage: {
          title: "Qasja në Faqen e Analitikës",
          navigate: "Navigoni te Analitika nga menyja e navigimit",
          imageAlt: "Navigimi i analitikës",
        },
        surveyDataAnalysis: {
          title: "Analiza e të Dhënave të Sondazhit",
          viewDataIntro: "Për të parë paraqitjen e të dhënave në formë grafikësh, ndiqni këta hapa:",
          steps: {
            selectSurvey: "Zgjidhni sondazhin e dëshiruar nga menyja zbritëse e etiketuar \"Zgjidhni sondazhin\". Lista e sondazheve do të shfaqet në menynë zbritëse.",
            chooseDateRange: "Zgjidhni intervalin e datave për të cilin dëshironi të shihni paraqitjen e të dhënave.",
            graphsDisplay: "Grafikët do të shfaqin të dhënat e numrit ose përqindjes bazuar në sondazhin dhe intervalin e datave të zgjedhura.",
            topLeads: "Grafiku gjithashtu paraqet \"Top 10 Leads\" të një sondazhi të veçantë.",
            viewAll: "Ka gjithashtu një opsion \"Shiko të Gjitha\" që e çon administratorin në një faqe ku të gjithë leads, së bashku me informacionin e tyre të lidhur, paraqiten në formë tabele.",
          },
          note: "Ju lutemi vini re se grafikët ofrojnë një analizë vizuale të të dhënave të sondazhit.",
          graphTypes: "Ekzistojnë katër lloje grafikësh dhe një listë të përdorura në këtë raport analize:",
          graphTypesList: {
            completion: "Analiza e Përfundimit",
            maturity: "Analiza e Pjekurisë",
            questionWise: "Analiza sipas Pyetjes",
            exit: "Analiza e Daljes",
            topLeads: "Top 10 Leads",
          },
        },
        dataAnalysisGraphs: {
          title: "Grafikët e Analizës së të Dhënave",
          completionAnalysis: {
            title: "Analiza e Përfundimit",
            desc1: "Ky grafik tregon numrin e njerëzve që kanë përfunduar sondazhin krahasuar me numrin total të pjesëmarrësve.",
            desc2: "Për shembull, në këtë grafik, \"përgjigjet totale të sondazhit\" i referohet numrit të përpjekjeve për përgjigje.",
          },
          maturityAnalysis: {
            title: "Analiza e Pjekurisë",
            desc1: "Ky grafik tregon përqindjen e Fazës së Pjekurisë të arritur për një sondazh specifik.",
            desc2: "Grafiku paraqet fazat në përqindje. Përqindja totale është gjithmonë 100%.",
          },
          questionWiseAnalysis: {
            title: "Analiza sipas Pyetjes",
            desc1: "Ky grafik paraqet shpërndarjen e përgjigjeve për çdo pyetje në sondazh.",
            desc2: "Analiza sipas pyetjes i referohet ekzaminimit dhe vlerësimit të përgjigjeve të sondazhit për çdo pyetje individuale.",
          },
          exitAnalysis: {
            title: "Analiza e Daljes",
            desc: "Analiza e daljes tregon në cilën pyetje përdoruesi fundor la sondazhin duke plotësuar formularin e gjenerimit të leads.",
          },
        },
        dataLeadsTable: {
          title: "Tabela e të Dhënave të Leads",
          topLeadsTitle: "Top 10 Leads",
          topLeadsDesc: "Tabela më poshtë shfaq informacionin e 10 përdoruesve më të fundit me pikët më të larta.",
          viewAllClick: "Klikoni \"Shiko të Gjitha\"",
          fields: {
            date: "Data e sondazhit tregohet kur formulari i gjenerimit të leads plotësohet nga përdoruesi fundor.",
            reportLink: "Lidhja e Raportit tregon lidhjen e raportit të gjeneruar duke plotësuar sondazhin.",
            firstName: "Fusha Emri shfaq emrin e përdoruesit fundor që iu përgjigj sondazhit.",
            lastName: "Fusha Mbiemri shfaq mbiemrin e përdoruesit.",
            email: "Fusha Adresa e Email-it shfaq email-in e lead-it.",
            phone: "Numri i Telefonit tregon numrin e telefonit të lead-it.",
            organization: "Emri i Organizatës tregon organizatën e lead-it.",
            industry: "Industria paraqet industrinë së cilës i përket lead-i.",
            responses: "Fusha Nr. i Përgjigjeve tregon numrin maksimal të pyetjeve të vizituara nga lead-i.",
            maturityLevel: "Niveli i pjekurisë tregon statusin e rekomanduar bazuar në pikëzimin e përgjigjes.",
            totalScore: "Pikëzimi total tregon pikëzimin total të përgjigjes.",
            completed: "Fusha e përfunduar tregon nëse sondazhi u përfundua ose jo.",
            uniqueId: "Fusha ID Unik paraqet një identifikues dallues të caktuar për çdo lead.",
            utmSource: "Fusha UTM Source kap informacionin e burimit të trafikut.",
            utmMedium: "Fusha UTM Medium specifikon mediumin e fushatës.",
            utmCampaign: "Fusha UTM Campaign regjistron emrin e fushatës.",
            termsConditions: "Fusha T & C tregon nëse lead-i ka pranuar kushtet dhe termat.",
            action: "Kolona Veprim ofron veprime të shpejta për çdo regjistrim.",
          },
          exportButton: "Butoni Eksporto në Excel",
          exportDesc: "Kur klikohet butoni eksporto, të dhënat e tabelës do të eksportohen në excel.",
          deleteSelectedTitle: "Butoni Fshi Leads e Zgjedhura",
          deleteSelectedSteps: {
            step1: "Zgjidhni një ose më shumë leads nga tabela duke klikuar kutinë e kontrollit pranë çdo regjistrimi.",
            step2: "Klikoni butonin Fshi leads e zgjedhura mbi tabelë.",
            step3: "Një popup konfirmimi do të shfaqet duke kërkuar miratim para fshirjes.",
            step4: "Pas konfirmimit, leads e zgjedhura do të hiqen përgjithmonë.",
          },
        },
      },

      // Activity Logs Page  
      activityLogs: {
        title: "Regjistri i Aktiviteteve",
        intro: "Regjistri i Aktiviteteve ofron një gjurmë auditimi vetëm për lexim të veprimeve të kryera nga përdoruesit dhe sistemi (p.sh., përditësime webhook, ndryshime statusi të sondazhit, editime të fazave). Çdo hyrje tregon mesazhin, llojin e veprimit, kush e ka kryer dhe datën dhe orën e saktë.",
        table: {
          title: "Tabela e regjistrit të aktiviteteve",
          intro: "Tabela liston aktivitetet e fundit me kolonat e mëposhtme:",
          columns: {
            message: "Mesazhi - Përshkrim i lexueshëm i ngjarjes (p.sh., Webhook eloqua për RCA Portuguese u përditësua nga shonbit).",
            action: "Veprimi - Etiketë kategorike e veprimit (p.sh., Përditëso webhooks, Përditëso statusin e eloqua, Shto webhook, Përditëso statusin e sondazhit, Shto formular Eloqua, Fshi fazën, Përditëso fazën, Shto fazën, Përditëso faqen e raportit, Përditëso emrin e raportit).",
            addedBy: "Shtuar nga - Përdoruesi që kreu veprimin (p.sh., shonbit) ose Sistemi për ngjarje automatike.",
            dateTime: "Data dhe ora - Koha e ngjarjes në DD-MM-YYYY | hh:mm A.",
          },
          note: "Rreshtat janë informativë; nuk ka kolonë veprimi të veçantë në këtë faqe. Përdorni faqosjen për të shfletuar historikun e plotë.",
          imageAlt: "Pamja e tabelës së regjistrit të aktiviteteve",
        },
        pagination: {
          title: "Faqosja dhe kontrollet e shfaqjes",
          rowsPerPage: "Rreshta për faqe - Zgjidhni sa regjistra të shfaqen për faqe (p.sh., 30).",
          rangeTotal: "Intervali dhe totali - Tregon pjesën aktuale dhe numrin total (p.sh., 1-30 nga 51).",
          navigation: "Navigimi - Përdorni kontrollin e parë/paraprak/tjetër/fund për të lëvizur midis faqeve.",
        },
      },

      tickets: {
        title: "Biletat",
        intro: "Biletat gjurmojnë alarmet e sigurisë dhe problemet manuale në platformë. Biletat e gjeneruara nga sistemi krijohen automatikisht për aktivitet të dyshimtë; përdoruesit me leje mund të krijojnë bileta manualisht, t’i caktojnë, përditësojnë statusin dhe shtojnë komente.",
        accessing: {
          title: "Hyrja në Faqen e Biletave",
          imageAlt: "Shiriti anësor kryesor me lidhjen Biletat",
          steps: {
            step1: "Hyni në portalin e administrimit.",
            step2: "Në shiritin anësor majtas, klikoni <b>Biletat</b> (kërkon lejen <b>M7 view_own</b>).",
            step3: "Faqja hapet në <b>/admin/tickets</b>.",
          },
        },
        listOverview: {
          title: "Lista e Biletave",
          imageAlt: "Lista e biletave me karta përmbledhëse, filtra dhe tabelë",
          intro: "Lista tregon të gjitha biletat që lejohen të shihni. Përdoruesit me view_all shohin edhe kartat e përmbledhjes sipas statusit në krye.",
          dashboardTitle: "Përmbledhja e panelit (vetëm view_all)",
          dashboardItems: {
            open: "Hapur — bileta në pritje të veprimit.",
            acknowledged: "Pranuar — bileta të parë por ende pa trajtuar.",
            inProgress: "Në progres — bileta që trajtohen aktualisht.",
            resolved: "Zgjidhur — bileta të mbyllura.",
            total: "Totali — numri i të gjitha biletave.",
          },
          filtersTitle: "Kërkim dhe filtra",
          filtersItems: {
            search: "Kërkim — filtron sipas titullit, të caktuarit ose kush e hapi biletën.",
            status: "Statusi — Hapur, Pranuar, Në progres, Zgjidhur ose Pozitiv i rremë.",
            severity: "Ashpërsia — Ulët, Mesatare, Lartë ose Kritike.",
            source: "Burimi — Sistemi (automatik) ose Përdoruesi (manual).",
            assignee: "I caktuari — filtron sipas përdoruesit ose Pa caktuar (vetëm view_all).",
          },
          tableTitle: "Kolonat e tabelës",
          tableItems: {
            title: "Titulli — klikoni titullin ose rreshtin për detaje.",
            type: "Lloji — kategoria e alarmit (p.sh. Forcë brutale, Manual).",
            severity: "Ashpërsia — etiketë me ngjyrë.",
            status: "Statusi — etiketë me ngjyrë.",
            source: "Burimi — sistem ose përdorues.",
            assignedTo: "Caktuar tek — përgjegjësi ose Pa caktuar.",
            created: "Krijuar — data dhe ora.",
            action: "Veprimi — butoni Shiko hap faqen e detajeve.",
          },
        },
        createTicket: {
          title: "Krijimi i një Bilete",
          imageAlt: "Modal krijimi bilete me titull, ashpërsi, përshkrim dhe caktim",
          items: {
            step1: "Klikoni <b>+ Krijo biletë</b> (kërkon <b>M7 view_own</b>).",
            step2: "Vendosni titullin dhe zgjidhni ashpërsinë (e detyrueshme). Opsionalisht shtoni përshkrim dhe caktoni përdorues.",
            step3: "Klikoni <b>Krijo biletë</b> për të ruajtur. Lista rifreskohet.",
            step4: "Biletat e sistemit krijohen automatikisht dhe nuk krijohen nga ky modal.",
          },
        },
        ticketDetail: {
          title: "Detajet e Biletës",
          imageAlt: "Faqja e detajeve me informacion, komente, status dhe caktim",
          items: {
            overview: "Paneli kryesor tregon titullin, përshkrimin, statusin, ashpërsinë, llojin e alarmit, burimin, IP, kush e hapi, të caktuarin dhe datat.",
            comments: "Seksioni i komenteve liston shënimet. Shtoni koment dhe klikoni Posto (kërkon M7 comment).",
            status: "Përdoruesit me M7 view_all mund të ndryshojnë statusin dhe të klikojnë Përditëso statusin.",
            assign: "Përdoruesit me M7 view_all mund të caktojnë biletën dhe të klikojnë Cakto.",
          },
        },
        alertTypes: {
          title: "Llojet e Alarmeve",
          items: {
            bruteForce: "Forcë brutale — përpjekje të përsëritura hyrjeje të dështuar.",
            unauthorizedToken: "Token i paautorizuar — përdorim token i pavlefshëm ose i skaduar.",
            unknownUser: "Përdorues i panjohur — hyrje nga llogari e panjohur.",
            inactiveUser: "Përdorues joaktiv — hyrje nga llogari e çaktivizuar.",
            suspiciousIp: "IP i dyshimtë — aktivitet nga IP e pazakontë.",
            afterHours: "Jashtë orarit — akses jashtë orarit të konfiguruar.",
            manual: "Manual — biletë e krijuar nga përdoruesi.",
          },
        },
        notifications: {
          title: "Cilësimet e Njoftimeve të Biletave",
          imageAlt: "Faqja e marrësve të njoftimeve për bileta",
          intro: "Administratorët mund të konfigurojnë kush merr email kur krijohen bileta të reja.",
          items: {
            step1: "Shkoni te <b>Cilësimet → Njoftimet e biletave</b> në <b>/admin/settings/ticket-notifications</b>.",
            step2: "Kërkoni përdorues dhe shtoni si marrës. Hiqni me kontrollin e fshirjes në çdo chip.",
            step3: "Klikoni <b>Ruaj</b> për të ruajtur listën.",
          },
        },
        permissionsNote: "M7 view_own lejon shikimin dhe krijimin e biletave. M7 view_all shton përmbledhjen, filtrin e caktimit, përditësimin e statusit dhe caktimin. M7 comment lejon komentet.",
      },

      // Dashboard/Survey List Page
      dashboard: {
        title: "Lista e Sondazheve",
        intro: "Lista e Sondazheve ofron një mënyrë të strukturuar për të menaxhuar, gjurmuar dhe redaktuar sondazhet. Përfshin opsione për të kërkuar, filtruar dhe krijuar sondazhe të reja duke shfaqur detaje thelbësore si statusin e përfundimit, gjuhën dhe detajet e publikimit.",
        
        navigating: {
          title: "Navigimi te Lista e Sondazheve",
          step1: "Hyni në llogarinë tuaj.",
          step2: "Në shiritin anësor të majtë, pas hyrjes së suksesshme përdoruesi mund të ridrejtohet te pamja e Listës së Sondazheve. Ose klikoni në Sondazh për të hyrë në faqen e Listës së Sondazheve.",
          step3: "Pamja e detajuar e sondazhit tregohet në imazh",
          imageAlt: "Navigimi i Listës së Sondazheve",
        },

        header: {
          title: "Seksioni i Kokës",
          userProfile: "Profili i Përdoruesit dhe Zgjedhësi i Gjuhës",
          userProfileItems: {
            languageDropdown: "Menyja e Gjuhës: Lejon përdoruesit të kalojnë mes gjuhëve të ndryshme.",
            userProfile: "Profili i Përdoruesit: Shfaq emrin dhe email-in e përdoruesit të lidhur.",
            dropdownMenu: "Menyja Zbritëse: Ka të ngjarë të përmbajë opsione për të dalë ose ndryshuar cilësimet e Profilit.",
          },
          breadcrumb: "Breadcrumb",
          breadcrumbDesc: "Në Listën e Sondazheve, emri i faqes aktualisht aktive shfaqet në anën e djathtë të paraqitjes. Kjo ndihmon përdoruesit të identifikojnë shpejt se në cilën seksion të platformës po punojnë.",
          createSurvey: "Butoni Krijo Sondazh",
          createSurveyItems: {
            location: "I vendosur në anën e djathtë si një buton i kuq i etiketuar + Krijo Sondazh.",
            purpose: "Lejon përdoruesit të krijojnë një sondazh të ri shpejt.",
          },
        },

        filters: {
          title: "Filtrat e Listës së Sondazheve",
          intro: "Ky seksion shfaq një listë sondazhesh me detaje, duke përfshirë statusin e përfundimit, gjuhën dhe detajet e modifikimit.",
          searchBar: "Shiriti i Kërkimit",
          searchBarDesc: "Përdoruesit mund të kërkojnë për një sondazh specifik duke futur një fjalë kyçe.",
          filtersTitle: "Filtrat",
          filtersDesc: "Lista e Sondazheve ofron opsione filtrimi për të ndihmuar përdoruesit të gjejnë dhe menaxhojnë sondazhet lehtësisht. Më poshtë janë detajet e Filtrit të Gjuhës dhe Filtrit të Statusit.",
          languageFilter: "Filtri i Gjuhës",
          languageFilterDesc: "Lejon përdoruesit të filtrojnë sondazhet bazuar në gjuhën në të cilën janë krijuar.",
          allLanguages: "Të gjitha - Tregon të gjitha sondazhet pavarësisht gjuhës.",
          statusFilter: "Filtri i Statusit",
          statusFilterDesc: "Ndihmon përdoruesit të filtrojnë sondazhet bazuar në statusin e publikimit.",
          statusOptions: {
            all: "Të Gjitha Sondazhet - Shfaq të gjitha sondazhet, pavarësisht statusit të tyre.",
            published: "Të Publikuara - Tregon vetëm sondazhet që janë aktive dhe të disponueshme për përgjigjësit.",
            draft: "Draft - Shfaq sondazhet që janë ende në modalitetin draft dhe nuk janë publikuar ende.",
            example: "Shembull Përdorimi: Nëse një përdorues zgjedh \"Draft\", vetëm sondazhet me statusin \"Draft\" do të tregohen, duke fshehur ato të publikuara.",
          },
        },

        tableColumns: {
          title: "Kolonat e Tabelës së Sondazheve",
          intro: "Lista e sondazheve shfaqet në format tabele me kolonat e mëposhtme:",
          surveyName: "Emri i Sondazhit - Shfaq titullin e sondazhit dhe numrin e seksioneve/pyetjeve.",
          completion: "Përfundimi / Përgjigjet - Tregon numrin e përgjigjeve të përfunduara kundrejt totalit të përgjigjeve.",
          language: "Gjuha - Tregon gjuhën e sondazhit.",
          status: "Statusi - Tregon nëse sondazhi është në statusin \"Draft\" ose \"I Publikuar\".",
          addedOn: "Shtuar më / Shtuar nga - Shfaq datën e krijimit të sondazhit dhe emrin e përdoruesit që e krijoi.",
          lastModified: "Modifikimi i Fundit - Tregon kur sondazhi u përditësua për herë të fundit.",
          publishedOn: "Publikuar më - Shfaq datën kur sondazhi u publikua.",
          actions: "Veprimet - Kjo kolonë përmban veprime të ndryshme që mund të kryhen në çdo sondazh. Shpjegimet e detajuara ofrohen më poshtë në seksionin 'Veprimet në listën e sondazheve'",
        },

        actions: {
          title: "Veprimet në listën e sondazheve",
          editSurvey: {
            label: "Veprimi 1 - Redakto Sondazhin:",
            desc: "Kur përdoruesi klikon në veprimin 'redakto sondazhin', do të ridrejtohet te ndërtuesi i P&P për redaktim. Në ndërtuesin e P&P, do të jetë në gjendje të menaxhojë kategoritë e sondazhit, duke përfshirë \"Seksionet\", \"Pyetjet dhe Përgjigjet\", \"Shto/Redakto variablat\", Menaxho \"Raportet\", Menaxho \"URL\".",
          },
          shareSurvey: {
            label: "Veprimi 2 - Shpërndaj Sondazhin:",
            desc: "'Shpërndaj sondazhin' çon te faqja 'publiko dhe shpërndaj' ku mund të personalizoni URL-në duke shtuar kombinime të ndryshme të variablave UTM për të gjurmuar burimin, mediumin dhe fushatën. URL-ja e sondazhit gjithashtu përfshin një opsion kopjo-ngjit për shpërndarje të lehtë.",
          },
          moveToPublish: {
            label: "Veprimi 3 - Kaloje në Publiko/Kaloje në Drafte:",
            desc: "Ky veprim lejon përdoruesin të publikojë një sondazh nëse është në Drafte ose të Kalojë një Sondazh në Drafte nëse tashmë është publikuar. Pasi sondazhi të publikohet, URL-ja e frontend-it të sondazhit bëhet e aksesueshme.",
          },
          viewLeads: {
            label: "Veprimi 4 - Shiko Leads:",
            desc: "Ky veprim ridrejtoni te faqja 'Të Gjithë Leads' në analitikë, duke treguar të gjitha përgjigjet e plotësuara nga përdoruesit fundorë për atë Sondazh të veçantë.",
          },
          duplicate: {
            label: "Veprimi 5 - Dupliko:",
            desc: "Ky funksion duplikon të gjitha të dhënat, variablat, raportet dhe të gjitha të dhënat e tjera nga sondazhi i zgjedhur nën një emër të ri sondazhi. Leads, Analitika, URL, megjithatë nuk kopjohen.",
          },
          delete: {
            label: "Veprimi 6 - Fshi:",
            desc: "Funksioni \"Fshi\" ndihmon në fshirjen e një Sondazhi. Çdo detaj i një sondazhi fshihet nga kudo duke përfshirë Leads, URL, P&P, Variablat.",
          },
        },
      },

      // Q&A Builder Page
      qandaBuilder: {
        title: "Ndërtuesi i P&P",
        intro: "Kjo ndërfaqe ofron një qasje të strukturuar dhe të lehtë për përdoruesin për krijimin dhe menaxhimin e sondazheve.",
        introItems: {
          organize: "Përdoruesit mund të organizojnë pyetjet në seksione",
          questionTypes: "Zgjedhin lloje të ndryshme pyetjesh (tekst, zgjedhje të shumta, etj.)",
          editManage: "Modifikojnë, fshijnë ose riorganizojnë pyetjet",
          navigate: "Navigojnë në mënyrë efikase nëpër mjetet e ndërtimit të sondazhit",
        },
        access: {
          title: "Qasja në Ndërtuesin e P&P",
          step1: "Navigoni te Sondazhet dhe modifikoni çdo sondazh duke klikuar butonin Veprime nga tabela e listimit.",
          step2: "Klikoni emrin e theksuar nga menuja zbritëse për të hapur faqen.",
          imageAlt: "Qasja në Ndërtuesin e P&P",
        },
        surveyInfo: {
          title: "Informacioni i Sondazhit (Zona Kryesore e Përmbajtjes)",
          surveyTitleLabel: "Titulli i Sondazhit:",
          surveyTitleValue: "\"IE Test\"",
          surveyDescriptionLabel: "Përshkrimi i Sondazhit:",
          surveyDescriptionValue: "Tekst vendmbajtës që tregon se përdoruesit mund të shtojnë një përshkrim të shkurtër të sondazhit.",
          duplicateNote: "Mund të dyfishoni pyetësorin aktual të sondazhit duke klikuar ikonën e dyfishimit",
          statsLabel: "Statistikat e Sondazhit:",
          statsValue: "Tregon se sondazhi përbëhet nga 7 seksione dhe 35 pyetje.",
          exampleLabel: "Për shembull",
          exampleDesc: "një sondazh \"IE Test\" mund të ketë 7 seksione dhe secili të përmbajë pyetje që lidhen me atë aspekt të veçantë të përvojës së klientit. Numri total i pyetjeve është 35",
          imageAlt: "Informacioni i Sondazhit",
        },
        sectionsQuestions: {
          title: "Seksionet dhe Pyetjet e Sondazhit",
          sectionTitle: "Seksioni:",
          sectionDesc: "Seksionet në një sondazh janë ndarje ose kategori që ndihmojnë në organizimin dhe strukturimin e pyetjeve. Ato përdoren për të grupuar pyetjet e lidhura. Kjo rrit qartësinë e sondazhit dhe ndihmon në analizën e të dhënave.",
          sectionItems: {
            focus: "Ky seksion fokusohet në mënyrën se si bizneset i qasen adoptimit të teknologjisë cloud.",
            describes: "Përshkruan faktorë si strategjia e biznesit, objektivat, gatishmëria për adoptim cloud dhe gatishmëria për investim.",
            questionsCount: "Në këtë shembull përmban 5 pyetje të lidhura me temën.",
            iconsNote: "Çdo seksion ka ikona pranë tij për modifikim, fshirje dhe dyfishim",
            exampleQuestionsTitle: "Shembuj pyetjesh:",
            exampleQuestion1: "\"Sa i rëndësishëm është adoptimi i teknologjisë cloud për rritjen dhe suksesin e biznesit tuaj?\" Lloji i përgjigjes: tekst",
            exampleQuestion2: "\"Çfarë niveli buxheti ka ndarë biznesi juaj për adoptimin e cloud?\" Lloji i përgjigjes: zgjedhje të shumta",
            questionIcons: "Çdo pyetje ka ikona pranë saj për modifikim, fshirje, dyfishim ose riorganizim.",
          },
          imageAlt: "Seksionet dhe Pyetjet e Sondazhit",
        },
        addButtons: {
          title: "Butonat Shto Pyetje dhe Shto Seksion",
          addQuestionTitle: "Shto Pyetje",
          addQuestionDesc: "Ky buton përdoret për të shtuar një pyetje të re në sondazh. Duke klikuar, hapet një formular ku përdoruesi mund të zgjedhë llojin e pyetjes (p.sh., zgjedhje të shumta, tekst, etj.) dhe të konfigurojë detajet. \"Shto Pyetje\" shton një pyetje brenda një seksioni ekzistues.",
          addSectionTitle: "Shto Seksion",
          addSectionDesc: "Ky buton përdoret për të shtuar një seksion të ri në sondazh. Seksionet ndihmojnë në organizimin e pyetjeve në kategori ose tema të ndryshme. \"Shto Seksion\" krijon një seksion të ri për të grupuar pyetjet e lidhura.",
          imageAlt: "Shto Pyetje dhe Seksion",
        },
        questionTypes: {
          title: "Llojet e Pyetjeve",
          intro: "Në dizajnin e sondazheve, lloje të ndryshme pyetjesh mund të përdoren për të mbledhur lloje të ndryshme informacioni. Disa lloje të zakonshme përfshijnë",
          multipleChoiceTitle: "Pyetje me Zgjedhje të Shumta:",
          multipleChoiceDesc: "Këto pyetje ofrojnë një listë opsionesh dhe të anketuarit duhet të zgjedhin një ose më shumë prej tyre. Ato përdoren kur dëshironi të ofroni opsione përgjigjeje të paracaktuara dhe janë efikase për mbledhjen e të dhënave të strukturuara dhe sasiore. Për shembull, \"Sa i rëndësishëm është adoptimi i teknologjisë cloud për biznesin tuaj? (Zgjidhni të gjitha që vlejnë).\"",
          textTitle: "Pyetje me Tekst:",
          textDesc: "Këto pyetje kërkojnë që të anketuarit të japin përgjigje në formë teksti. Ato përdoren kur dëshironi përgjigje të hapura dhe t'u lejoni pjesëmarrësve të shprehin mendimet e tyre. Për shembull, \"Përshkruani përvojën tuaj me produktin tonë me fjalët tuaja.\"",
          opinionTitle: "Pyetje të Shkallës së Opinionit",
          opinionDesc: "Këto pyetje u lejojnë të anketuarve të vlerësojnë opinionin ose kënaqësinë në një shkallë, zakonisht nga e mirë në e keqe ose nga pajtohem plotësisht në nuk pajtohem. Ato përdoren për të mbledhur të dhëna sasiore dhe për të matur qëndrime ose perceptime. Për shembull, \"Në një shkallë nga 1 deri në 5, si do ta vlerësonit kënaqësinë tuaj të përgjithshme me shërbimin ndaj klientit?\"",
          imageAlt: "Llojet e Pyetjeve",
        },
      },

      // Form Builder Page
      formBuilder: {
        title: "Ndërtuesi i Formularëve",
        intro: "Ndërtuesi i Formularëve është një ndërfaqe intuitive që u lejon përdoruesve të krijojnë dhe personalizojnë formularë për mbledhjen e të dhënave, vlerësime dhe sondazhe. Mjeti ofron një mënyrë të lehtë për të konfiguruar fushat e formularit, për të vendosur hyrje të detyrueshme dhe për të parë paraprakisht formularin përpara publikimit.",
        access: {
          title: "Qasja në ndërtuesin e formularëve",
          step1: "Navigoni te Sondazhet dhe modifikoni çdo sondazh duke klikuar butonin Veprime nga tabela e listimit.",
          step2: "Klikoni emrin e theksuar nga menuja zbritëse për të hapur faqen.",
          imageAlt: "Qasja në Ndërtuesin e Formularëve",
        },
        leadForm: {
          title: "Faqja e Formularit të Leads",
          breadcrumbDesc: "Personalizoni faqen e formularit të leads ku përdoruesit plotësojnë sondazhin. Gjithashtu shfaqni emrin e sondazhit të zgjedhur në breadcrumb të faqes së Ndërtuesit të Formularëve.",
          headingDesc: "Shkruani titullin dhe përshkrimin për të dhënë një kuptim më të mirë të formularit.",
          addFieldDesc: "Klikoni ikonën \"Shto fushë\" në fund të formularit. \"Shto Fushë\" do të shtojë një fushë të re duke ofruar opsionin për të zgjedhur \"Tipin\" sipas nevojës. Tipet mund të jenë – \"Fushë Teksti\", \"Zonë teksti\", \"Email\", \"Meny rënëse\", \"Numri i telefonit\", \"Kutitë e zgjedhjes\", \"Butonat radio\", \"I fshehur\". Shpjegimi i detajuar i fushave me shembuj të përgjithshëm gjendet te faqja e Llojeve të fushave të hyrjes.",
          addedFieldDesc: "Fusha e shtuar shfaqet si fusha e fundit me \"Tipin\" e zgjedhur. Fusha e hyrjes shfaq \"Etiketën\" dhe \"Placeholder\" së bashku me ikonat \"Modifiko\", \"Fshij\", çelësin \"E kërkuar\" dhe ikonën e rirenditjes. Kjo mundëson personalizimin e hyrjeve dhe etiketave në formular.",
          imageAlt: "Faqja e Formularit të Leads",
        },
        userGuide: {
          title: "Udhëzuesi i Përdoruesit: Ndërtuesi i Formularëve",
          step1Title: "Hapi 1: Konfigurimi i Fushës",
          editIconDesc: "Ikona e modifikimit - përdoret për të modifikuar detajet e fushës. Me klikim, shfaqet një popup ku \"Emri\", \"Etiketa\", \"placeholder\" dhe opsionet për \"Butonat radio\" dhe \"Kutitë e zgjedhjes\" mund të modifikohen. Të gjitha fushat janë të detyrueshme.",
          noteDesc: "SHËNIM - Rekomandohet të mos modifikohet emri i fushës. Është një emër unik i shtuar si \"variabël e lead gen\". Nëse emri i fushës modifikohet, duhet të jepet një emër unik për secilën fushë, pasi do të jetë e vështirë të gjurmohet regjistri i lidhur me atë emër.",
          step2Title: "Hapi 2: Fshij fushën",
          step2Desc: "Përdoruesit kanë mundësinë të fshijnë çdo fushë në çdo kohë. Popup-i i konfirmimit të fshirjes do të shfaqet në ekran.",
          step4Title: "Hapi 4: Shto fusha të reja",
          step4Desc: "\"Shto fushë\" mund të klikohet për të shtuar një fushë të re dhe tipi i saj mund të zgjidhet nga menuja zbritëse.",
          step5Title: "Hapi 5: Pamja paraprake e formularit",
          step5Desc: "Kjo pamje ju jep një parapamje të formularit të lead-it, duke ju lejuar të shihni si do të duket për përdoruesit. Rishikoni paraqitjen dhe dizajnin e formularit.",
          step6Title: "Hapi 6: Personalizimi i butonit CTA",
          step6Desc: "Mund të personalizoni edhe rreshtimin e butonit të dërgimit. Si parazgjedhje, teksti është \"submit\" dhe i rreshtuar majtas. Ruani ndryshimet e nevojshme duke klikuar butonin ruaj.",
          imageAlt: "Udhëzuesi i Ndërtuesit të Formularëve",
        },
      },

      // Report Builder Page
      reportBuilder: {
        title: "Ndërtuesi i Raporteve",
        intro: "Ndërtuesi i Raporteve ndihmon në krijimin e një raporti gjithëpërfshirës për të paraqitur informacione, gjetje ose analiza të një sondazhi të veçantë. Zakonisht përfshin të dhëna, fakte dhe njohuri të mbledhura përmes hulumtimit. Raporti mund të personalizohet për të përcjellë informacion në mënyrë të qartë dhe të organizuar, shpesh për të informuar vendimmarrjen ose për të dhënë rekomandime. Raporti mund të shkarkohet në format PDF. Rezultatet në formë grafikësh gjithashtu mund të shfaqen. Rekomandime të pyetësorit për përdoruesit gjithashtu mund të jepen.",
        featuresTitle: "Karakteristikat e një raporti -",
        features: {
          edit: "Modifiko - Ky funksion ju lejon të bëni ndryshime në një raport.",
          duplicate: "Dupliko - Me këtë funksion, mund të krijoni një kopje të raportit, duke përfshirë të gjitha faqet, në të njëjtin rend, por me një emër tjetër.",
          default: "Bëje Parazgjedhje - Ky funksion ju lejon të zgjidhni një raport specifik si parazgjedhje për një sondazh të caktuar.",
          delete: "Fshi - Ky funksion ju lejon të hiqni një raport nga sistemi",
        },
        builder: {
          title: "Ndërtuesi i Raporteve",
          step1: "Navigoni te Sondazhet dhe modifikoni çdo sondazh duke klikuar butonin Veprime nga tabela e listimit.",
          step2: "Klikoni emrin e theksuar nga menuja zbritëse për të hapur faqen.",
          imageAlt: "Qasja në Ndërtuesin e Raporteve",
        },
        editorView: {
          title: "Pamja e Editorit",
          intro: "Ky mjet ndihmon në ndërtimin e raportit tuaj të personalizuar duke përdorur pronat e ofruara. Pikat më poshtë shpjegojnë panelin e sipërm të editorit dhe veçoritë e tij një nga një.",
          items: {
            responsive: "Raporti responsiv mund të bëhet për desktop, tablet dhe mobile",
            highlight: "Komponenti i pamjes thekson çdo komponent në faqen e raportit individualisht.",
            preview: "Ikona e syrit shërben për të parë paraprakisht faqen e raportit.",
            fullscreen: "Ikona e ekranit të plotë përdoret për të ndryshuar madhësinë (Maksimizo/Minimizo) të faqes së raportit. Redaktimi bëhet kur faqja është e maksimizuar. Faqet e raportit mund të ndërrohen kur janë të minimizuara.",
            viewCode: "Shiko kodin përdoret për të parë kodin HTML dhe CSS pas faqes së raportit.",
            undoRedo: "Ikonat \"Zhbëj\" dhe \"Ribëj\" përdoren për të zhbërë gabime, si p.sh. fshirja e fjalës së gabuar. Funksioni ribëj rikthen veprimet e zhbëra më parë.",
            download: "Shigjeta e shkarkimit për të shkarkuar raportin.",
            deleteAll: "Përdorni ikonën e fshirjes për të hequr të gjithë elementët nga raporti.",
            blocks: "Hapni menunë e blloqeve për të parë elementët.",
          },
        },
        basicElements: {
          title: "Elementet bazë përfshijnë",
          items: {
            columns: "Kolona - Kombinime të ndryshme kolonash mund të përdoren sipas kërkesave të raportit.",
            text: "Tekst - përdoret për të futur tekst në faqen e raportit",
            link: "Link - përdoret për të vendosur lidhje web për butona ose tekst",
            media: "Imazhe, video dhe harta mund të përfshihen në faqen e raportit sipas nevojës, duke i tërhequr dhe lëshuar.",
          },
        },
        formElements: {
          title: "Elementet e formularit përfshijnë",
          items: {
            intro: "Nëse keni nevojë të përfshini një formular në raportin tuaj, ka disa opsione të disponueshme:",
            types: "Disponohen lloje të ndryshme fushash hyrëse. Ato mund të përdoren sipas nevojës. Një shpjegim i shkurtër i secilës fushë dhe përdorimi i saj i përgjithshëm jepet në seksionin \"Llojet e fushave të hyrjes\".",
          },
        },
        extraElements: {
          title: "Elementet ekstra përfshijnë",
          items: {
            tooltip: "Tooltip - Një tooltip është një kuti e vogël pop-up që shfaqet kur përdoruesi kalon ose klikon mbi një element, si një buton ose ikonë. Ai ofron informacion shtesë ose kontekst për elementin, duke ndihmuar përdoruesit të kuptojnë funksionin e tij. Tooltips shpesh përdoren për të dhënë përshkrime të shkurtra ose udhëzime dhe përmirësojnë përvojën e përdoruesit.",
            countdown: "Numërimi mbrapsht - Numërimi mbrapsht është një element vizual që shfaq një kohëmatës që numëron deri në një datë ose ngjarje të caktuar. Mund të përdoret për të krijuar ndjesi urgjence ose për të theksuar një afat apo datë të rëndësishme. Mund të personalizohet për të shfaqur ditët, orët, minutat dhe sekondat e mbetura.",
            customCode: "Kod i personalizuar - Kodi i personalizuar i referohet aftësisë për të shtuar kodin tuaj në raport. Ky funksion lejon përfshirjen e skripteve ose copëzave të kodit për të modifikuar sjelljen ose pamjen e raportit. Ofron fleksibilitet dhe mundësi personalizimi përtej funksionaliteteve të integruara. Kërkon njohuri programimi për implementim të saktë.",
            typed: "Typed - Elementi \"Typed\" lejon shtimin e një efekti shkrimi në tekst, duke simuluar shkrimin e përmbajtjes. Ky efekt mund të përdoret për të krijuar ndërveprim dhe për të angazhuar lexuesit. Teksti shfaqet karakter pas karakteri me një vonesë të vogël dhe mund të personalizohet.",
            tabs: "Tabs - Tabs në kontekstin e ndërtuesit të raporteve janë një funksion që lejon krijimin e seksioneve ose faqeve të shumta brenda raportit. Çdo tab përfaqëson një seksion ku mund të organizoni dhe paraqisni informacione të ndryshme. Tabs e bëjnë raportin më të strukturuar, duke lejuar lexuesit të fokusohen në një seksion dhe të kalojnë te të tjerët kur është e nevojshme.",
          },
        },
        customization: {
          title: "Opsionet e përgjithshme të personalizimit dhe dimensionet:",
          intro: "Kur përfshihen elementë në shpjegimet e mësipërme, ky panel hapet për personalizim të mëtejshëm në raportin tuaj.",
          generalTitle: "Të përgjithshme",
          generalDesc: "Këtu ka opsione të ndryshme ku mund të vendoset pozicioni i elementit si static,",
          positionTitle: "Pozicioni në CSS",
          positionItems: {
            static: "Static - Ky është vlera e parazgjedhur. Elementët me position: static vendosen sipas rrjedhës normale të dokumentit dhe nuk ndikohen nga top, bottom, left, right ose z-index.",
            relative: "Relative - Elementët me position: relative vendosen relativisht me pozicionin e tyre normal në rrjedhën e dokumentit. Duke përdorur top, bottom, left dhe right mund të rregullohet pozicioni.",
            absolute: "Absolute - Elementët me position: absolute vendosen relativisht me paraardhësin më të afërt të pozicionuar (me pozicion jo static). Nëse nuk ka, vendoset relativisht me viewport. Top, bottom, left dhe right përdoren për pozicionim.",
            fixed: "Fixed - Elementët me position: fixed vendosen relativisht me viewport dhe nuk lëvizin kur faqja skrollohet. Top, bottom, left dhe right përdoren për pozicionim.",
          },
          floatDisplayTitle: "Float dhe Display në CSS",
          floatDisplayIntro: "Këto veti CSS janë mjete të fuqishme për kontrollin e paraqitjes dhe pozicionimit të elementëve në një faqe.",
          floatItem: "float - Përdoret për të përcaktuar nëse një element duhet të notojë majtas, djathtas ose aspak. Kur një element noton, ai del nga rrjedha normale dhe elementët e tjerë rrjedhin rreth tij. Vlerat e mundshme janë left, right dhe none.",
          displayItem: "display - Përdoret për të përcaktuar si duhet të shfaqet një element. Vlerat më të zakonshme janë block, inline dhe none.",
          displaySubItems: {
            block: "Elementët block fillojnë në rresht të ri dhe zënë gjithë gjerësinë e disponueshme. Mund të kenë margjina, padding dhe gjerësi/lartësi.",
            inline: "Elementët inline nuk fillojnë në rresht të ri dhe zënë vetëm gjerësinë e nevojshme. Nuk mund të kenë gjerësi/lartësi dhe margjinat/padding ndikojnë vetëm tekstin përreth.",
            none: "none përdoret për të fshehur plotësisht një element. Elementi nuk shfaqet dhe nuk zë hapësirë në faqe.",
          },
          dimensionTitle: "Dimensioni",
          dimensionDesc: "Për të dhënë dimensione dhe hapësirë të përshtatshme për çdo element, mund të përdorni dhe të vendosni vetitë për çdo element individualisht duke përdorur këtë panel.",
          dimensionIntro: "Në CSS, vetitë e mëposhtme përdoren për të kontrolluar dimensionet dhe hapësirën e elementëve",
          dimensionItems: {
            width: "Width - Përdoret për të vendosur gjerësinë e një elementi. Mund të specifikohet në pixela (px), përqindje (%) ose njësi të tjera relative.",
            maxWidth: "Max-width - Përdoret për të vendosur gjerësinë maksimale që mund të ketë një element. Nëse përmbajtja e tejkalon, do të përshtatet në kufirin e specifikuar.",
            maxHeight: "Max-height - Përdoret për të vendosur lartësinë maksimale që mund të ketë një element. Nëse përmbajtja e tejkalon, do të përshtatet në kufirin e specifikuar.",
            margin: "Margin - Përdoret për të kontrolluar hapësirën rreth një elementi. Vendos hapësirën mes kufirit të elementit dhe elementëve ngjitur. Mund të ketë vlera të ndryshme për secilën anë ose një vlerë të vetme.",
            padding: "Padding - Përdoret për të kontrolluar hapësirën mes përmbajtjes së elementit dhe kufirit të tij. Mund të ketë vlera të ndryshme për secilën anë ose një vlerë të vetme.",
            note: "Këto veti janë thelbësore për kontrollin e paraqitjes dhe hapësirës së elementëve në një faqe. Ato lejojnë krijimin e dizajneve responsivë dhe tërheqës vizualisht.",
          },
        },
        typography: {
          title: "Tipografia",
          intro: "Tipografia i referohet stilit, rregullimit dhe pamjes së tekstit në një dokument ose dizajn. Ajo përfshin zgjedhjen e fontit, madhësinë e fontit, trashësinë, hapësirën midis rreshtave dhe rreshtimin. Zgjedhjet tipografike ndikojnë lexueshmërinë, pamjen vizuale dhe përvojën e përdoruesit.",
          items: {
            fontFamily: "Familja e Fontit - Vetia font-family përdoret për të specifikuar fontin ose listën e fonteve për tekstin. Për shembull: font-family: Arial, sans-serif; do të përdorë Arial nëse është i disponueshëm.",
            fontSize: "Madhësia e Fontit - Vetia font-size përcakton madhësinë e tekstit. Mund të specifikohet në pixela (px), përqindje (%) etj.",
            fontWeight: "Trashësia e Fontit - Vetia font-weight përcakton trashësinë e tekstit. Mund të jetë normal, bold, bolder, lighter ose vlera numerike 100, 200, …, 900.",
            letterSpacing: "Hapësira midis Shkronjave - Vetia letter-spacing kontrollon hapësirën ndërmjet shkronjave.",
            color: "Ngjyra - Vetia color përcakton ngjyrën e tekstit. Mund të specifikohet me emra ngjyrash, kode heksadecimale, RGB ose HSL.",
            lineHeight: "Lartësia e Rreshtit - Vetia line-height përcakton lartësinë e rreshtit të tekstit.",
            textAlign: "Rreshtimi i Tekstit - Vetia text-align përcakton rreshtimin horizontal të tekstit (left, right, center ose justify).",
            textShadow: "Hije e Tekstit - Kjo veti shton hije në tekst dhe lejon të përcaktoni ngjyrën dhe offset-et.",
          },
          decorationsTitle: "Dekorime",
          decorationsItems: {
            backgroundColor: "Ngjyra e Sfondit - Vetia background-color përcakton ngjyrën e sfondit të një elementi.",
            border: "Kufiri - Vetia border krijon një kufi rreth elementit dhe përcakton gjerësinë, stilin dhe ngjyrën.",
            borderRadius: "Rrezja e Kufirit - Vetia border-radius krijon qoshe të rrumbullakosura.",
            boxShadow: "Hija e Kutisë - Vetia box-shadow shton hije në element.",
            background: "Background - Vetia background vendos disa veti të sfondit në të njëjtën kohë.",
            gradient: "Gradient - Një gradient është një kalim i butë mes dy ose më shumë ngjyrave.",
            note: "Pa theksuar fjalët, këto veti dhe vlera mund të përdoren për të personalizuar pamjen e elementëve në një raport, duke shtuar ngjyra, kufij, qoshe të rrumbullakosura, hije dhe gradiente.",
          },
          extraTitle: "Ekstra",
          extraItems: {
            opacity: "Opaciteti - Opaciteti i referohet nivelit të transparencës së një elementi. Vetia opacity varion nga 0 në 1, ku 0 është plotësisht transparente dhe 1 është plotësisht e dukshme.",
            transitions: "Tranzicionet - Tranzicionet në CSS lejojnë animacione të buta midis gjendjeve të ndryshme të një elementi.",
            transform: "Transform - Transformimet në CSS lejojnë manipulimin e formës, madhësisë ose pozicionit të një elementi.",
            transformDetails: {
              layout: "Layout: Personalizoni paraqitjen e çdo faqeje, duke përfshirë vendosjen e imazheve dhe tekstit.",
              dimensions: "Dimensionet: Rregulloni dimensionet e elementëve në raport.",
              variables: "Variabla të personalizuar: Përfshini variabla ose të dhëna të personalizuara në raport.",
              formulas: "Formula të personalizuara: Përdorni formula të personalizuara për të llogaritur vlera.",
              stages: "Grafikët e fazave: Shfaqni grafikë ose vizualizime për fazat ose progresin.",
              recommendations: "Rekomandime: Përfshini rekomandime ose sugjerime bazuar në të dhënat e sondazhit.",
              overview: "Përmbledhje: Jepni një përmbledhje të rezultateve të sondazhit.",
              sequence: "Rreshtimi i sekuencës së faqeve: Rregulloni rendin e faqeve në raport.",
            },
          },
          note: "Ju lutemi vini re se këto opsione personalizimi mund të ndryshojnë në varësi të veçorive dhe kapaciteteve të mjetit të ndërtimit të raporteve që po përdorni.",
        },
      },

      // Variables Page
      variables: {
        title: "Variablat",
        intro: "Kjo platformë është për menaxhimin e sondazheve dhe formularëve të gjenerimit të udhëheqjes. Variablat e lead-gen janë emra unikë të fushave të përdorura për kapur të dhënat e formularit. Variablat mund të referohen në raporte duke përdorur një sintaksë specifike. Cilësimet e profilit të përdoruesit dhe gjuhës janë të aksesueshme në këndin e sipërm djathtas.",
        
        accessingVariables: {
          title: "Aksesimi i Variablave",
          steps: {
            step1: "Navigoni te Sondazhet dhe editoni çdo sondazh duke klikuar butonin Veprimet nga tabela e listimit.",
            step2: "Klikoni në emrin e theksuar nga lista rënëse për të hapur faqen.",
          },
          imageAlt1: "Navigimi",
          imageAlt2: "Navigimi",
        },

        leadGenVariable: {
          title: "Variabla e Lead Gen",
          intro: "Variablat e lead gen janë emra variablash të ruajtura nga emrat e 'Form builder'. Variablat e Lead Gen ofrojnë një mënyrë për të kapur dhe përdorur të dhënat nga një formular Gjenerimi i Udhëheqjes. Çdo fushë në formular i caktohet një emër unik, i cili mund të përdoret për të referuar dhe shfaqur vlerën përkatëse të fushës në raporte ose formula të personalizuara. Sintaksa për të aksesuar vlerën e një variabli lead gen është #[leadForm:fieldName]#.",
          usage: "Sintaksa e Përdorimit: Për të aksesuar vlerën e një variabli lead gen, përdorni sintaksën e mëposhtme: #[leadForm:fieldName]#. Zëvendësoni 'fieldName' me emrin unik të fushës së dëshiruar. Sintaksa e variablit lead gen duhet të jetë e mbyllur brenda #[ dhe ]#.",
          example: "Shembull: Supozoni se keni një formular Lead Gen me fushat dhe emrat unikë të mëposhtëm:",
          exampleFields: {
            firstName: "Emri (fieldName: \"firstName\")",
            lastName: "Mbiemri (fieldName: \"lastName\")",
            email: "Email (fieldName: \"email\")",
          },
          displayValue: "Për të shfaqur vlerën e fushës Emri në një raport, do të përdornit sintaksën e variablit lead gen si më poshtë: #[leadForm:firstName]#",
          dynamicReplacement: "Kjo sintaksë do të zëvendësohet dinamikisht me vlerën aktuale të futur nga përdoruesi në fushën Emri të formularit Lead Gen.",
          notesTitle: "Shënime:",
          notes: {
            note1: "1. Sintaksa e variablit lead gen është e ndjeshme ndaj shkronjave të mëdha dhe të vogla, prandaj sigurohuni që të përdorni kapitalizimin e saktë për emrat e fushave.",
            note2: "2. Sigurohuni që emri i fushës i përdorur në sintaksë të përputhet me emrin unik të caktuar fushës në formularin Lead Gen.",
            note3: "3. Sintaksa e variablit lead gen mund të përdoret në kontekste të ndryshme, të tilla si raporti, formula të personalizuara.",
          },
          conclusion: "Duke shfrytëzuar sintaksën e variablit lead gen, ju mund të aksesoni dhe inkorporoni lehtësisht të dhënat e formularit lead gen në raportet, llogaritjet ose proceset tuaja të personalizuara. Kjo lejon raportim dinamik dhe të personalizuar bazuar në informacionin specifik të mbledhur përmes formularëve të gjenerimit të udhëheqjes.",
          imageAlt: "Variabla e Lead Gen",
        },

        customVariables: {
          title: "Variablat e Personalizuara",
          intro: "Variablat e Personalizuara ofrojnë një mënyrë për të caktuar vlera dinamike bazuar në kushtet e përcaktuara në modulin Stage. Këto variabla janë të pavarura dhe kanë emra unikë që mund të përdoren për të referuar dhe përdorur vlerat e tyre në të gjithë sistemin. Sintaksa për të aksesuar vlerën e një variabli të personalizuar është #[fieldName]#.",
          usage: "Sintaksa e Përdorimit: Për të aksesuar vlerën e një variabli të personalizuar, përdorni sintaksën e mëposhtme: #[fieldName]#. Zëvendësoni 'fieldName' me emrin unik të variablit të personalizuar të dëshiruar. Sintaksa e variablit të personalizuar duhet të jetë e mbyllur brenda #[ dhe ]#. Rekomandimet shfaqen në seksionin e raportit i cili mund të personalizohet duke përdorur 'Variablat e Personalizuara'.",
          example: "Shembull: Supozoni se keni një formular sondazhi ku pjesëmarrësit japin komente dhe vlerësojnë përvojën e tyre të përgjithshme në një shkallë nga 1 në 10. Bazuar në përgjigjen e tyre, ju dëshironi të caktoni një rekomandim për një variabël të personalizuar të quajtur \"rekomandimi\". Rekomandimi do të tregojë nëse pjesëmarrësi do të merrte shërbimin tuaj, me vlera të tilla si \"Po\", \"Jo\" ose \"Ndoshta\". Rekomandimi mund të jepet për çdo pyetje sipas përgjigjes së zgjedhur duke përdorur variablat e personalizuara.",
          displayValue: "Për të përdorur vlerën e variablit të personalizuar \"rekomandimi\" në një raport ose për ta shfaqur atë në një email, do të përdornit sintaksën e variablit të personalizuar si më poshtë: # [rekomandimi]#",
          dynamicReplacement: "Kjo sintaksë do të zëvendësohet dinamikisht me vlerën aktuale të caktuar variablit të personalizuar \"rekomandimi\" në modulin Stage, bazuar në vlerësimin e përvojës së përgjithshme të pjesëmarrësit të sondazhit.",
          notesTitle: "Shënime:",
          notes: {
            note1: "Sintaksa e variablit të personalizuar është e ndjeshme ndaj shkronjave të mëdha dhe të vogla, prandaj sigurohuni që të përdorni kapitalizimin e saktë për emrin e variablit.",
            note2: "Sigurohuni që emri i variablit të përdorur në sintaksë të përputhet me emrin unik të caktuar variablit të personalizuar në modulin Stage.",
            note3: "Variablat e personalizuara mund të caktohen vlera bazuar në kushte ose llogaritje të ndryshme, duke lejuar trajtim dinamik dhe të personalizuar të të dhënave.",
            note4: "Sintaksa e variablit të personalizuar mund të përdoret në kontekste të ndryshme, të tilla si raportet, ose çdo vend tjetër ku dëshironi të përdorni vlerën e një variabli të personalizuar.",
          },
          conclusion: "Duke shfrytëzuar sintaksën e variablit të personalizuar, ju mund të caktoni dhe aksesoni vlera dinamike bazuar në kushte të përcaktuara, duke mundësuar menaxhim fleksibël dhe të personalizueshëm të të dhënave brenda sistemit. Në shembullin e mësipërm, variabli i personalizuar \"rekomandimi\" përfaqëson rekomandimin bazuar në përgjigjen e sondazhit të pjesëmarrësit",
          editTitle: "Edito variabël të personalizuar",
          editIntro: "Veçoria \"Edito Variabël\" ju lejon të modifikoni çdo variabël në sistem, duke përfshirë emrin, përshkrimin ose sintaksën e saj. Kjo veçori ju mundëson të bëni ndryshime në variablat ekzistuese dhe t'i përditësoni ato sipas kërkesave tuaja.",
          editStepsTitle: "Për të përdorur veçorinë \"Edito Variabël\":",
          editSteps: {
            step1: "Shkoni në seksionin ku ndodhet variabli. Navigoni në seksionin ose faqen e duhur brenda aplikacionit software ku variabli ruhet ose shfaqet.",
            step2: "Gjeni variablin që dëshironi të editoni. Identifikoni variablin specifik që dëshironi të modifikoni midis listës së variablave të shfaqur në faqe.",
            step3: "Klikoni në butonin ose opsionin \"Edito\" të lidhur me variablin. Ka të ngjarë që të ketë një buton \"Edito\" ose një opsion të ngjashëm pranë ose poshtë detajeve të variablit. Klikoni në të për të filluar procesin e editimit.",
            step4: "Bëni ndryshimet e nevojshme në emrin, përshkrimin ose sintaksën e variablit. Pasi të jeni në modalitetin e editimit, ju mund të modifikoni aspekte të ndryshme të variablit, të tilla si emri, përshkrimi ose sintaksa. Përditësoni fushat sipas nevojës.",
            step5: "Ruani modifikimet. Pasi të keni bërë ndryshimet e dëshiruara, kërkoni një buton \"Ruaj\" ose një opsion të ngjashëm për të aplikuar modifikimet në variabël. Klikoni në të për të ruajtur editimet tuaja. Disa sisteme mund të ruajnë automatikisht ndryshimet kur i bëni ato.",
            conclusion: "Duke përdorur veçorinë \"Edito Variabël\", ju mund të përditësoni dhe personalizoni lehtësisht variablat tuaja sipas nevojës, duke siguruar që ato të përfaqësojnë saktësisht informacionin ose funksionalitetin e dëshiruar.",
          },
          deleteTitle: "Fshi variabël të personalizuar",
          deleteIntro: "Fshirja e një Variabli të Personalizuar do të heqë të gjitha vlerat e lidhura me atë Variabël të Personalizuar specifik në sistem. Kjo përfshin çdo rekomandim në raporte ose formula të personalizuara që përdorin variablin. Variablat e Personalizuara që përdoren për të përcaktuar kushte në faza, formula të personalizuara, rekomandim në Q&A Builder do të hiqen të gjitha nëse një variabël i personalizuar fshihet.",
          addTitle: "Shto variabël të ri",
          addIntro: "Për të shtuar një variabël të ri në projekt, klikoni në butonin Shto variabël të ri. Një popup do të shfaqet në ekran, duke shfaqur një formular që është i njëjtë me atë të përdorur në ikonën e editimit të kësaj faqeje.",
          addFields: {
            label: "etiketë - shtoni emrin e etiketës me maksimum 250 karaktere",
            type: "Zgjidhni llojin e variablit nga opsionet 'numër', 'tekst me një rresht' ose 'tekst me shumë rreshta'.",
            name: "Futni Emrin e variablit pa hapësira dhe me maksimum 50 karaktere.",
            description: "Jepni një Përshkrim të variablit dhe shpjegoni përdorimin e tij.",
          },
          addConclusion: "Klikoni në butonin \"Shto variabël të ri\" për të shtuar një variabël. Variabli i ri do të përditësohet në listë.",
          imageAlt: "Variablat e Personalizuara",
          imageAltEdit: "Edito variabël të personalizuar",
          imageAltDelete: "Fshi variabël të personalizuar",
          imageAltAddButton: "Butoni Shto variabël të ri",
          imageAltAdd: "Shto variabël të ri",
        },

        customFormula: {
          title: "Formula e Personalizuar",
          intro: "Formulat e Personalizuara ofrojnë një mënyrë për të krijuar llogaritje dhe shprehje dinamike duke përdorur variabla nga Lead Gen, të Personalizuara dhe variabla të P&P. Këto formula caktohen emra unikë dhe mund të përdoren si variabla vetë në raporte dhe faza. Sintaksa për të aksesuar vlerën e një formule të personalizuar është #[nomeFormula]#.",
          usage: "Sintaksa e Përdorimit: Për të aksesuar vlerën e një formule të personalizuar, përdorni sintaksën e mëposhtme: #[nomeFormula]#. Zëvendësoni \"nomeFormula\" me emrin unik të caktuar formulës së personalizuar të dëshiruar. Sintaksa e formulës së personalizuar duhet të jetë e mbyllur brenda #[ dhe ]#.",
          example: "Shembull: Supozoni se keni një sondazh me pyetje të shumta dhe dëshironi të llogaritni përqindjen e përgjithshme të rezultatit të sondazhit bazuar në pikët e fituara dhe pikët maksimale. Ju mund të krijoni një formulë të personalizuar të quajtur \"përqindjaEPërgjithshme\" për të kryer këtë llogaritje.",
          displayValue: "Për të përdorur vlerën e variablit të personalizuar \"rekomandimi\" në një raport ose për ta shfaqur atë në një email, do të përdornit sintaksën e variablit të personalizuar si më poshtë: # [rekomandimi]#",
          formulaDefinition: "Përkufizimi i formulës së personalizuar mund të duket si kjo: përqindjaEPërgjithshme = (pikët_e_fituara_pyt_1 + pikët_e_fituara_pyt_2 + ... ) / (pikët_maksimale_pyt_1 + pikët_maksimale_pyt_2 + ...) * 100",
          usageExample: "Për të përdorur vlerën e formulës së personalizuar \"përqindjaEPërgjithshme\" në një raport ose faza të mëtejshme, do të përdornit sintaksën e formulës së personalizuar si më poshtë: përqindjaEPërgjithshme Kjo sintaksë do të zëvendësohet dinamikisht me vlerën aktuale të llogaritur të caktuar formulës së personalizuar \"përqindjaEPërgjithshme\".",
          notesTitle: "Shënime:",
          notes: {
            note1: "Sintaksa e formulës së personalizuar është e ndjeshme ndaj shkronjave të mëdha dhe të vogla, prandaj sigurohuni që të përdorni kapitalizimin e saktë për emrin e formulës.",
            note2: "Sigurohuni që emri i formulës i përdorur në sintaksë të përputhet me emrin unik të caktuar formulës së personalizuar.",
            note3: "Formulat e personalizuara mund të përfshijnë variabla nga Lead Gen dhe variabla të P&P, duke lejuar llogaritje dinamike bazuar në të dhënat e mbledhura.",
            note4: "Sintaksa e formulës së personalizuar mund të përdoret në kontekste të ndryshme, të tilla si shabllonet e raporteve, kushtet e fazave.",
          },
          conclusion: "Duke përdorur sintaksën e formulës së personalizuar, ju mund të krijoni llogaritje dhe shprehje të fuqishme bazuar në variabla nga Lead Gen dhe variabla të P&P. Në shembullin e dhënë, formula e personalizuar \"përqindjaEPërgjithshme\" llogarit përqindjen e përgjithshme të rezultatit të sondazhit bazuar në pikët e fituara dhe pikët maksimale nga pyetjet individuale të sondazhit.",
          editTitle: "Edito Formulë",
          editIntro: "Veçoria \"Edito Formulë\" ju lejon të modifikoni çdo formulë nga seksioni \"Formula e Personalizuar\". Kjo veçori ju mundëson të bëni ndryshime në formulat ekzistuese dhe t'i përditësoni ato sipas kërkesave tuaja.",
          editStepsTitle: "Për të përdorur veçorinë \"Edito Formulë\":",
          editSteps: {
            step1: "Shkoni në seksionin \"Formula e Personalizuar\" të sistemit.",
            step2: "Gjeni formulën që dëshironi të editoni.",
            step3: "Klikoni në butonin ose opsionin \"Edito\" të lidhur me formulën.",
            step4: "Bëni ndryshimet e nevojshme në formulë.",
            step5: "Ruani modifikimet.",
            conclusion: "Duke përdorur veçorinë \"Edito Formulë\", ju mund të përditësoni dhe rregulloni lehtësisht formulat tuaja të personalizuara sipas nevojës, duke siguruar që ato të reflektojnë llogaritjet dhe shprehjet e dëshiruara.",
          },
          deleteTitle: "Fshi formulë",
          deleteIntro: "Fshirja e një formule është një veprim i përhershëm që duhet bërë me kujdes. Kur fshini një formulë, ajo do të hiqet nga të gjitha seksionet dhe raportet ku përdoret. Kjo do të thotë që çdo llogaritje ose shprehje që mbështetet në formulë nuk do të jetë më e vlefshme.",
          deleteWarning1: "Është e rëndësishme të theksohet se fshirja e një formule do të fshijë gjithashtu të gjitha llogaritjet që bazohen në të. Kjo mund të ketë një ndikim të konsiderueshëm në analizën tuaj të të dhënave dhe raportimin. Prandaj, rekomandohet të rishikoni me kujdes pasojat para se të vazhdoni me fshirjen.",
          deleteWarning2: "Nëse nuk jeni të sigurt për fshirjen e një formule, është një praktikë e mirë të krijoni një kopje rezervë ose të bëni një kopje të dokumentit tuaj para se të bëni ndonjë ndryshim. Në këtë mënyrë, ju mund të ktheheni në versionin e mëparshëm nëse është e nevojshme.",
          deleteStepsTitle: "Për të fshirë një formulë, ndiqni këto hapa:",
          deleteSteps: {
            step1: "Gjeni formulën që dëshironi të fshini.",
            step2: "Klikoni në butonin ose opsionin \"Fshi\" të lidhur me formulën.",
            step3: "Konfirmoni fshirjen kur ju kërkohet.",
          },
          deleteWarning3: "Pasi formula të fshihet, ajo nuk mund të rikuperohet. Prandaj, është e rëndësishme të kontrolloni dy herë vendimin tuaj para se të vazhdoni.",
          deleteConclusion: "Mos harroni, fshirja e një formule është një veprim i përhershëm dhe mund të ketë një ndikim të konsiderueshëm në analizën tuaj të të dhënave dhe raportimin. Sigurohuni të merrni parasysh me kujdes pasojat para se të fshini ndonjë formulë.",
          addTitle: "Shto formulë",
          addIntro: "mund të Shtohen formula të reja duke klikuar në butonin Shto formulë të personalizuar, Në këtë mund të shtohet etiketë dhe emër për formulën",
          addFieldIntro: "Në fushën e hyrjes së formulës, ju mund të përdorni variabla së bashku me operatorë. Operatori mund të zgjidhet nga simbolet ose të shtypur nga tastiera. Këtu është një shpjegim i thjeshtë i kuptimit dhe përdorimit të operatorëve:",
          operatorsIntro: "Në kontekstin e formulave dhe shprehjeve matematikore, operatorët janë simbole ose karaktere që përfaqësojnë operacione ose veprime specifike që duhen kryer në një ose më shumë operandë. Këtu janë disa operatorë të përdorur zakonisht:",
          operators: {
            addition: "Mbledhja (+) Shton dy ose më shumë variabla së bashku.",
            subtraction: "Zbritja ( - ) Zbret një variabël nga tjetri.",
            multiplication: "Shumëzimi ( * ) Shumëzon dy ose më shumë variabla .",
            division: "Pjesëtimi ( / ) Pjesëton një variabël me tjetrin.",
            brackets: "Kllapat janë përdorur në shprehjet e rregullta për të përcaktuar modele. Ato mund të specifikojnë përsëritje ose intervale karakteresh.",
            variable: "( x ) mund të referohet në një variabël ose vlerë që është pjesë e një formule ose llogaritjeje. Përdoret për të demonstruar përdorimin e operatorëve dhe variablave në formula.",
          },
          operatorsConclusion: "Këto janë vetëm disa shembuj të operatorëve të përdorur zakonisht në llogaritjet matematikore. Operatorët mund të kombinohen për të krijuar formula dhe shprehje komplekse për të kryer llogaritje të avancuara dhe manipulime të të dhënave.",
          precedenceIntro: "Kur përdorni operatorë në formula, është e rëndësishme të merrni parasysh rendin e operacioneve, të njohur gjithashtu si përparësi. Rendi i operacioneve diktojnë sekuencën në të cilën vlerësohen operatorët. Rendi i zakonshëm i operacioneve është:",
          precedence: {
            parentheses: "<b>Kllapat:</b> Operacionet brenda kllapave vlerësohen së pari.",
            exponents: "<b>Eksponentët:</b> Eksponencimi kryhet më pas.",
            multiplicationDivision: "<b>Shumëzimi dhe Pjesëtimi:</b> Këto operacione kryhen nga e majta në të djathtë.",
            additionSubtraction: "<b>Mbledhja dhe Zbritja:</b> Këto operacione kryhen nga e majta në të djathtë.",
          },
          precedenceNote: "Përdorimi i kllapave mund të anashkalojë rendin e paracaktuar të operacioneve dhe të sigurojë që operacione specifike të vlerësohen së pari.",
          precedenceConclusion: "Duke kuptuar dhe përdorur operatorët në mënyrë efektive, ju mund të krijoni formula dhe shprehje të fuqishme për të kryer llogaritje dhe manipuluar të dhënat në formulat tuaja të personalizuara.",
          imageAlt: "Formula e Personalizuar",
          imageAltEdit: "Edito Formulë",
          imageAltDelete: "Fshi Formulë",
          imageAltAdd: "Shto Formulë",
          imageAltOperators: "Formulë",
        },

        utmVariables: {
          title: "Variablat UTM",
          intro: "Variablat unikë që mund të kalohen në 'Burimi', 'Mjeti' dhe 'Fushata'. Këto variabla mund të përdoren për të krijuar URL të reja për një sondazh të caktuar, duke mundësuar ndjekjen e burimeve të përgjigjeve. Duke inkorporuar variablat UTM në URL, ju mund të identifikoni dhe analizoni efektivitetin e fushatave të ndryshme të marketingut ose kanaleve në gjenerimin e përgjigjeve të sondazhit. Variablat mund të shtohen, editohen ose fshihen.",
          context: "Në kontekstin e një URL, burimi, mjeti dhe fushata janë variabla UTM që mund të përdoren për të ndjekur dhe analizuar efektivitetin e fushatave të ndryshme të marketingut ose kanaleve në gjenerimin e përgjigjeve të sondazhit.",
          source: "<b>Burimi</b> - Parametri i burimit identifikon nga vjen trafiku. Ai tregon platformën specifike, faqen e internetit ose aplikacionin që po drejton trafikun në sondazh. Për shembull, nëse po ndani lidhjen e sondazhit në Facebook, mund të përdorni \"facebook\" si vlerë për parametrin e burimit.",
          medium: "<b>Mjeti</b> - Parametri i mjetit identifikon mjetin ose kanalin e marketingut përmes të cilit po ndahet lidhja e sondazhit. Ai specifikon llojin e trafikut, të tilla si email, media sociale ose reklama banner. Për shembull, nëse po ndani lidhjen e sondazhit përmes një fushate email, mund të përdorni \"email\" si vlerë për parametrin e mjetit.",
          campaign: "<b>Fushata</b> - Parametri i fushatës përdoret për të identifikuar një fushatë ose promovim specifik të marketingut. Ai ndihmon në dallimin e iniciativave ose strategjive të ndryshme brenda një burimi dhe mjeti. Për shembull, nëse po ekzekutoni fushata të shumta në Facebook, mund të përdorni emra unikë të fushatës si \"shitje_vere\" ose \"promocion_festash\" si vlerë për parametrin e fushatës.",
          conclusion: "Duke inkorporuar këto variabla UTM në URL të sondazhit, ju mund të ndiqni dhe matni performancën e burimeve, mjeteve dhe fushatave të ndryshme në gjenerimin e përgjigjeve të sondazhit. Kjo ju lejon të analizoni efektivitetin e përpjekjeve tuaja të marketingut dhe të merrni vendime të bazuara në të dhëna.",
          imageAlt: "Variablat UTM",
        },

        stages: {
          title: "Fazat",
          intro: "Fazat mundësojnë që pjesëmarrësit të marrin komente dhe rekomandime të personalizuara bazuar në përgjigjet e tyre dhe rezultatin që kanë arritur. Kjo do të thotë që pjesëmarrësit jo vetëm do të marrin udhëzime të përgjithshme, por edhe njohuri dhe sugjerime specifike që janë relevante për performancën e tyre individuale.",
          summary: "Në përmbledhje, fazat e personalizueshme në sistem ofrojnë një qasje gjithëpërfshirëse dhe të përshtatur për komente dhe rekomandime. Duke lejuar përdoruesit të përcaktojnë faza si nivele të ndryshme dhe duke ofruar njohuri të personalizuara.",
          addTitle: "Për të shtuar një fazë, ndiqni këto hapa:",
          addSteps: {
            step1: "Shkoni në seksionin \"Fazat\" të sistemit.",
            step2: "Klikoni në butonin ose opsionin \"Shto Fazë\".",
            step3: "Jepni një emër dhe përshkrim për fazën e re.",
            step4: "Zgjidhni një formulë nga lista rënëse \"Nëse emri i formulës\", e cila ishte personalizuar më parë në seksionin \"variabël - formulë e personalizuar\" dhe tani shfaqet në listën rënëse.",
            step5: "Zgjidhni një gamë për rezultatin e daljes së formulës së zgjedhur, dmth. nga një gamë në një gamë tjetër. Nëse rezultatet e daljes bien brenda një game specifike, zgjidhni një variabël të personalizuar për atë formulë dhe shfaqni një mesazh rekomandimi për atë gamë e cila do të shtyhet në editorin e ofruar.",
            step6: "Kushtet mund të aplikohen duke përdorur \"Variablat e personalizuara\". Butoni \"+\" mund të përdoret për të shtuar një kusht të ri, dhe butoni \"-\" mund të përdoret për të hequr çdo kusht ekzistues.",
            step7: "Personalizoni rekomandimet dhe komentet specifike për fazën.",
            step8: "Ruani ndryshimet.",
          },
          addConclusion: "Ky proces ju lejon të përcaktoni dhe krijoni faza të reja në sistem, secila me kriteret, rekomandimet dhe komentet e veta të përshtatura për performancën dhe përparimin e pjesëmarrësve.",
          editTitle: "Për të edituar një fazë, ndiqni këto hapa:",
          editSteps: {
            step1: "Shkoni në seksionin \"Fazat\" të sistemit.",
            step2: "Gjeni fazën që dëshironi të editoni.",
            step3: "Shtimi i çdo kushti për të pasur një fazë të caktuar mund të bëhet.",
            step4: "Çdo kusht paraprak mund gjithashtu të hiqet duke përdorur variabla të personalizuar.",
            step5: "Klikoni në butonin ose opsionin \"Edito\" të lidhur me fazën.",
            step6: "Bëni ndryshimet e nevojshme në emrin, përshkrimin ose rekomandimet e fazës.",
            step7: "Ruani modifikimet.",
          },
          deleteTitle: "Për të fshirë një fazë, ndiqni këto hapa:",
          deleteSteps: {
            step1: "Shkoni në seksionin \"Fazat\" të sistemit.",
            step2: "Gjeni fazën që dëshironi të fshini.",
            step3: "Klikoni në butonin ose opsionin \"Fshi\" të lidhur me fazën.",
            step4: "Konfirmoni fshirjen kur ju kërkohet.",
          },
          deleteWarning: "Ju lutemi vini re se fshirja e një faze do ta heqë atë përgjithmonë nga sistemi, së bashku me çdo rekomandim dhe komente të lidhura. Sigurohuni të rishikoni me kujdes pasojat para se të vazhdoni me fshirjen.",
          conclusion: "Duke përdorur veçoritë e personalizimit të fazave, ju mund të përshtatni sistemin për të përmbushur kërkesat tuaja specifike dhe të ofroni komente dhe rekomandime të personalizuara për pjesëmarrësit në faza të ndryshme të përparimit të tyre.",
          imageAlt: "Fazat",
          imageAltEdit: "edito një fazë",
        },
      },

      // Publish & Share Page
      publishShare: {
        title: "Publiko dhe Shpërndaj",
        intro: "Ju mund të personalizoni URL duke shtuar kombinime të ndryshme të variablave UTM për të ndjekur burimin, mjetin dhe fushatën. URL e sondazhit gjithashtu përfshin një opsion kopjimi dhe ngjitjeje për ndarje të lehtë.",
        
        accessing: {
          title: "Aksesimi i Publiko dhe Shpërndaj",
          steps: {
            step1: "Navigoni te Sondazhet dhe editoni çdo sondazh duke klikuar butonin Veprimet nga tabela e listimit.",
            step2: "Klikoni në emrin e theksuar nga lista rënëse për të hapur faqen.",
          },
          imageAlt1: "Navigimi",
          imageAlt2: "Navigimi",
        },

        urls: {
          title: "URL-të",
          defaultLink: "Si parazgjedhje, ka tashmë një lidhje të dhënë për sondazhin e zgjedhur në faqe. Kjo lidhje mund të përdoret për qëllimin tuaj specifik. Megjithatë, është e mundur që të preferoni një lidhje URL më specifike për identifikim më të lehtë dhe personalizim unik.",
          createNewTitle: "<b> Krijo URL të Re -</b> Për ta kryer këtë, ju mund të përdorni butonin \"Krijo URL të Re\" për të krijuar një URL të re dhe të personalizuar. Këtu janë hapat për ta bërë këtë:",
          steps: {
            step1: "Gjeni butonin \"Krijo URL të Re\".",
            step2: "Klikoni në buton për të filluar procesin.",
            step3: "Ndiqni kërkesat ose udhëzimet e dhëna për të personalizuar URL-në tuaj të re.",
            step4: "<b>Emri</b> - Futni emrin për URL. Për shembull, nëse dëshironi të përdorni sondazhin për të mbledhur të dhëna nga media sociale si Facebook, mund të jepni emrin e lidhjes si 'lidhja_facebook'.",
            step5: "Variabli UTM tashmë është filluar në variabël/ Variablat e Fushatës UTM.",
            step6: "<b>Burimi</b> - Parametri i burimit identifikon nga vjen trafiku. Ai tregon platformën specifike, faqen e internetit ose aplikacionin që po drejton trafikun në sondazh. Për shembull, nëse po ndani lidhjen e sondazhit në Facebook, mund të përdorni \"facebook\" si vlerë për parametrin e burimit",
            step7: "<b>Mjeti</b> - Parametri i mjetit identifikon mjetin ose kanalin e marketingut përmes të cilit po ndahet lidhja e sondazhit. Ai specifikon llojin e trafikut, të tilla si email, media sociale ose reklama banner. Për shembull, nëse po ndani lidhjen e sondazhit përmes një fushate email, mund të përdorni \"email\" si vlerë për parametrin e mjetit.",
            step8: "<b>Fushata</b> - Parametri i fushatës përdoret për të identifikuar një fushatë ose promovim specifik të marketingut. Ai ndihmon në dallimin e iniciativave ose strategjive të ndryshme brenda një burimi dhe mjeti. Për shembull, nëse po ekzekutoni fushata të shumta në Facebook, mund të përdorni emra unikë të fushatës si \"shitje_vere\" ose \"promocion_festash\" si vlerë për parametrin e fushatës.",
            step9: "Duke inkorporuar këto variabla UTM në URL të sondazhit, ju mund të ndiqni dhe matni performancën e burimeve, mjeteve dhe fushatave të ndryshme në gjenerimin e përgjigjeve të sondazhit. Kjo ju lejon të analizoni efektivitetin e përpjekjeve tuaja të marketingut dhe të merrni vendime të bazuara në të dhëna.",
            step10: "<b>Përmbajtja</b> - Në kontekstin e një URL, parametri utm_content përdoret për të identifikuar se çfarë specifikisht u klikua për të sjellë përdoruesin në faqe. Ai ndihmon në ndjekjen dhe analizimin e efektivitetit të elementeve të ndryshme brenda një fushate ose kanali të marketingut. Për shembull, nëse keni lidhje ose reklama të shumta në Facebook që çojnë në sondazh, mund t'i dalloni ato duke përdorur vlera të ndryshme për parametrin utm_content. Këtu është një shembull se si parametri utm_content mund të përdoret:",
            step11: "Për të shtuar specifikime shtesë në URL, ju mund të përdorni opsionin 'Shto parametër' për më shumë parametra në url",
            step12: "Pasi të keni futur të gjithë informacionin e nevojshëm, klikoni në butonin \"Përditëso\" për të finalizuar krijimin e URL-së tuaj të re. Kjo URL do të shfaqet në tabelën në të njëjtën faqe si më poshtë",
          },
          contentExamples: {
            example1: "Nëse keni një lidhje logoje dhe një lidhje teksti që drejton përdoruesit në sondazh, mund të përdorni utm_content=lidhjalogos për lidhjen e logos dhe utm_content=lidhjetekst për lidhjen e tekstit. Kjo do t'ju ndihmojë të dalloni dhe analizoni performancën e secilit element specifik brenda fushatës së Facebook.",
            example2: "Duke inkorporuar parametrin utm_content në URL të sondazhit, ju mund të ndiqni dhe matni performancën e elementeve të ndryshme të përmbajtjes në gjenerimin e përgjigjeve të sondazhit. Kjo ju lejon të vlerësoni efektivitetin e përpjekjeve tuaja të marketingut dhe të merrni vendime të bazuara në të dhëna.",
          },
          imageAlt1: "Krijo URL të Re",
          imageAlt2: "Butoni Krijo URL të Re",
          imageAlt3: "Krijo URL të Re",
        },

        tableListingFields: {
          title: "Fushat e Listimit të Tabelës",
          intro: "Si parazgjedhje, ka tashmë një lidhje të dhënë për sondazhin e zgjedhur në faqe. Kjo lidhje mund të përdoret për qëllimin tuaj specifik. Megjithatë, është e mundur që të preferoni një lidhje URL më specifike për identifikim më të lehtë dhe personalizim unik.",
          fields: {
            name: "<b>Emri</b> - ruan emrin e lidhjes",
            response: "<b>Përgjigja</b> - është numri i përgjigjeve të gjeneruara në këtë lidhje të caktuar",
            createdDate: "<b>Data e krijimit</b> - shfaq datën kur lidhja u përditësua.",
            status: "<b>Statusi</b> - tregon statusin aktiv dhe joaktiv të lidhjes",
            actions: "<b>Veprimet</b> - mund të editohet, fshihet dhe kopjohet lidhja duke klikuar në lidhjet përkatëse",
          },
          conclusion: "Duke ndjekur këto hapa, ju do të jeni në gjendje të krijoni një URL të re dhe të personalizuar për qëllimin tuaj. Mund të krijohet sa më shumë URL të nevojshme për qëllimet tuaja specifike.",
          imageAlt: "Fushat e Listimit të Tabelës",
        },
      },

      // Integration Page
      integration: {
        title: "Integrimi",
        intro: "Moduli i Integrimit ju lejon të lidhni platformën tuaj të sondazhit me Eloqua dhe të hartoni fushat e formularit të udhëheqjes për sinkronizim të pandërprerë të të dhënave. Pasi të krijohet një hartim, dërgesat e formularit rrjedhin në fushatat tuaja Eloqua automatikisht.",
        
        accessingModule: {
          title: "Aksesimi i Modulit të Integrimit",
          steps: {
            step1: "Navigoni te <b>Sondazhi</b> i zgjedhur, më pas klikoni skedën <b>Integrim</b> brenda një Sondazhi.",
            step2: "Ju do të shihni statusin aktual të Formularit Eloqua dhe një listë të hartimeve të fushave ekzistuese.",
            step3: "Përdorni shirtin e kërkimit për të gjetur një hartim ose filtrin <b>Rendit sipas</b> për të ndryshuar rendin.",
          },
          imageAlt: "Pamja e Përgjithshme e Skedës së Integrimit",
        },

        listingPage: {
          title: "Kuptimi i Faqes së Listimit",
          intro: "Listimi shfaq kolonat e mëposhtme:",
          columns: {
            leadFormField: "<b>Fusha e Formularit të Udhëheqjes</b> — Fusha e përcaktuar në formularin tuaj të gjenerimit të udhëheqjes.",
            eloquaFormField: "<b>Fusha e Formularit Eloqua</b> — Fusha përkatëse e formularit Eloqua për sinkronizimin e të dhënave.",
            action: "<b>Veprim</b> — Fshi (🗑️) për të hequr hartimin.",
          },
          statusTitle: "Në krye të faqes, ju gjithashtu do të gjeni <b>statusin e Formularit Eloqua</b>:",
          status: {
            active: "<b>Aktiv</b> <span class=\"badge bg-success text-white\">Aktiv</span> — I aktivizuar dhe duke sinkronizuar të dhëna.",
            inactive: "<b>Joaktiv</b> <span class=\"badge bg-danger text-white\">Joaktiv</span> — I çaktivizuar; dërgesat nuk do të sinkronizohen.",
          },
        },

        toggleStatus: {
          title: "Ndërrimi i Statusit të Sinkronizimit Eloqua",
          intro: "Ju mund të aktivizoni ose çaktivizoni shpejt sinkronizimin Eloqua duke përdorur <b>listën rënëse të statusit</b> pranë titullit të faqes:",
          steps: {
            step1: "Zgjidhni <b>Aktiv</b> për të aktivizuar sinkronizimin e të dhënave të formularit.",
            step2: "Zgjidhni <b>Joaktiv</b> për të ndërprerë sinkronizimin pa fshirë hartimet.",
            step3: "Kur është <b>Joaktiv</b>, të gjitha hartimet ekzistuese ruhen, por asnjë e dhënë nuk dërgohet.",
          },
          imageAlt: "Ndërrimi i Statusit Eloqua",
        },

        addMapping: {
          title: "Shtimi i Hartimit të Ri",
          intro: "Për të shtuar një hartim të ri fushe, klikoni butonin e kuq <b>+ Shto Hartim</b> në këndin e sipërm djathtas:",
          steps: {
            step1: "<b>Fusha e Formularit të Udhëheqjes</b> — Zgjidhni fushën e formularit që dëshironi të hartoni nga lista rënëse.",
            step2: "<b>Fusha e Formularit Eloqua</b> — Futni emrin e saktë të fushës Eloqua në të cilën po hartoni.",
            step3: "Klikoni <b>Ruaj</b> për të krijuar hartimin ose <b>Anulo</b> për të hedhur.",
          },
          note: "<b>Shënim:</b> Sigurohuni që emri i fushës Eloqua të përputhet me atë të përcaktuar në fushatën tuaj Eloqua për të shmangur humbjen e të dhënave.",
        },

        deleteMapping: {
          title: "Fshirja e një Hartimi",
          intro: "Për të hequr një hartim ekzistues, klikoni <b>ikonën Fshi (🗑️)</b> në veprimin e rreshtit.",
          steps: {
            step1: "Do të shfaqet një dialog konfirmimi: <em>\"Konfirmo Fshirjen\"</em>",
            step2: "Klikoni <b>Konfirmo Fshirjen</b> për të hequr hartimin përgjithmonë.",
            step3: "Klikoni <b>Mbyll</b> për të anuluar dhe mbajtur hartimin.",
          },
          warning: "<b>Paralajmërim:</b> Hartimet e fshira nuk mund të rikuperohen. Ju do t'ju duhet t'i shtoni sërish manualisht.",
        },
      },

      // Webhooks Page
      webhooks: {
        title: "Webhooks",
        intro: "Moduli Webhook ju lejon të krijoni trigerë që dërgojnë të dhëna në sisteme të jashtme kur ndodhin ngjarje si dërgimi i formularëve. Ju mund të konfiguroni URL-të e webhook, parametrat dhe statusin përmes një ndërfaqeje të thjeshtë.",
        accessing: {
          title: "Aksesimi i Webhook",
          steps: {
            step1: "Navigoni te Sondazhet, zgjidhni një sondazh (p.sh., RCA Survey) dhe klikoni skedën Webhook.",
            step2: "Do të shihni një listë të webhooks ekzistuese, së bashku me opsionet për t'i shtuar, edituar ose fshirë.",
            step3: "Klikoni butonin e kuq + Shto Webhook për të hapur modalin e krijimit.",
          },
          imageAlt: "Modal Krijo Webhook",
        },
        create: {
          title: "Krijo Webhook të Ri",
          intro: "Në modalin Krijo Webhook të Ri, plotësoni detajet e mëposhtme:",
          fields: {
            name: "Emri* - Një emër për webhook.",
            eventsTitle: "Ngjarjet - Zgjidhni ngjarjen e trigerit për webhook nga menuja rënëse:",
            eventLeadForm: "Në dërgimin e formularit të lead - Aktivizohet kur dërgohet formulari i gjenerimit të lead.",
            eventResponse: "Në dërgimin e përgjigjes - Aktivizohet kur dërgohet një pyetje/përgjigje individuale.",
            eventSurvey: "Në dërgimin e sondazhit - Ekzekutohet kur përfundon dërgimi i plotë i sondazhit.",
            methodTitle: "Metoda* - Zgjidhni metodën HTTP për të thirrur endpoint-in tuaj.",
            methodPost: "POST - E rekomanduar. Dërgon të dhëna në trupin e kërkesës.",
            methodGet: "GET - Dërgon të dhëna si parametra në query string.",
            methodPutPatch: "PUT / PATCH - Semantikë përditësimi; trupi mbështetet.",
            methodDelete: "DELETE - Semantikë fshirjeje; query/trup sipas API-së suaj.",
            contentTypeTitle: "Content-Type* - Si kodifikohet payload.",
            contentJson: "application/json - Dërgon trup JSON (parazgjedhje për shumicën e API-ve).",
            contentForm: "application/x-www-form-urlencoded - Çifte çelës-vlerë të koduara në trup.",
            contentMultipart: "multipart/form-data - Përdoret kur ngarkohen skedarë ose pjesë të përziera.",
            contentText: "text/plain - Payload tekst i papërpunuar.",
            dataFormatTitle: "Formati i të Dhënave* - Ku/çfarë strukture përdorin të dhënat.",
            dataJson: "JSON Body - Çifte çelës-vlerë të serializuara si JSON në trupin e kërkesës.",
            dataForm: "Form Data - Çifte çelës-vlerë në trup (punon me x-www-form-urlencoded ose multipart/form-data).",
            dataQuery: "Query String - Çifte çelës-vlerë të bashkangjitura në URL (e zakonshme me GET).",
            dataFormatNote: "Opsionet e disponueshme mund të ndryshojnë në bazë të Metodës dhe Content-Type të zgjedhur.",
            url: "URL* - Endpoint-i i synuar ku webhook do të dërgojë të dhëna (duhet të jetë HTTPS).",
            description: "Përshkrimi - Shënime shtesë opsionale.",
            addParameters: "Shto Parametra* - Lejon të përcaktoni parametra çelës-vlerë që do të dërgohen me kërkesën e webhook.",
            addParametersStep1: "Shkruani një emër dhe zgjidhni llojin e parametrave nga menuja rënëse.",
            addParametersStep2: "Klikoni Shto për ta përfshirë në payload.",
            generatedUrl: "URL e Gjeneruar e Webhook - Shfaq URL-në përfundimtare me parametrat e shtuar.",
            status: "Statusi - Zgjidhni Aktiv ose Joaktiv duke përdorur butonin radio në fund.",
            actions: "Klikoni + Shto për të ruajtur ose Anulo për të hedhur poshtë ndryshimet.",
          },
          imageAlt: "Lista e Webhook",
        },
        listing: {
          title: "Listimi i Webhook",
          intro: "Të gjitha webhooks e krijuara do të shfaqen në një tabelë me fushat e mëposhtme:",
          columns: {
            webhook: "Webhook - Emri i webhook.",
            url: "URL - Një ikonë lidhjeje që tregon praninë e endpoint-it.",
            status: "Statusi - Tregon nëse webhook është Aktiv ose Joaktiv.",
            action: "Veprimi - Opsione për Edito dhe Fshi për të menaxhuar webhook.",
          },
          search: "Përdorni shiritin e kërkimit për të gjetur webhooks me emër.",
          sort: "Përdorni menunë rënëse Rendit sipas për të riorganizuar sipas më të fundit ose kritereve të tjera.",
          imageAlt: "Lista e Webhook",
        },
        activation: {
          title: "Statusi i Aktivizimit",
          intro: "Çdo webhook ka një status që mund të ndryshohet gjatë krijimit:",
          active: "Aktiv - Webhook do të aktivizohet kur ndodh ngjarja e zgjedhur.",
          inactive: "Joaktiv - Webhook do të ruhet por nuk do të ekzekutohet derisa të aktivizohet.",
          note: "Përdorni butonat radio për të vendosur këtë status përpara se të klikoni + Shto.",
        },
      },

      // Questionnaire Page
      questionnaire: {
        title: "Pyetësori",
        intro: "Paneli i Konfigurimit të Pyetësorit është një ndërfaqe për konfigurimin e pyetësorëve të bazuar në vlerësim. Ky panel u lejon përdoruesve të personalizojnë titujt e faqeve, butonat e navigimit, detajet e raportit dhe përmbajtjen e vlerësimit sipas fazave.",
        keyFeatures: {
          title: "Veçoritë Kryesore",
          pageCustomization: "Personalizimi i faqes - Vendosni Titullin e Faqes për të përcaktuar titullin e pyetësorit. Konfiguroni butonat Tjetri, Dërgo dhe Kthehu me tekst të personalizuar.",
          reportSection: "Seksioni i Raportit - Personalizoni Titullin e Faqes së Raportit dhe Titullin e Raportit. Përcaktoni faza të ndryshme të raportit.",
          stagesConfig: "Konfigurimi i Fazave - Përcaktoni faza të shumta (Faza 1, Faza 2, etj.). Jepni përshkrime për secilën fazë në fushat e tekstit.",
          save: "Funksionaliteti i ruajtjes - Një buton Ruaj u lejon përdoruesve të ruajnë konfigurimet e tyre.",
        },
        accessing: {
          title: "Aksesimi i Pyetësorit",
          steps: {
            step1: "Navigoni te Sondazhet dhe editoni çdo sondazh duke klikuar butonin Veprime nga tabela e listimit.",
            step2: "Klikoni në emrin e theksuar nga lista rënëse për të hapur faqen.",
          },
          imageAlt1: "Navigimi",
          imageAlt2: "Navigimi",
        },
        fields: {
          title: "Shpjegim i Detajuar i Fushave",
          pageHeading: {
            title: "Titulli i Faqes",
            desc1: "Kjo fushë u lejon përdoruesve të përcaktojnë titullin kryesor të pyetësorit.",
            desc2: "Shembull: Vodafone Business Rapid Cloud Assessment.",
            imageAlt: "Titulli i Faqes",
          },
          buttonConfig: {
            title: "Konfigurimi i Butonave",
            next: "Butoni Tjetri: Teksti që shfaqet për të kaluar në hapin tjetër.",
            submit: "Butoni Dërgo: Tekst i personalizueshëm për dërgim (p.sh., Merrni rekomandimin tuaj të personalizuar).",
            back: "Butoni Kthehu: Përcakton tekstin për butonin e kthimit prapa.",
            imageAlt: "Konfigurimi i Butonave",
          },
          reportSection: {
            title: "Seksioni i Raportit",
            reportPageHeading: "Titulli i Faqes së Raportit: Titulli për raportin e gjeneruar pas përfundimit të pyetësorit.",
            reportHeading: "Titulli i Raportit: Titulli kryesor i raportit.",
            stageField: "Fusha e Fazës: Një fushë përshkrimi e përgjithshme për procesin e fazës.",
            imageAlt: "Seksioni i Raportit",
          },
          stages: {
            title: "Fazat",
            desc1: "Lejon konfigurimin e fazave individuale në raport.",
            desc2: "Çdo fazë (Faza 1, Faza 2, etj.) ka një fushë teksti të dedikuar për të futur përshkrimet relevante.",
            imageAlt: "Fazat",
          },
          saveButton: {
            title: "Butoni Ruaj",
            desc1: "I vendosur në fund.",
            desc2: "Klikimi i Ruaj ruan të gjitha konfigurimet e vendosura.",
            imageAlt: "Butoni",
          },
          conclusion: "Paneli i Konfigurimit të Pyetësorit ofron një mënyrë të strukturuar për të përcaktuar përmbajtjen, navigimin dhe detajet e raportit. Ai siguron fleksibilitet duke lejuar personalizimin e etiketave të butonave, titujve dhe fazave.",
        },
      },

      // Thank You Page
      thankYouPage: {
        title: "Faqja e Falënderimit",
        intro: "Kjo faqe siguron që përdoruesit të marrin një përvojë të personalizuar pas përfundimit të një vlerësimi duke shfaqur dinamikisht të dhënat e tyre dhe duke ofruar akses në raporte.",
        accessing: {
          title: "Aksesimi i Faqes së Falënderimit",
          steps: {
            step1: "Navigoni te Sondazhet dhe editoni çdo sondazh duke klikuar butonin Veprime nga tabela e listimit.",
            step2: "Klikoni në emrin e theksuar nga lista rënëse për të hapur faqen.",
          },
          imageAlt1: "Navigimi",
          imageAlt2: "Navigimi",
        },
        keyElements: {
          title: "Elementët Kryesorë",
          pageHeading: {
            title: "Titulli i Faqes",
            items: {
              item1: "Përdor placeholder-a dinamikë (#[leadForm:firstName]#, #[leadForm:lastName]#, #[leadForm:company]#, #[maturity_stage]#) për të personalizuar mesazhin me të dhënat e përdoruesit.",
              item2: "Shembull: John Doe, kompania juaj ABC Corp është në fazën Growth",
            },
          },
          pageDescription: {
            title: "Përshkrimi i Faqes",
            items: {
              item1: "Shfaq një mesazh konfirmimi pas përfundimit të sondazhit.",
              item2: "Shembull: Raporti juaj i personalizuar është i disponueshëm! Eksploroni të dhënat dhe rekomandimet tuaja sot.",
            },
          },
          buttons: {
            title: "Butonat",
            items: {
              item1: "Butoni Shiko Raportin: Lejon përdoruesit të shohin raportin e gjeneruar.",
              item2: "Butoni Shkarko Raportin: Lejon përdoruesit të shkarkojnë raportin.",
            },
          },
          saveButton: {
            title: "Butoni Ruaj",
            items: {
              item1: "Ruan ndryshimet e bëra në këtë konfigurim të Faqes së Falënderimit.",
            },
          },
          conclusion: "Kjo faqe siguron që përdoruesit të marrin një përvojë të personalizuar pas përfundimit të një vlerësimi duke shfaqur dinamikisht të dhënat e tyre dhe duke ofruar akses në raporte.",
          imageAlt: "Elementët Kryesorë",
        },
      },

      // Privacy Policy Page
      privacyPolicy: {
        title: "Politika e Privatësisë",
        intro: "Konfiguroni dhe mirëmbani përmbajtjen e politikës së privatësisë që u shfaqet përgjigjësve. Ky editor mbështet tekst të pasur, lidhje, ngjyra dhe tabela.",
        accessing: {
          title: "Aksesimi i Politikës së Privatësisë",
          steps: {
            step1: "Shkoni te Sondazhet → Veprimet → Edito Sondazhin.",
            step2: "Hapni Konfigurimin dhe zgjidhni Politikën e Privatësisë.",
          },
          imageAlt: "Navigimi i politikës së privatësisë",
        },
        editorBasics: {
          title: "Bazat e Editorit",
          formattingTitle: "Formatimi",
          formattingItems: {
            item1: "Përdorni Bold, Italic, Underline dhe Tituj (H1–H3) për strukturë.",
            item2: "Krijoni lista me Listë me pika dhe Listë të numëruar.",
            item3: "Futni lidhje duke përdorur butonin Link.",
          },
          tablesTitle: "Tabelat",
          tableItems: {
            item1: "Futni një tabelë përmes butonit Table dhe më pas shtoni tekst në qeliza.",
            item2: "Përdoreni për të strukturuar seksione si kategoritë e të dhënave dhe periudhat e ruajtjes.",
          },
          imageAlt: "Tabela e editorit të politikës",
        },
        saving: {
          title: "Ruajtja e Ndryshimeve",
          items: {
            item1: "Klikoni Ruaj për të ruajtur ndryshimet në politikën e privatësisë së sondazhit aktual.",
            item2: "Përmbajtja e ruajtur do të shfaqet kudo që politika e privatësisë renderohet për përgjigjësit.",
          },
        },
      },

      // Create Survey Page
      createSurvey: {
        title: "Krijo Sondazh",
        intro: "Duke ndjekur këto hapa, mund të krijoni një pyetësor të ri dhe ta personalizoni sipas kërkesave tuaja. Ky udhëzues përdoruesi ofron udhëzime hap pas hapi se si të krijoni një pyetësor të ri duke përdorur platformën e dhënë.",
        guide: {
          title: "Udhëzuesi i Përdoruesit: Krijo Sondazh të Ri",
          steps: {
            step1Title: "Hapi 1: Krijo Sondazh të Ri",
            step1Desc: "Klikoni butonin Krijo Sondazh të Ri për të nisur procesin e krijimit të një pyetësori të ri nga faqja Lista e Sondazheve.",
            step2Title: "Hapi 2: Emërtoni Sondazhin",
            step2Desc: "Kjo fushë e detyrueshme përdoret për të vendosur një emër të përshtatshëm për sondazhin. Klikoni butonin Tjetri për të vazhduar.",
            step3Title: "Hapi 3: Përshkrim i shkurtër i sondazhit",
            step3Desc: "Shkruani një përshkrim të shkurtër të sondazhit tuaj dhe klikoni Tjetri për të vazhduar. Kjo fushë është gjithashtu e detyrueshme.",
            step4Title: "Hapi 4: Numri i seksioneve në sondazh",
            step4Desc: "Specifikoni numrin e seksioneve që dëshironi të përfshini dhe klikoni Tjetri për të vazhduar.",
            step4Note: "Shënim - Numri duhet të jetë më i madh se 0 dhe kjo fushë është e detyrueshme.",
            step5Title: "Hapi 5: Emrat dhe përshkrimi i seksioneve",
            step5Desc: "Vendosni emrat e secilit seksion që dëshironi të përfshini dhe specifikoni përshkrimin.",
            step5Note: "Shënim - Vendosja e emrit të seksionit është e detyrueshme, por përshkrimi është opsional.",
            step6Title: "Hapi 6: Zgjidh gjuhën",
            step6Desc1: "Zgjidhni gjuhën e dëshiruar për sondazhin nga opsionet e disponueshme. Mund të zgjidhni edhe shumë gjuhë. Duke dërguar sondazhin, ai do të shtohet në listën e sondazheve.",
            step6Desc2: "Klikoni butonin Dërgo për të kaluar në fazën tjetër. Sondazhi krijohet dhe shfaqet në krye të Listës së Sondazheve.",
            step6Note: "Shënim - Si parazgjedhje, zgjidhet gjuha angleze. Nëse përdoruesit duhet të modifikojnë ndonjë detaj ose ndjejnë se diçka është humbur në hapat e mëparshëm, mund ta bëjnë përmes veçorisë Veprime në tabelën e listës së sondazheve.",
            step7Title: "Hapi 7: Menaxhoni sondazhin në faqen e panelit",
            step7Desc: "Për të aksesuar dhe menaxhuar sondazhin, shkoni te faqja e panelit. Prej aty mund të bëni editime shtesë, ta ndani sondazhin ose ta kopjoni për raste të ndryshme përdorimi.",
          },
          imageAlt1: "Krijo Sondazh të Ri",
          imageAlt2: "Emërtoni Sondazhin",
          imageAlt3: "Përshkrim i shkurtër",
          imageAlt4: "Numri i seksioneve",
          imageAlt5: "Emrat e seksioneve",
          imageAlt6: "Zgjidh gjuhën",
          imageAlt7: "Menaxho Sondazhin",
        },
      },

      // Recommendations Page
      recommendations: {
        title: "Rekomandime të Pyetjeve dhe Fazave me Variabla të Personalizuara",
        overview: {
          title: "Shto rekomandime në nivel pyetjeje me variabla të personalizuara",
          intro1: "Variablat e personalizuara janë çifte të thjeshta emër‑vlerë që mund t'i shfaqni kudo me #[fieldName]#. Rekomandimi është vlera e ruajtur në një nga këto variabla. Kur dikush përgjigjet në Ndërtuesin e P&P, ju vendosni se cila variabël merr cilin tekst.",
          intro2: "Krijoni një Variabël të Personalizuar për pyetjen, p.sh. #[response_1_recommendation]#. Në Ndërtuesin e P&P, lidheni këtë variabël me opsionet dhe caktoni një tekst rekomandimi të ndryshëm për secilin opsion. Kur një respondent zgjedh një opsion, teksti i atij opsioni ruhet në variabël. Në raport, vendosni placeholder‑in #[response_1_recommendation]# dhe do të shfaqet rekomandimi i zgjedhur.",
          exampleTitle: "Shembull:",
          exampleIntro: "Supozoni se keni Pyetjen 1 me opsionet dhe tekstet:",
          exampleOptions: {
            optionA: "Opsioni A — Kryeni një vlerësim bazë brenda 30 ditësh.",
            optionB: "Opsioni B — Zgjeroni fushën për të përfshirë rrezikun e palëve të treta.",
            optionC: "Opsioni C — Planifikoni një auditim të plotë dhe plan të boshllëqeve.",
          },
          variableName: "Emri i variablit: #[response_1_recommendation]#",
          linkText: "Lidhni #[response_1_recommendation]# me çdo opsion në Ndërtuesin e P&P dhe vendosni tekstin përkatës si vlerë.",
        },
        createVariables: {
          title: "Krijoni variabla të personalizuara për rekomandime",
          intro: "Në këtë hap krijoni variablat që do të mbajnë tekstin e rekomandimit. Klikoni Shto Variabël të Re. Do të shfaqet një popup me të njëjtin formular si në ikonën e editimit.",
          steps: {
            step1: "Shkoni te Variablat e Personalizuara → Shto Variabël të Re.",
            step2Title: "Plotësoni formularin:",
            fields: {
              label: "Etiketë — shtoni një etiketë të lexueshme (maks. 250 karaktere).",
              type: "Lloji — zgjidhni: numër, tekst me një rresht, ose tekst me shumë rreshta. (Përdorni shumë rreshta për paragrafë/lista.)",
              variableName: "Emri i variablit — vendosni një emër unik pa hapësira, maks. 50 karaktere. Emrat janë të ndjeshëm ndaj shkronjave. Shembull: #[response_1_recommendation]#.",
              description: "Përshkrimi — shpjegoni shkurtimisht ku dhe si do të përdoret variabli.",
            },
            step3: "Klikoni Shto Variabël të Re për të ruajtur. Variabli do të shfaqet në listë.",
          },
        },
        linkOptions: {
          title: "Lidhni variablat me opsionet në Ndërtuesin e P&P",
          intro: "Pasi të keni krijuar variablat, hapi tjetër është t'i lidhni me pyetjet dhe opsionet në Ndërtuesin e P&P. Këtu vendoset se cili rekomandim shfaqet në raport.",
          steps: {
            step1: "Hapni Sondazhet → Ndërtuesi i P&P dhe zgjidhni sondazhin.",
            step2: "Gjeni pyetjen dhe opsionet e përgjigjeve.",
            step3: "Në anën e djathtë të opsionit, klikoni ikonën C për të lidhur variabla.",
            step4Title: "Në dialog, klikoni Shto Variabël. Do të specifikoni:",
            step4Items: {
              selectVariable: "Zgjidh variablin: p.sh. #[response_1_recommendation]#.",
              enterValue: "Shkruani vlerën: tekstin e rekomandimit që duhet të shfaqet në raport.",
            },
            step5: "Klikoni Ruaj. Variabli dhe vlera shfaqen nën opsion.",
            reuseTitle: "Mund të përdorni të njëjtin variabël në disa opsione.",
            reuseItems: {
              item1: "Vendosni tekst rekomandimi të ndryshëm për secilin opsion.",
              item2: "Kur zgjidhet opsioni, teksti ruhet në variabël.",
            },
          },
        },
        stageRecommendations: {
          title: "Shto rekomandime në nivel faze me variabla të personalizuara",
          intro: "Rekomandimet në nivel faze caktohen për fazën e përgjithshme. Variablat mbushen me tekstin tuaj kur rezultati bie në atë fazë.",
          howToTitle: "Si të shtoni rekomandime në nivel faze:",
          steps: {
            step1: "Shkoni te Fazat dhe klikoni Shto Fazë.",
            step2: "Vendosni Emrin dhe Përshkrimin.",
            step3: "Zgjidhni një formulë If nga dropdown‑i.",
            step4: "Përcaktoni intervalin e pikëve (p.sh., 40–69).",
            step5Title: "Te Variablat & Rekomandimet, klikoni + për të shtuar variabla:",
            step5Items: {
              item1: "Zgjidhni variablin (p.sh. #[response_1_recommendation]#).",
              item2: "Shkruani tekstin e rekomandimit.",
              item3: "Përsëritni + për variabla të tjera.",
              item4: "Përdorni – për të hequr një variabël.",
            },
            step6: "Klikoni Ruaj. Faza ruan rekomandimet në variablat e mapuara.",
          },
          editTitle: "Editimi i rekomandimeve në nivel faze:",
          editItems: {
            item1: "Hapni Fazat dhe klikoni Edito.",
            item2: "Shtoni ose hiqni variabla me + dhe –.",
            item3: "Përditësoni tekstin e rekomandimit.",
            item4: "Klikoni Ruaj për të aplikuar ndryshimet.",
          },
          deleteTitle: "Fshirja e një faze:",
          deleteItems: {
            item1: "Shkoni te Fazat dhe klikoni Fshi.",
            item2: "Konfirmoni fshirjen — hiqen faza dhe mapimet.",
          },
          conclusion: "Duke përdorur variabla të personalizuara në nivel faze, rekomandimet bëhen më strategjike dhe të lidhura me progresin e përgjithshëm.",
        },
      },

      // Survey Groups Page
      surveyGroups: {
        title: "Grupet e Sondazheve",
        intro: "Grupet e Sondazheve lejojnë kombinimin e versioneve të ndryshme të gjuhës në një link. Respondentët mund të ndërrojnë gjuhën gjatë plotësimit.",
        createGroup: {
          title: "1. Krijimi i një Grupi Sondazhi",
          beforeStart: "Para se të filloni: sigurohuni që keni krijuar sondazhe individuale për çdo gjuhë.",
          createTitle: "Si të krijoni një grup:",
          steps: {
            step1: "Shkoni te Sondazhet → Grupet e Sondazheve.",
            step2: "Klikoni butonin Krijo Grup.",
            step3: "Plotësoni detajet e grupit.",
            step4: "Klikoni Shto Sondazh për të përzgjedhur sondazhet.",
            step5: "Rregull i rëndësishëm: vetëm një sondazh për gjuhë.",
            step6: "Shtoni të gjitha versionet e gjuhës së nevojshme.",
            step7: "Klikoni Krijo Grup për të ruajtur.",
          },
          imageAlt1: "Krijo grup sondazhi",
          listFieldsTitle: "Fushat e listës së grupeve:",
          listFields: {
            groupName: "Emri i Grupit — Emri i grupit në listë.",
            totalSurveys: "Totali i Sondazheve — Numri i versioneve të gjuhës.",
            addedOn: "Shtuar më / nga — Kur dhe nga kush u krijua grupi.",
            action: "Veprim — Menu me tre pika (⋮):",
            actionItems: {
              rename: "Riemërto — Ndryshon emrin (URL nuk ndryshon)",
              delete: "Fshi Grupin — Fshin grupin përgjithmonë",
            },
          },
          renameTitle: "Si të riemërtoni një grup:",
          renameSteps: {
            step1: "Nga lista e grupeve, gjeni grupin.",
            step2: "Klikoni Veprime → Riemërto.",
            step3: "Vendosni emrin e ri.",
            step4: "Klikoni Ruaj.",
          },
          deleteTitle: "Si të fshini një grup:",
          deleteSteps: {
            step1: "Nga lista e grupeve, gjeni grupin.",
            step2: "Klikoni Veprime → Fshi Grupin.",
            step3: "Paralajmërimi shpjegon:",
            step4: "Klikoni Konfirmo Fshirjen.",
          },
          deleteItems: {
            item1: "Grupi fshihet përgjithmonë",
            item2: "Të gjitha linket ndalojnë së punuari",
            item3: "Sondazhet individuale NUK fshihen",
          },
          imageAlt2: "Fshi grup sondazhi",
        },
        manageGroup: {
          title: "2. Menaxhimi i Sondazheve në Grup",
          intro: "Pas krijimit, mund të shtoni sondazhe, t'i rirenditni, vendosni sondazhin kryesor dhe t'i hiqni.",
          addSurveysTitle: "Shtimi i sondazheve në grup:",
          addSurveysSteps: {
            step1: "Shkoni te Sondazhet → Sondazhet.",
            step2: "Zgjidhni sondazhet duke i shënuar.",
            step2Items: {
              item1: "Mund të zgjidhni shumë sondazhe.",
              item2: "Vetëm një sondazh për gjuhë për grup.",
            },
            step3: "Klikoni Shto në Grup.",
            step3Items: {
              item1: "Butoni shfaqet vetëm kur ka zgjedhje.",
            },
            step4: "Hapet dropdown me kërkim dhe lista të grupeve.",
            step5: "Opsionale: përdorni kërkimin.",
            step6: "Klikoni emrin e grupit.",
            step7: "Klikoni Shto.",
            step8: "Sondazhet u shtuan në grup.",
          },
          imageAlt3: "Shto sondazhe në grup",
          manageTitle: "Menaxhimi i sondazheve brenda grupit:",
          manageIntro: "Për të menaxhuar (rirenditje, kryesor, heqje, leads):",
          manageSteps: {
            step1: "Shkoni te Sondazhet → Grupet e Sondazheve.",
            step2: "Klikoni emrin e grupit.",
            step3: "Hapet faqja e detajeve me tabelë.",
            step4: "Ka dy skeda: Sondazh dhe Variablat e Fushatës UTM.",
            step4Items: {
              item1: "Sondazh — Menaxhoni sondazhet",
              item2: "Variablat UTM — Variabla gjurmimi",
            },
            step5: "Në skedën Sondazh shfaqen:",
            fields: {
              surveyName: "Emri i Sondazhit — Emri me badge (Primary).",
              completion: "Përfundim / Përgjigje — Përfunduar vs total.",
              language: "Gjuha — Gjuha e sondazhit.",
              status: "Statusi — Published ose Draft.",
              assignTeam: "Ekipi — Ekipi i caktuar.",
              addedOn: "Shtuar më / nga — Data dhe autori.",
              responses: "Përgjigje — Numri total.",
              publishedOn: "Publikuar më — Data e publikimit.",
              action: "Veprim — Menu (⋮) me:",
              actionItems: {
                primary: "Bëje Primary — Gjuha e paracaktuar",
                viewLeads: "Shiko Leads — Shiko përgjigjet",
                remove: "Hiq nga Grupi — Heq sondazhin",
              },
            },
          },
          imageAlt4: "Menaxho sondazhet në grup",
          utmTitle: "Shtimi i Variablave të Fushatës UTM:",
          utmIntro: "Variablat UTM ndihmojnë të gjurmoni burimin e përgjigjeve.",
          utmSteps: {
            step1: "Në faqen e grupit, klikoni skedën Variablat UTM.",
            step2: "Do të shihni Source, Medium dhe Campaign.",
            step3: "Klikoni + Shto për të shtuar variabla.",
            step4: "Shkruani vlerën (p.sh., facebook, social, spring_sale).",
            step5: "Klikoni jashtë ose shtypni Tab për të ruajtur.",
            step6: "Për të fshirë, klikoni ikonën − dhe konfirmoni.",
            step7: "Këto vlera shfaqen kur krijoni linke UTM.",
          },
          imageAlt5: "Variablat UTM",
        },
        links: {
          title: "3. Gjenerimi dhe ndarja e linkeve të grupit",
          intro: "Çdo grup ka një link unik. Mund të ndani linkun bazë ose të krijoni linke me UTM.",
          basicTitle: "Merrni linkun bazë:",
          basicSteps: {
            step1: "Nga lista e grupeve, klikoni Share Survey Group.",
            step2: "Shfaqet Base Link: https://yourplatform.com/group/your-group-slug",
            step3: "Klikoni Copy Link.",
          },
          imageAlt6: "Ndani linkun e grupit",
          campaignTitle: "Krijoni linke fushate me UTM:",
          campaignIntro: "Parametrat UTM ndihmojnë të gjurmoni burimin e përgjigjeve.",
          campaignSteps: {
            step1: "Në Share Survey Group, klikoni Create New URL.",
            step2: "Plotësoni fushat e fushatës:",
            fields: {
              name: "Name* — Emër i brendshëm",
              source: "Source* — Burimi i trafikut",
              medium: "Medium* — Kanali i marketingut",
              campaign: "Campaign* — Emri i fushatës",
              content: "Content — Opsionale",
            },
            step3: "Opsionale: klikoni Add Parameters.",
            step4: "Klikoni Create.",
            step5: "Gjenerohet linku UTM.",
            step6: "Kopjoni dhe ndani linkun.",
          },
          imageAlt7: "Krijo link fushate",
          whatHappensTitle: "Çfarë ndodh kur hapet linku:",
          whatHappensItems: {
            item1: "Sondazhi Primary ngarkohet i pari.",
            item2: "Shfaqet ndërruesi i gjuhës.",
            item3: "Gjuha mund të ndërrohet pa rifreskim.",
          },
          imageAlt8: "Ndërruesi i gjuhës",
        },
        analytics: {
          title: "4. Shiko Analytics për Grupet",
          intro: "Grupet kanë analytics të veçantë, me përgjigje të kombinuara ose sipas gjuhës.",
          accessTitle: "Si të aksesoni analytics:",
          accessSteps: {
            step1: "Shkoni te Analytics.",
            step2: "Në dropdown Select Survey shfaqen: Individual Surveys dhe Survey Groups.",
            step2Items: {
              item1: "Individual Surveys — Sondazhe normale",
              item2: "Survey Groups — Grupet",
            },
            step3: "Zgjidhni grupin për të parë analytics.",
          },
          whatYouSeeTitle: "Çfarë mund të shihni:",
          combined: "Pamje e kombinuar — Përgjigje totale dhe metrika në të gjitha gjuhët.",
          byLanguageTitle: "Sipas gjuhës — filtroni për:",
          byLanguageItems: {
            item1: "Përgjigje sipas gjuhës",
            item2: "Norma përfundimi sipas gjuhës",
            item3: "Performanca e pyetjeve sipas gjuhës",
          },
          imageAlt9: "Analytics i grupit",
        },
      },

      // Settings - Account Settings
      accountSettings: {
        title: "Profili",
        intro: "Faqja Profili ju lejon të menaxhoni informacionin personal, duke përfshirë foton e profilit, emailin, emrin e përdoruesit, rajonin, zonën kohore dhe gjuhën e paracaktuar.",
        accessing: {
          title: "Aksesimi i Profilit",
          intro: "Për të hapur faqen Profili:",
          steps: {
            step1: "Klikoni <b>Cilësimet</b> në shiritin anësor kryesor.",
            step2: "Nën <b>Cilësimet e përgjithshme</b>, klikoni <b>Profili</b>.",
            step3: "Faqja hapet në <b>/admin/settings/profile</b>.",
          },
          imageAlt: "Menuja e cilësimeve me Profili",
        },
        availableFields: {
          title: "Fushat dhe Funksionet e Disponueshme",
          generalInfo: {
            title: "Informacion i Përgjithshëm",
            profilePhoto: "Foto e profilit: ngarkoni, ndryshoni ose hiqni foton përmes kontrolleve të avatarit.",
            email: "Email: shfaq emailin e regjistruar të përdoruesit.",
            userName: "Emri i përdoruesit: emri i shfaqjes, i modifikueshëm.",
            imageAlt: "Formulari i llogarisë me foto profili dhe fusha",
          },
          localization: {
            title: "Cilësimet e Lokalizimit",
            region: "Rajoni: zgjidhni rajonin gjeografik nga lista.",
            country: "Shteti: zgjidhni një shtet nga opsionet.",
            timeZone: "Zona kohore: vendosni zonën kohore të preferuar.",
            defaultLanguage: "Gjuha e paracaktuar: zgjidhni gjuhën e ndërfaqes.",
          },
          savingChanges: {
            title: "Ruajtja e Ndryshimeve",
            step1: "Pasi të bëhen ndryshimet, klikoni butonin 'Ruaj Ndryshimet' për të përditësuar cilësimet e llogarisë.",
            step2: "Nëse mungojnë fushat e detyrueshme ose janë të pavlefshme, do të shfaqet një mesazh gabimi.",
            imageAlt: "Butoni ruaj ndryshimet",
          },
        },
        assignedTeamsRoles: {
          title: "Ekipet dhe Rolet e Caktuara",
          intro: "Ky seksion shfaq listën e ekipeve ku përdoruesi bën pjesë së bashku me rolet e caktuara. Ai shfaqet në fund të formularit të Cilësimeve të Llogarisë për referencë të shpejtë.",
          teams: "Ekipet: Tregon emrin e secilit ekip ku bën pjesë përdoruesi.",
          roles: "Rolet: Shfaq rolin përkatës të përdoruesit brenda atij ekipi (p.sh., Admin, Editor).",
          conclusion: "Ky seksion nuk është i redaktueshëm dhe është vetëm për qëllime informative, duke ofruar shikueshmëri mbi aksesin në ekipe dhe lejet e caktuara për përdoruesin.",
          imageAlt: "Ekipet dhe rolet e caktuara",
        },
      },

      securitySettings: {
        title: "Cilësimet e Sigurisë",
        intro: "Kontrollon sjelljen e hyrjes për të gjithë përdoruesit: SSO Microsoft, MFA, bllokimi i llogarisë dhe skadimi i fjalëkalimit.",
        accessing: {
          title: "Aksesimi i Cilësimeve të Sigurisë",
          steps: {
            step1: "Klikoni <b>Cilësimet</b> në shiritin anësor kryesor.",
            step2: "Nën <b>Cilësimet e përgjithshme</b>, klikoni <b>Siguria</b> (kërkon <b>M9 view</b>).",
            step3: "Faqja hapet në <b>/admin/settings/security</b>.",
          },
          imageAlt: "Menuja me Siguria",
        },
        options: {
          title: "Opsionet e Sigurisë",
          imageAlt: "Faqja e sigurisë me çelësa",
          items: {
            sso: "SSO Microsoft — shfaq ose fshih butonin Hyr me Microsoft.",
            mfa: "Autentifikim multifaktor (MFA) — kod 6-shifror me email pas fjalëkalimit.",
            lockout: "Bllokimi i llogarisë — blloko pas përpjekjeve të dështuara.",
            passwordExpiry: "Skadimi i fjalëkalimit — detyro ndryshimin pas X ditësh.",
            save: "Klikoni <b>Ruaj ndryshimet</b> për të aplikuar.",
          },
        },
        permissionsNote: "Kërkon M9 view për të hapur dhe M9 edit për të ruajtur.",
      },

      ticketNotificationSettings: {
        title: "Njoftimet e Biletave",
        intro: "Konfiguroni kush merr email kur krijohen ose caktohen bileta.",
        accessing: {
          title: "Aksesimi i Njoftimeve të Biletave",
          steps: {
            step1: "Klikoni <b>Cilësimet</b> në shiritin anësor kryesor.",
            step2: "Nën <b>Cilësimet e përgjithshme</b>, klikoni <b>Njoftimet</b> (kërkon <b>M10 view</b>).",
            step3: "Faqja hapet në <b>/admin/settings/ticket-notifications</b>.",
          },
          imageAlt: "Menuja me Njoftimet e biletave",
        },
        recipients: {
          title: "Marrësit",
          imageAlt: "Konfigurimi i marrësve të njoftimeve",
          items: {
            search: "Kërkoni përdorues sipas emrit ose emailit dhe shtoni si marrës.",
            add: "Të zgjedhurit shfaqen si chips në listë.",
            remove: "Hiqni me kontrollin e fshirjes në çdo chip.",
            save: "Klikoni <b>Ruaj</b> për të ruajtur listën.",
          },
        },
        permissionsNote: "Kërkon M10 view për të hapur dhe M10 edit për të ruajtur.",
      },

      organizationDetails: {
        privacyPolicy: {
          title: "Politika e Privatësisë",
          intro: "Editoni Politikën e Privatësisë së organizatës në faqen e hyrjes. Është e ndarë nga politikat për sondazh.",
          accessing: {
            title: "Aksesimi i Politikës së Privatësisë",
            imageAlt: "Menuja me Politika e privatësisë të zgjedhur",
            steps: {
              step1: "Klikoni <b>Cilësimet</b> në shiritin anësor kryesor.",
              step2: "Nën <b>Detajet e organizatës</b>, klikoni <b>Politika e privatësisë</b>.",
              step3: "Faqja hapet në <b>/admin/settings/organization-details/privacy-policy</b>.",
            },
          },
          editor: {
            title: "Redaktimi i Politikës së Privatësisë",
            imageAlt: "Redaktori i politikës së privatësisë",
            items: {
              toolbar: "Shiriti i formatimit: të trasha, tituj, lista, lidhje, tabela dhe ngjyrë.",
              content: "Editoni tekstin e politikës së privatësisë në redaktor.",
              save: "Klikoni <b>Ruaj ndryshimet</b> për të publikuar në faqen e hyrjes.",
            },
          },
          permissionsNote: "Kërkon M8 view për të hapur dhe M8 edit për të ruajtur.",
        },
        termsOfUse: {
          title: "Kushtet e Përdorimit",
          intro: "Editoni Kushtet e Përdorimit të organizatës në faqen e hyrjes.",
          accessing: {
            title: "Aksesimi i Kushteve të Përdorimit",
            imageAlt: "Menuja me Kushtet e përdorimit të zgjedhura",
            steps: {
              step1: "Klikoni <b>Cilësimet</b> në shiritin anësor kryesor.",
              step2: "Nën <b>Detajet e organizatës</b>, klikoni <b>Kushtet e përdorimit</b>.",
              step3: "Faqja hapet në <b>/admin/settings/organization-details/terms-of-use</b>.",
            },
          },
          editor: {
            title: "Redaktimi i Kushteve të Përdorimit",
            imageAlt: "Redaktori i kushteve të përdorimit",
            items: {
              toolbar: "Shiriti i formatimit: të trasha, tituj, lista, lidhje, tabela dhe ngjyrë.",
              content: "Editoni tekstin e kushteve të përdorimit në redaktor.",
              save: "Klikoni <b>Ruaj ndryshimet</b> për të publikuar në faqen e hyrjes.",
            },
          },
          permissionsNote: "Kërkon M8 view për të hapur dhe M8 edit për të ruajtur.",
        },
        cookiePolicy: {
          title: "Politika e Cookies",
          intro: "Editoni Politikën e Cookies të organizatës në faqen e hyrjes. Është e ndarë nga politikat për sondazh.",
          accessing: {
            title: "Aksesimi i Politikës së Cookies",
            imageAlt: "Menuja me Politika e cookies të zgjedhur",
            steps: {
              step1: "Klikoni <b>Cilësimet</b> në shiritin anësor kryesor.",
              step2: "Nën <b>Detajet e organizatës</b>, klikoni <b>Politika e cookies</b>.",
              step3: "Faqja hapet në <b>/admin/settings/organization-details/cookie-policy</b>.",
            },
          },
          editor: {
            title: "Redaktimi i Politikës së Cookies",
            imageAlt: "Redaktori i politikës së cookies",
            items: {
              toolbar: "Shiriti i formatimit: të trasha, tituj, lista, lidhje, tabela dhe ngjyrë.",
              content: "Editoni tekstin e politikës së cookies në redaktor.",
              save: "Klikoni <b>Ruaj ndryshimet</b> për të publikuar në faqen e hyrjes.",
            },
          },
          permissionsNote: "Kërkon M8 view për të hapur dhe M8 edit për të ruajtur.",
        },
      },

      // Settings - Users
      users: {
        title: "Përdoruesit",
        accessing: {
          title: "Aksesimi i Përdoruesve",
          intro: "Navigimi te menaxhimi i përdoruesve:",
          steps: {
            step1: "Klikoni <b>Cilësimet</b> në shiritin anësor kryesor.",
            step2: "Nën <b>Menaxhimi i përdoruesve</b>, klikoni <b>Përdoruesit</b> (kërkon <b>M3 view</b>).",
            step3: "Faqja hapet në <b>/admin/settings/user-permissions</b>.",
          },
          imageAlt: "Menuja me Përdoruesit",
        },
        search: {
          title: "Kërkimi i një Përdoruesi",
          steps: {
            step1: "Përdorni shiritin e kërkimit në krye për të gjetur një përdorues specifik me emër ose email.",
            step2: "Lista do të përditësohet dinamikisht ndërsa shkruani.",
          },
          imageAlt: "Shiriti i kërkimit të përdoruesve",
        },
        listOverview: {
          title: "Përmbledhja e Listës së Përdoruesve",
          intro: "Lista e përdoruesve shfaq kolonat e mëposhtme:",
          columns: {
            name: "Emri - Shfaq emrin e përdoruesit së bashku me inicialet në një rreth me ngjyrë.",
            defaultLanguage: "Gjuha e paracaktuar - Shfaq gjuhën e vendosur për përdoruesin.",
            timezone: "Zona kohore - Tregon zonën kohore të zgjedhur nga përdoruesi.",
            actions: "Veprimet - Ofron opsione për të edituar ose fshirë përdoruesin.",
          },
          imageAlt: "Tabela e përdoruesve",
          actionsImageAlt: "Veprimet e përdoruesit",
        },
        editing: {
          title: "Editimi i një Përdoruesi",
          steps: {
            step1: "Klikoni ikonën Edito (laps) në kolonën Veprim.",
            step2: "Modifikoni detajet e përdoruesit si emri, emaili, gjuha dhe zona kohore.",
            step3: "Klikoni Ruaj për të aplikuar ndryshimet.",
          },
          imageAlt: "Edito përdoruesin",
        },
        deleting: {
          title: "Fshirja e një Përdoruesi",
          steps: {
            step1: "Klikoni ikonën Fshi (kosh) në kolonën Veprim.",
            step2: "Do të shfaqet një popup konfirmimi.",
            step3: "Klikoni Konfirmo për të hequr përdoruesin përgjithmonë.",
          },
          imageAlt: "Fshi përdoruesin",
        },
        addUser: {
          title: "Shtimi i një Përdoruesi të Ri",
          steps: {
            step1: "Klikoni butonin + Krijo Përdorues (butoni i kuq në këndin e sipërm djathtas).",
            step2: "Do të shfaqet një formular ku mund të vendosni:",
          },
          saveAction: "Klikoni Ruaj për të krijuar përdoruesin.",
          fields: {
            fullName: "Emri i plotë - Shfaq emrin e plotë të përdoruesit.",
            email: "Adresa e emailit - Një përdorues i ri do të duhet të vendosë një fjalëkalim",
            password: "Fjalëkalimi - Shfaq një fushë fjalëkalimi të maskuar (●●●●●). Klikimi i butonit 'X' mund të pastrojë fushën e fjalëkalimit.",
            timezone: "Zona kohore - Një listë rënëse për të zgjedhur zonën kohore të përdoruesit.",
            defaultLanguage: "Gjuha e paracaktuar - Një listë rënëse për të zgjedhur gjuhën e preferuar.",
          },
          saveOptions: {
            cancel: "Anulo - Mbyll modalin pa ruajtur ndryshimet.",
            save: "Ruaj - Ruaj detajet e përditësuara të përdoruesit.",
          },
          imageAlt: "Formulari i krijimit të përdoruesit",
        },
      },

      // Settings - Roles
      roles: {
        title: "Rolet",
        introLine1: "Në këtë seksion, rolet jepen dhe lejet caktohen për çdo rol.",
        introLine2: "Një rol është një grup i përcaktuar përgjegjësish dhe lejesh që i caktohet një përdoruesi ose një grupi përdoruesish brenda një organizate. Ai përcakton çfarë veprimesh dhe operacionesh mund të kryejë një përdorues në një sistem ose aplikacion. Rolet ndihmojnë në menaxhimin e kontrollit të aksesit dhe në zbatimin e masave të sigurisë duke dhënë ose kufizuar privilegje sipas përgjegjësive dhe funksioneve të përdoruesit.",
        accessing: {
          title: "Aksesimi i Cilësimeve të Llogarisë dhe filtrit",
          intro: "Navigimi te seksioni i menaxhimit të Roleve:",
          steps: {
            step1: "Shkoni te menyja Cilësimet në shiritin anësor të majtë.",
            step2: "Nën <b>Menaxhimi i përdoruesve</b>, klikoni <b>Rolet</b> (kërkon <b>M4 view</b>).",
            step3: "Ky seksion ju lejon të menaxhoni të gjitha Rolet në organizatën tuaj.",
          },
          imageAlt: "Navigimi i roleve",
        },
        search: {
          title: "Kërkimi i një Roli:",
          steps: {
            step1: "Përdorni shiritin e kërkimit në krye për të gjetur një rol specifik me emër.",
            step2: "Lista do të përditësohet dinamikisht ndërsa shkruani.",
          },
          imageAlt: "Shiriti i kërkimit të roleve",
        },
        listOverview: {
          title: "Përmbledhja e Listës së Roleve",
          intro: "Lista e roleve shfaq kolonat e mëposhtme:",
          columns: {
            srNo: "Nr. - Shfaq numrin serial.",
            names: "Emrat - Shfaq emrin e rolit (p.sh., Admin, Viewer, Editor).",
            dateAdded: "Data e shtimit - Tregon datën kur u shtua.",
            actions: "Veprimet - Ofron opsione për të edituar ose fshirë rolin.",
          },
          imageAlt: "Tabela e roleve",
        },
        editing: {
          title: "Editimi i një Roli",
          steps: {
            step1: "Klikoni ikonën Edito (laps) në kolonën Veprim.",
            step2: "Modifikoni detajet e rolit si emri i rolit, roli prind, lejet etj.",
            step3: "Klikoni Ruaj për të aplikuar ndryshimet.",
          },
          imageAlt: "Edito rol",
        },
        deleting: {
          title: "Fshirja e një Roli",
          steps: {
            step1: "Klikoni ikonën Fshi (kosh) në kolonën Veprim.",
            step2: "Do të shfaqet një popup konfirmimi.",
            step3: "Klikoni Konfirmo për të hequr rolin përgjithmonë.",
          },
          imageAlt: "Fshi rol",
        },
        addRole: {
          title: "Shto Rol të Ri",
          steps: {
            step1: "Klikoni butonin + Krijo Rol (butoni i kuq në këndin e sipërm djathtas).",
            step2: "Do të shfaqet një formular ku mund të vendosni:",
          },
          formImageAlt: "Formulari i rolit",
          permissionsImageAlt: "Lejet e rolit",
          fields: {
            roleName: "Emri i rolit (Fushë teksti) - Kjo fushë është ku administratori shkruan emrin e rolit të ri.",
            parentRole: "Zgjidhni Rolin Prind (Menu rënëse) - Kjo listë lejon zgjedhjen e një roli ekzistues si model ose bazë hierarkie për rolin e ri.",
            permissionsTitle: "Seksioni i Lejeve - Shfaq kategori të ndryshme (Sondazh, Analitikë, Përdorues, Role, Ekipe) me opsione përkatëse lejesh:",
            view: "Shiko - Lejon rolin të shohë përmbajtje ose të dhëna në sistem/aplikacion. Përdoruesit me këtë leje mund të shohin dhe të aksesojnë informacionin por nuk mund të ndryshojnë apo fshijnë.",
            write: "Shkruaj - I jep përdoruesit aftësinë të krijojë, të editojë ose të modifikojë përmbajtje ose të dhëna.",
            read: "Lexo - Ofron akses vetëm për lexim. Përdoruesit me këtë leje mund të shohin përmbajtjen por nuk kanë mundësi ta ndryshojnë.",
            delete: "Fshi - Lejon rolin të fshijë përmbajtje ose të dhëna nga sistemi ose aplikacioni.",
            checkbox: "Çdo leje paraqitet si një kuti zgjedhjeje që mund të shënohet ose hiqet.",
            parentRolesNote: "Për të zgjedhur rolin prind, janë vetëm tre role të përcaktuara: Admin, Executor dhe Editor. Roli Admin ka të gjitha të drejtat dhe lejet dhe nuk mund të editohet ose fshihet. Ai mban nivelin më të lartë të autoritetit dhe mund të menaxhojë role të tjera.",
            permissionsNote: "Sipas nevojave, roli mund të caktohet me lejet e dëshiruara për sondazh, analitikë, përdorues, role dhe ekipe.",
          },
          saveAction: "Klikoni Ruaj për të krijuar rolin.",
          saveOptions: {
            cancel: "Anulo - Mbyll modalin pa ruajtur ndryshimet.",
            save: "Ruaj - Ruan detajet e përditësuara të rolit.",
          },
        },
      },

      // Settings - Teams
      teams: {
        title: "Ekipet",
        introLine1: "Ekipet janë një mënyrë për të organizuar grupe përdoruesish brenda një organizate. Çdo ekip mund të ketë emrin e vet unik dhe përbëhet nga role të caktuara për secilin përdorues. Është e mundur që ekipe të ndryshme të kenë të njëjtin përdorues me role të ndryshme, dhe disa ekipe mund të kenë të njëjtin emër. Në këtë rast të gjitha lejet bashkohen për atë emër.",
        introLine2: "Ekipet janë të dukshme vetëm për përdoruesit që kanë krijuar ose janë pjesë e një ekipi specifik. Krijuesi i ekipit ka mundësinë të menaxhojë anëtarët duke i shtuar, edituar ose fshirë. Megjithatë, ai nuk mund të heqë ose fshijë emrin e tij nga ekipi.",
        introLine3: "Ekipet ofrojnë një qasje të strukturuar për bashkëpunim dhe u lejojnë përdoruesve të punojnë së bashku në projekte ose detyra specifike. Ato ndihmojnë në përmirësimin e komunikimit, caktimin e përgjegjësive dhe lehtësimin e punës efikase në ekip.",
        accessing: {
          title: "Aksesimi i Cilësimeve të Llogarisë dhe filtrit",
          intro: "Navigimi te seksioni i Ekipeve:",
          steps: {
            step1: "Shkoni te menyja Cilësimet në shiritin anësor të majtë.",
            step2: "Nën <b>Menaxhimi i përdoruesve</b>, klikoni <b>Ekipet</b> (kërkon <b>M5 view</b>).",
            step3: "Ky seksion ju lejon të menaxhoni të gjitha Ekipet në organizatën tuaj.",
          },
          imageAlt: "Navigimi i ekipeve",
        },
        search: {
          title: "Kërkimi i një Ekipi:",
          steps: {
            step1: "Përdorni shiritin e kërkimit në krye për të gjetur një ekip specifik me emër.",
            step2: "Lista do të përditësohet dinamikisht ndërsa shkruani.",
          },
          imageAlt: "Shiriti i kërkimit të ekipeve",
        },
        sort: {
          title: "Filtro renditjeje:",
          steps: {
            step1: "Filtro renditjeje e disponueshme për akses të lehtë në të dhëna.",
            step2: "Ka dy opsione për renditje alfabetike në rritje (A-Z) ose zbritje (Z-A).",
          },
          imageAlt: "Filtro renditjeje",
        },
        listOverview: {
          title: "Përmbledhja e Listës së Ekipeve",
          intro: "Lista e ekipeve shfaq kolonat e mëposhtme:",
          columns: {
            teamName: "Emri i ekipit - Shfaq emrin e ekipit.",
            member: "Anëtar - Shfaq emrin e rolit (p.sh., Admin, Viewer, Editor).",
            actions: "Veprimet - Ofron opsione për të edituar, fshirë ose dalë nga ekipi.",
          },
          imageAlt: "Tabela e ekipeve",
        },
        editing: {
          title: "Editimi i një Ekipi",
          intro: "Ndërfaqja Edito Ekipin lejon administratorët të modifikojnë një ekip ekzistues duke përditësuar emrin, duke shtuar anëtarë të rinj me role specifike dhe duke menaxhuar anëtarët ekzistues.",
          steps: {
            step1: "Klikoni ikonën Edito (laps) në kolonën Veprim.",
            step2: "Emri i ekipit - Fusha e inputit shfaq emrin aktual të ekipit dhe lejon editimin.",
            step3: "Butonat Ruaj dhe Anulo (Emri i ekipit).",
            step4: "Ruaj - Ruan emrin e përditësuar të ekipit.",
            step5: "Anulo - Hedh poshtë çdo ndryshim të bërë në emrin e ekipit.",
            step6: "Seksioni Shto Anëtar",
            step7: "Shto Anëtar (Menu rënëse) - Lejon zgjedhjen e një përdoruesi për t'u shtuar në ekip.",
            step8: "Zgjidh Rolin (Menu rënëse) - Cakton një rol për anëtarin e zgjedhur.",
            step9: "Butoni Ruaj - Konfirmon dhe shton anëtarin e zgjedhur me rolin e caktuar.",
            step10: "Butoni Anulo - Hedh poshtë zgjedhjen e anëtarit.",
            step11: "Lista e Anëtarëve të Shtuar - Shfaq listën e përdoruesve aktualë në ekip, së bashku me rolet e tyre. Çdo rresht përmban:",
            step12: "Emri i Anëtarit - Emri i anëtarit të ekipit.",
            step13: "Menu rënëse e Rolës - Lejon përditësimin e rolit të përdoruesit direkt nga lista. Ndryshimet aplikohen pasi të ruhet.",
            step14: "Ikona Fshi - Heq përdoruesin nga ekipi kur klikohet.",
            step15: "Kjo ndërfaqe është thelbësore për menaxhimin e ekipeve, caktimin e roleve dhe kontrollin e aksesit të përdoruesve brenda sistemit.",
          },
          imageAlt: "Edito ekip",
        },
        deleting: {
          title: "Fshirja e një Përdoruesi",
          steps: {
            step1: "Klikoni ikonën Fshi (kosh) në kolonën Veprim.",
            step2: "Do të shfaqet një popup konfirmimi.",
            step3: "Klikoni Konfirmo për të hequr përdoruesin përgjithmonë.",
          },
          imageAlt: "Fshi përdoruesin",
        },
        manageMembers: {
          title: "Menaxho Anëtarët (Ikona shigjetë)",
          steps: {
            step1: "Hap ndërfaqen e menaxhimit të anëtarëve të ekipit, duke mundësuar shtimin ose heqjen e anëtarëve.",
          },
        },
        addTeam: {
          title: "Shto Ekip të Ri",
          steps: {
            step1: "Klikoni butonin + Krijo Rol (butoni i kuq në këndin e sipërm djathtas).",
            step2: "Përdoruesi ridrejtohet në faqen e shtimit të ekipit.",
            step3: "Kur vendosni emrin e ekipit dhe ruani, do të krijohet një ekip i ri.",
          },
          imageAlt: "Formulari i shtimit të ekipit",
        },
      },

      // Common elements
      common: {
        note: "Shënim:",
        steps: "Hapat:",
        whenToUse: "Kur të përdoret:",
        safetyFeatures: "Veçoritë e Sigurisë:",
        imageAlt: "Pamja e ekranit",
      },
    },
  },
};

export default docTranslations;

